/* Audit fix 2026-09-07 — designer funnel events (attempt → success → selected → cart).
   Pushed to dataLayer (GA4 via GTM/Shopify), gtag when present, and Shopify analytics. */
window.ftTrack = window.ftTrack || function (name, params) {
  try {
    const p = Object.assign({ event_category: "ai_designer" }, params || {});
    (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, p));
    if (typeof window.gtag === "function") window.gtag("event", name, p);
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === "function") window.Shopify.analytics.publish(name, p);
  } catch (_) {}
};
/**
 * AI Designer — Phase 2 (chat thread + image references + iteration)
 *
 * State the JS owns:
 *   - conversation: array of turns ({ role, text, images, imageUrl, prompt, mode })
 *   - pendingFiles: File[] selected via the attach button, not yet sent
 *   - lastImageUrl: most recent assistant image URL — sent as previousImageUrl on
 *                   the next turn so the model auto-iterates on it.
 *
 * Networking:
 *   - POSTs multipart/form-data to {worker}/api/generate
 *   - Fields: prompt, turnstileToken, previousImageUrl?, references[]?
 *   - Response: { url, prompt, mode }
 */
(function () {
  const root = document.querySelector(".ai-chat");
  if (!root) return;

  const workerUrl = (root.dataset.workerUrl || "").replace(/\/$/, "");
  const turnstileSiteKey = root.dataset.turnstileSitekey || "";
  const defaultVariantId = root.dataset.defaultVariantId || "";
  const printPrefix = root.dataset.printPrefix || "";
  const teesCollectionUrl = root.dataset.teesCollectionUrl || "/collections/all";
  const myDesignsUrl = root.dataset.myDesignsUrl || "/pages/my-designs";
  const inspirationUrl = root.dataset.inspirationUrl || "/pages/inspiration";

  const form = root.querySelector("[data-ai-form]");
  const promptEl = root.querySelector("[data-ai-prompt]");
  const sendBtn = root.querySelector("[data-ai-send]");
  const attachBtn = root.querySelector("[data-ai-attach]");
  const fileInput = root.querySelector("[data-ai-file]");
  const attachmentsEl = root.querySelector("[data-ai-attachments]");
  const threadEl = root.querySelector("[data-ai-thread]");
  const emptyEl = root.querySelector("[data-ai-empty]");
  const resetBtn = root.querySelector("[data-ai-reset]");

  const TPL_USER = root.querySelector('[data-ai-template="user"]');
  const TPL_ASSISTANT = root.querySelector('[data-ai-template="assistant"]');
  const TPL_LOADING = root.querySelector('[data-ai-template="loading"]');
  const TPL_ERROR = root.querySelector('[data-ai-template="error"]');

  const MAX_REFS = 4;
  const MAX_BYTES = 10 * 1024 * 1024;
  // Accept anything the normalizeImage step has a fighting chance of
  // decoding via the browser's <img> tag + canvas. Includes iPhone HEIC
  // (Safari decodes natively, other browsers will fail at decode time
  // with a clearer message).
  const ACCEPTED = [
    "image/png", "image/jpeg", "image/jpg", "image/webp",
    "image/heic", "image/heif", "image/gif", "image/bmp",
    "image/svg+xml", "image/tiff", "image/avif"
  ];
  // File extensions to match when MIME type isn't set (some browsers/devices
  // don't populate file.type reliably for HEIC, screenshots, etc.).
  const ACCEPTED_EXT = /\.(png|jpe?g|webp|heic|heif|gif|bmp|svg|tiff?|avif)$/i;

  // === State ===
  let conversation = [];
  let pendingFiles = [];
  let lastImageUrl = null;
  let isSending = false;
  // selectedPresetPrompt: the style descriptor string appended to the
  // customer's words on send. Empty string means "None / no preset".
  let selectedPresetPrompt = "";

  // ============= Style preset chips =============
  const presetButtons = root.querySelectorAll("[data-ai-preset-id]");
  presetButtons.forEach(function (btn) {
    btn.addEventListener("click", function () {
      presetButtons.forEach(function (b) {
        b.classList.remove("ai-chat__preset--active");
      });
      btn.classList.add("ai-chat__preset--active");
      selectedPresetPrompt = btn.dataset.aiPresetPrompt || "";
    });
  });

  // ============= Turnstile =============
  let turnstileWidgetId = null;
  let pendingResolve = null;
  let pendingReject = null;

  function clearPending() { pendingResolve = null; pendingReject = null; }

  function renderTurnstileWhenReady() {
    if (!window.turnstile || !turnstileSiteKey) {
      setTimeout(renderTurnstileWhenReady, 250);
      return;
    }
    const widgetEl = root.querySelector(".cf-turnstile");
    if (!widgetEl || widgetEl.dataset.rendered === "1") return;
    turnstileWidgetId = window.turnstile.render(widgetEl, {
      sitekey: turnstileSiteKey,
      size: "invisible",
      callback: function (token) {
        if (pendingResolve) {
          const r = pendingResolve; clearPending(); r(token);
        }
      },
      "error-callback": function () {
        if (pendingReject) {
          const r = pendingReject; clearPending();
          r(new Error("Bot check failed — please refresh and try again."));
        }
      },
      "timeout-callback": function () {
        if (pendingReject) {
          const r = pendingReject; clearPending();
          r(new Error("Bot check timed out — please refresh and try again."));
        }
      },
    });
    widgetEl.dataset.rendered = "1";
  }
  renderTurnstileWhenReady();

  function executeTurnstile() {
    return new Promise(function (resolve, reject) {
      if (!window.turnstile || turnstileWidgetId === null) {
        return reject(new Error("Bot check not loaded yet — please wait a second."));
      }
      if (pendingReject) pendingReject(new Error("Replaced"));
      pendingResolve = resolve;
      pendingReject = reject;
      try { window.turnstile.reset(turnstileWidgetId); } catch (_) {}
      try { window.turnstile.execute(turnstileWidgetId); } catch (e) { clearPending(); return reject(e); }
      setTimeout(function () {
        if (pendingReject === reject) {
          clearPending();
          // Tear down the stale widget so the next call gets a fresh one.
          try {
            if (turnstileWidgetId !== null) window.turnstile.remove(turnstileWidgetId);
          } catch (_) {}
          turnstileWidgetId = null;
          const widgetEl = root.querySelector(".cf-turnstile");
          if (widgetEl) widgetEl.dataset.rendered = "";
          renderTurnstileWhenReady();
          reject(new Error("Bot check took too long — please click again."));
        }
      }, 60000);
    });
  }

  // ============= Helpers =============
  function autoGrowTextarea() {
    promptEl.style.height = "auto";
    promptEl.style.height = Math.min(promptEl.scrollHeight, 200) + "px";
  }

  function readAsDataURL(file) {
    return new Promise(function (resolve, reject) {
      const reader = new FileReader();
      reader.onload = function () { resolve(reader.result); };
      reader.onerror = function () { reject(new Error("Couldn't read " + file.name)); };
      reader.readAsDataURL(file);
    });
  }

  /**
   * POST a FormData payload via XMLHttpRequest.
   *
   * Why not fetch? This storefront has 4 third-party scripts (HulkApps, Avada,
   * shop_events_listener, web-pixels) that monkey-patch window.fetch. One of
   * them throws "Unsafe attempt to load URL" inside its interceptor, causing
   * every fetch on the page to fail with TypeError: Failed to fetch. XHR isn't
   * wrapped by any of them.
   */
  function postFormDataXHR(url, formData, onProgress) {
    return new Promise(function (resolve, reject) {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      xhr.responseType = "json";
      xhr.timeout = 5 * 60 * 1000; // 5 minute hard ceiling
      if (onProgress && xhr.upload) {
        xhr.upload.onprogress = function (e) {
          if (e.lengthComputable) onProgress(e.loaded / e.total);
        };
      }
      xhr.onload = function () {
        const status = xhr.status;
        const body = xhr.response;
        if (status >= 200 && status < 300) {
          resolve(body);
        } else {
          const msg = (body && body.error) || ("Server returned " + status);
          const err = new Error(msg);
          err.status = status;
          reject(err);
        }
      };
      xhr.onerror = function () {
        reject(new Error("Network error during upload"));
      };
      xhr.ontimeout = function () {
        reject(new Error("Upload timed out — try again"));
      };
      xhr.onabort = function () {
        reject(new Error("Upload was canceled"));
      };
      xhr.send(formData);
    });
  }

  /**
   * Normalize an image to a clean PNG before upload.
   *
   * iPhone photos and other camera files often include problematic EXIF
   * orientation, embedded color profiles, or are HEIC files masquerading as
   * JPEGs. OpenAI's image edit endpoint can reject these as "invalid_image_file"
   * even though they look fine in browsers.
   *
   * Repaints the image to a canvas and re-exports as a vanilla 8-bit PNG, with
   * the longest side capped at 2048px to keep payload size sane.
   */
  /**
   * Lazy-load the heic2any library from a CDN. Cached on window.heic2any
   * after first load. Only fetched when a customer actually uploads a
   * HEIC file — no cost for everyone else.
   */
  let _heic2anyLoadPromise = null;
  function ensureHeic2any() {
    if (window.heic2any) return Promise.resolve(window.heic2any);
    if (_heic2anyLoadPromise) return _heic2anyLoadPromise;
    _heic2anyLoadPromise = new Promise(function (resolve, reject) {
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js";
      script.onload = function () {
        if (window.heic2any) resolve(window.heic2any);
        else reject(new Error("HEIC converter loaded but not available"));
      };
      script.onerror = function () {
        reject(new Error("Couldn't load the HEIC converter (network issue?). Try uploading a JPEG instead."));
      };
      document.head.appendChild(script);
    });
    return _heic2anyLoadPromise;
  }

  /**
   * Convert a HEIC/HEIF file to JPEG using the heic2any library. Returns
   * a fresh File object the rest of the pipeline can handle.
   */
  async function convertHeicToJpeg(file) {
    const heic2any = await ensureHeic2any();
    const out = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.9
    });
    // heic2any can return a single blob or an array (for multi-image HEICs)
    const blob = Array.isArray(out) ? out[0] : out;
    const baseName = (file.name || "photo").replace(/\.(heic|heif)$/i, "");
    return new File([blob], baseName + ".jpg", { type: "image/jpeg" });
  }

  async function normalizeImage(file) {
    // 1024 matches OpenAI's working size, keeps uploads small and fast.
    const MAX_DIM = 1024;

    // HEIC files: convert to JPEG first (works in every browser, including
    // Chrome/Firefox/Edge which can't decode HEIC natively). Adds a few
    // seconds for the conversion but means iPhone users don't have to do
    // anything special.
    const isHeic = /\.(heic|heif)$/i.test(file.name || "") ||
                   /heic|heif/i.test(file.type || "");
    let workingFile = file;
    if (isHeic) {
      try {
        workingFile = await convertHeicToJpeg(file);
      } catch (e) {
        throw new Error(
          "Couldn't convert your iPhone photo (HEIC). " +
          (e && e.message ? e.message : "Try uploading a JPEG version instead.")
        );
      }
    }

    const dataUrl = await readAsDataURL(workingFile);
    const img = await new Promise(function (resolve, reject) {
      const i = new Image();
      i.onload = function () { resolve(i); };
      i.onerror = function () {
        // HEIC was already handled up front, so anything that fails here
        // is a corrupt/unsupported file. Give a friendly path forward.
        reject(new Error(
          "Couldn't read '" + (file.name || "your image") + "'. " +
          "Try saving it as a PNG or JPEG and uploading that version."
        ));
      };
      i.src = dataUrl;
    });

    let w = img.naturalWidth;
    let h = img.naturalHeight;
    if (!w || !h) throw new Error(file.name + " has zero dimensions.");

    if (Math.max(w, h) > MAX_DIM) {
      const scale = MAX_DIM / Math.max(w, h);
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported in this browser.");
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise(function (resolve, reject) {
      canvas.toBlob(
        function (b) {
          if (b) resolve(b);
          else reject(new Error("Couldn't normalize " + file.name + "."));
        },
        "image/png",
      );
    });

    // Pull a clean filename ending in .png
    const baseName = file.name.replace(/\.[^.]+$/, "") || "reference";
    return new File([blob], baseName + ".png", { type: "image/png" });
  }

  /**
   * Make a tiny thumbnail data URL (~168x168) for chat display. Vastly smaller
   * than the original — usually 5–20 KB — so the chat thread can hold dozens
   * of bubbles without running the page out of memory.
   */
  async function makeThumbnail(file) {
    const TARGET = 168; // 84 displayed × 2 for retina
    const dataUrl = await readAsDataURL(file);
    const img = await new Promise(function (resolve, reject) {
      const i = new Image();
      i.onload = function () { resolve(i); };
      i.onerror = function () { reject(new Error("Couldn't decode " + file.name)); };
      i.src = dataUrl;
    });
    let w = img.naturalWidth || TARGET;
    let h = img.naturalHeight || TARGET;
    const scale = Math.min(1, TARGET / Math.max(w, h));
    w = Math.max(1, Math.round(w * scale));
    h = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return dataUrl; // fallback
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.7);
  }

  function fileToFriendlyError(file) {
    // Accept by MIME type OR by file extension. iPhone HEIC files sometimes
    // arrive with no MIME set; some Android browsers don't tag screenshots
    // either. Letting either signal pass through means we attempt the
    // decode and surface a clearer error from the canvas step if it fails.
    const validType = ACCEPTED.includes((file.type || "").toLowerCase());
    const validExt = ACCEPTED_EXT.test(file.name || "");
    if (!validType && !validExt) {
      return "'" + (file.name || "file") + "' doesn't look like an image we can use. Try a PNG, JPEG, or HEIC.";
    }
    if (file.size > MAX_BYTES) {
      return "'" + file.name + "' is too big (max " + (MAX_BYTES / 1024 / 1024) + " MB). Try a smaller version.";
    }
    return null;
  }

  function showEmptyState(show) {
    if (show) {
      emptyEl.hidden = false;
      threadEl.hidden = true;
      resetBtn.hidden = true;
    } else {
      emptyEl.hidden = true;
      threadEl.hidden = false;
      resetBtn.hidden = false;
    }
  }

  function scrollToBottom() {
    // Smooth-scroll the page so the latest bubble is in view.
    requestAnimationFrame(function () {
      const lastChild = threadEl.lastElementChild;
      if (lastChild) lastChild.scrollIntoView({ behavior: "smooth", block: "end" });
    });
  }

  // ============= Render: chat bubbles =============
  function renderUserTurn(turn) {
    const node = TPL_USER.content.firstElementChild.cloneNode(true);
    const textEl = node.querySelector("[data-text]");
    const imagesEl = node.querySelector("[data-images]");
    if (turn.text) {
      textEl.textContent = turn.text;
    } else {
      textEl.remove();
    }
    if (turn.images && turn.images.length) {
      imagesEl.hidden = false;
      turn.images.forEach(function (src) {
        const img = document.createElement("img");
        // Lock dimensions before src to avoid full-size flash.
        img.width = 84;
        img.height = 84;
        img.style.width = "84px";
        img.style.height = "84px";
        img.style.objectFit = "cover";
        img.loading = "lazy";
        img.decoding = "async";
        img.src = src;
        img.alt = "Reference image";
        imagesEl.appendChild(img);
      });
    }
    threadEl.appendChild(node);
    scrollToBottom();
  }

  function renderAssistantTurn(turn) {
    const node = TPL_ASSISTANT.content.firstElementChild.cloneNode(true);
    const img = node.querySelector("[data-image]");
    img.loading = "lazy";
    img.decoding = "async";
    img.src = turn.imageUrl;
    img.alt = "AI design: " + (turn.prompt || "");

    // Wire up the print-ready download button (single size — worker uses default).
    node.querySelectorAll('[data-action="download"]').forEach(function (btn) {
      btn.addEventListener("click", function () {
        downloadPrintReady(turn, btn);
      });
    });

    // Wire up the share button (Web Share API + clipboard fallback).
    node.querySelectorAll('[data-action="share"]').forEach(function (btn) {
      btn.addEventListener("click", function () {
        if (!window.AIDesignShare) return;
        const original = btn.innerHTML;
        btn.disabled = true;
        window.AIDesignShare.share({
          url: turn.imageUrl,
          prompt: turn.prompt,
        }).finally(function () {
          btn.innerHTML = original;
          btn.disabled = false;
        });
      });
    });

    // Shop tees → show a quick workflow popup, then navigate. Stashes a flag
    // so the destination product page can highlight the "Use my AI design"
    // link when the customer arrives from chat.
    //
    // Wizard handoff: if the order wizard is enabled for this visitor
    // (window.OrderWizard.gate.allowed), the wizard's document-level capture
    // handler intercepts the click first. We still tag the button with the
    // design URL + prompt so the wizard can read them reliably (instead of
    // walking the DOM to guess), and we bail out of the popup-showing path
    // when the wizard is going to take over.
    const shopBtn = node.querySelector('[data-action="shop-tees"]');
    if (shopBtn) {
      shopBtn.setAttribute("href", teesCollectionUrl);
      shopBtn.setAttribute("data-design-url", turn.imageUrl || "");
      shopBtn.setAttribute("data-design-prompt", turn.prompt || "");
      shopBtn.addEventListener("click", function (e) {
        // Defer to the order wizard if it's active for this visitor.
        if (window.OrderWizard
            && window.OrderWizard.gate
            && window.OrderWizard.gate.allowed) {
          // Wizard's capture handler already ran preventDefault. Don't show
          // the legacy workflow popup.
          return;
        }
        e.preventDefault();
        showShopWorkflowPopup({
          designUrl: turn.imageUrl,
          designPrompt: turn.prompt,
          destination: teesCollectionUrl,
        });
      });
    }

    // "Use this design" — the primary action on the embedded generator
    // (snippets/ft-ai-generator.liquid, toggled open from a product page's
    // upload block via assets/ft-mockup-builder.js). Deliberately a
    // different data-action than "shop-tees": the wizard's document-level
    // capture handler only intercepts [data-action="shop-tees"], so this
    // one reaches this handler untouched instead of getting swept into the
    // wizard modal — the whole point of embedding it in place is to NOT
    // leave the page. Hands the image straight to ft-mockup-builder.js's
    // own upload pipeline (background removal, crop, DPI check, re-upload)
    // via the hook it exposes, so nothing about processing an AI-generated
    // image is duplicated here.
    const useOnPageBtn = node.querySelector('[data-action="use-on-page"]');
    if (useOnPageBtn) {
      useOnPageBtn.addEventListener("click", function () {
        const mbRoot = document.querySelector("[data-ft-mockup-builder]");
        const sideId = mbRoot && mbRoot.__ftAiGenerateTarget && mbRoot.__ftAiGenerateTarget();
        if (!mbRoot || !mbRoot.__ftAttachGeneratedArt || !sideId) {
          renderError("Couldn't find where to attach this design — try reopening “Generate artwork with AI” from the upload area.");
          return;
        }
        const original = useOnPageBtn.textContent;
        useOnPageBtn.disabled = true;
        useOnPageBtn.textContent = "Adding…";
        mbRoot.__ftAttachGeneratedArt(sideId, turn.imageUrl, "ai-design.png", turn.prompt)
          .catch(function (err) {
            console.error("[ai-designer] attach to page failed", err);
            renderError("Couldn't attach that design — please try again.");
          })
          .finally(function () {
            useOnPageBtn.disabled = false;
            useOnPageBtn.textContent = original;
          });
      });
    }

    // Admin-only "Add to Inspiration" button — visible only when Roland has
    // flipped himself admin via ?admin=TOKEN. Promotes this design to the
    // public showcase via the Worker.
    const promoteBtn = node.querySelector('[data-action="promote"]');
    if (promoteBtn) {
      const isAdminUser =
        !!(window.AIDesignInspiration && window.AIDesignInspiration.isAdmin());
      if (isAdminUser) {
        promoteBtn.hidden = false;
        promoteBtn.addEventListener("click", function () {
          if (!window.AIDesignInspiration) return;
          const original = promoteBtn.innerHTML;
          promoteBtn.disabled = true;
          promoteBtn.textContent = "Adding…";
          window.AIDesignInspiration
            .promote(workerUrl, { url: turn.imageUrl, prompt: turn.prompt })
            .then(function () {
              promoteBtn.classList.add("ai-msg__promote--done");
              promoteBtn.textContent = "✓ Added to Inspiration";
            })
            .catch(function (err) {
              renderError("Couldn't promote: " + (err.message || err));
              promoteBtn.innerHTML = original;
              promoteBtn.disabled = false;
            });
        });
      } else {
        // Non-admin customers get a "Submit to gallery / Get my free tee"
        // button in the same slot. Their submission lands in the moderation
        // queue for Roland to approve before it goes public.
        // attachSubmitButton may return null if the promo's been claimed —
        // in that case we just remove the slot entirely.
        if (window.AIDesignInspiration && window.AIDesignInspiration.attachSubmitButton) {
          const submitBtn = window.AIDesignInspiration.attachSubmitButton({
            workerUrl: workerUrl,
            url: turn.imageUrl,
            prompt: turn.prompt,
          });
          if (submitBtn) {
            // Reuse the .ai-msg__promote slot's parent so the layout stays clean.
            promoteBtn.parentNode.insertBefore(submitBtn, promoteBtn);
          }
        }
        promoteBtn.remove();
      }
    }

    threadEl.appendChild(node);
    scrollToBottom();
    return node;
  }

  function renderLoadingBubble(label) {
    const node = TPL_LOADING.content.firstElementChild.cloneNode(true);
    const txt = node.querySelector("[data-loading-text]");
    if (label) txt.textContent = label;
    threadEl.appendChild(node);
    scrollToBottom();
    return node;
  }

  function renderError(message) {
    const node = TPL_ERROR.content.firstElementChild.cloneNode(true);
    node.querySelector("[data-text]").textContent = message;
    threadEl.appendChild(node);
    scrollToBottom();
  }

  // ============= Attachments =============
  /**
   * Inject CSS that overrides the base .ai-chip styles. The base styles
   * make the chip very small (28px thumb) which customers were missing.
   * These overrides make the chip a prominent "attached" card with
   * brand-orange highlight + status text + bigger thumbnail.
   */
  function injectChipStyles() {
    if (document.getElementById("ai-chip-prominent-styles")) return;
    const s = document.createElement("style");
    s.id = "ai-chip-prominent-styles";
    s.textContent =
      ".ai-chip{background:#fff7ed!important;border:2px solid #ff6b00!important;padding:8px 10px!important;max-width:280px!important;gap:10px!important;align-items:center}" +
      ".ai-chip img{width:48px!important;height:48px!important;max-width:48px!important;max-height:48px!important;border-radius:8px!important;border:1px solid rgba(0,0,0,.06)}" +
      ".ai-chip__body{display:flex;flex-direction:column;gap:2px;min-width:0;flex:1}" +
      ".ai-chip__name{font-weight:700!important;color:#1a1a1a!important;font-size:13px!important;max-width:170px!important;line-height:1.2}" +
      ".ai-chip__status{font-size:11px;font-weight:700;color:#ff6b00;text-transform:uppercase;letter-spacing:.04em;display:flex;align-items:center;gap:4px}" +
      ".ai-chip__remove{font-size:18px!important;color:#1a1a1a!important;padding:2px 6px!important;border-radius:4px!important;background:rgba(0,0,0,.06)!important}" +
      ".ai-chip__remove:hover{background:rgba(0,0,0,.12)!important}" +
      "@keyframes ai-chip-pop{0%{transform:scale(.85);opacity:0}60%{transform:scale(1.06)}100%{transform:scale(1);opacity:1}}" +
      ".ai-chip{animation:ai-chip-pop .3s ease-out}" +
      ".ai-attach-toast{position:fixed;left:50%;bottom:120px;transform:translateX(-50%);z-index:99999;background:#000;color:#fff;padding:10px 18px;border-radius:999px;font-size:14px;font-weight:700;box-shadow:0 8px 24px rgba(0,0,0,.3);display:flex;align-items:center;gap:8px;font-family:inherit;pointer-events:none;animation:ai-toast-in .3s ease-out}" +
      ".ai-attach-toast.is-out{animation:ai-toast-out .3s ease-in forwards}" +
      "@keyframes ai-toast-in{from{transform:translate(-50%,20px);opacity:0}to{transform:translate(-50%,0);opacity:1}}" +
      "@keyframes ai-toast-out{from{transform:translate(-50%,0);opacity:1}to{transform:translate(-50%,20px);opacity:0}}";
    document.head.appendChild(s);
  }
  injectChipStyles();

  /**
   * Show a temporary "✓ X attached" toast at the bottom of the screen
   * so customers see clear confirmation their file is loaded — even if
   * the chip itself is below the fold.
   */
  function showAttachToast(message) {
    const existing = document.querySelector(".ai-attach-toast");
    if (existing) existing.remove();
    const toast = document.createElement("div");
    toast.className = "ai-attach-toast";
    toast.innerHTML = "✓ " + message;
    document.body.appendChild(toast);
    setTimeout(function () {
      toast.classList.add("is-out");
      setTimeout(function () { toast.remove(); }, 350);
    }, 2200);
  }

  function renderAttachments() {
    attachmentsEl.innerHTML = "";
    if (!pendingFiles.length) {
      attachmentsEl.hidden = true;
      return;
    }
    attachmentsEl.hidden = false;
    pendingFiles.forEach(function (file, idx) {
      const chip = document.createElement("div");
      chip.className = "ai-chip";
      const img = document.createElement("img");
      img.alt = file.name;
      img.width = 48;
      img.height = 48;
      img.style.width = "48px";
      img.style.height = "48px";
      img.style.objectFit = "cover";
      img.loading = "lazy";
      img.decoding = "async";
      readAsDataURL(file).then(function (dataUrl) { img.src = dataUrl; });
      chip.appendChild(img);

      // Two-line body: filename + "READY TO USE" status
      const body = document.createElement("div");
      body.className = "ai-chip__body";
      const caption = document.createElement("span");
      caption.className = "ai-chip__name";
      caption.textContent = file.name;
      body.appendChild(caption);
      const status = document.createElement("span");
      status.className = "ai-chip__status";
      status.textContent = "✓ Attached — ready to use";
      body.appendChild(status);
      chip.appendChild(body);

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "ai-chip__remove";
      remove.setAttribute("aria-label", "Remove " + file.name);
      remove.textContent = "×";
      remove.addEventListener("click", function () {
        pendingFiles.splice(idx, 1);
        renderAttachments();
      });
      chip.appendChild(remove);

      attachmentsEl.appendChild(chip);
    });
  }

  // Called from assets/ft-mockup-builder.js when the customer opens this
  // panel for a print location that isn't the one they already designed —
  // attaches the finished design from the other location as a reference
  // image so a "matching" prompt actually has something to match against,
  // instead of the customer having no visible next step (Roland's
  // 2026-08-31 feedback). Silently no-ops past MAX_REFS rather than
  // bumping something the customer attached themselves.
  root.__ftAiAddReferenceFile = function (file, toastMessage) {
    if (pendingFiles.length >= MAX_REFS) return false;
    pendingFiles.push(file);
    renderAttachments();
    if (toastMessage) showAttachToast(toastMessage);
    return true;
  };

  attachBtn.addEventListener("click", function () { fileInput.click(); });

  // Expand the file picker's accept list to include iPhone HEIC photos
  // and other common formats. The picker on iOS still lets users pick any
  // photo regardless, but this widens it for desktop pickers and gives
  // Android browsers more options too.
  fileInput.accept = "image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif,image/gif,image/bmp,.heic,.heif,.png,.jpg,.jpeg,.webp,.gif,.bmp";

  fileInput.addEventListener("change", function () {
    const incoming = Array.from(fileInput.files || []);
    fileInput.value = ""; // reset so picking the same file again still fires
    let added = 0;
    for (const f of incoming) {
      if (pendingFiles.length >= MAX_REFS) {
        renderError("You can attach up to " + MAX_REFS + " images per message.");
        break;
      }
      const err = fileToFriendlyError(f);
      if (err) { renderError(err); continue; }
      pendingFiles.push(f);
      added++;
    }
    renderAttachments();
    if (added > 0) {
      const msg = added === 1
        ? incoming[incoming.length - 1].name + " attached"
        : added + " photos attached";
      showAttachToast(msg);
    }
  });

  // Drag-and-drop onto the composer
  ["dragover", "dragenter"].forEach(function (ev) {
    form.addEventListener(ev, function (e) {
      e.preventDefault();
      form.classList.add("is-dragging");
    });
  });
  ["dragleave", "drop"].forEach(function (ev) {
    form.addEventListener(ev, function (e) {
      e.preventDefault();
      form.classList.remove("is-dragging");
    });
  });
  form.addEventListener("drop", function (e) {
    const files = Array.from(e.dataTransfer ? e.dataTransfer.files : []);
    let added = 0;
    let lastName = "";
    for (const f of files) {
      if (pendingFiles.length >= MAX_REFS) break;
      const err = fileToFriendlyError(f);
      if (err) { renderError(err); continue; }
      pendingFiles.push(f);
      added++;
      lastName = f.name;
    }
    renderAttachments();
    if (added > 0) {
      showAttachToast(added === 1 ? lastName + " attached" : added + " photos attached");
    }
  });

  // ============= Suggestions =============
  root.querySelectorAll("[data-ai-suggestion]").forEach(function (btn) {
    btn.addEventListener("click", function () {
      promptEl.value = btn.textContent.trim();
      autoGrowTextarea();
      promptEl.focus();
    });
  });

  // ============= Send =============
  promptEl.addEventListener("input", autoGrowTextarea);
  promptEl.addEventListener("keydown", function (e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      form.dispatchEvent(new Event("submit", { cancelable: true }));
    }
  });

  form.addEventListener("submit", async function (e) {
    e.preventDefault();
    if (isSending) return;

    const prompt = (promptEl.value || "").trim();
    if (!prompt) return;
    if (!workerUrl) {
      renderError("This page isn't configured yet — set the Worker URL in the section settings.");
      return;
    }

    isSending = true;
    sendBtn.disabled = true;
    window.ftTrack("ai_generate_attempt", { prompt_length: prompt.length });

    // Capture and clear inputs
    const files = pendingFiles.slice();
    pendingFiles = [];
    renderAttachments();
    promptEl.value = "";
    autoGrowTextarea();

    showEmptyState(false);

    // Render user turn — generate small thumbnails (rather than holding the
    // full-size data URLs in memory). Cuts memory use by ~95% per attachment
    // and prevents the page from crashing after several iterations.
    let thumbDataUrls = [];
    try {
      thumbDataUrls = await Promise.all(files.map(makeThumbnail));
    } catch (_) {
      thumbDataUrls = [];
    }
    // We only keep the prompt text in conversation history. Image data lives
    // on the rendered <img> elements only; once the bubble is rendered we
    // don't need to retain anything in JS.
    conversation.push({ role: "user", text: prompt });
    renderUserTurn({ role: "user", text: prompt, images: thumbDataUrls });
    thumbDataUrls = null; // help GC release the strings sooner

    // Render loading bubble
    const isEdit = !!lastImageUrl || files.length > 0;
    const loadingNode = renderLoadingBubble(
      isEdit ? "Refining your design…" : "Drawing your design…",
    );

    try {
      // Normalize uploads to clean PNGs (handles iPhone HEIC/EXIF/color
      // profile issues and caps file size).
      let normalizedFiles = [];
      if (files.length) {
        try {
          normalizedFiles = await Promise.all(files.map(normalizeImage));
        } catch (normErr) {
          throw new Error(normErr.message || "Couldn't read your reference images.");
        }
      }

      const turnstileToken = await executeTurnstile();

      // Build the final prompt: customer's words + selected style preset (if any).
      // The print prefix is added server-side by the Worker.
      const finalPrompt = selectedPresetPrompt
        ? prompt + ", in " + selectedPresetPrompt
        : prompt;

      const fd = new FormData();
      fd.append("prompt", finalPrompt);
      fd.append("turnstileToken", turnstileToken);
      if (printPrefix) fd.append("printPrefix", printPrefix);
      if (lastImageUrl) fd.append("previousImageUrl", lastImageUrl);
      normalizedFiles.forEach(function (file) {
        fd.append("references", file, file.name);
      });

      // XHR (not fetch) — see postFormDataXHR comments. Updates loading text
      // during upload so the user knows things are happening.
      const loadingTextEl = loadingNode.querySelector("[data-loading-text]");
      const data = await postFormDataXHR(
        workerUrl + "/api/generate",
        fd,
        function (frac) {
          if (loadingTextEl) {
            const pct = Math.round(frac * 100);
            loadingTextEl.textContent =
              pct < 100 ? "Uploading… " + pct + "%" : (isEdit ? "Refining your design…" : "Drawing your design…");
          }
        },
      );

      // Replace the loading bubble with the actual assistant message
      loadingNode.remove();
      const assistantTurn = {
        role: "assistant",
        imageUrl: data.url,
        prompt: data.prompt || prompt,
        mode: data.mode || "generate",
      };
      conversation.push(assistantTurn);
      window.ftTrack("ai_generate_success");
      renderAssistantTurn(assistantTurn);

      lastImageUrl = data.url;

      // Auto-save to the persistent design library so the customer can come
      // back to /pages/my-designs at any time. Thumbnail is generated async;
      // if it fails we still save with no thumbnail (will fall back to the
      // R2 URL when rendered).
      saveToLibrary(assistantTurn).catch(function (e) {
        console.warn("Library save failed", e);
      });
    } catch (err) {
      console.error(err);
      loadingNode.remove();
      window.ftTrack("ai_generate_error", { message: String((err && err.message) || err).slice(0, 120) });
      renderError(err.message || "We couldn't make that one — try a different prompt.");
    } finally {
      isSending = false;
      sendBtn.disabled = false;
      promptEl.focus();
    }
  });

  // ============= Reset =============
  resetBtn.addEventListener("click", function () {
    if (!confirm("Start a new conversation? Your current designs will be cleared.")) return;
    conversation = [];
    lastImageUrl = null;
    pendingFiles = [];
    threadEl.innerHTML = "";
    renderAttachments();
    showEmptyState(true);
    promptEl.focus();
  });

  // ============= Library save =============
  /**
   * Save a generated design to the persistent library. Generates a small JPEG
   * thumbnail from the R2 draft URL so the gallery page can render quickly
   * without re-downloading 1MB PNGs.
   */
  async function saveToLibrary(turn) {
    if (!window.AIDesignLibrary) return;
    let thumbnail = "";
    try {
      thumbnail = await makeThumbnailFromUrl(turn.imageUrl);
    } catch (e) {
      console.warn("Thumbnail generation failed", e);
    }
    window.AIDesignLibrary.save({
      prompt: turn.prompt,
      draftUrl: turn.imageUrl,
      thumbnail: thumbnail,
    });
    // Now that the customer has a design worth saving, prompt for email so
    // they can come back to it from any device. Banner gates itself on
    // dismissed/subscribed state — safe to call after every save.
    try { maybeShowEmailBanner(); } catch (e) { console.warn("banner", e); }
  }

  /** Fetch a remote image and produce a small base64 JPEG thumbnail. */
  async function makeThumbnailFromUrl(url) {
    const TARGET = 200;
    if (!window.AIDesignFetch) throw new Error("Library not loaded");
    const blob = await window.AIDesignFetch.fetchBlob(url);
    const objectUrl = URL.createObjectURL(blob);
    try {
      const img = await new Promise(function (resolve, reject) {
        const i = new Image();
        i.onload = function () { resolve(i); };
        i.onerror = function () { reject(new Error("decode failed")); };
        i.src = objectUrl;
      });
      let w = img.naturalWidth || TARGET;
      let h = img.naturalHeight || TARGET;
      const scale = Math.min(1, TARGET / Math.max(w, h));
      w = Math.max(1, Math.round(w * scale));
      h = Math.max(1, Math.round(h * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return "";
      ctx.drawImage(img, 0, 0, w, h);
      return canvas.toDataURL("image/jpeg", 0.7);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  // ============= Print-ready download =============
  /**
   * Regenerate a chosen draft design at print quality (2K or 4K, transparent
   * PNG with quality=high), then trigger a browser download of the resulting
   * file. The customer takes that file to whichever product page accepts a
   * file upload.
   */
  /**
   * Download the existing draft URL directly — no Worker call, no regen.
   * Uses XHR-based blob fetch to bypass Shopify's pixel-script fetch wrappers.
   */
  async function downloadPrintReady(turn, btn) {
    btn.disabled = true;
    const originalLabel = btn.textContent;
    btn.textContent = "Downloading…";

    try {
      if (!window.AIDesignFetch) {
        throw new Error("Library not loaded yet — refresh and try again.");
      }
      const blob = await window.AIDesignFetch.fetchBlob(turn.imageUrl);
      const objectUrl = URL.createObjectURL(blob);

      const filename =
        "ai-design-" +
        (turn.prompt || "design").slice(0, 32).replace(/[^a-z0-9-_]+/gi, "-") +
        ".png";

      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(function () { URL.revokeObjectURL(objectUrl); }, 30000);

      btn.textContent = "Downloaded ✓";
      setTimeout(function () {
        btn.textContent = originalLabel;
        btn.disabled = false;
      }, 2500);
    } catch (err) {
      console.error(err);
      renderError(err.message || "Couldn't download.");
      btn.textContent = originalLabel;
      btn.disabled = false;
    }
  }

  // ============= Shop workflow popup =============
  /**
   * Quick 3-step "here's what's next" overlay shown when the customer clicks
   * "Put it on a tee →" from a chat design. Sets a sessionStorage flag so
   * the product page they land on can highlight the AI design link.
   */
  function showShopWorkflowPopup(opts) {
    opts = opts || {};
    const designUrl = opts.designUrl || "";
    const designPrompt = opts.designPrompt || "";
    const destination = opts.destination || "/collections/all";

    // Stash the design info — product page reads this on load and pre-attaches.
    try {
      sessionStorage.setItem("ai_design_pending", JSON.stringify({
        url: designUrl,
        prompt: designPrompt,
        ts: Date.now(),
      }));
    } catch (_) {}

    // Build the overlay
    const overlay = document.createElement("div");
    overlay.className = "ai-shop-flow";
    overlay.innerHTML =
      '<div class="ai-shop-flow__sheet" role="dialog" aria-modal="true">' +
        '<button type="button" class="ai-shop-flow__close" aria-label="Close" data-close>×</button>' +
        '<h2 class="ai-shop-flow__title">Here\'s how to use your design</h2>' +
        '<ol class="ai-shop-flow__steps">' +
          '<li><span class="ai-shop-flow__num">1</span><span><strong>Pick a blank.</strong> Tee, hoodie, singlet, kids tee — whatever you want to print on.</span></li>' +
          '<li><span class="ai-shop-flow__num">2</span><span>On the product page, click <strong>★ Use my AI design</strong> — we\'ll highlight it for you.</span></li>' +
          '<li><span class="ai-shop-flow__num">3</span><span>Pick your size, hit Add to Cart, done.</span></li>' +
        '</ol>' +
        '<button type="button" class="ai-shop-flow__cta" data-go>Got it — let\'s go →</button>' +
      '</div>';

    document.body.appendChild(overlay);
    document.body.classList.add("ai-shop-flow-open");

    function dismiss(navigate) {
      overlay.remove();
      document.body.classList.remove("ai-shop-flow-open");
      document.removeEventListener("keydown", esc);
      if (navigate) window.location.href = destination;
    }
    function esc(e) { if (e.key === "Escape") dismiss(false); }

    overlay.querySelector("[data-close]").addEventListener("click", function () { dismiss(false); });
    overlay.querySelector("[data-go]").addEventListener("click", function () { dismiss(true); });
    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) dismiss(false);
    });
    document.addEventListener("keydown", esc);

    // Auto-focus the CTA so they can hit Enter/Return
    setTimeout(function () {
      const cta = overlay.querySelector("[data-go]");
      if (cta) cta.focus();
    }, 50);
  }

  // ============= Inspiration rail (curated showcase) =============
  /**
   * Renders the rail of curated designs above the chat. Hidden automatically
   * if the showcase is empty or the worker call fails — so visitors never see
   * a sad empty placeholder during early days. Tiles are visual-only; we no
   * longer offer a "remix this" flow (showcase rather than copy).
   */
  function renderInspirationRail() {
    if (!window.AIDesignInspiration) {
      // Inspiration module hasn't loaded yet — defer briefly.
      setTimeout(renderInspirationRail, 200);
      return;
    }
    const railEl = root.querySelector("[data-ai-inspiration-rail]");
    if (!railEl) return;
    window.AIDesignInspiration.renderRail(railEl, {
      workerUrl: workerUrl,
      heading: "Inspiration — what others have made",
    });
  }

  // ============= Initial state =============
  // Pick up admin token if present in URL, then render rail.
  if (window.AIDesignInspiration && window.AIDesignInspiration.maybeSetAdminFromQuery) {
    window.AIDesignInspiration.maybeSetAdminFromQuery();
  } else {
    // Defer until the inspiration script is parsed.
    setTimeout(function () {
      if (window.AIDesignInspiration && window.AIDesignInspiration.maybeSetAdminFromQuery) {
        window.AIDesignInspiration.maybeSetAdminFromQuery();
      }
    }, 250);
  }
  showEmptyState(true);
  autoGrowTextarea();
  renderInspirationRail();
  enhanceEmptyState();
  enhanceInspirationRail();

  // ============= Stepped empty state =============
  /**
   * Replace the bare-bones empty state with three numbered "steps" cards
   * that walk new customers through the process: 1) describe, 2) pick a
   * style (optional), 3) add references (optional). After the first
   * generation the existing showEmptyState logic hides this and the chat
   * thread takes over.
   *
   * Reuses the existing data-ai-suggestion and data-ai-preset-id hooks
   * so all interactions stay wired through the existing event listeners.
   */
  function enhanceEmptyState() {
    if (!emptyEl) return;
    if (document.getElementById("ai-steps-styles")) return;

    // ---- CSS ----
    const s = document.createElement("style");
    s.id = "ai-steps-styles";
    s.textContent =
      ".ai-steps{display:flex;flex-direction:column;gap:14px;margin:18px 0 8px;font-family:inherit}" +
      ".ai-step{display:flex;gap:14px;background:var(--ai-surface,#fff);border:1px solid var(--ai-line,#d0d0d0);border-radius:12px;padding:16px;align-items:flex-start}" +
      ".ai-step__num{flex-shrink:0;width:32px;height:32px;border-radius:50%;background:#000;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:15px}" +
      ".ai-step__body{flex:1;min-width:0}" +
      ".ai-step__title{font-size:15px;font-weight:700;color:var(--ai-ink,#1a1a1a);margin:0 0 4px;display:flex;align-items:center;gap:8px;flex-wrap:wrap}" +
      ".ai-step__optional{font-size:11px;font-weight:700;color:#ff6b00;text-transform:uppercase;letter-spacing:.05em;background:#fff7ed;padding:2px 7px;border-radius:99px}" +
      ".ai-step__desc{font-size:13px;color:var(--ai-soft,#5a5a5a);margin:0 0 0;line-height:1.45}" +
      ".ai-step__desc em{color:var(--ai-ink,#1a1a1a);font-style:italic;font-weight:600}" +
      ".ai-step__styles{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px}" +
      ".ai-step__upload{display:inline-flex;align-items:center;gap:8px;background:#fff;border:2px dashed #ff6b00;color:#ff6b00;padding:10px 16px;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit;transition:background .12s ease;margin-top:10px}" +
      ".ai-step__upload:hover{background:#fff7ed}" +
      ".ai-tip{padding:12px 16px;background:#000;color:#fff;border-radius:10px;font-size:13px;line-height:1.45;display:flex;gap:10px;align-items:flex-start}" +
      ".ai-tip__icon{flex-shrink:0;font-size:18px;line-height:1}" +
      "@media (max-width:600px){.ai-step{padding:14px;gap:10px}.ai-step__num{width:28px;height:28px;font-size:13px}.ai-step__title{font-size:14px}}";
    document.head.appendChild(s);

    // ---- Replace the empty state HTML ----
    emptyEl.innerHTML =
      '<div class="ai-steps">' +
        // STEP 1 — Describe (simplified — no example pills that look like multiple-choice)
        '<div class="ai-step">' +
          '<div class="ai-step__num">1</div>' +
          '<div class="ai-step__body">' +
            '<div class="ai-step__title">Describe what you want</div>' +
            '<p class="ai-step__desc">Type your idea in the box at the bottom of this page. Keep it simple <em>("Holden burnout", "FIFO crew logo")</em> or get specific <em>("1970 Monaro at the drag strip with checkered flags, retro 80s style")</em> — the more detail, the better the result.</p>' +
          '</div>' +
        '</div>' +
        // STEP 2 — Style (optional) — style chips will be cloned in below
        '<div class="ai-step">' +
          '<div class="ai-step__num">2</div>' +
          '<div class="ai-step__body">' +
            '<div class="ai-step__title">Pick a style <span class="ai-step__optional">Optional</span></div>' +
            '<p class="ai-step__desc">Set the look and feel. Tap one below, or skip and the AI will choose.</p>' +
            '<div class="ai-step__styles" id="ai-step-styles-slot"></div>' +
          '</div>' +
        '</div>' +
        // STEP 3 — Upload your own photos (optional)
        '<div class="ai-step">' +
          '<div class="ai-step__num">3</div>' +
          '<div class="ai-step__body">' +
            '<div class="ai-step__title">Upload your own photos <span class="ai-step__optional">Optional</span></div>' +
            '<p class="ai-step__desc">Want the AI to <em>include</em> your photo in the design — your race car, work truck, machinery, business logo, team photo, pet, existing artwork? Upload it here. Or use a photo just for style inspiration. Up to 4 photos.</p>' +
            '<button class="ai-step__upload" type="button" data-ai-step-upload>📎 Upload my photos</button>' +
          '</div>' +
        '</div>' +
        // CONVERSATION TIP
        '<div class="ai-tip">' +
          '<span class="ai-tip__icon">💬</span>' +
          '<span>Once your design appears, just keep chatting — say <em>"make it brighter"</em>, <em>"add lightning"</em>, <em>"darker background"</em> and the AI will tweak it. Iterate until it\'s perfect.</span>' +
        '</div>' +
      '</div>';

    // ---- Wire the new "Upload reference photos" button to the existing file input ----
    const uploadBtn = emptyEl.querySelector("[data-ai-step-upload]");
    if (uploadBtn && fileInput) {
      uploadBtn.addEventListener("click", function () { fileInput.click(); });
    }

    // ---- Clone the existing style preset chips into step 2 ----
    // Originals live in the .ai-chat__presets bar above the input. We clone
    // them into the step 2 slot so the customer sees them inline. The clones
    // delegate clicks to the originals (which already have the selection
    // logic wired), and a MutationObserver keeps the clones' active state
    // in sync.
    const slot = document.getElementById("ai-step-styles-slot");
    const presetBar = root.querySelector(".ai-chat__presets");
    if (slot && presetBar) {
      const originals = Array.prototype.slice.call(presetBar.querySelectorAll("[data-ai-preset-id]"));
      const clones = [];
      originals.forEach(function (original) {
        const clone = original.cloneNode(true);
        // Remove the data-ai-preset-id so the existing click handler doesn't
        // double-fire (the original listener uses presetButtons captured at
        // boot, so clones aren't caught — but defensive removal keeps it safe).
        // Actually we KEEP it for MutationObserver targeting. Wire click below.
        clone.classList.add("ai-step__style-chip");
        clone.addEventListener("click", function (e) {
          e.preventDefault();
          original.click(); // triggers the original's existing handler
        });
        slot.appendChild(clone);
        clones.push(clone);
      });
      // Sync active class from originals → clones via MutationObserver
      originals.forEach(function (original, idx) {
        const observer = new MutationObserver(function () {
          const clone = clones[idx];
          if (!clone) return;
          if (original.classList.contains("ai-chat__preset--active")) {
            clone.classList.add("ai-chat__preset--active");
          } else {
            clone.classList.remove("ai-chat__preset--active");
          }
        });
        observer.observe(original, { attributes: true, attributeFilter: ["class"] });
      });
      // Sync initial state in case "None" or another preset is active by default
      originals.forEach(function (original, idx) {
        const clone = clones[idx];
        if (clone && original.classList.contains("ai-chat__preset--active")) {
          clone.classList.add("ai-chat__preset--active");
        }
      });
      // Hide the original presets bar while the empty state is showing,
      // restore when it's hidden (so iteration still has the chips at the bottom).
      function syncPresetBarVisibility() {
        if (!emptyEl.hidden) {
          presetBar.style.display = "none";
        } else {
          presetBar.style.display = "";
        }
      }
      syncPresetBarVisibility();
      const visObs = new MutationObserver(syncPresetBarVisibility);
      visObs.observe(emptyEl, { attributes: true, attributeFilter: ["hidden"] });
    }
  }

  /**
   * The inspiration rail is a horizontal carousel of community designs that
   * ai-inspiration.js populates. Without a heading, it's not obvious what
   * it is. Add a clear label + "see all" link so customers know they can
   * browse for ideas.
   */
  function enhanceInspirationRail() {
    const rail = root.querySelector("[data-ai-inspiration-rail]");
    if (!rail) return;
    if (rail.previousElementSibling && rail.previousElementSibling.classList && rail.previousElementSibling.classList.contains("ai-rail-heading")) return;
    if (document.getElementById("ai-rail-styles")) return;

    const s = document.createElement("style");
    s.id = "ai-rail-styles";
    s.textContent =
      ".ai-rail-heading{display:flex;align-items:center;justify-content:space-between;margin:18px 0 10px;padding:0 4px;font-family:inherit}" +
      ".ai-rail-heading__title{font-size:14px;font-weight:700;color:var(--ai-ink,#1a1a1a);text-transform:uppercase;letter-spacing:.04em;display:flex;align-items:center;gap:8px}" +
      ".ai-rail-heading__icon{font-size:18px}" +
      ".ai-rail-heading__more{font-size:13px;font-weight:700;color:#ff6b00;text-decoration:none}" +
      ".ai-rail-heading__more:hover{text-decoration:underline}" +
      ".ai-rail-heading__sub{font-size:12px;font-weight:500;color:var(--ai-soft,#5a5a5a);text-transform:none;letter-spacing:0}";
    document.head.appendChild(s);

    const heading = document.createElement("div");
    heading.className = "ai-rail-heading";
    const inspirationUrl = root.dataset.inspirationUrl || "/pages/inspiration";
    heading.innerHTML =
      '<div class="ai-rail-heading__title">' +
        '<span class="ai-rail-heading__icon">✨</span>' +
        '<span>Browse for inspiration ' +
          '<span class="ai-rail-heading__sub">— scroll →</span>' +
        '</span>' +
      '</div>' +
      '<a class="ai-rail-heading__more" href="' + inspirationUrl + '">See all →</a>';

    // Observe rail visibility — only insert heading when rail actually has children
    function maybeInsert() {
      if (rail.children.length > 0 && rail.style.display !== "none") {
        if (!rail.previousElementSibling || !rail.previousElementSibling.classList.contains("ai-rail-heading")) {
          rail.parentNode.insertBefore(heading, rail);
        }
      }
    }
    maybeInsert();
    // Re-check periodically since the rail loads async
    const interval = setInterval(maybeInsert, 500);
    setTimeout(function () { clearInterval(interval); }, 8000);
  }

  // ============= Email capture banner + modal =============
  /**
   * Register the worker URL with the cloud library as soon as the page boots.
   * Library may not be loaded yet (different script files, no guarantee of
   * order); retry briefly until it appears.
   */
  function registerCloudWorker() {
    if (!workerUrl) return;
    if (window.AIDesignLibrary && window.AIDesignLibrary.cloud) {
      window.AIDesignLibrary.cloud.setWorkerUrl(workerUrl);
      // Magic link handler already ran on library load — but cloudPull
      // needs the worker URL. If a token came in via the URL, try the pull
      // now that we have the URL configured.
      if (window.AIDesignLibrary.cloud.getToken()) {
        window.AIDesignLibrary.cloud.pull().catch(function (e) {
          console.warn("Initial cloud pull failed", e);
        });
      }
      return;
    }
    setTimeout(registerCloudWorker, 100);
  }
  registerCloudWorker();

  // Inject scoped styles once. Kept inline so this whole feature is
  // contained inside ai-designer.js — no liquid template edits required.
  function injectEmailBannerStyles() {
    if (document.getElementById("ai-email-banner-styles")) return;
    const s = document.createElement("style");
    s.id = "ai-email-banner-styles";
    s.textContent =
      ".ai-eml-banner{display:flex;align-items:center;gap:12px;margin:12px 0;padding:14px 16px;background:linear-gradient(135deg,#fff8f0,#fff1e0);border:1px solid #ffd9b3;border-radius:12px;box-shadow:0 1px 0 rgba(255,107,0,0.08);font-family:inherit;animation:ai-eml-in 0.32s cubic-bezier(.2,.7,.3,1)}" +
      "@keyframes ai-eml-in{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}" +
      ".ai-eml-banner__icon{flex:0 0 auto;width:36px;height:36px;border-radius:50%;background:#ff6b00;color:#fff;display:flex;align-items:center;justify-content:center;font-size:18px}" +
      ".ai-eml-banner__body{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:2px}" +
      ".ai-eml-banner__title{font-size:14px;font-weight:700;color:#1a1a1a;line-height:1.2}" +
      ".ai-eml-banner__sub{font-size:12px;color:#5a5a5a;line-height:1.35}" +
      ".ai-eml-banner__cta{flex:0 0 auto;padding:9px 16px;border:0;border-radius:8px;background:#1a1a1a;color:#fff;font-size:13px;font-weight:700;cursor:pointer;letter-spacing:.02em;white-space:nowrap;transition:transform .15s ease, background .15s ease}" +
      ".ai-eml-banner__cta:hover{background:#000;transform:translateY(-1px)}" +
      ".ai-eml-banner__dismiss{flex:0 0 auto;width:28px;height:28px;border:0;border-radius:50%;background:transparent;color:#9a9a9a;font-size:20px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0}" +
      ".ai-eml-banner__dismiss:hover{background:rgba(0,0,0,0.05);color:#1a1a1a}" +
      ".ai-eml-banner--signed{background:#f0fdf4;border-color:#bbf7d0}" +
      ".ai-eml-banner--signed .ai-eml-banner__icon{background:#22c55e}" +
      "@media (max-width:480px){.ai-eml-banner{flex-wrap:wrap}.ai-eml-banner__body{flex-basis:calc(100% - 60px)}.ai-eml-banner__cta{width:100%}}" +
      /* Modal */
      ".ai-eml-overlay{position:fixed;inset:0;background:rgba(15,23,42,0.55);z-index:99998;display:flex;align-items:center;justify-content:center;padding:16px;animation:ai-eml-fade .2s ease}" +
      "@keyframes ai-eml-fade{from{opacity:0}to{opacity:1}}" +
      ".ai-eml-modal{background:#fff;border-radius:14px;max-width:460px;width:100%;max-height:90vh;overflow-y:auto;box-shadow:0 24px 64px rgba(0,0,0,0.32);font-family:inherit;animation:ai-eml-pop .22s cubic-bezier(.2,.7,.3,1)}" +
      "@keyframes ai-eml-pop{from{opacity:0;transform:translateY(12px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}" +
      ".ai-eml-modal__header{display:flex;align-items:center;justify-content:space-between;padding:18px 20px;border-bottom:1px solid #eef0f3}" +
      ".ai-eml-modal__title{font-size:16px;font-weight:700;color:#1a1a1a;margin:0}" +
      ".ai-eml-modal__close{width:32px;height:32px;border:0;border-radius:8px;background:transparent;color:#5a5a5a;font-size:22px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0}" +
      ".ai-eml-modal__close:hover{background:#f5f5f5;color:#1a1a1a}" +
      ".ai-eml-modal__body{padding:20px}" +
      ".ai-eml-modal__intro{font-size:14px;color:#5a5a5a;line-height:1.5;margin:0 0 16px}" +
      ".ai-eml-modal__benefits{margin:0 0 18px;padding:0;list-style:none;font-size:13px;color:#1a1a1a}" +
      ".ai-eml-modal__benefits li{padding:6px 0 6px 24px;position:relative;line-height:1.4}" +
      ".ai-eml-modal__benefits li::before{content:'✓';position:absolute;left:0;top:6px;color:#22c55e;font-weight:700}" +
      ".ai-eml-modal__field{display:block;margin:0 0 14px}" +
      ".ai-eml-modal__label{display:block;font-size:13px;font-weight:600;color:#1a1a1a;margin:0 0 6px}" +
      ".ai-eml-modal__input{width:100%;padding:12px 14px;border:1px solid #d4d4d4;border-radius:9px;font-size:15px;font-family:inherit;color:#1a1a1a;background:#fff;box-sizing:border-box;transition:border-color .15s ease, box-shadow .15s ease}" +
      ".ai-eml-modal__input:focus{outline:0;border-color:#ff6b00;box-shadow:0 0 0 3px rgba(255,107,0,0.15)}" +
      ".ai-eml-modal__input.is-error{border-color:#ef4444;box-shadow:0 0 0 3px rgba(239,68,68,0.12)}" +
      ".ai-eml-modal__error{font-size:13px;color:#ef4444;margin:6px 0 0;min-height:18px}" +
      ".ai-eml-modal__actions{display:flex;gap:10px;margin-top:18px}" +
      ".ai-eml-modal__submit{flex:1 1 auto;padding:13px 18px;border:0;border-radius:9px;background:#ff6b00;color:#fff;font-size:15px;font-weight:700;cursor:pointer;letter-spacing:.02em;transition:transform .15s ease, background .15s ease}" +
      ".ai-eml-modal__submit:hover:not(:disabled){background:#e85f00;transform:translateY(-1px)}" +
      ".ai-eml-modal__submit:disabled{opacity:.6;cursor:not-allowed;transform:none}" +
      ".ai-eml-modal__skip{flex:0 0 auto;padding:13px 18px;border:0;border-radius:9px;background:#f5f5f5;color:#5a5a5a;font-size:14px;font-weight:600;cursor:pointer}" +
      ".ai-eml-modal__skip:hover{background:#e5e5e5;color:#1a1a1a}" +
      ".ai-eml-modal__fineprint{font-size:11px;color:#9a9a9a;margin:14px 0 0;line-height:1.4;text-align:center}" +
      ".ai-eml-modal__success{padding:24px;text-align:center}" +
      ".ai-eml-modal__success-icon{width:56px;height:56px;border-radius:50%;background:#22c55e;color:#fff;display:flex;align-items:center;justify-content:center;font-size:28px;margin:0 auto 12px}" +
      ".ai-eml-modal__success-title{font-size:18px;font-weight:700;color:#1a1a1a;margin:0 0 6px}" +
      ".ai-eml-modal__success-sub{font-size:14px;color:#5a5a5a;line-height:1.5;margin:0 0 18px}" +
      ".ai-eml-modal__success-email{font-weight:700;color:#1a1a1a}" +
      ".ai-eml-modal__copy-link{display:inline-flex;align-items:center;gap:8px;padding:10px 14px;background:#f5f5f5;border:0;border-radius:8px;font-size:13px;font-weight:600;color:#1a1a1a;cursor:pointer;margin-bottom:14px}" +
      ".ai-eml-modal__copy-link:hover{background:#e5e5e5}";
    document.head.appendChild(s);
  }

  let bannerEl = null;

  /**
   * Decide whether to show the email-capture banner and render it if so.
   * Idempotent: safe to call repeatedly. Updates an existing banner rather
   * than creating duplicates.
   *
   * Shown when: not signed in, not dismissed, has saved designs.
   * Hidden when: signed in (modal already confirmed), dismissed, or empty.
   */
  function maybeShowEmailBanner() {
    if (!window.AIDesignLibrary || !window.AIDesignLibrary.banner) return;
    const lib = window.AIDesignLibrary;
    const signedIn = lib.cloud && lib.cloud.isSignedIn();
    const hasDesigns = lib.list().length > 0;
    const dismissed = lib.banner.dismissed();

    if (signedIn || dismissed || !hasDesigns) {
      removeBanner();
      return;
    }

    injectEmailBannerStyles();
    if (!bannerEl) {
      bannerEl = document.createElement("div");
      bannerEl.className = "ai-eml-banner";
      // Insert above the chat thread (or whichever container is the
      // designer's main content area).
      const target = root.querySelector("[data-ai-thread]") || root.firstElementChild;
      if (target && target.parentNode) {
        target.parentNode.insertBefore(bannerEl, target);
      } else {
        root.appendChild(bannerEl);
      }
    }
    bannerEl.innerHTML =
      '<div class="ai-eml-banner__icon" aria-hidden="true">🔗</div>' +
      '<div class="ai-eml-banner__body">' +
        '<div class="ai-eml-banner__title">Save your designs to any device</div>' +
        '<div class="ai-eml-banner__sub">Get a private link to open your library on your phone, laptop or tablet.</div>' +
      '</div>' +
      '<button type="button" class="ai-eml-banner__cta" data-ai-eml-open>Get my link</button>' +
      '<button type="button" class="ai-eml-banner__dismiss" aria-label="Dismiss" data-ai-eml-dismiss>×</button>';
    bannerEl.querySelector("[data-ai-eml-open]").addEventListener("click", openEmailModal);
    bannerEl.querySelector("[data-ai-eml-dismiss]").addEventListener("click", function () {
      lib.banner.markDismissed();
      removeBanner();
    });
  }

  function removeBanner() {
    if (bannerEl && bannerEl.parentNode) bannerEl.parentNode.removeChild(bannerEl);
    bannerEl = null;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c];
    });
  }

  /**
   * Open the email-capture modal. Two phases:
   *   1) Email entry form — POST to /api/library/save on submit
   *   2) Success state — confirms email was bound + magic link copied
   */
  function openEmailModal() {
    if (document.querySelector(".ai-eml-overlay")) return; // already open
    injectEmailBannerStyles();

    const overlay = document.createElement("div");
    overlay.className = "ai-eml-overlay";
    overlay.innerHTML =
      '<div class="ai-eml-modal" role="dialog" aria-modal="true" aria-labelledby="ai-eml-title">' +
        '<div class="ai-eml-modal__header">' +
          '<h2 class="ai-eml-modal__title" id="ai-eml-title">Get a link to your library</h2>' +
          '<button type="button" class="ai-eml-modal__close" aria-label="Close" data-ai-eml-close>×</button>' +
        '</div>' +
        '<div class="ai-eml-modal__body" data-ai-eml-body>' +
          '<p class="ai-eml-modal__intro">Pop your email in and we\'ll give you a private link to your design library:</p>' +
          '<ul class="ai-eml-modal__benefits">' +
            '<li>Open your library on your phone, laptop or tablet</li>' +
            '<li>Designs auto-sync as you create them</li>' +
            '<li>Private — only you have the link</li>' +
          '</ul>' +
          '<form data-ai-eml-form novalidate>' +
            '<label class="ai-eml-modal__field">' +
              '<span class="ai-eml-modal__label">Your email</span>' +
              '<input type="email" class="ai-eml-modal__input" name="email" autocomplete="email" placeholder="you@example.com" required data-ai-eml-input />' +
              '<div class="ai-eml-modal__error" data-ai-eml-error aria-live="polite"></div>' +
            '</label>' +
            '<div class="ai-eml-modal__actions">' +
              '<button type="submit" class="ai-eml-modal__submit" data-ai-eml-submit>Get my link</button>' +
              '<button type="button" class="ai-eml-modal__skip" data-ai-eml-skip>Not now</button>' +
            '</div>' +
            '<p class="ai-eml-modal__fineprint">We\'ll show you the link on the next screen — copy it somewhere safe. No marketing emails.</p>' +
          '</form>' +
        '</div>' +
      '</div>';
    document.body.appendChild(overlay);

    function close() {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.removeEventListener("keydown", onKey);
    }
    function onKey(e) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("keydown", onKey);
    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) close(); // click outside modal
    });
    overlay.querySelector("[data-ai-eml-close]").addEventListener("click", close);
    overlay.querySelector("[data-ai-eml-skip]").addEventListener("click", function () {
      if (window.AIDesignLibrary && window.AIDesignLibrary.banner) {
        window.AIDesignLibrary.banner.markDismissed();
      }
      removeBanner();
      close();
    });

    const form = overlay.querySelector("[data-ai-eml-form]");
    const input = overlay.querySelector("[data-ai-eml-input]");
    const errorEl = overlay.querySelector("[data-ai-eml-error]");
    const submitBtn = overlay.querySelector("[data-ai-eml-submit]");
    const body = overlay.querySelector("[data-ai-eml-body]");

    // Pre-fill with last known email if we have one (e.g., they signed out)
    try {
      if (window.AIDesignLibrary && window.AIDesignLibrary.cloud) {
        const last = window.AIDesignLibrary.cloud.getEmail();
        if (last) input.value = last;
      }
    } catch (_) {}
    setTimeout(function () { input.focus(); }, 50);

    form.addEventListener("submit", async function (e) {
      e.preventDefault();
      const email = (input.value || "").trim().toLowerCase();
      input.classList.remove("is-error");
      errorEl.textContent = "";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        input.classList.add("is-error");
        errorEl.textContent = "That doesn't look like a valid email.";
        input.focus();
        return;
      }
      submitBtn.disabled = true;
      submitBtn.textContent = "Saving…";
      try {
        const result = await window.AIDesignLibrary.cloud.save(email);
        // Subscription succeeded — mark + render success state in-modal.
        if (window.AIDesignLibrary.banner) {
          window.AIDesignLibrary.banner.markSubscribed();
        }
        renderSuccess(body, email, result);
        // Also refresh the banner so it shows the "signed in" state.
        maybeShowEmailBanner();
      } catch (err) {
        console.error("Library save failed", err);
        input.classList.add("is-error");
        errorEl.textContent = err.message || "Couldn't save — please try again.";
        submitBtn.disabled = false;
        submitBtn.textContent = "Save my designs";
      }
    });

    function renderSuccess(container, email, result) {
      // Build the magic-link URL so the customer can copy it and open on
      // another device. Token is in result.token.
      const myUrl = (function () {
        try {
          const u = new URL(myDesignsUrl, window.location.origin);
          if (result && result.token) u.searchParams.set("lib", result.token);
          return u.toString();
        } catch (_) {
          return myDesignsUrl + (result && result.token ? "?lib=" + result.token : "");
        }
      })();
      container.innerHTML =
        '<div class="ai-eml-modal__success">' +
          '<div class="ai-eml-modal__success-icon" aria-hidden="true">✓</div>' +
          '<h3 class="ai-eml-modal__success-title">Your library is saved</h3>' +
          '<p class="ai-eml-modal__success-sub">Copy this link and save it somewhere — open it on any device to access your designs.</p>' +
          '<input type="text" class="ai-eml-modal__input" value="' + escapeHtml(myUrl) + '" readonly data-ai-eml-link style="margin-bottom:10px;font-size:12px" />' +
          '<button type="button" class="ai-eml-modal__copy-link" data-ai-eml-copy>📋 Copy link</button>' +
          '<p class="ai-eml-modal__fineprint">This device is already signed in — new designs will sync automatically.</p>' +
          '<div class="ai-eml-modal__actions" style="justify-content:center;margin-top:6px">' +
            '<button type="button" class="ai-eml-modal__submit" data-ai-eml-done style="flex:0 1 auto">Keep designing</button>' +
          '</div>' +
        '</div>';
      const copyBtn = container.querySelector("[data-ai-eml-copy]");
      const linkInput = container.querySelector("[data-ai-eml-link]");
      // Auto-select the link text on click so customers can easily copy manually.
      if (linkInput) {
        linkInput.addEventListener("click", function () { linkInput.select(); });
      }
      copyBtn.addEventListener("click", async function () {
        try {
          await navigator.clipboard.writeText(myUrl);
          copyBtn.textContent = "✓ Link copied";
          setTimeout(function () { copyBtn.textContent = "📋 Copy link"; }, 2200);
        } catch (_) {
          if (linkInput) { linkInput.select(); linkInput.focus(); }
        }
      });
      container.querySelector("[data-ai-eml-done]").addEventListener("click", close);
    }
  }

  // Run once on init in case there are existing designs already (returning
  // visitor on a different device before signing up).
  setTimeout(function () {
    try { maybeShowEmailBanner(); } catch (e) { console.warn("init banner", e); }
  }, 600);
})();


/* ==========================================================================
   === ORDER WIZARD (Phase 1a foundation, appended) =========================

   Self-contained AI ordering wizard. Lives at the bottom of ai-designer.js
   so it's guaranteed to load with the designer. No section, no separate
   asset, no theme.liquid edit needed.

   Three-layer gating: theme flag / localStorage.aiAdminToken / ?wizard=test
   Triggers on clicks to [data-action="shop-tees"] or [data-wizard-trigger].

   Spec: ai-design-build/WIZARD.md
   ========================================================================== */
(function () {
  "use strict";

  // Guard against double-loading. If the wizard code runs twice (e.g. because
  // both ai-designer.js and a leftover order-wizard.js are loaded), this
  // global flag stops the second instance from registering duplicate
  // listeners — which would cause every click and chat message to fire twice.
  if (window.__OrderWizardBooted) {
    if (window.console && console.info) {
      console.info("[OrderWizard] second instance detected — skipping. Remove the duplicate script include.");
    }
    return;
  }
  window.__OrderWizardBooted = true;

  // Captured now (synchronously, at top-level script execution) rather than
  // hardcoded, so the sibling ft-print-zones.json lookup below always
  // resolves against whichever theme is actually serving this file — the
  // CDN path embeds a numeric theme id that isn't stable across
  // live/dev/future theme changes. document.currentScript is only valid
  // during this initial synchronous run, never inside a later callback.
  const SELF_SCRIPT_URL = (document.currentScript && document.currentScript.src) ||
    (function () {
      const s = document.querySelector('script[src*="ai-designer.js"]');
      return s ? s.src : "";
    })();

  const DEFAULTS = {
    enabled: false,
    defaultColour: "Black",
    // Colours that are kept in stock for core-range products. Customers
    // can still buy non-stock colours of core products through the regular
    // PDP flow — the wizard just filters its colour picker to these
    // fast-ship options to keep the "ships in days" promise honest.
    // Add more colours here as you start stocking them.
    coreRangeFastColours: ["black"],
    // Real, enforced bulk ladder as of 2026-08-26 — mirrors the Hulk
    // Discounts tier tables on "combined discounts" (shirts) and the
    // per-print-location offers (back/chest/large-front/sleeves/hemline),
    // all rebuilt to this exact same 10/18–20/25–50/30–100/33–200/35
    // ladder. This table was previously forced empty (see the removed
    // "hard-force" line below) because the OLD ladder here was
    // display-only — the wizard would quote a discount at checkout time
    // that nothing actually applied, so the cart charged full price and
    // customers abandoned confused. That's fixed now in the other
    // direction: Hulk Discounts actually enforces this ladder at
    // checkout regardless of what the wizard shows, so a wizard that
    // stays silent about it just quotes a HIGHER total than the cart
    // will actually charge — same trust problem, opposite sign. Keeping
    // this table in sync with Hulk's real config is what makes the
    // wizard's number match the cart's number; if the Hulk tiers ever
    // change, this needs to change with them.
    tierTable: [
      { minQty: 10,  percentOff: 18 },
      { minQty: 20,  percentOff: 25 },
      { minQty: 50,  percentOff: 30 },
      { minQty: 100, percentOff: 33 },
      { minQty: 200, percentOff: 35 }
    ],
    // Fallback only, used until resolvePrintProducts() resolves and
    // overwrites these with the live price (see PRINT_PRODUCT_HANDLES) —
    // kept current so the brief pre-resolution window, or a resolution
    // failure, doesn't show/charge a stale number either. GST-inclusive
    // prices as of 2026-08-25.
    printPrices: { front_left_chest: 8.53, large_front: 12.05, back_full: 12.05 },
    leadTimes: { adult_tee: 4, adult_hoodie: 10, adult_long_sleeve: 7, kids_tee: 5, kids_hoodie: 10 },
    workerUrl: "https://ai-design-worker.60ft-ink.workers.dev",
    rushFeeHandle: "rush-order-fee",
    // Hard-coded so the wizard doesn't depend on the product being
    // published to the Online Store sales channel. Found via the actual
    // Shopify product 'Rush order fee' on 60ftink.com.au. Variant IDs
    // are stable across publication state changes.
    rushFeeVariantId: 52233959833902,
    // Unit price of the Shopify 'Rush order fee' product. Kept at $1 so the
    // qty matches the calculated dollar fee exactly. The cart-consolidator
    // IIFE at the bottom of this file hides the resulting line items and
    // displays a single 'Rush production fee — $N' summary row instead,
    // so customers never see the underlying "225 × $1" mess.
    rushFeeUnitPrice: 1,
    // surchargePct is % of pre-discount subtotal. minFee + maxFee cap the
    // result so small orders still pay a meaningful fee and big orders
    // aren't punished for size.
    rushOptions: [
      { key: "standard", label: "Standard production",   desc: (window.FT_PRODUCTION && window.FT_PRODUCTION.standard) || "10–14 business days", surchargePct: 0 },
      { key: "rush_5",   label: "Rush production",       desc: "Ready within 5 business days",        surchargePct: 25, minFee: 50, maxFee: 750 },
      { key: "same_day", label: "Same day print + ship", desc: "Order before 11am, ships same day",   surchargePct: 75, minFee: 50, maxFee: 750 }
    ]
  };
  const CONFIG = Object.assign({}, DEFAULTS, (window.WIZARD_CONFIG || {}));

  function gateCheck() {
    let adminToken = "";
    try { adminToken = localStorage.getItem("aiAdminToken") || ""; } catch (_) {}
    // Handle URL flags:
    //   ?wizard=test → enable test-mode flag (persists for this browser)
    //   ?wizard=off  → set kill switch — actively disables the wizard for
    //                  this browser only (used if something breaks for a
    //                  customer and they need the classic flow)
    //   ?wizard=on   → clear the kill switch
    let disabled = false;
    try {
      const params = new URLSearchParams(window.location.search);
      const wParam = params.get("wizard");
      if (wParam === "test") {
        localStorage.setItem("wizardTestMode", "1");
      } else if (wParam === "off") {
        localStorage.setItem("wizardDisabled", "1");
        localStorage.removeItem("wizardTestMode");
      } else if (wParam === "on") {
        localStorage.removeItem("wizardDisabled");
      }
      disabled = localStorage.getItem("wizardDisabled") === "1";
    } catch (_) {}
    let urlTest = false;
    try { urlTest = localStorage.getItem("wizardTestMode") === "1"; } catch (_) {}

    // Explicit kill switch always wins
    if (disabled)         return { allowed: false, reason: "disabled" };
    // Positive overrides (kept so existing admin/test workflows still work)
    if (CONFIG.enabled)   return { allowed: true, reason: "flag" };
    if (adminToken)       return { allowed: true, reason: "admin" };
    if (urlTest)          return { allowed: true, reason: "url" };
    // Default: ON for all customers (was OFF during development).
    // To disable for a customer, send them a link with ?wizard=off
    return { allowed: true, reason: "default" };
  }
  const GATE = gateCheck();

  if (window.console && console.info) {
    console.info("[OrderWizard] script loaded.",
      GATE.allowed ? "Gate ALLOWED via " + GATE.reason
                   : "Gate DENIED — set localStorage.aiAdminToken or visit with ?wizard=test");
  }
  if (!GATE.allowed) {
    window.OrderWizard = { state: null, config: CONFIG, gate: GATE };
    return;
  }

  function injectStyles() {
    if (document.getElementById("ow-injected-styles")) return;
    const css = `
:root{--ow-orange:#ff6b00;--ow-text:#1a1a1a;--ow-text-dim:#5a5a5a;--ow-border:#d0d0d0;--ow-border-strong:#1a1a1a;--ow-bg:#fff;--ow-bg-soft:#f6f6f6;--ow-bg-ai:#f1f1f1;--ow-bg-user:#ffe8d4;--ow-shadow:0 30px 80px rgba(0,0,0,.35);--ow-radius:14px;--ow-radius-sm:10px}
.ow-product-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px}
.ow-product-card{display:flex;flex-direction:column;border:2px solid var(--ow-border);border-radius:var(--ow-radius-sm);background:var(--ow-bg);cursor:pointer;overflow:hidden;text-align:left;padding:0;font-family:inherit;transition:border-color .12s ease,transform .06s ease}
.ow-product-card:hover{border-color:var(--ow-border-strong)}
.ow-product-card.is-selected{border-color:var(--ow-orange)}
.ow-product-card__img{width:100%;aspect-ratio:1/1;object-fit:cover;background:#eee;display:block}
.ow-product-card__body{padding:10px 12px}
.ow-product-card__title{font-size:13px;font-weight:700;line-height:1.25;margin:0 0 4px;color:var(--ow-text)}
.ow-product-card__price{font-size:12px;color:var(--ow-text-dim)}
.ow-product-card{position:relative}
.ow-fast-ship-badge{position:absolute;top:8px;left:8px;display:inline-flex;align-items:center;gap:4px;background:var(--ow-orange);color:#fff;padding:3px 8px;font-size:10px;font-weight:700;border-radius:4px;letter-spacing:.04em;text-transform:uppercase;box-shadow:0 2px 6px rgba(255,107,0,0.3);z-index:2}
.ow-fast-ship-note{font-size:11px;color:var(--ow-orange);font-weight:600;margin-top:2px}
.ow-rush-opt.is-disabled{opacity:.5;cursor:not-allowed;background:#f5f5f5}
.ow-rush-opt__lock{display:block;font-size:11px;color:var(--ow-orange);font-weight:600;margin-top:4px;text-transform:uppercase;letter-spacing:.03em}
.ow-swatches{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:14px}
.ow-swatch{display:flex;flex-direction:column;align-items:center;gap:4px;background:transparent;border:none;padding:4px 6px;cursor:pointer;font-family:inherit;color:var(--ow-text);max-width:80px}
.ow-swatch__dot{width:36px;height:36px;border-radius:50%;border:2px solid var(--ow-border);background:#000;display:block;flex-shrink:0}
.ow-swatch.is-selected .ow-swatch__dot{border-color:var(--ow-orange);box-shadow:0 0 0 2px #fff inset}
.ow-swatch__name{font-size:11px;line-height:1.2;font-weight:500;text-align:center;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:74px}
.ow-swatch.is-selected .ow-swatch__name{font-weight:700}
.ow-sizes{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:14px}
.ow-size{appearance:none;border:2px solid var(--ow-border);background:var(--ow-bg);color:var(--ow-text);min-width:48px;height:42px;font-weight:700;font-size:14px;border-radius:var(--ow-radius-sm);cursor:pointer;padding:0 12px;font-family:inherit}
.ow-size:hover{border-color:var(--ow-border-strong)}
.ow-size.is-selected{border-color:var(--ow-orange);background:#000;color:#fff}
.ow-size[disabled]{opacity:.3;cursor:not-allowed}
.ow-print-options{display:flex;flex-direction:column;gap:8px;margin-bottom:14px}
.ow-print-option{display:flex;align-items:center;gap:12px;border:2px solid var(--ow-border);background:var(--ow-bg);border-radius:var(--ow-radius-sm);padding:12px 14px;cursor:pointer;text-align:left;font-family:inherit;width:100%}
.ow-print-option:hover{border-color:var(--ow-border-strong)}
.ow-print-option.is-selected{border-color:var(--ow-orange)}
.ow-print-option__thumb{flex-shrink:0;width:46px;height:52px;display:flex;align-items:center;justify-content:center}
.ow-print-option__thumb svg{width:100%;height:100%;display:block}
.ow-print-option__body{flex:1;min-width:0}
.ow-print-option__title{font-size:14px;font-weight:700;color:var(--ow-text)}
.ow-print-option__desc{font-size:12px;color:var(--ow-text-dim);margin-top:2px}
.ow-print-option__price{font-size:14px;font-weight:700;color:var(--ow-orange);white-space:nowrap}
.ow-summary{background:var(--ow-bg-soft);border-radius:var(--ow-radius-sm);padding:14px 16px;margin-bottom:14px;font-size:14px}
.ow-summary__row{display:flex;justify-content:space-between;margin-bottom:6px}
.ow-summary__row:last-child{margin-bottom:0;padding-top:8px;border-top:1px solid var(--ow-border);font-weight:700}
.ow-summary__label{color:var(--ow-text-dim)}
.ow-summary__val{color:var(--ow-text)}
.ow-step__title{font-size:14px;font-weight:700;margin:0 0 12px;color:var(--ow-text);letter-spacing:.02em}
.ow-loading{padding:24px;text-align:center;color:var(--ow-text-dim);font-size:13px}
.ow-error{padding:12px 14px;background:#fff0e6;border-left:3px solid #c4262d;color:#7a1818;font-size:13px;border-radius:var(--ow-radius-sm);margin-bottom:12px}
.ow-exit-link{font-size:13px;color:var(--ow-text-dim);text-align:center;margin:6px 0 14px}
.ow-exit-link a{color:var(--ow-orange);font-weight:700;text-decoration:none;margin-left:6px}
.ow-exit-link a:hover{text-decoration:underline}
.ow-artwork-row{border:1px solid var(--ow-border);border-radius:var(--ow-radius-sm);padding:12px;margin-bottom:12px;background:var(--ow-bg-soft)}
.ow-artwork-row__header{font-weight:700;font-size:14px;margin-bottom:8px;color:var(--ow-text)}
.ow-artwork-row__preview{display:flex;align-items:center;gap:12px;margin-bottom:10px}
.ow-artwork-row__preview img{width:60px;height:60px;object-fit:cover;border-radius:6px;border:1px solid var(--ow-border);background:#fff}
.ow-artwork-row__noimg{width:60px;height:60px;border:1px dashed var(--ow-border);border-radius:6px;display:flex;align-items:center;justify-content:center;color:var(--ow-text-dim);font-size:11px}
.ow-artwork-row__source{font-size:12px;color:var(--ow-text-dim)}
.ow-artwork-buttons{display:flex;gap:6px;flex-wrap:wrap}
.ow-artwork-btn{flex:1;min-width:84px;padding:8px 10px;background:#fff;border:1px solid var(--ow-border);border-radius:6px;font-size:13px;font-weight:600;color:var(--ow-text);cursor:pointer;font-family:inherit}
.ow-artwork-btn.is-active{background:#000;color:#fff;border-color:#000}
.ow-artwork-btn:hover:not(.is-active){border-color:var(--ow-border-strong)}
.ow-artwork-btn[disabled]{opacity:.4;cursor:not-allowed}
.ow-saved-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(70px,1fr));gap:8px;margin-top:10px;padding:10px;background:#fff;border-radius:6px;max-height:200px;overflow-y:auto;border:1px solid var(--ow-border)}
.ow-saved-thumb{aspect-ratio:1/1;padding:0;border:2px solid var(--ow-border);border-radius:6px;cursor:pointer;background:#eee;overflow:hidden;font-family:inherit}
.ow-saved-thumb:hover{border-color:var(--ow-orange)}
.ow-saved-thumb img{width:100%;height:100%;object-fit:cover;display:block}
.ow-saved-empty{grid-column:1/-1;font-size:13px;color:var(--ow-text-dim);padding:14px;text-align:center}
.ow-reorder-toggle{display:flex;align-items:flex-start;gap:8px;padding:10px 12px;background:var(--ow-bg-soft);border:1px solid var(--ow-border);border-radius:var(--ow-radius-sm);font-size:13px;color:var(--ow-text);cursor:pointer;margin-bottom:12px}
.ow-reorder-toggle input{margin-top:2px;flex-shrink:0}
.ow-optin-reorder{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;margin-top:12px;padding:12px 14px;background:var(--ow-bg-soft);border:1.5px dashed var(--ow-border-strong);border-radius:var(--ow-radius-sm);font-size:14px;font-weight:600;color:var(--ow-text);text-align:center;cursor:pointer;font-family:inherit;appearance:none}
.ow-optin-reorder:hover{background:var(--ow-bg-user);border-style:solid}
.ow-optin-reorder svg{flex-shrink:0}
.ow-reorder-fields{margin-bottom:12px}
.ow-reorder-note{font-size:12px;color:var(--ow-text-dim);margin:0 0 8px}
.ow-field-label{display:block;font-size:12px;font-weight:700;color:var(--ow-text-dim);margin:10px 0 4px}
.ow-text-input,.ow-textarea{width:100%;border:1px solid var(--ow-border);border-radius:6px;padding:10px 12px;font-size:14px;font-family:inherit;color:var(--ow-text);background:#fff;box-sizing:border-box}
.ow-textarea{min-height:80px;resize:vertical}
.ow-text-input:focus,.ow-textarea:focus{outline:none;border-color:var(--ow-orange)}
.ow-upload-progress{font-size:12px;color:var(--ow-text-dim);margin-top:6px}
.ow-qty{display:flex;align-items:center;gap:10px;margin-bottom:14px}
.ow-qty__label{font-size:13px;font-weight:700;color:var(--ow-text)}
.ow-qty__group{display:inline-flex;align-items:stretch;border:2px solid var(--ow-border-strong);border-radius:var(--ow-radius-sm);overflow:hidden;background:#fff}
.ow-qty__btn{background:#fff;border:none;width:44px;height:46px;font-size:20px;font-weight:700;cursor:pointer;color:var(--ow-text);font-family:inherit;flex-shrink:0}
.ow-qty__btn:hover{background:var(--ow-bg-soft)}
.ow-qty__btn[disabled]{opacity:.3;cursor:not-allowed}
.ow-qty__val{width:64px;border:none;text-align:center;font-size:18px;font-weight:700;font-family:inherit;color:var(--ow-text);background:#fff;-moz-appearance:textfield;padding:0 4px}
.ow-qty__val::-webkit-outer-spin-button,.ow-qty__val::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
.ow-qty__val:focus{outline:none;background:var(--ow-bg-soft)}
.ow-success{text-align:center;padding:24px 16px}
.ow-success__icon{font-size:36px;margin-bottom:6px}
.ow-success__title{font-size:18px;font-weight:700;margin:0 0 6px;color:var(--ow-text)}
.ow-success__msg{font-size:14px;color:var(--ow-text-dim);margin:0 0 14px;line-height:1.4}
.ow-mockup{display:flex;gap:12px;align-items:flex-start;margin-bottom:14px;flex-wrap:wrap}
.ow-mockup__view{flex:1;min-width:160px;background:var(--ow-bg-soft);border:1px solid var(--ow-border);border-radius:var(--ow-radius-sm);overflow:hidden;text-align:center;position:relative}
.ow-mockup__img{display:block;width:100%;height:auto;background:#fff}
.ow-mockup__loading{padding:60px 12px;font-size:13px;color:var(--ow-text-dim)}
.ow-mockup__label{padding:6px 8px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:var(--ow-text-dim);background:var(--ow-bg);border-top:1px solid var(--ow-border)}
.ow-mockup__product-tabs{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px}
.ow-mockup__product-tabs[hidden]{display:none}
.ow-mockup__product-tab{appearance:none;border:2px solid var(--ow-border);background:var(--ow-bg);color:var(--ow-text);font:inherit;font-size:12px;font-weight:700;padding:6px 12px;border-radius:999px;cursor:pointer}
.ow-mockup__product-tab.is-active{border-color:var(--ow-orange);background:#fff7ed;color:var(--ow-orange)}
.ow-mockup__step-count{margin:0 0 10px;font-size:12px;font-weight:700;color:var(--ow-text-dim);text-transform:uppercase;letter-spacing:.04em}
.ow-mockup__stage{position:relative;width:100%;max-width:340px;margin:0 auto;aspect-ratio:1/1;background:var(--ow-bg-soft);border:1px solid var(--ow-border);border-radius:var(--ow-radius-sm);overflow:hidden;touch-action:none}
.ow-mockup__garment{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;pointer-events:none;user-select:none}
.ow-mockup__artlayer{position:absolute;cursor:move;touch-action:none}
.ow-mockup__artlayer.is-selected{outline:1px dashed rgba(255,107,0,.6);outline-offset:2px}
.ow-mockup__art{display:block;width:100%;height:100%;object-fit:fill;pointer-events:none;user-select:none;background:repeating-conic-gradient(rgba(0,0,0,.05) 0% 25%,transparent 0% 50%) 50%/16px 16px}
.ow-mockup__handle{display:none;position:absolute;right:-8px;bottom:-8px;width:18px;height:18px;border-radius:50%;background:var(--ow-orange);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35);cursor:nwse-resize;touch-action:none}
.ow-mockup__artlayer.is-selected .ow-mockup__handle{display:block}
.ow-mockup__loading{position:absolute;inset:0;display:flex;flex-direction:column;gap:10px;align-items:center;justify-content:center;background:rgba(255,255,255,.9);font-size:12px;font-weight:600;color:var(--ow-text-dim);text-align:center;padding:0 16px}
.ow-mockup__loading[hidden]{display:none}
.ow-mockup__spinner{display:inline-block;width:26px;height:26px;border:3px solid rgba(0,0,0,.12);border-top-color:var(--ow-orange);border-radius:50%;animation:ow-mockup-spin .8s linear infinite}
@keyframes ow-mockup-spin{to{transform:rotate(360deg)}}
.ow-mockup__size-chip{margin-top:10px;padding:8px 12px;border-radius:8px;background:rgba(255,107,0,.08);border:1px solid rgba(255,107,0,.3);color:#9a5b12;font-weight:700;font-size:14px;text-align:center}
.ow-mockup__size-chip[hidden]{display:none}
.ow-mockup__size-chip--approx{background:rgba(0,0,0,.04);border-color:rgba(0,0,0,.15);color:var(--ow-text-dim);font-weight:600}
.ow-mockup__hint{font-size:12px;color:var(--ow-text-dim);line-height:1.4;margin:10px 0 4px;text-align:center}
.ow-mockup__reset{display:block;margin:2px auto 4px;background:none;border:none;font:inherit;font-size:12px;color:var(--ow-orange);text-decoration:underline;cursor:pointer;padding:4px}
.ow-mockup__maxsize{display:block;width:100%;margin-top:8px;padding:9px 14px;border:1.5px solid var(--ow-border-strong);border-radius:var(--ow-radius-sm);background:transparent;color:var(--ow-text);font:inherit;font-size:13px;font-weight:700;cursor:pointer;transition:border-color .12s ease,background .12s ease}
.ow-mockup__maxsize:hover{border-color:var(--ow-orange);background:rgba(255,107,0,.06)}
.ow-mockup__reset[hidden]{display:none}
.ow-approval-note{font-size:13px;color:var(--ow-text);line-height:1.45;background:var(--ow-bg-soft);border-left:3px solid var(--ow-orange);padding:12px 14px;border-radius:6px;margin-bottom:14px}
.ow-approval-note strong{font-weight:700;color:var(--ow-text)}
.ow-summary__row--discount .ow-summary__label,.ow-summary__row--discount .ow-summary__val{color:var(--ow-orange);font-weight:700}
.ow-summary__row--total .ow-summary__val{font-size:16px}
.ow-summary__row--subtotal{font-size:13px;color:var(--ow-text-dim)}
.ow-summary__row--subtotal .ow-summary__val{text-decoration:line-through}
.ow-running{display:flex;align-items:baseline;flex-wrap:wrap;gap:2px 10px;padding:8px 18px;background:var(--ow-bg-soft);border-top:1px solid var(--ow-border);border-bottom:1px solid var(--ow-border);font-size:13px;flex-shrink:0}
.ow-running[hidden]{display:none!important}
.ow-running__label{color:var(--ow-text-dim)}
.ow-running__val{font-weight:700;font-size:17px;color:var(--ow-text)}
.ow-running__note{flex-basis:100%;font-size:11px;color:var(--ow-text-dim)}
.ow-artwork-btn--create{border-color:var(--ow-orange);color:var(--ow-orange);font-weight:700}
.ow-totals{display:flex;gap:12px;margin-bottom:14px;flex-wrap:wrap}
.ow-totals__field{flex:1;min-width:140px;display:flex;flex-direction:column;gap:6px}
.ow-totals__label{font-size:13px;font-weight:700;color:var(--ow-text)}
.ow-totals__stepper{display:flex;align-items:stretch;border:2px solid var(--ow-border-strong);border-radius:var(--ow-radius-sm);overflow:hidden;background:#fff}
.ow-totals__btn{background:#fff;border:none;width:44px;height:46px;font-size:20px;font-weight:700;cursor:pointer;color:var(--ow-text);font-family:inherit;flex-shrink:0}
.ow-totals__btn:hover{background:var(--ow-bg-soft)}
.ow-totals__input{flex:1;border:none;text-align:center;font-size:18px;font-weight:700;font-family:inherit;color:var(--ow-text);background:#fff;-moz-appearance:textfield;padding:0 4px;min-width:0}
.ow-totals__input::-webkit-outer-spin-button,.ow-totals__input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
.ow-totals__input:focus{outline:none;background:var(--ow-bg-soft)}
.ow-totals__hint{font-size:11px;color:var(--ow-text-dim)}
.ow-product-pick-grid{display:flex;flex-direction:column;gap:8px;margin-bottom:14px}
.ow-product-pick-row{display:flex;align-items:center;gap:12px;border:2px solid var(--ow-border);background:var(--ow-bg);border-radius:var(--ow-radius-sm);padding:10px 12px;cursor:pointer;font-family:inherit;width:100%;text-align:left}
.ow-product-pick-row:hover{border-color:var(--ow-border-strong)}
.ow-product-pick-row.is-selected{border-color:var(--ow-orange);background:#fff7ed}
.ow-product-pick-row__check{width:22px;height:22px;border:2px solid var(--ow-border);border-radius:5px;display:flex;align-items:center;justify-content:center;flex-shrink:0;color:#fff;font-size:14px;font-weight:700;background:#fff}
.ow-product-pick-row.is-selected .ow-product-pick-row__check{background:var(--ow-orange);border-color:var(--ow-orange)}
.ow-product-pick-row__body{flex:1;min-width:0}
.ow-product-pick-row__title{font-size:14px;font-weight:700;color:var(--ow-text)}
.ow-product-pick-row__desc{font-size:12px;color:var(--ow-text-dim)}
.ow-size-matrix{margin-bottom:14px;border:1px solid var(--ow-border);border-radius:var(--ow-radius-sm);overflow:hidden}
.ow-size-matrix__header{padding:10px 12px;background:var(--ow-bg-soft);font-size:13px;font-weight:700;color:var(--ow-text);border-bottom:1px solid var(--ow-border);display:flex;justify-content:space-between;align-items:center;gap:8px}
.ow-size-matrix__total{font-size:12px;color:var(--ow-text-dim);font-weight:600}
.ow-size-matrix__body{display:grid;grid-template-columns:repeat(auto-fit,minmax(108px,1fr));gap:1px;background:var(--ow-border)}
.ow-size-cell{display:flex;flex-direction:column;align-items:center;gap:6px;padding:10px 8px;background:#fff}
.ow-size-cell__label{font-size:11px;font-weight:700;color:var(--ow-text-dim);text-transform:uppercase;letter-spacing:.04em}
.ow-size-cell__stepper{display:inline-flex;align-items:stretch;border:1px solid var(--ow-border);border-radius:5px;overflow:hidden;background:#fff}
.ow-size-cell__btn{background:#fff;border:none;width:26px;height:32px;font-size:14px;font-weight:700;cursor:pointer;color:var(--ow-text);font-family:inherit;flex-shrink:0;padding:0}
.ow-size-cell__btn:hover{background:var(--ow-bg-soft)}
.ow-size-cell__input{width:42px;border:none;text-align:center;font-family:inherit;font-size:14px;font-weight:600;color:var(--ow-text);background:#fff;-moz-appearance:textfield;padding:0 2px;min-width:0}
.ow-size-cell__input:focus{outline:none;background:var(--ow-bg-soft)}
.ow-size-cell__input::-webkit-outer-spin-button,.ow-size-cell__input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
.ow-group-progress{font-size:12px;color:var(--ow-text-dim);padding:8px 12px;background:var(--ow-bg-soft);border-radius:6px;margin-bottom:12px;text-align:center}
.ow-group-progress strong{color:var(--ow-orange);font-weight:700}
.ow-last-chance{margin:14px 0;padding:12px 14px;background:#fff;border:2px dashed var(--ow-border-strong);border-radius:var(--ow-radius-sm);font-size:13px;line-height:1.4;color:var(--ow-text);display:flex;flex-direction:column;gap:8px;align-items:flex-start}
.ow-last-chance strong{color:var(--ow-text);font-weight:700;display:block}
.ow-last-chance .ow-btn{font-size:13px;padding:8px 14px;align-self:stretch}
.ow-tier-stretch-hint{background:#fff7ed;border:2px solid var(--ow-orange);border-radius:var(--ow-radius-sm);padding:12px 14px;margin-bottom:14px;font-size:14px;line-height:1.45;color:var(--ow-text)}
.ow-tier-stretch-hint[hidden]{display:none!important}
.ow-tier-stretch-hint strong{color:var(--ow-orange);font-weight:700}
.ow-bundle-options{display:flex;flex-direction:column;gap:10px;margin-bottom:14px}
.ow-bundle-opt{display:flex;align-items:center;gap:12px;padding:14px;border:2px solid var(--ow-border);background:var(--ow-bg);border-radius:var(--ow-radius-sm);text-align:left;cursor:pointer;font-family:inherit;width:100%;position:relative}
.ow-bundle-opt:hover{filter:brightness(1.02)}
/* Strong selected state — black outline + checkmark badge, easy to spot
   against the green/red/orange category colours. */
.ow-bundle-opt.is-selected{background:#fff!important;outline:3px solid #000;outline-offset:-1px;z-index:1}
.ow-bundle-opt.is-selected::before{content:"✓ SELECTED";position:absolute;top:-11px;left:12px;background:#000;color:#fff;padding:3px 10px;font-size:10px;font-weight:700;border-radius:3px;letter-spacing:.04em;z-index:2}
/* Dim non-selected options when one IS selected so the chosen one really pops */
.ow-bundle-options:has(.is-selected) .ow-bundle-opt:not(.is-selected){opacity:.45;filter:saturate(.4)}
.ow-bundle-options:has(.is-selected) .ow-bundle-opt:not(.is-selected):hover{opacity:.75;filter:saturate(.8)}
/* Color-coded badges */
.ow-bundle-opt.is-cheapest{border-color:#2f7d3a;background:#f1faf3}
.ow-bundle-opt.is-cheapest::after{content:"YOUR SELECTION";position:absolute;top:-9px;right:12px;background:#2f7d3a;color:#fff;padding:2px 8px;font-size:10px;font-weight:700;border-radius:3px;letter-spacing:.04em}
.ow-bundle-opt.is-stretch{border-color:var(--ow-orange);background:#fff7ed}
.ow-bundle-opt.is-stretch::after{content:"CURRENT BUNDLE OFFER";position:absolute;top:-9px;right:12px;background:var(--ow-orange);color:#fff;padding:2px 8px;font-size:10px;font-weight:700;border-radius:3px;letter-spacing:.04em}
.ow-bundle-opt.is-worst{border-color:#c4262d;background:#fdf2f2}
.ow-bundle-opt.is-worst::after{content:"WORST VALUE";position:absolute;top:-9px;right:12px;background:#c4262d;color:#fff;padding:2px 8px;font-size:10px;font-weight:700;border-radius:3px;letter-spacing:.04em}
.ow-bundle-opt__rrp{font-size:11px;color:var(--ow-text-dim);font-weight:600;white-space:nowrap;text-align:right;margin-top:2px;text-decoration:line-through}
.ow-bundle-opt__body{flex:1;min-width:0}
.ow-bundle-opt__label{font-size:14px;font-weight:700;color:var(--ow-text);margin-bottom:2px}
.ow-bundle-opt__detail{font-size:12px;color:var(--ow-text-dim);line-height:1.35}
.ow-bundle-opt__price{font-size:18px;font-weight:700;color:var(--ow-text);white-space:nowrap;text-align:right}
.ow-bundle-opt__per{font-size:12px;color:var(--ow-text-dim);font-weight:600;white-space:nowrap;text-align:right;margin-top:2px}
.ow-bundle-opt__shipping{font-size:11px;color:var(--ow-orange);font-weight:700;white-space:nowrap;text-align:right;margin-top:4px;text-transform:uppercase;letter-spacing:.04em}
.ow-bundle-opt__shipping-out{font-size:11px;color:var(--ow-text-dim);font-weight:600;white-space:nowrap;text-align:right;margin-top:4px}
.ow-bundle-opt__save{font-size:11px;color:var(--ow-orange);font-weight:700;white-space:nowrap;text-align:right;margin-top:2px}
.ow-bundle-intro{font-size:13px;color:var(--ow-text-dim);line-height:1.45;margin:0 0 12px;padding:10px 12px;background:var(--ow-bg-soft);border-left:3px solid var(--ow-orange);border-radius:6px}
.ow-bundle-intro strong{color:var(--ow-text);font-weight:700}
.ow-rush-options{display:flex;flex-direction:column;gap:10px;margin-bottom:14px}
.ow-rush-opt{display:flex;align-items:center;gap:12px;padding:14px;border:2px solid var(--ow-border);background:var(--ow-bg);border-radius:var(--ow-radius-sm);text-align:left;cursor:pointer;font-family:inherit;width:100%}
.ow-rush-opt:hover{border-color:var(--ow-border-strong)}
.ow-rush-opt.is-selected{border-color:var(--ow-orange);background:#fff7ed}
.ow-rush-opt__body{flex:1;min-width:0}
.ow-rush-opt__label{font-size:14px;font-weight:700;color:var(--ow-text);margin-bottom:2px}
.ow-rush-opt__desc{font-size:12px;color:var(--ow-text-dim)}
.ow-rush-opt__pricecol{text-align:right;white-space:nowrap}
.ow-rush-opt__price{font-size:16px;font-weight:700;color:var(--ow-orange)}
.ow-rush-opt__pct{font-size:11px;color:var(--ow-text-dim);font-weight:600}
.ow-rush-opt__free{font-size:14px;font-weight:700;color:var(--ow-text-dim);letter-spacing:.04em}
/* Rush Production Fee row tidying — hide qty controls so cart shows just the price */
.ow-rush-fee-row [class*="quantity"],
.ow-rush-fee-row input[name^="updates"],
.ow-rush-fee-row input[name="quantity"],
.ow-rush-fee-row [data-quantity-input],
.ow-rush-fee-row .cart-item__quantity,
.ow-rush-fee-row .cart-item-quantity,
.ow-rush-fee-row .quantity-selector,
.ow-rush-fee-row .quantity__input,
.ow-rush-fee-row [class*="qty"],
.ow-rush-fee-row .cart__qty-input,
.ow-rush-fee-row .product-quantity-box{display:none!important}
/* Floating "Start your order" launcher — bottom-right corner, always visible */
/* Bottom-LEFT to avoid colliding with the live chat widget which lives bottom-right */
.ow-launch-btn{position:fixed;bottom:18px;left:18px;z-index:9998;background:#000;color:#fff;border:2px solid #000;border-radius:999px;padding:12px 18px;font-size:14px;font-weight:700;cursor:pointer;font-family:inherit;box-shadow:0 8px 20px rgba(0,0,0,.25);transition:background .12s ease,transform .06s ease;display:inline-flex;align-items:center;gap:8px}
.ow-launch-btn:hover{background:var(--ow-orange);border-color:var(--ow-orange)}
.ow-launch-btn:active{transform:translateY(1px)}
.ow-launch-btn[hidden]{display:none!important}
.ow-launch-btn__icon{display:inline-flex;width:18px;height:18px;align-items:center;justify-content:center}
@media (max-width:600px){.ow-launch-btn{padding:10px 14px;font-size:13px;bottom:14px;left:14px}}
/* Order this design — buttons injected onto gallery tiles */
.ow-tile-order-btn{display:block;width:100%;margin-top:8px;padding:8px 10px;background:#000;color:#fff;border:2px solid #000;border-radius:6px;font-size:12px;font-weight:700;cursor:pointer;font-family:inherit;letter-spacing:.02em;transition:background .12s ease}
.ow-tile-order-btn:hover{background:var(--ow-orange);border-color:var(--ow-orange)}
/* Validation — highlight missing fields with an orange pulse so customers
   know what to fix when they try to Continue without completing a step. */
@keyframes ow-pulse{0%,100%{box-shadow:0 0 0 0 rgba(255,107,0,0)}50%{box-shadow:0 0 0 6px rgba(255,107,0,.35)}}
.ow-missing{animation:ow-pulse 0.7s ease-in-out 2;border-color:var(--ow-orange)!important;border-radius:var(--ow-radius-sm);outline:2px solid var(--ow-orange);outline-offset:2px}
.ow-step-error{padding:10px 14px;background:#fff7ed;border-left:3px solid var(--ow-orange);border-radius:6px;font-size:13px;font-weight:600;color:var(--ow-text);margin-bottom:12px;line-height:1.4}
.ow-step-error[hidden]{display:none!important}
.ow-tier-card{border:2px solid var(--ow-border-strong);border-radius:var(--ow-radius-sm);background:#fff;padding:12px 14px;margin-bottom:14px}
.ow-tier-card__current{font-weight:700;font-size:14px;color:var(--ow-text);margin-bottom:2px;display:flex;align-items:center;gap:6px}
.ow-tier-card__current--active{color:var(--ow-orange)}
.ow-tier-card__check{display:inline-flex;width:16px;height:16px;background:var(--ow-orange);color:#fff;border-radius:50%;align-items:center;justify-content:center;font-size:11px;font-weight:700;flex-shrink:0}
.ow-tier-card__next{font-size:13px;color:var(--ow-text-dim);margin-bottom:10px}
.ow-tier-card__next strong{color:var(--ow-text);font-weight:700}
.ow-tier-table{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--ow-border);border:1px solid var(--ow-border);border-radius:6px;overflow:hidden;font-size:13px;margin-top:8px}
.ow-tier-table__row{display:contents}
.ow-tier-table__cell{background:#fff;padding:6px 10px;color:var(--ow-text)}
.ow-tier-table__row--current .ow-tier-table__cell{background:#fff7ed;color:var(--ow-orange);font-weight:700}
.ow-tier-table__header .ow-tier-table__cell{background:var(--ow-bg-soft);font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:var(--ow-text-dim)}
body.ow-no-scroll{overflow:hidden}
#order-wizard-root{position:fixed;inset:0;z-index:99999;pointer-events:none}
#order-wizard-root[hidden]{display:none!important}
.ow-modal{position:fixed;inset:0;display:flex;align-items:stretch;justify-content:center;pointer-events:auto}
.ow-modal[hidden]{display:none!important}
.ow-modal__backdrop{position:absolute;inset:0;background:rgba(0,0,0,.55);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);animation:ow-fade-in .18s ease both}
.ow-modal__box{position:relative;width:100%;max-width:100%;background:var(--ow-bg);color:var(--ow-text);display:flex;flex-direction:column;overflow:hidden;animation:ow-slide-up .24s cubic-bezier(.22,1,.36,1) both;border-radius:0;box-shadow:var(--ow-shadow);max-height:100dvh;font-family:inherit}
@media(min-width:720px){.ow-modal__box{margin:auto;max-width:600px;max-height:90vh;border-radius:var(--ow-radius);border:2px solid var(--ow-border-strong)}}
.ow-testmode-banner{background:var(--ow-orange);color:#fff;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;padding:8px 14px;text-align:center;flex-shrink:0}
.ow-testmode-banner[hidden]{display:none!important}
.ow-modal__header{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:16px 18px;border-bottom:1px solid var(--ow-border);flex-shrink:0}
.ow-modal__title{font-size:18px;font-weight:700;margin:0;letter-spacing:-.01em}
.ow-modal__close{background:transparent;border:1px solid var(--ow-border);width:36px;height:36px;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--ow-text)}
.ow-modal__close:hover{background:var(--ow-bg-soft);border-color:var(--ow-border-strong)}
.ow-design-context{display:flex;gap:12px;align-items:center;padding:14px 18px;background:var(--ow-bg-soft);border-bottom:1px solid var(--ow-border);flex-shrink:0}
.ow-design-context[hidden]{display:none!important}
.ow-design-context__img{width:56px;height:56px;object-fit:cover;border-radius:var(--ow-radius-sm);background:#eee;flex-shrink:0;border:1px solid var(--ow-border)}
.ow-design-context__meta{display:flex;flex-direction:column;gap:2px;flex:1;min-width:0}
.ow-design-context__label{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--ow-text-dim)}
.ow-design-context__prompt{font-size:14px;line-height:1.3;color:var(--ow-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ow-modal__body{flex:1 1 auto;min-height:0;overflow:hidden;display:flex;flex-direction:column}
/* Chat is the "let it shrink" component: starts visible, grows up to a cap,
   but always yields height to the step (the actionable part) when there's
   not enough room. */
.ow-modal__chat{flex:0 1 auto;min-height:0;max-height:28vh;overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;background:var(--ow-bg)}
/* Step is the "always visible" component: takes whatever space it needs,
   scrolls internally only when its own content overflows the remaining
   space. No fixed max-height so a tall step (size matrix etc.) gets
   priority. */
.ow-modal__steps{flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;border-top:1px solid var(--ow-border);background:var(--ow-bg)}
.ow-chat{padding:14px 18px;display:flex;flex-direction:column;gap:10px}
/* Hide the legacy ai-attach UI (the per-file-input "Use my AI design" link
   + the marketing banner) while the wizard modal is open. Both are still
   needed when a customer is in 30-day classic mode — they only get in the
   way when the wizard's currently driving the order. */
body.ow-no-scroll .ai-attach-link__wrap,
body.ow-no-scroll .ai-attach-banner{display:none!important}
.ow-chat__bubble{padding:10px 14px;border-radius:14px;font-size:15px;line-height:1.4;max-width:85%;word-wrap:break-word}
.ow-chat__bubble--ai{background:var(--ow-bg-ai);color:var(--ow-text);align-self:flex-start;border-bottom-left-radius:4px}
.ow-chat__bubble--user{background:var(--ow-bg-user);color:var(--ow-text);align-self:flex-end;border-bottom-right-radius:4px;font-weight:600}
.ow-chat__bubble--in{animation:ow-bubble-in 0.22s cubic-bezier(.2,.7,.3,1)}
@keyframes ow-bubble-in{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
.ow-chat__typing{display:inline-flex;gap:5px;align-items:center;padding:12px 14px!important;width:fit-content}
.ow-chat__dot{width:7px;height:7px;border-radius:50%;background:currentColor;opacity:0.35;display:inline-block;animation:ow-typing 1.25s infinite ease-in-out}
.ow-chat__dot:nth-child(2){animation-delay:0.18s}
.ow-chat__dot:nth-child(3){animation-delay:0.36s}
@keyframes ow-typing{0%,60%,100%{transform:translateY(0);opacity:0.35}30%{transform:translateY(-4px);opacity:0.95}}
.ow-step{padding:14px 18px 20px;border-top:1px solid var(--ow-border);background:var(--ow-bg);flex-shrink:0}
.ow-step[hidden]{display:none!important}
.ow-actions{display:flex;flex-direction:column;gap:10px}
@media(min-width:480px){.ow-actions{flex-direction:row;flex-wrap:wrap}.ow-actions .ow-btn{flex:1;min-width:140px}}
.ow-btn{appearance:none;border:2px solid var(--ow-border-strong);padding:14px 20px;font-size:15px;font-weight:700;border-radius:var(--ow-radius-sm);cursor:pointer;text-align:center;font-family:inherit}
.ow-btn:active{transform:translateY(1px)}
.ow-btn--primary{background:#000;color:#fff}
.ow-btn--primary:hover{background:var(--ow-orange);border-color:var(--ow-orange)}
.ow-btn--ghost{background:transparent;color:var(--ow-text)}
.ow-btn--ghost:hover{background:var(--ow-bg-soft)}
@keyframes ow-fade-in{from{opacity:0}to{opacity:1}}
@keyframes ow-slide-up{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
@media(prefers-reduced-motion:reduce){.ow-modal__backdrop,.ow-modal__box{animation:none}}
`;
    const styleEl = document.createElement("style");
    styleEl.id = "ow-injected-styles";
    styleEl.textContent = css;
    document.head.appendChild(styleEl);
  }

  function injectDom() {
    if (document.getElementById("order-wizard-root")) return;
    const html = `
<div id="order-wizard-root" hidden>
  <div class="ow-modal" role="dialog" aria-modal="true" aria-labelledby="ow-modal-title" hidden>
    <div class="ow-modal__backdrop" data-ow-close></div>
    <div class="ow-modal__box">
      <div class="ow-testmode-banner" id="ow-testmode-banner" hidden>TEST MODE — customers don't see this yet</div>
      <header class="ow-modal__header">
        <h2 class="ow-modal__title" id="ow-modal-title">Order your design</h2>
        <button class="ow-modal__close" type="button" data-ow-close aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </header>
      <div class="ow-modal__body">
      <div class="ow-design-context" id="ow-design-context">
        <img class="ow-design-context__img" id="ow-design-context-img" alt="Your design">
        <div class="ow-design-context__meta">
          <span class="ow-design-context__label">Your design</span>
          <span class="ow-design-context__prompt" id="ow-design-context-prompt"></span>
        </div>
      </div>
      <div class="ow-modal__chat" id="ow-chat-wrap">
        <div class="ow-chat" id="ow-chat" aria-live="polite"></div>
      </div>
      <div class="ow-running" id="ow-running" hidden aria-live="polite">
        <span class="ow-running__label">Estimated total so far</span>
        <span class="ow-running__val" id="ow-running-val"></span>
        <span class="ow-running__note" id="ow-running-note"></span>
      </div>
      <div class="ow-modal__steps">
      <section class="ow-step" data-step="optin">
        <div class="ow-actions">
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="optin-yes">Yeah, help me</button>
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="optin-no">I'll take it from here</button>
        </div>
        <button class="ow-optin-reorder" type="button" data-ow-action="reorder-shortcut">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
          Reordering a design we've already printed?
        </button>
      </section>
      <section class="ow-step" data-step="audience" hidden>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="audience-solo">Just me</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="audience-group">A group</button>
        </div>
        <button class="ow-optin-reorder" type="button" data-ow-action="reorder-shortcut">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
          Reordering a design we've already printed?
        </button>
      </section>
      <section class="ow-step" data-step="solo-category" hidden>
        <h3 class="ow-step__title">What gear are you after?</h3>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="pick-category" data-category="tee">Tees</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="pick-category" data-category="hoodie">Hoodies</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="pick-category" data-category="both">Both</button>
        </div>
      </section>

      <section class="ow-step" data-step="solo-product" hidden>
        <h3 class="ow-step__title" id="ow-product-title">What gear?</h3>
        <div class="ow-product-grid" id="ow-product-grid">
          <div class="ow-loading">Loading...</div>
        </div>
        <p class="ow-exit-link">
          Looking for something else?
          <a href="#" data-ow-action="browse-all" id="ow-browse-all">Browse the full shop &rarr;</a>
        </p>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-category">← Back</button>
        </div>
      </section>

      <section class="ow-step" data-step="solo-variant" hidden>
        <h3 class="ow-step__title" id="ow-variant-title">Colour & size</h3>
        <div class="ow-swatches" id="ow-swatches"></div>
        <div class="ow-sizes" id="ow-sizes"></div>
        <div class="ow-qty">
          <span class="ow-qty__label">How many?</span>
          <div class="ow-qty__group">
            <button class="ow-qty__btn" type="button" data-ow-action="qty-dec" aria-label="Decrease quantity">−</button>
            <input class="ow-qty__val" id="ow-qty-val" type="number" min="1" max="200" value="1" inputmode="numeric">
            <button class="ow-qty__btn" type="button" data-ow-action="qty-inc" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <div class="ow-tier-card" id="ow-tier-card"></div>
        <div class="ow-step-error" id="ow-variant-error" hidden></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-product">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="variant-continue" id="ow-variant-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="solo-print" hidden>
        <h3 class="ow-step__title">Where would you like your prints to be?</h3>
        <div class="ow-print-options" id="ow-print-options"></div>
        <div class="ow-step-error" id="ow-print-error" hidden></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-variant">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="print-continue" id="ow-print-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="solo-artwork" hidden>
        <h3 class="ow-step__title">Pick artwork for each print</h3>
        <label class="ow-reorder-toggle">
          <input type="checkbox" id="ow-reorder-toggle" data-ow-action="toggle-reorder">
          Reordering a previous design? Tell us anything that'll help us find it.
        </label>
        <div class="ow-reorder-fields" id="ow-reorder-fields" hidden>
          <p class="ow-reorder-note">Your size and quantity are already set from the steps above &mdash; this is just so we know which design to use.</p>
          <label class="ow-field-label" for="ow-reorder-order-number">Order number, your name, or anything that helps us find it (optional)</label>
          <input type="text" class="ow-text-input" id="ow-reorder-order-number" placeholder="e.g. #2413, or your name">
          <label class="ow-field-label" for="ow-reorder-details">Which design are we reordering? (optional)</label>
          <textarea class="ow-textarea" id="ow-reorder-details" placeholder="e.g. Same front chest design as that order"></textarea>
        </div>
        <div id="ow-artwork-list"></div>
        <div class="ow-error" id="ow-artwork-error" hidden></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-print">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="artwork-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="solo-mockup" hidden>
        <h3 class="ow-step__title" id="ow-mockup-title">Position your design</h3>
        <p class="ow-mockup__step-count" id="ow-mockup-step-count"></p>
        <div class="ow-mockup__stage" id="ow-mockup-stage">
          <img class="ow-mockup__garment" id="ow-mockup-garment" alt="">
          <div class="ow-mockup__artlayer" id="ow-mockup-artlayer">
            <img class="ow-mockup__art" id="ow-mockup-art" alt="" draggable="false">
            <div class="ow-mockup__handle" id="ow-mockup-handle" data-handle="br"></div>
          </div>
          <div class="ow-mockup__loading" id="ow-mockup-loading" hidden>
            <span class="ow-mockup__spinner" aria-hidden="true"></span>
            <span class="ow-mockup__loading-text">Cleaning up your artwork…</span>
          </div>
        </div>
        <div class="ow-mockup__size-chip" id="ow-mockup-size-chip" hidden></div>
        <button class="ow-mockup__maxsize" type="button" data-ow-action="mockup-maxsize">Make it as big as possible</button>
        <p class="ow-mockup__hint" id="ow-mockup-hint">Drag to reposition, or use the corner handle to resize.</p>
        <button class="ow-mockup__reset" type="button" data-ow-action="mockup-reset" id="ow-mockup-reset" hidden>Use original artwork (undo background clean-up)</button>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="mockup-back">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="mockup-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="solo-bundle-deal" hidden>
        <h3 class="ow-step__title">There's a better deal available</h3>
        <p class="ow-bundle-intro" id="ow-bundle-intro"></p>
        <div class="ow-bundle-options" id="ow-bundle-options"></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-print">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="bundle-continue" id="ow-bundle-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="solo-rush" hidden>
        <h3 class="ow-step__title">When do you need it?</h3>
        <div class="ow-rush-options" id="ow-rush-options"></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-mockup">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="rush-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="solo-confirm" hidden>
        <h3 class="ow-step__title">All sorted?</h3>
        <div class="ow-approval-note">
          <strong>What happens next:</strong> we check every design before printing. If anything looks off we'll contact you before your order goes to press.
        </div>
        <div class="ow-summary" id="ow-summary"></div>
        <div class="ow-error" id="ow-cart-error" hidden></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-mockup">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="add-to-cart" id="ow-add-to-cart">Add to cart</button>
        </div>
      </section>

      <section class="ow-step" data-step="group-totals" hidden>
        <h3 class="ow-step__title">How many in the group?</h3>
        <div class="ow-totals">
          <div class="ow-totals__field">
            <span class="ow-totals__label">Adults</span>
            <div class="ow-totals__stepper">
              <button class="ow-totals__btn" type="button" data-ow-action="group-adults-dec" aria-label="Decrease adults">−</button>
              <input class="ow-totals__input" id="ow-group-adults" type="number" min="0" max="500" value="0" inputmode="numeric">
              <button class="ow-totals__btn" type="button" data-ow-action="group-adults-inc" aria-label="Increase adults">+</button>
            </div>
            <span class="ow-totals__hint">Tees / hoodies</span>
          </div>
          <div class="ow-totals__field">
            <span class="ow-totals__label">Kids</span>
            <div class="ow-totals__stepper">
              <button class="ow-totals__btn" type="button" data-ow-action="group-kids-dec" aria-label="Decrease kids">−</button>
              <input class="ow-totals__input" id="ow-group-kids" type="number" min="0" max="500" value="0" inputmode="numeric">
              <button class="ow-totals__btn" type="button" data-ow-action="group-kids-inc" aria-label="Increase kids">+</button>
            </div>
            <span class="ow-totals__hint">Kids' sizes</span>
          </div>
        </div>
        <div class="ow-tier-card" id="ow-group-tier-card"></div>
        <div class="ow-step-error" id="ow-group-totals-error" hidden></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-audience">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="group-totals-continue" id="ow-group-totals-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="group-products" hidden>
        <h3 class="ow-step__title">What gear are you after?</h3>
        <p class="ow-step__hint" style="font-size:12px;color:var(--ow-text-dim);margin:0 0 12px">Tap as many as you want — mix tees, hoodies, adult and kids gear in the one order.</p>
        <div class="ow-product-pick-grid" id="ow-group-product-pick"></div>
        <div class="ow-step-error" id="ow-group-products-error" hidden></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-group-totals">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="group-products-continue" id="ow-group-products-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="group-sizes" hidden>
        <h3 class="ow-step__title">Sizes for each product</h3>
        <div class="ow-group-progress" id="ow-group-progress"></div>
        <div id="ow-group-size-matrices"></div>
        <div class="ow-error" id="ow-group-sizes-error" hidden></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-group-products">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="group-sizes-continue" id="ow-group-sizes-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="group-colour" hidden>
        <h3 class="ow-step__title" id="ow-group-colour-title">Colour</h3>
        <p style="font-size:12px;color:var(--ow-text-dim);margin:0 0 10px">One colour across all the gear in this order.</p>
        <div class="ow-swatches" id="ow-group-swatches"></div>
        <div class="ow-step-error" id="ow-group-colour-error" hidden></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-group-sizes">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="group-colour-continue" id="ow-group-colour-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="group-print" hidden>
        <h3 class="ow-step__title">Where would you like your prints to be?</h3>
        <p style="font-size:12px;color:var(--ow-text-dim);margin:0 0 10px">Applies to every item in the order.</p>
        <div class="ow-print-options" id="ow-group-print-options"></div>
        <div class="ow-step-error" id="ow-group-print-error" hidden></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-group-colour">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="group-print-continue" id="ow-group-print-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="group-bundle-deal" hidden>
        <h3 class="ow-step__title">There's a better deal available</h3>
        <p class="ow-bundle-intro" id="ow-group-bundle-intro"></p>
        <div class="ow-bundle-options" id="ow-group-bundle-options"></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-group-print">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="group-bundle-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="group-artwork" hidden>
        <h3 class="ow-step__title">Pick artwork for each print</h3>
        <p style="font-size:12px;color:var(--ow-text-dim);margin:0 0 10px">Same artwork applies to every garment.</p>
        <label class="ow-reorder-toggle">
          <input type="checkbox" id="ow-group-reorder-toggle" data-ow-action="group-toggle-reorder">
          Reordering a previous design? Tell us anything that'll help us find it.
        </label>
        <div class="ow-reorder-fields" id="ow-group-reorder-fields" hidden>
          <p class="ow-reorder-note">Your sizes and quantities are already set from the steps above &mdash; this is just so we know which design to use.</p>
          <label class="ow-field-label" for="ow-group-reorder-order-number">Order number, your name, or anything that helps us find it (optional)</label>
          <input type="text" class="ow-text-input" id="ow-group-reorder-order-number" placeholder="e.g. #2413, or your name">
          <label class="ow-field-label" for="ow-group-reorder-details">Which design are we reordering? (optional)</label>
          <textarea class="ow-textarea" id="ow-group-reorder-details" placeholder="e.g. Same front chest design as that order"></textarea>
        </div>
        <div id="ow-group-artwork-list"></div>
        <div class="ow-error" id="ow-group-artwork-error" hidden></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-group-print">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="group-artwork-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="group-mockup" hidden>
        <h3 class="ow-step__title" id="ow-group-mockup-title">Position your design</h3>
        <p class="ow-mockup__step-count" id="ow-group-mockup-step-count"></p>
        <div class="ow-mockup__product-tabs" id="ow-group-mockup-product-tabs"></div>
        <div class="ow-mockup__stage" id="ow-group-mockup-stage">
          <img class="ow-mockup__garment" id="ow-group-mockup-garment" alt="">
          <div class="ow-mockup__artlayer" id="ow-group-mockup-artlayer">
            <img class="ow-mockup__art" id="ow-group-mockup-art" alt="" draggable="false">
            <div class="ow-mockup__handle" id="ow-group-mockup-handle" data-handle="br"></div>
          </div>
          <div class="ow-mockup__loading" id="ow-group-mockup-loading" hidden>
            <span class="ow-mockup__spinner" aria-hidden="true"></span>
            <span class="ow-mockup__loading-text">Cleaning up your artwork…</span>
          </div>
        </div>
        <div class="ow-mockup__size-chip" id="ow-group-mockup-size-chip" hidden></div>
        <button class="ow-mockup__maxsize" type="button" data-ow-action="group-mockup-maxsize">Make it as big as possible</button>
        <p class="ow-mockup__hint" id="ow-group-mockup-hint">Same artwork and placement applies to every garment. Drag to reposition, or use the corner handle to resize.</p>
        <button class="ow-mockup__reset" type="button" data-ow-action="group-mockup-reset" id="ow-group-mockup-reset" hidden>Use original artwork (undo background clean-up)</button>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="group-mockup-back">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="group-mockup-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="group-rush" hidden>
        <h3 class="ow-step__title">When do you need it?</h3>
        <div class="ow-rush-options" id="ow-group-rush-options"></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-group-mockup">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="group-rush-continue">Continue</button>
        </div>
      </section>

      <section class="ow-step" data-step="group-confirm" hidden>
        <h3 class="ow-step__title">All sorted?</h3>
        <div class="ow-approval-note">
          <strong>What happens next:</strong> we check every design before printing — if anything looks off on any garment, we'll contact you before your order goes to press.
        </div>
        <div class="ow-summary" id="ow-group-summary"></div>
        <div class="ow-tier-stretch-hint" id="ow-group-stretch-hint" hidden></div>
        <div class="ow-last-chance">
          <strong>Did you get everyone?</strong> Forgot someone in the group? Add more sizes before you check out.
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-group-sizes-from-confirm">Add more sizes</button>
        </div>
        <div class="ow-error" id="ow-group-cart-error" hidden></div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="back-to-group-mockup">← Back</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="group-add-to-cart" id="ow-group-add-to-cart">Add to cart</button>
        </div>
      </section>

      <section class="ow-step" data-step="solo-success" hidden>
        <div class="ow-success">
          <div class="ow-success__icon">✓</div>
          <h3 class="ow-success__title" id="ow-success-title">Added to cart!</h3>
          <p class="ow-success__msg">Want to use this design on something else, or head to checkout?</p>
        </div>
        <div class="ow-actions">
          <button class="ow-btn ow-btn--ghost" type="button" data-ow-action="add-another">Add another item</button>
          <button class="ow-btn ow-btn--primary" type="button" data-ow-action="goto-cart">Go to cart →</button>
        </div>
      </section>
      </div><!-- /.ow-modal__steps -->
      </div><!-- /.ow-modal__body -->
    </div>
  </div>
</div>
`;
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html;
    document.body.appendChild(wrapper.firstElementChild);
  }

  function boot() {
    injectStyles();
    injectDom();
    injectLaunchButton();
    bindListeners();
    // Cart cleanup runs once at boot + on a slight delay to catch
    // any async cart rendering. Doesn't error on non-cart pages.
    tidyRushFeeInCart();
    setTimeout(tidyRushFeeInCart, 500);
    setTimeout(tidyRushFeeInCart, 1500);
    // Re-tidy when the cart updates (themes often dispatch this event)
    document.addEventListener("cart:refresh", tidyRushFeeInCart);
    document.addEventListener("cart:updated", tidyRushFeeInCart);
    // Inject "Order this design" buttons into inspiration + my-designs tiles
    // wherever this script loads (one-time, deferred so DOM is ready).
    setTimeout(injectGalleryOrderButtons, 600);
    setTimeout(injectGalleryOrderButtons, 1800);
  }

  /**
   * Floating "Start your order" launcher in the bottom-right corner of
   * every page. Hidden when:
   *   - The wizard modal is currently open
   *   - We're on the AI designer page (already has its own CTA)
   *   - We're on /cart or /checkout (customer is past the decision point)
   */
  function injectLaunchButton() {
    if (document.getElementById("ow-launch-btn")) return;
    if (/\/cart|\/checkout|\/account/i.test(location.pathname)) return;
    // Product pages (2026-09-02, order-path cleanup brief): the inline
    // builder is already on screen there, so a second "Start your order"
    // entry point floating over it is redundant — suppressed here, kept
    // everywhere else it still earns its place (home, collections, etc).
    if (/\/products\//i.test(location.pathname)) return;
    // Merch PDPs suppress all wizard entry points — flag is set by the PDP
    // launcher gate after it fetches the product's tags (any tag containing
    // "merch"). See the PDP wizard launcher IIFE for the gate logic.
    if (window.__owSuppressLauncher) return;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.id = "ow-launch-btn";
    btn.className = "ow-launch-btn";
    btn.setAttribute("data-ow-launch", "1");
    btn.setAttribute("aria-label", "Start your order");
    btn.innerHTML =
      '<span class="ow-launch-btn__icon">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
          '<polyline points="9 18 15 12 9 6"/>' +
        '</svg>' +
      '</span>' +
      'Start your order';
    btn.addEventListener("click", function () {
      // Open the wizard without a design URL — triggers the "Got a design ready?" step
      openModal(null, null);
    });
    document.body.appendChild(btn);

    // Hide the launcher when the wizard is open, show again when closed.
    const observer = new MutationObserver(function () {
      const root = document.getElementById("order-wizard-root");
      const isOpen = root && !root.hasAttribute("hidden");
      if (isOpen) btn.setAttribute("hidden", "");
      else btn.removeAttribute("hidden");
    });
    const root = document.getElementById("order-wizard-root");
    if (root) observer.observe(root, { attributes: true, attributeFilter: ["hidden"] });
  }

  /**
   * Scan the page for design tiles in the inspiration showcase, my-designs
   * library, or all-designs admin gallery. For each tile, inject a
   * "Order this design →" button that opens the wizard pre-loaded with
   * that design.
   *
   * We don't need to know exact selectors from the Liquid templates because
   * we look for any image that's an R2 design URL (which is what all three
   * gallery types render).
   */
  function injectGalleryOrderButtons() {
    const pages = /\/pages\/(inspiration|my-designs|all-designs)/i;
    if (!pages.test(location.pathname)) return;
    // Find every img whose src is an R2 design URL
    const imgs = document.querySelectorAll("img");
    imgs.forEach(function (img) {
      const src = img.src || "";
      if (!/r2\.dev\/(designs|customer-uploads|prints)\//.test(src) &&
          !/\/cdn\/(designs|customer-uploads|prints)\//.test(src)) return;
      // Find the tile container — try common patterns
      const tile = img.closest("[class*=tile], [class*=card], [class*=grid-item], [class*=design], li, article, figure") || img.parentElement;
      if (!tile || tile.querySelector("[data-wizard-trigger]")) return; // already injected
      // Extract prompt if available — most tiles have it in a sibling element
      let prompt = "";
      const promptEl = tile.querySelector("[class*=prompt], [class*=caption], [class*=title]");
      if (promptEl) prompt = (promptEl.textContent || "").trim().slice(0, 200);
      // Build the order button
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "ow-tile-order-btn";
      btn.setAttribute("data-wizard-trigger", "1");
      btn.setAttribute("data-design-url", src);
      btn.setAttribute("data-design-prompt", prompt);
      btn.textContent = "Order this design →";
      // Append to the tile
      tile.appendChild(btn);
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  const state = {
    open: false, step: "optin",
    designUrl: null, designPrompt: null,
    audience: null,
    selectedCategory: null,    // "tee" | "hoodie" | "both"
    products: [],              // cached recommended products
    printProducts: {},         // resolved at boot: { front_left_chest, large_front, back_full }
    selectedProduct: null,     // { handle, title, variants, images, options, tags }
    selectedColour: null,
    selectedSize: null,
    selectedVariantId: null,
    selectedQuantity: 1,
    selectedPrintLocations: [],// array of keys: ["large_front", "back_full", "front_left_chest"]
    printArtwork: {},          // map: location → artwork URL (overwritten in place with the processed/re-uploaded URL once the mockup step runs)
    artworkSources: {},        // map: location → "current" | "upload" | "saved"
    artworkLabels: {},         // map: location → human-readable label (filename for uploads, prompt for AI/saved)
    savedPickerOpen: null,     // which location's saved-designs grid is expanded
    printPlacement: {},        // map: location → {x,y,w,h} fractional rect (0-1, same space as printZone()) — where the customer dragged/resized it
    printBgRemoved: {},        // map: location → bool, whether auto background/black removal changed the artwork
    printArtworkOriginal: {},  // map: location → pre-processing URL, kept only for the mockup step's own "reset to original" control
    printArtworkNaturalSize: {}, // map: location → {w,h} px of the FINAL (cropped) artwork — needed for placement fit + live mm display
    mockupActiveLocation: null,// which print location's placement UI is currently shown (when multiple locations selected)
    cartError: null,

    // Reorder mode (staff-assisted) — customer wants the same design as a
    // past order, no new artwork to upload/position. When active, the
    // artwork picker + mockup step are skipped entirely; the cart line
    // just carries the order number + a free-text note for staff to match
    // up manually against the original order.
    reorderMode: false,
    reorderOrderNumber: "",
    reorderDetails: "",

    // ===== Group flow state =====
    groupAdults: 0,            // number of adults (informational, used for tier preview)
    groupKids: 0,              // number of kids
    groupSelectedProductTypes: [], // ["adult_tee", "adult_hoodie", "kids_tee", "kids_hoodie"]
    groupSelectedProducts: {},     // map: productType → selected product object (variants/colours/sizes from Shopify)
    groupSizes: {},                // map: productType → { S: qty, M: qty, ... }
    groupColour: null,             // single colour string across all products
    groupPrintLocations: [],       // shared print locations: ["large_front", "back_full", "front_left_chest"]
    groupArtwork: {},              // map: location → URL  (same as solo.printArtwork pattern)
    groupArtworkSources: {},       // map: location → "current" | "upload" | "saved"
    groupArtworkLabels: {},        // map: location → readable label
    groupSavedPickerOpen: null,    // which location's saved-designs grid is open in group flow
    groupPrintPlacement: {},       // map: location → {x,y,w,h} fractional rect — shared across every selected garment type
    groupBgRemoved: {},            // map: location → bool
    groupArtworkOriginal: {},      // map: location → pre-processing URL
    groupArtworkNaturalSize: {},   // map: location → {w,h} px of the FINAL (cropped) artwork
    groupMockupActiveLocation: null,   // which location's placement UI is currently shown
    groupMockupActiveProductType: null,// which garment type's photo the mockup canvas is currently previewing

    // Reorder mode (staff-assisted) — group flow equivalent of reorderMode above.
    groupReorderMode: false,
    groupReorderOrderNumber: "",
    groupReorderDetails: "",

    // ===== Bundle comparison =====
    bundleProducts: {},            // resolved at modal open: handle → { variantId, price, title }
    bundleSelection: null,         // null = individuals; or { handle, qty, fillerQty } when bundle chosen (solo)
    groupBundleSelection: null,    // group bundle pick — same shape as bundleSelection
    bundleExtraSizes: {},          // size → qty for the extras when a stretch bundle is picked (group flow)
    rushFeeProduct: null,          // { variantId, price, handle, title } resolved at open
    rushSelection: "standard"      // "standard" | "rush_5" | "same_day" — same for solo + group
  };

  // Bundle configs — match each Shopify bundle product. Order matters: bigger
  // bundles last so the comparison can score them honestly.
  const BUNDLE_RULES = [
    {
      handle: "10-staple-tees-printed-front-and-back",
      qty: 10,
      productMatch: function (p) {
        const t = ((p.title || "") + " " + (p.product_type || "")).toLowerCase();
        return /staple|5001/.test(t) && /tee|t-shirt/.test(t)
            && !/long|l\/s|hood/.test(t);
      },
      label: "10 Staple Tees printed F+B"
    },
    {
      handle: "20-staple-tees-printed-front-and-back",
      qty: 20,
      productMatch: function (p) {
        const t = ((p.title || "") + " " + (p.product_type || "")).toLowerCase();
        return /staple|5001/.test(t) && /tee|t-shirt/.test(t)
            && !/long|l\/s|hood/.test(t);
      },
      label: "20 Staple Tees printed F+B"
    },
    {
      handle: "10-as-colour-stencil-hoods-printed-front-and-back",
      qty: 10,
      productMatch: function (p) {
        const t = ((p.title || "") + " " + (p.product_type || "")).toLowerCase();
        return /(stencil.*hood|as.?colour.*hood|5102)/.test(t);
      },
      label: "10 AS Colour Stencil Hoods printed F+B"
    },
    {
      handle: "10-as-colour-staple-long-sleeve-shirts-printed-front-and-back",
      qty: 10,
      productMatch: function (p) {
        const t = ((p.title || "") + " " + (p.product_type || "")).toLowerCase();
        return /(long.?sleeve|l\/s|5020)/.test(t);
      },
      label: "10 AS Colour Long Sleeve printed F+B"
    }
  ];

  // ------------------------------------------------------------------------
  // Shopify product fetchers
  // ------------------------------------------------------------------------

  // Cache the products.json result briefly so we don't refetch on every
  // step transition, but refresh between modal opens so newly-tagged
  // products show up immediately.
  let _productsCache = null;
  let _productsCacheTs = 0;
  const PRODUCTS_CACHE_TTL_MS = 60 * 1000; // 60s
  async function fetchAllProducts(forceFresh) {
    const now = Date.now();
    if (!forceFresh && _productsCache && (now - _productsCacheTs) < PRODUCTS_CACHE_TTL_MS) {
      return _productsCache;
    }
    // Shopify's /products.json caps at 250 per page. Walk pages until we
    // get a short page (last page) or hit the safety cap. 60ft Ink has
    // ~684 products today so 3-4 pages is normal; 10 page cap covers
    // future growth without running away.
    const all = [];
    const MAX_PAGES = 10;
    try {
      for (let page = 1; page <= MAX_PAGES; page++) {
        const r = await fetch("/products.json?limit=250&page=" + page + "&_=" + now,
          { credentials: "same-origin" });
        if (!r.ok) break;
        const data = await r.json();
        const items = data.products || [];
        all.push.apply(all, items);
        if (items.length < 250) break; // last page
      }
      _productsCache = all;
      _productsCacheTs = now;
      return all;
    } catch (e) {
      console.warn("[OrderWizard] fetch products failed", e);
      _productsCache = all;
      _productsCacheTs = now;
      return all;
    }
  }

  function isBundle(product) {
    const tags = (product.tags || "").toString().toLowerCase();
    return /qty-10|qty-20|special/.test(tags);
  }

  function isPrintable(product) {
    const tags = (product.tags || "").toString().toLowerCase();
    return tags.indexOf("printable") !== -1;
  }

  function isRecommended(product) {
    const tags = (product.tags || "").toString().toLowerCase();
    // Accept "wizard-recommended", "wizard recommended", "Wizard-Recommended", etc.
    return /wizard.?recommended/.test(tags);
  }

  /**
   * Core-range products are kept in stock at all times in specific colours
   * (see CONFIG.coreRangeFastColours) so we can print + ship them
   * dramatically faster than the rest of the catalog. Detected via the
   * 'core-range' product tag — friendly aliases 'core_range', 'fast-ship',
   * 'in-stock' also accepted so Roland's tagging convention is flexible.
   */
  function isCoreRange(product) {
    if (!product) return false;
    const tags = (product.tags || "").toString().toLowerCase();
    return /core.?range|fast.?ship|in.?stock/.test(tags);
  }

  /**
   * Filter a list of colour names down to the ones core-range products
   * are kept in stock for. Comparison is case-insensitive so 'Black' and
   * 'black' both match. Returns the original list when no core filter
   * applies (non-core product).
   */
  function filterColoursForCore(colours, product) {
    if (!product || !isCoreRange(product)) return colours;
    const allowed = (CONFIG.coreRangeFastColours || []).map(function (c) {
      return String(c).toLowerCase();
    });
    if (!allowed.length) return colours;
    const filtered = colours.filter(function (c) {
      return allowed.indexOf(String(c).toLowerCase()) >= 0;
    });
    // Safety net — if no colour matches the allowed list (mis-tagged
    // product or bad config), fall back to showing everything rather
    // than leaving the customer staring at an empty picker.
    return filtered.length ? filtered : colours;
  }

  /**
   * Categorise a product as 'tee' | 'hoodie' | 'long_sleeve' | 'headwear' |
   * 'other'. Uses product_type + title heuristics so we don't need every
   * product to be tagged explicitly. Mirrors the same logic used in
   * ai-design-attach.liquid for mockup picking.
   */
  function categorize(product) {
    const t = (((product.product_type || "") + " " + (product.title || "")) || "").toLowerCase();
    if (/hood/.test(t)) return "hoodie";
    if (/long.?sleeve|longsleeve|l\/s/.test(t)) return "long_sleeve";
    if (/cap|hat|beanie/.test(t)) return "headwear";
    if (/tee|t-shirt|tshirt|t shirt|shirt|tank|singlet|vest|crew|polo|sweat|jumper|sweater/.test(t)) return "tee";
    return "other";
  }

  function matchesCategory(product, category) {
    if (!category || category === "both") {
      const c = categorize(product);
      return c === "tee" || c === "hoodie" || c === "long_sleeve";
    }
    if (category === "tee") {
      // Tees + long-sleeves grouped under "Tees" for simplicity
      const c = categorize(product);
      return c === "tee" || c === "long_sleeve";
    }
    if (category === "hoodie") return categorize(product) === "hoodie";
    return false;
  }

  // Print add-on products, fetched directly by handle — same proven pattern
  // resolveBundleProducts() already uses below, price read live off the
  // response rather than assumed. This used to match on an exact hardcoded
  // price ($7.75 / $10.95) instead, which silently broke completely when
  // the GST-inclusive price switch changed these products' real prices to
  // $8.53 / $12.05 — confirmed on real order #2401 (2026-08-20): the price
  // match found nothing for EITHER print location, so both print charges
  // were silently dropped from the cart while the shirt itself still
  // added fine, undercharging the order by $41.16 with no error anywhere.
  // Handles are the stable identifier here, exactly like BUNDLE_RULES
  // already relies on for bundle products — a price can drift at any time
  // for reasons that have nothing to do with the wizard (a storewide GST
  // change, a manual price edit), a handle only changes if the product
  // itself is deleted and recreated.
  const PRINT_PRODUCT_HANDLES = {
    front_left_chest: "front-left-chest-print-1",
    large_front: "front-centre-full-size-print",
    back_full: "back-centre-full-size-print"
  };

  /**
   * Identify the three print add-on products by handle. Returns null
   * entries if any can't be found — wizard will warn but still let the
   * customer order with just the design URL on the main product.
   */
  async function resolvePrintProducts() {
    const found = { front_left_chest: null, large_front: null, back_full: null };
    await Promise.all(Object.keys(PRINT_PRODUCT_HANDLES).map(async function (key) {
      try {
        const r = await fetch("/products/" + PRINT_PRODUCT_HANDLES[key] + ".json", { credentials: "same-origin" });
        if (!r.ok) return;
        const data = await r.json();
        const p = data.product;
        if (!p || !p.variants || !p.variants[0]) return;
        found[key] = { variantId: p.variants[0].id, price: parseFloat(p.variants[0].price), title: p.title };
      } catch (e) {
        console.warn("[OrderWizard] print product resolve failed for", key, e);
      }
    }));
    if (window.console && console.info) {
      console.info("[OrderWizard] print products resolved:",
        Object.keys(found).map(function (k) {
          return k + ": " + (found[k] ? found[k].title + " (id " + found[k].variantId + ", $" + found[k].price + ")" : "NOT FOUND");
        }).join(" | "));
    }
    // Self-heal the on-screen/rush-fee price config (CONFIG.printPrices) to
    // whatever these products actually cost right now, so a future price
    // change corrects itself everywhere the wizard reads a price from,
    // instead of only fixing what actually gets charged at cart-add and
    // leaving every displayed price/rush-fee calc silently stale like this
    // bug did.
    Object.keys(found).forEach(function (key) {
      if (found[key]) CONFIG.printPrices[key] = found[key].price;
    });
    return found;
  }

  /**
   * Recommended products to show in the picker. Filters out bundles
   * (qty-10/qty-20/Special) and the print add-on products themselves.
   * Falls back to all Printable products if no `wizard-recommended` tag
   * exists yet — so the wizard works even before Roland tags anything.
   */
  /**
   * Fetch products from a specific Shopify collection by handle. Returns
   * null if the collection doesn't exist (404) so the caller can fall
   * back. Uses the public `/collections/<handle>/products.json` endpoint
   * — no auth required.
   */
  async function fetchCollectionProducts(handle) {
    try {
      const r = await fetch("/collections/" + handle + "/products.json?limit=250&_=" + Date.now(),
        { credentials: "same-origin" });
      if (!r.ok) return null;
      const data = await r.json();
      return data.products || [];
    } catch (e) {
      console.warn("[OrderWizard] collection fetch failed for", handle, e);
      return null;
    }
  }

  async function fetchRecommendedProducts(category) {
    const printVariantIds = Object.values(state.printProducts || {})
      .filter(Boolean).map(function (p) { return p.variantId; });

    function notPrintAddon(p) {
      return !(p.variants && p.variants[0] && printVariantIds.indexOf(p.variants[0].id) >= 0);
    }
    function passCategory(p) {
      return !category || matchesCategory(p, category);
    }

    let source = "";
    let list = [];

    // FIRST: try the curated Shopify collection. This is the source of
    // truth — Roland adds/removes products from this collection via the
    // Shopify admin's drag-and-drop. No tag matching, no ambiguity.
    const curated = await fetchCollectionProducts("wizard-recommended");

    if (curated && curated.length) {
      list = curated.filter(function (p) {
        return !isBundle(p) && notPrintAddon(p) && passCategory(p);
      });
      source = "Wizard Recommended collection";
    } else {
      // FALLBACK 1: tag-based detection (old approach — still works if
      // collection doesn't exist yet)
      const all = await fetchAllProducts();
      const anyTagged = all.some(function (p) {
        return !isBundle(p) && notPrintAddon(p) && isRecommended(p);
      });
      if (anyTagged) {
        list = all.filter(function (p) {
          return !isBundle(p) && notPrintAddon(p) && isRecommended(p) && passCategory(p);
        });
        source = "wizard-recommended tag";
      } else {
        // FALLBACK 2: any Printable product
        list = all.filter(function (p) {
          return !isBundle(p) && notPrintAddon(p) && isPrintable(p) && passCategory(p);
        });
        source = "Printable (no curation set up yet)";
      }
    }

    list = list.slice(0, 12);

    if (window.console && console.info) {
      console.info("[OrderWizard] picker — category:", category || "all",
        "| source:", source, "| count:", list.length,
        "| titles:", list.map(function (p) { return p.title; }));
    }
    return list;
  }

  // ------------------------------------------------------------------------
  // Step renderers
  // ------------------------------------------------------------------------

  function renderProductGrid() {
    const grid = $("#ow-product-grid");
    if (!grid) return;
    const titleEl = $("#ow-product-title");
    if (titleEl) {
      const cat = state.selectedCategory;
      titleEl.textContent = cat === "tee"    ? "Which tee?"
                          : cat === "hoodie" ? "Which hoodie?"
                          : "Pick your gear";
    }
    grid.innerHTML = '<div class="ow-loading">Loading...</div>';
    fetchRecommendedProducts(state.selectedCategory).then(function (products) {
      state.products = products;
      if (!products.length) {
        const empty = state.selectedCategory === "hoodie"
          ? "No hoodies tagged yet — try Tees, or Both."
          : "No recommended products yet — tag some with <code>wizard-recommended</code> in Shopify.";
        grid.innerHTML = '<div class="ow-error">' + empty + '</div>';
        return;
      }
      // Sort core-range products to the top so customers see their
      // fastest-ship options first. Original order preserved within each group.
      const sortedProducts = products.slice().sort(function (a, b) {
        const ac = isCoreRange(a) ? 1 : 0;
        const bc = isCoreRange(b) ? 1 : 0;
        return bc - ac;
      });

      grid.innerHTML = "";
      sortedProducts.forEach(function (p) {
        const img = p.images && p.images[0] ? p.images[0].src : "";
        const price = p.variants && p.variants[0] ? "$" + parseFloat(p.variants[0].price).toFixed(2) : "";
        const core = isCoreRange(p);
        const badge = core ? '<span class="ow-fast-ship-badge">⚡ Ships fast</span>' : '';
        // Show which colours qualify for fast-ship — pulled from CONFIG so
        // adding more in-stock colours later doesn't need a copy edit.
        const fastColours = (CONFIG.coreRangeFastColours || []).map(function (c) {
          return String(c).charAt(0).toUpperCase() + String(c).slice(1);
        }).join(", ");
        const blackNote = core
          ? '<p class="ow-fast-ship-note">In stock · ' + (fastColours || "fast-ship colours") + ' only</p>'
          : '';
        const card = document.createElement("button");
        card.type = "button";
        card.className = "ow-product-card";
        card.setAttribute("data-ow-action", "product-pick");
        card.setAttribute("data-product-handle", p.handle);
        card.innerHTML =
          badge +
          '<img class="ow-product-card__img" src="' + img + '" alt="' + (p.title || "") + '">' +
          '<div class="ow-product-card__body">' +
            '<p class="ow-product-card__title">' + (p.title || "") + '</p>' +
            '<p class="ow-product-card__price">From ' + price + '</p>' +
            blackNote +
          '</div>';
        grid.appendChild(card);
      });
    });
  }

  /**
   * Map a Shopify colour name (e.g. "Black", "Forest Green", "Royal Blue",
   * "Heather Charcoal") to a CSS background colour. Looks for known colour
   * keywords as substrings — handles compound names like "Heather Royal"
   * by matching "royal". Returns null if no match (falls back to black
   * default in CSS).
   */
  function colourToCss(name) {
    const n = (name || "").toLowerCase();
    // Order matters: more specific names first so substring matching
    // ("Cardinal Red" → cardinal, not red) works correctly.
    const map = [
      // Off-whites / neutrals
      ["bone",          "#e6dfd1"],
      ["natural",       "#e8e1cf"],
      ["cream",         "#f1ead5"],
      ["white",         "#ffffff"],

      // Tans / browns
      ["sand",          "#dccd9e"],
      ["khaki",         "#8a7a4a"],
      ["camel",         "#c19a6b"],
      ["copper",        "#b87333"],
      ["bronze",        "#a87f3a"],
      ["tan",           "#a07750"],
      ["chocolate",     "#3d2a1e"],
      ["brown",         "#5a3a24"],

      // Yellows / oranges
      ["mustard",       "#c89c2b"],
      ["yellow",        "#e6c14a"],
      ["orange",        "#ff6b00"],
      ["coral",         "#e96a59"],

      // Reds / pinks
      ["cardinal",      "#8c1c1c"],
      ["burgundy",      "#5e1a25"],
      ["maroon",        "#5e1a25"],
      ["red",           "#c4262d"],
      ["pink",          "#d96e9d"],

      // Purples
      ["lavender",      "#b9a4d6"],
      ["purple",        "#5b3886"],

      // Blues (specific before generic)
      ["cobalt",        "#0047ab"],
      ["royal",         "#1d4796"],
      ["navy",          "#1a2440"],
      ["denim",         "#3b5b8a"],
      ["sky",           "#74b6e3"],
      ["pacific",       "#2b6fb3"],
      ["mid blue",      "#2f5db0"],
      ["blue",          "#1d4796"],

      // Teals / mints / greens (specific before generic)
      ["teal",          "#1f6b6a"],
      ["mint",          "#9bd6b8"],
      ["bottle",        "#0e3823"],
      ["forest",        "#1f4d33"],
      ["army",          "#4b5320"],
      ["olive",         "#52582a"],
      ["lime",          "#a3c842"],
      ["sage",          "#b8c2a8"],
      ["green",         "#2f7d3a"],

      // Greys / blacks (asphalt + coal before charcoal/grey/black)
      ["asphalt",       "#3e3e44"],
      ["coal",          "#1c1c1c"],
      ["pewter",        "#5a5a5a"],
      ["heather",       "#9c9c9c"],
      ["marle",         "#a0a0a0"],
      ["charcoal",      "#3a3a3a"],
      ["graphite",      "#383838"],
      ["slate",         "#576470"],
      ["silver",        "#c0c0c0"],
      ["grey",          "#888888"],
      ["gray",          "#888888"],
      ["black",         "#000000"]
    ];
    for (let i = 0; i < map.length; i++) {
      if (n.indexOf(map[i][0]) >= 0) return map[i][1];
    }
    return null; // CSS default (black) will apply
  }

  /** Detect which option name on this product is colour vs size. */
  function classifyOptions(product) {
    const opts = product.options || [];
    let colourIdx = -1, sizeIdx = -1;
    opts.forEach(function (o, i) {
      const name = ((typeof o === "string") ? o : (o.name || "")).toLowerCase();
      if (/colour|color/.test(name)) colourIdx = i;
      else if (/size/.test(name)) sizeIdx = i;
    });
    // Fallback heuristic — assume [Colour, Size] order if names aren't named.
    if (colourIdx === -1 && sizeIdx === -1) {
      colourIdx = 0; sizeIdx = 1;
    } else if (colourIdx === -1) {
      colourIdx = sizeIdx === 0 ? 1 : 0;
    } else if (sizeIdx === -1) {
      sizeIdx = colourIdx === 0 ? 1 : 0;
    }
    return { colourIdx: colourIdx, sizeIdx: sizeIdx };
  }

  function uniqueOptionValues(product, idx) {
    const seen = {};
    const out = [];
    (product.variants || []).forEach(function (v) {
      const val = v["option" + (idx + 1)];
      if (val && !seen[val]) { seen[val] = true; out.push(val); }
    });
    return out;
  }

  function variantsAvailableForColour(product, colourIdx, sizeIdx, colour) {
    return (product.variants || []).filter(function (v) {
      return v["option" + (colourIdx + 1)] === colour;
    });
  }

  function findVariantId(product, colourIdx, sizeIdx, colour, size) {
    const v = (product.variants || []).find(function (vr) {
      return vr["option" + (colourIdx + 1)] === colour
          && vr["option" + (sizeIdx + 1)] === size;
    });
    return v ? v.id : null;
  }

  function renderVariantPicker() {
    const product = state.selectedProduct;
    if (!product) return;
    const titleEl = $("#ow-variant-title");
    if (titleEl) titleEl.textContent = product.title + " — colour & size";

    const cls = classifyOptions(product);
    let colours = uniqueOptionValues(product, cls.colourIdx);
    // Core-range products keep all their colour variants (so the regular
    // PDP flow still sells them) but the wizard only offers fast-ship
    // colours so the "ships in days" promise stays honest.
    colours = filterColoursForCore(colours, product);
    if (!state.selectedColour || colours.indexOf(state.selectedColour) < 0) {
      // Try to default to CONFIG.defaultColour (Black) if available
      const defaultC = colours.find(function (c) {
        return c.toLowerCase() === (CONFIG.defaultColour || "Black").toLowerCase();
      });
      state.selectedColour = defaultC || colours[0] || null;
      state.selectedSize = null;
    }

    const swatchEl = $("#ow-swatches");
    if (swatchEl) {
      swatchEl.innerHTML = "";
      colours.forEach(function (c) {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "ow-swatch" + (c === state.selectedColour ? " is-selected" : "");
        b.setAttribute("data-ow-action", "pick-colour");
        b.setAttribute("data-value", c);
        b.title = c;
        b.setAttribute("aria-label", c);

        const dot = document.createElement("span");
        dot.className = "ow-swatch__dot";
        const css = colourToCss(c);
        if (css) dot.setAttribute("style", "background:" + css);
        b.appendChild(dot);

        const name = document.createElement("span");
        name.className = "ow-swatch__name";
        name.textContent = c;
        b.appendChild(name);

        swatchEl.appendChild(b);
      });
    }

    const sizeEl = $("#ow-sizes");
    if (sizeEl) {
      sizeEl.innerHTML = "";
      const availableSizes = variantsAvailableForColour(product, cls.colourIdx, cls.sizeIdx, state.selectedColour)
        .map(function (v) { return { value: v["option" + (cls.sizeIdx + 1)], avail: v.available !== false }; });
      availableSizes.forEach(function (s) {
        if (!s.value) return;
        const b = document.createElement("button");
        b.type = "button";
        b.className = "ow-size" + (s.value === state.selectedSize ? " is-selected" : "");
        if (!s.avail) b.disabled = true;
        b.setAttribute("data-ow-action", "pick-size");
        b.setAttribute("data-value", s.value);
        b.textContent = s.value;
        sizeEl.appendChild(b);
      });
    }

    state.selectedVariantId = (state.selectedColour && state.selectedSize)
      ? findVariantId(product, cls.colourIdx, cls.sizeIdx, state.selectedColour, state.selectedSize)
      : null;

    const qtyInput = $("#ow-qty-val");
    if (qtyInput) qtyInput.value = String(state.selectedQuantity || 1);

    renderTierCard();
    // No longer disabling the Continue button here — instead, the
    // variant-continue handler validates on click and shows a clear
    // error + highlight on what the customer missed.
  }

  /**
   * Live discount tier card. Shows the customer:
   *   - Which tier their current quantity puts them in
   *   - How many more items to unlock the next tier
   *   - A small table of all tiers with their current row highlighted
   *
   * Updates as the customer changes the qty stepper.
   */
  function renderTierCard() {
    const wrap = $("#ow-tier-card");
    if (!wrap) return;
    const tiers = CONFIG.tierTable || [];
    if (!tiers.length) { wrap.innerHTML = ""; return; }

    const qty = Math.max(1, state.selectedQuantity || 1);

    // Find current tier (largest minQty where qty >= minQty)
    let current = null;
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (qty >= tiers[i].minQty) { current = tiers[i]; break; }
    }
    // Find next tier (smallest minQty above current qty)
    let next = null;
    for (let i = 0; i < tiers.length; i++) {
      if (qty < tiers[i].minQty) { next = tiers[i]; break; }
    }

    let html = "";

    if (current) {
      html +=
        '<div class="ow-tier-card__current ow-tier-card__current--active">' +
          '<span class="ow-tier-card__check">✓</span>' +
          'You\'re saving ' + current.percentOff + '% on every item' +
        '</div>';
    } else {
      html +=
        '<div class="ow-tier-card__current">' +
          'No bulk discount yet — order a few more to unlock savings' +
        '</div>';
    }

    if (next) {
      const need = next.minQty - qty;
      html +=
        '<div class="ow-tier-card__next">' +
          'Add <strong>' + need + ' more</strong> to get ' +
          '<strong>' + next.percentOff + '% off</strong> the lot' +
        '</div>';
    } else if (current) {
      html += '<div class="ow-tier-card__next">You\'re at the top tier 🔥</div>';
    }

    html += '<div class="ow-tier-table">';
    html +=
      '<div class="ow-tier-table__row ow-tier-table__header">' +
        '<div class="ow-tier-table__cell">Quantity</div>' +
        '<div class="ow-tier-table__cell">Discount</div>' +
      '</div>';
    tiers.forEach(function (t) {
      const isCurrent = current && t.minQty === current.minQty;
      html +=
        '<div class="ow-tier-table__row' + (isCurrent ? ' ow-tier-table__row--current' : '') + '">' +
          '<div class="ow-tier-table__cell">' + t.minQty + '+ items</div>' +
          '<div class="ow-tier-table__cell">' + t.percentOff + '% off</div>' +
        '</div>';
    });
    html += '</div>';

    wrap.innerHTML = html;
  }

  // SVG tee silhouettes with the print zone highlighted in brand orange.
  // viewBox 100x110, viewer-perspective (front-of-tee = customer's left
  // appears on viewer's right). Designed to be tiny but legible.
  const TEE_SVG_OUTLINE =
    '<path d="M 18 8 L 30 2 Q 50 14 70 2 L 82 8 L 96 26 L 82 36 L 82 105 L 18 105 L 18 36 L 4 26 Z" ' +
    'fill="#f6f6f6" stroke="#1a1a1a" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>';
  const TEE_SVG_OUTLINE_BACK =
    '<path d="M 18 8 L 30 2 L 70 2 L 82 8 L 96 26 L 82 36 L 82 105 L 18 105 L 18 36 L 4 26 Z" ' +
    'fill="#f6f6f6" stroke="#1a1a1a" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>';

  const SVG_LARGE_FRONT =
    '<svg viewBox="0 0 100 110" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      TEE_SVG_OUTLINE +
      // Large front print starts ~8–10 cm below the collar (upper chest)
      // and runs down to the mid-body, mirroring real DTF placement.
      '<rect x="30" y="22" width="40" height="56" fill="#ff6b00" rx="2"/>' +
    '</svg>';

  const SVG_BACK_FULL =
    '<svg viewBox="0 0 100 110" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      TEE_SVG_OUTLINE_BACK +
      '<rect x="32" y="22" width="36" height="60" fill="#ff6b00" rx="2"/>' +
      '<text x="50" y="56" text-anchor="middle" fill="#fff" font-size="11" font-weight="700" font-family="Arial,sans-serif">BACK</text>' +
    '</svg>';

  const SVG_FRONT_LEFT_CHEST =
    '<svg viewBox="0 0 100 110" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      TEE_SVG_OUTLINE +
      // Front Left Chest = customer's left chest = viewer's right side.
      // Positioned at upper chest, where a real pocket-logo print lives
      // (about 8–10 cm below the collar, slightly inboard of the shoulder).
      '<rect x="56" y="18" width="14" height="14" fill="#ff6b00" rx="1.5"/>' +
    '</svg>';

  function renderPrintPicker() {
    const wrap = $("#ow-print-options");
    if (!wrap) return;
    wrap.innerHTML = "";

    const opts = [
      { key: "large_front",      title: "Large Front",      desc: "Up to 38 × 48 cm.",                    price: CONFIG.printPrices.large_front,      svg: SVG_LARGE_FRONT },
      { key: "back_full",        title: "Back Full Size",   desc: "Up to 38 × 48 cm.",                    price: CONFIG.printPrices.back_full,        svg: SVG_BACK_FULL },
      { key: "front_left_chest", title: "Front Left Chest", desc: "Up to 10 × 10 cm, on the left chest.", price: CONFIG.printPrices.front_left_chest, svg: SVG_FRONT_LEFT_CHEST }
    ];
    opts.forEach(function (o) {
      const isSel = state.selectedPrintLocations.indexOf(o.key) >= 0;
      const b = document.createElement("button");
      b.type = "button";
      b.className = "ow-print-option" + (isSel ? " is-selected" : "");
      b.setAttribute("data-ow-action", "toggle-print");
      b.setAttribute("data-key", o.key);
      b.innerHTML =
        '<div class="ow-print-option__thumb">' + o.svg + '</div>' +
        '<div class="ow-print-option__body">' +
          '<div class="ow-print-option__title">' + o.title + '</div>' +
          '<div class="ow-print-option__desc">' + o.desc + '</div>' +
        '</div>' +
        '<div class="ow-print-option__price">+$' + o.price.toFixed(2) + '</div>';
      wrap.appendChild(b);
    });

    // Continue is always enabled — print-continue handler validates on click.
  }

  // ------------------------------------------------------------------------
  // Artwork picker (per print location)
  // ------------------------------------------------------------------------

  function getSavedDesigns() {
    try {
      if (window.AIDesignLibrary && typeof window.AIDesignLibrary.list === "function") {
        return window.AIDesignLibrary.list() || [];
      }
    } catch (_) {}
    return [];
  }

  function locationLabel(loc) {
    return loc === "large_front" ? "Large Front" :
           loc === "back_full"   ? "Back Full Size" :
           loc === "front_left_chest" ? "Front Left Chest" : loc;
  }

  // Audit fix 2026-09-06: the artwork intro used to say "Default is the
  // design you just made" even when no design existed (homepage start).
  function artworkIntroMessage() {
    return state.designUrl
      ? "Pick the artwork for each print. Default is the design you just made — you can swap any of them."
      : "Now the artwork. For each print you can create a design with AI, upload your own file, or pick one of your saved designs.";
  }

  function sourceLabel(src) {
    return src === "later"   ? "You'll send this artwork after ordering — we'll email a mockup to approve before printing" :
           src === "current" ? "Using the design you just made" :
           src === "upload"  ? "Using your uploaded file" :
           src === "saved"   ? "Using one of your saved designs" : "";
  }

  function renderArtworkList() {
    const wrap = document.getElementById("ow-artwork-list");
    if (!wrap) return;
    wrap.innerHTML = "";

    const hasAi = !!state.designUrl;
    const saved = getSavedDesigns();

    state.selectedPrintLocations.forEach(function (loc) {
      const url = state.printArtwork[loc] || "";
      const source = state.artworkSources[loc] || "";
      const showSavedPanel = state.savedPickerOpen === loc;

      const row = document.createElement("div");
      row.className = "ow-artwork-row";

      let previewHtml = url
        ? '<img src="' + url + '" alt="' + locationLabel(loc) + ' artwork">'
        : '<div class="ow-artwork-row__noimg">' + (source === "later" ? "Sending later" : "Pick one") + '</div>';

      row.innerHTML =
        '<div class="ow-artwork-row__header">' + locationLabel(loc) + ' print</div>' +
        '<div class="ow-artwork-row__preview">' +
          previewHtml +
          '<div class="ow-artwork-row__source">' + sourceLabel(source) + '</div>' +
        '</div>' +
        '<div class="ow-artwork-buttons">' +
          (hasAi
            ? '<button class="ow-artwork-btn' + (source === "current" ? " is-active" : "") +
                '" type="button" data-ow-action="pick-current" data-location="' + loc + '">My AI design</button>'
            // Audit fix 2026-09-06: was a disabled button whose only explanation
            // was a hover tooltip. Now a real way to make one, with the order kept.
            : '<button class="ow-artwork-btn ow-artwork-btn--create" type="button" data-ow-action="create-design" data-location="' + loc + '">✨ Create a design with AI</button>') +
          '<button class="ow-artwork-btn' + (source === "upload" ? " is-active" : "") +
            '" type="button" data-ow-action="pick-upload" data-location="' + loc + '">' +
            'Upload file' +
          '</button>' +
          '<button class="ow-artwork-btn' + (showSavedPanel ? " is-active" : "") +
            '" type="button" data-ow-action="toggle-saved" data-location="' + loc + '">' +
            'My saved designs' +
          '</button>' +
          // Audit fix 2026-09-07: same four choices as the product page.
          '<button class="ow-artwork-btn' + (source === "later" ? " is-active" : "") +
            '" type="button" data-ow-action="pick-later" data-location="' + loc + '">Send artwork later</button>' +
        '</div>';

      if (showSavedPanel) {
        const grid = document.createElement("div");
        grid.className = "ow-saved-grid";
        if (!saved.length) {
          grid.innerHTML = '<div class="ow-saved-empty">No saved designs yet — save them as you make them on the designer.</div>';
        } else {
          saved.forEach(function (d) {
            const thumb = document.createElement("button");
            thumb.type = "button";
            thumb.className = "ow-saved-thumb";
            thumb.setAttribute("data-ow-action", "pick-saved-design");
            thumb.setAttribute("data-location", loc);
            thumb.setAttribute("data-design-url", d.draftUrl || "");
            thumb.title = d.prompt || "Saved design";
            thumb.innerHTML = '<img src="' + (d.thumbnail || d.draftUrl || "") +
              '" alt="' + (d.prompt || "saved design").replace(/"/g, "&quot;") + '">';
            grid.appendChild(thumb);
          });
        }
        row.appendChild(grid);
      }

      // Hidden file input for this location's upload button
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = "image/png,image/jpeg,image/webp,image/svg+xml,.pdf,.ai,.eps,.tif,.tiff";
      fileInput.style.display = "none";
      fileInput.setAttribute("data-ow-file", loc);
      fileInput.addEventListener("change", function () {
        const f = fileInput.files && fileInput.files[0];
        if (f) handleFileUpload(loc, f);
      });
      row.appendChild(fileInput);

      wrap.appendChild(row);
    });
  }

  async function handleFileUpload(location, file) {
    const errEl = document.getElementById("ow-artwork-error");
    if (errEl) { errEl.setAttribute("hidden", ""); errEl.textContent = ""; }

    // Show "Uploading..." in the source label for this location
    const row = document.querySelector('[data-ow-file="' + location + '"]');
    const srcEl = row && row.parentElement && row.parentElement.querySelector(".ow-artwork-row__source");
    if (srcEl) srcEl.textContent = "Uploading " + file.name + "...";

    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await fetch(CONFIG.workerUrl + "/api/customer-upload", {
        method: "POST",
        body: formData
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt || ("HTTP " + r.status));
      }
      const data = await r.json();
      if (!data || !data.url) throw new Error("Upload returned no URL");
      state.printArtwork[location] = data.url;
      state.artworkSources[location] = "upload";
      state.artworkLabels[location] = file.name;
      state.savedPickerOpen = null;
      renderArtworkList();
    } catch (e) {
      console.warn("[OrderWizard] file upload failed", e);
      if (errEl) {
        errEl.textContent = "Upload failed: " + (e.message || e);
        errEl.removeAttribute("hidden");
      }
      renderArtworkList();
    }
  }

  function renderConfirmSummary() {
    const sum = $("#ow-summary");
    if (!sum) return;
    const product = state.selectedProduct;
    const qty = Math.max(1, state.selectedQuantity || 1);
    const productPrice = product && product.variants && product.variants[0]
      ? parseFloat(product.variants[0].price) : 0;

    // ----- BUNDLE PATH summary -----
    const sel = state.bundleSelection;
    if (sel && sel.key !== "individuals" && sel.bundles && sel.bundles.length > 0) {
      let html = "";
      sel.bundles.forEach(function (bRule) {
        const bp = state.bundleProducts[bRule.handle];
        if (!bp) return;
        html +=
          '<div class="ow-summary__row"><span class="ow-summary__label">' + bp.title + '</span>' +
          '<span class="ow-summary__val">$' + bp.price.toFixed(2) + '</span></div>';
      });
      if (sel.fillerQty > 0) {
        let printsPerUnit = 0;
        state.selectedPrintLocations.forEach(function (k) { printsPerUnit += CONFIG.printPrices[k] || 0; });
        const fillerPerUnit = productPrice + printsPerUnit;
        const fillerSubtotal = fillerPerUnit * sel.fillerQty;
        const fillerTier = activeTierFor(sel.fillerQty);
        const fillerDiscountPct = fillerTier ? fillerTier.percentOff : 0;
        const fillerDiscountAmount = fillerSubtotal * fillerDiscountPct / 100;
        const fillerNet = fillerSubtotal - fillerDiscountAmount;
        html +=
          '<div class="ow-summary__row"><span class="ow-summary__label">' + sel.fillerQty + ' extra individual' + (sel.fillerQty === 1 ? '' : 's') + ' + prints</span>' +
          '<span class="ow-summary__val">$' + fillerNet.toFixed(2) + '</span></div>';
      }
      html +=
        '<div class="ow-summary__row ow-summary__row--total">' +
          '<span class="ow-summary__label">Total</span>' +
          '<span class="ow-summary__val">$' + sel.total.toFixed(2) + '</span></div>';
      sum.innerHTML = html;
      const errEl = $("#ow-cart-error");
      if (errEl) {
        if (state.cartError) { errEl.textContent = state.cartError; errEl.removeAttribute("hidden"); }
        else errEl.setAttribute("hidden", "");
      }
      return;
    }

    // ----- INDIVIDUALS PATH (original) -----
    let printsPerUnit = 0;
    const printLines = state.selectedPrintLocations.map(function (k) {
      const price = CONFIG.printPrices[k] || 0;
      printsPerUnit += price;
      const label = locationLabel(k);
      return { label: label, price: price };
    });
    const perUnit = productPrice + printsPerUnit;
    const subtotal = perUnit * qty;
    const qtyTag = qty > 1 ? ' × ' + qty : '';

    // Bulk discount: same percentage applies to both garment and prints,
    // driven by garment quantity (matches the Hulk config rule).
    const tiers = CONFIG.tierTable || [];
    let activeTier = null;
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (qty >= tiers[i].minQty) { activeTier = tiers[i]; break; }
    }
    const discountPct = activeTier ? activeTier.percentOff : 0;
    const discountAmount = subtotal * discountPct / 100;
    const total = subtotal - discountAmount;

    let html =
      '<div class="ow-summary__row"><span class="ow-summary__label">' + (product ? product.title : "Product") + qtyTag + '</span>' +
      '<span class="ow-summary__val">' + state.selectedColour + ' · ' + state.selectedSize + ' · $' + (productPrice * qty).toFixed(2) + '</span></div>';
    printLines.forEach(function (l) {
      html += '<div class="ow-summary__row"><span class="ow-summary__label">' + l.label + qtyTag + '</span>' +
              '<span class="ow-summary__val">+$' + (l.price * qty).toFixed(2) + '</span></div>';
    });

    if (activeTier) {
      html += '<div class="ow-summary__row ow-summary__row--subtotal">' +
              '<span class="ow-summary__label">Full price (no discount)</span>' +
              '<span class="ow-summary__val">$' + subtotal.toFixed(2) + '</span></div>';
      html += '<div class="ow-summary__row ow-summary__row--discount">' +
              '<span class="ow-summary__label">Bulk discount (' + discountPct + '% off)</span>' +
              '<span class="ow-summary__val">−$' + discountAmount.toFixed(2) + '</span></div>';
    }
    // Rush production fee — off the DISCOUNTED total, not the pre-discount
    // subtotal, so this matches what Hulk Discounts actually charges once
    // the customer lands on the real cart.
    const rushOpt = (CONFIG.rushOptions || []).find(function (o) { return o.key === state.rushSelection; });
    let rushFee = 0;
    if (rushOpt && rushOpt.surchargePct > 0) {
      const rushResult = computeRushFee(rushOpt, total);
      rushFee = rushResult.fee;
      const note = rushResult.hitMin ? ' (min $' + rushOpt.minFee + ')'
                 : rushResult.hitMax ? ' (capped)'
                 : ' (' + rushOpt.surchargePct + '%)';
      html += '<div class="ow-summary__row" style="color:var(--ow-orange)">' +
              '<span class="ow-summary__label">' + rushOpt.label + note + '</span>' +
              '<span class="ow-summary__val">+$' + rushFee.toFixed(2) + '</span></div>';
    }
    const grandTotal = total + rushFee;
    if (state.reorderMode) {
      html += '<div class="ow-summary__row" style="color:var(--ow-text-dim);font-size:12px">' +
              '<span class="ow-summary__label">Reorder of order ' + (state.reorderOrderNumber || "") + '</span>' +
              '<span class="ow-summary__val"></span></div>';
    }
    html += '<div class="ow-summary__row ow-summary__row--total"><span class="ow-summary__label">Total</span>' +
            '<span class="ow-summary__val">$' + grandTotal.toFixed(2) + '</span></div>';

    sum.innerHTML = html;

    const errEl = $("#ow-cart-error");
    if (errEl) {
      if (state.cartError) { errEl.textContent = state.cartError; errEl.removeAttribute("hidden"); }
      else errEl.setAttribute("hidden", "");
    }
  }

  // ------------------------------------------------------------------------
  // BUNDLE COMPARISON
  // ------------------------------------------------------------------------

  /**
   * Resolve each bundle's variant ID + current price by fetching its product
   * JSON directly (no need to scan the whole catalog). Cached on state.
   */
  async function resolveBundleProducts() {
    const out = {};
    await Promise.all(BUNDLE_RULES.map(async function (rule) {
      try {
        const r = await fetch("/products/" + rule.handle + ".json", { credentials: "same-origin" });
        if (!r.ok) return;
        const data = await r.json();
        const p = data.product;
        if (!p || !p.variants || !p.variants[0]) return;
        out[rule.handle] = {
          variantId: p.variants[0].id,
          price: parseFloat(p.variants[0].price),
          title: p.title,
          handle: p.handle,
          qty: rule.qty
        };
      } catch (e) {
        console.warn("[OrderWizard] bundle resolve failed for", rule.handle, e);
      }
    }));
    if (window.console && console.info) {
      console.info("[OrderWizard] bundle products resolved:",
        Object.keys(out).map(function (h) { return h + " → $" + out[h].price; }).join(" | ") || "(none)");
    }
    return out;
  }

  /**
   * Build comparison options for the current solo selection. Returns an
   * array of paths, each with a label, total, and the cart-add metadata
   * needed if the customer picks it. The first item is always the
   * "individuals" path (the customer's current plan).
   *
   * Returns null if no comparison is possible (wrong prints, no matching
   * bundle, etc.) — caller should skip the step in that case.
   */
  // ------------------------------------------------------------------------
  // Bundle stacking helpers — used by both solo + group bundle computation
  // so a customer asking for 30 tees sees "20-pack + 10-pack" stacked at
  // best bundle pricing, not just the closest single bundle with fillers.
  // ------------------------------------------------------------------------

  /**
   * Enumerate every plausible combination of bundle products that could
   * cover the requested quantity. Returns options-shaped objects with a
   * `bundles[]` array (1 entry per bundle instance) plus the derived
   * total / filler / stretch info.
   *
   * Caps per-bundle count at ceil(qty / bundle.qty) + 1 so the search
   * space stays bounded.
   */
  function enumerateBundleCombinations(matchingBundles, qty, priceForN) {
    const bundleInfo = matchingBundles.map(function (rule) {
      const bp = state.bundleProducts[rule.handle];
      return bp ? { handle: rule.handle, qty: rule.qty, price: bp.price, title: bp.title } : null;
    }).filter(Boolean);
    if (!bundleInfo.length) return [];
    // Try biggest bundles first so multi-bundle combos read intuitively
    bundleInfo.sort(function (a, b) { return b.qty - a.qty; });

    const results = [];
    function recurse(idx, picks) {
      if (idx >= bundleInfo.length) {
        if (picks.length === 0) return; // pure-individuals path handled separately
        const garmentsFromBundles = picks.reduce(function (s, b) { return s + b.qty; }, 0);
        const bundlesSubtotal = picks.reduce(function (s, b) { return s + b.price; }, 0);
        const diff = qty - garmentsFromBundles;
        let fillerQty = 0;
        let stretch = false;
        let extraSizesNeeded = 0;
        let totalGarments = garmentsFromBundles;
        let total = bundlesSubtotal;
        if (diff > 0) {
          // Bundles don't cover full qty — add filler individuals at tier-discounted price
          fillerQty = diff;
          total += priceForN(diff);
          totalGarments = qty;
        } else if (diff < 0) {
          // Bundles cover more than asked — stretch (free extras)
          stretch = true;
          extraSizesNeeded = -diff;
        }
        results.push({
          bundles: picks.map(function (p) {
            return { handle: p.handle, qty: p.qty, price: p.price, title: p.title };
          }),
          garments: totalGarments,
          bundlesSubtotal: bundlesSubtotal,
          fillerQty: fillerQty,
          total: total,
          stretch: stretch,
          extraSizesNeeded: extraSizesNeeded
        });
        return;
      }
      const b = bundleInfo[idx];
      const maxCount = Math.ceil(qty / b.qty) + 1;
      for (let n = 0; n <= maxCount; n++) {
        const slot = [];
        for (let i = 0; i < n; i++) slot.push(b);
        recurse(idx + 1, picks.concat(slot));
      }
    }
    recurse(0, []);

    // Dedup by total (within $1)
    results.sort(function (a, b) { return a.total - b.total; });
    const dedup = [];
    results.forEach(function (r) {
      if (!dedup.some(function (d) { return Math.abs(d.total - r.total) < 1; })) {
        dedup.push(r);
      }
    });
    return dedup;
  }

  /**
   * Group identical-qty bundles into a readable label.
   *   [10] → "10-pack"
   *   [20, 10] → "20-pack + 10-pack"
   *   [10, 10, 10] → "3× 10-pack"
   *   [20, 10, 10] → "20-pack + 2× 10-pack"
   */
  function buildBundleLabel(bundles) {
    const grouped = {};
    bundles.forEach(function (b) { grouped[b.qty] = (grouped[b.qty] || 0) + 1; });
    return Object.keys(grouped)
      .sort(function (a, b) { return parseInt(b, 10) - parseInt(a, 10); })
      .map(function (qty) {
        const n = grouped[qty];
        return (n > 1 ? n + "× " : "") + qty + "-pack";
      }).join(" + ");
  }

  /**
   * Split a customer's combined size map across multiple bundles. Used when
   * a stacked option (e.g. 20-pack + 10-pack) needs each Shopify bundle
   * line item to know what's in it for fulfilment.
   *
   *   combinedSizes = { S:5, M:15, L:10 }
   *   bundleQtys    = [20, 10]
   *   → [ {S:5, M:15}, {L:10} ]   (walks sizes in declared order, fills 20-pack first)
   */
  function splitSizesAcrossBundles(combinedSizes, bundleQtys) {
    const sizeKeys = Object.keys(combinedSizes).filter(function (s) {
      return (parseInt(combinedSizes[s], 10) || 0) > 0;
    });
    const remaining = {};
    sizeKeys.forEach(function (s) { remaining[s] = parseInt(combinedSizes[s], 10) || 0; });
    return bundleQtys.map(function (target) {
      const slot = {};
      let need = target;
      for (let i = 0; i < sizeKeys.length && need > 0; i++) {
        const s = sizeKeys[i];
        const take = Math.min(remaining[s], need);
        if (take > 0) {
          slot[s] = take;
          remaining[s] -= take;
          need -= take;
        }
      }
      return slot;
    });
  }

  function computeBundleOptions() {
    const product = state.selectedProduct;
    if (!product) return null;

    // Bundles include LARGE FRONT + BACK FULL prints. Offer the bundle
    // whenever the customer wants Back + any front print (Large Front or
    // Front Left Chest). If they picked Front Left Chest, the bundle
    // upgrades them to Large Front — we call that out in the intro.
    const hasFrontish = state.selectedPrintLocations.indexOf("large_front") >= 0 ||
                        state.selectedPrintLocations.indexOf("front_left_chest") >= 0;
    const hasBack = state.selectedPrintLocations.indexOf("back_full") >= 0;
    if (!hasFrontish || !hasBack) return null;

    const qty = Math.max(1, state.selectedQuantity || 1);
    const printsPerUnit = (CONFIG.printPrices.large_front || 0) + (CONFIG.printPrices.back_full || 0);
    const finalOptions = buildBundleOptionsForProduct(product, qty, printsPerUnit) || [];
    // Older solo "stretch" copy referenced the customer's typed qty — restore
    // that flavour now that we share the helper with group flow.
    finalOptions.forEach(function (o) {
      if (o.stretch) {
        o.detail = "You'd get " + o.extraSizesNeeded + " extra printed garment" +
                   (o.extraSizesNeeded === 1 ? "" : "s") +
                   " on top of the " + qty + " you asked for";
      }
    });
    if (finalOptions.length < 2) return null;
    return finalOptions;
  }

  /**
   * Same shape as computeBundleOptions but for the group flow. Triggers
   * when:
   *   - Group has exactly ONE product type selected (mixed-type orders
   *     can't use a bundle cleanly)
   *   - Both Large Front + Back prints selected
   *   - That product type matches a known bundle's productMatch
   *
   * Returns null when no comparison is useful.
   */
  /**
   * Build a digestible bundle-options comparison for a single product +
   * quantity. Reusable across solo and per-type group computation.
   * Returns array of option objects (individuals + bundle stacks + stretch),
   * or null when no meaningful comparison exists.
   */
  function buildBundleOptionsForProduct(product, qty, printsPerUnit) {
    if (!product || qty <= 0) return null;
    const matchingBundles = BUNDLE_RULES.filter(function (rule) {
      return state.bundleProducts[rule.handle] && rule.productMatch(product);
    });
    if (!matchingBundles.length) return null;

    const productPrice = product.variants && product.variants[0]
      ? parseFloat(product.variants[0].price) : 0;
    const perUnit = productPrice + printsPerUnit;

    function priceForN(n) {
      const sub = perUnit * n;
      const t = activeTierFor(n);
      const disc = t ? sub * t.percentOff / 100 : 0;
      return sub - disc;
    }

    const options = [];
    options.push({
      key: "individuals",
      label: qty + " × " + product.title + " (individuals)",
      detail: (function () {
        const t = activeTierFor(qty);
        // With no tier ladder (packs are the bulk pricing), individuals are
        // plain per-item pricing — say so without implying a missing discount.
        return t ? "with " + t.percentOff + "% bulk discount" : "standard per-item pricing";
      })(),
      total: priceForN(qty),
      garments: qty,
      bundles: [],
      fillerQty: 0
    });

    const combos = enumerateBundleCombinations(matchingBundles, qty, priceForN);
    combos.forEach(function (combo) {
      const labelBase = buildBundleLabel(combo.bundles);
      const label = labelBase + (combo.fillerQty > 0
        ? " + " + combo.fillerQty + " individual" + (combo.fillerQty === 1 ? "" : "s") : "");
      let detail;
      if (combo.stretch) {
        detail = "You'd get " + combo.extraSizesNeeded + " extra printed garment" +
                 (combo.extraSizesNeeded === 1 ? "" : "s") + " — pick the sizes below";
      } else if (combo.fillerQty > 0) {
        const fillerCost = priceForN(combo.fillerQty);
        detail = "Bundles cover " + (combo.garments - combo.fillerQty) +
                 " · " + combo.fillerQty + " extra at $" + fillerCost.toFixed(2);
      } else {
        detail = "All " + combo.garments + " at bundle pricing — no add-ons";
      }
      options.push({
        key: (combo.stretch ? "stretch-" : "stack-") +
             combo.bundles.map(function (b) { return b.handle + "x1"; }).join("+"),
        label: label,
        detail: detail,
        total: combo.total,
        garments: combo.garments,
        bundles: combo.bundles,
        fillerQty: combo.fillerQty,
        stretch: combo.stretch,
        extraSizesNeeded: combo.extraSizesNeeded || 0
      });
    });

    options.sort(function (a, b) { return a.total - b.total; });
    const dedup = [];
    options.forEach(function (o) {
      if (!dedup.some(function (d) { return Math.abs(d.total - o.total) < 1; })) dedup.push(o);
    });
    const individuals = dedup.find(function (o) { return o.key === "individuals"; });
    const nonStretchBundles = dedup.filter(function (o) {
      return o.key !== "individuals" && !o.stretch;
    }).slice(0, 2);
    const firstStretch = dedup.find(function (o) { return o.stretch; });
    const finalOptions = [];
    if (individuals) finalOptions.push(individuals);
    nonStretchBundles.forEach(function (o) { finalOptions.push(o); });
    if (firstStretch && !nonStretchBundles.includes(firstStretch)) finalOptions.push(firstStretch);
    finalOptions.sort(function (a, b) { return a.total - b.total; });
    if (finalOptions.length < 2) return null;
    return finalOptions;
  }

  /**
   * Per-type bundle comparison for the group flow. Returns an array of
   * sections, one per selected product type. Each section has its own
   * options array (individuals + bundles) so customers can mix-and-match
   * — pick bundle for tees, individuals for kids, etc. Types without
   * a matching bundle product surface as `hasBundles: false` so the UI
   * can still acknowledge them ("no bundle available — priced individually").
   */
  function computeGroupBundleOptions() {
    const types = state.groupSelectedProductTypes || [];
    if (!types.length) return null;
    // Bundles include LARGE FRONT + BACK FULL. Trigger on Back + any front
    // print (Large Front or Front Left Chest).
    const hasFrontish = state.groupPrintLocations.indexOf("large_front") >= 0 ||
                        state.groupPrintLocations.indexOf("front_left_chest") >= 0;
    const hasBack = state.groupPrintLocations.indexOf("back_full") >= 0;
    if (!hasFrontish || !hasBack) return null;

    const printsPerUnit = (CONFIG.printPrices.large_front || 0) + (CONFIG.printPrices.back_full || 0);
    const sections = [];
    types.forEach(function (type) {
      const product = state.groupSelectedProducts[type];
      if (!product) return;
      const sizes = state.groupSizes[type] || {};
      let qty = 0;
      Object.keys(sizes).forEach(function (s) { qty += parseInt(sizes[s], 10) || 0; });
      if (qty <= 0) return;
      const typeMeta = GROUP_PRODUCT_TYPES.find(function (t) { return t.key === type; }) ||
                       { key: type, title: type };
      const options = buildBundleOptionsForProduct(product, qty, printsPerUnit);
      if (options) {
        sections.push({ type: type, typeMeta: typeMeta, qty: qty, options: options, hasBundles: true });
      } else {
        // No bundle rules match — surface a 'no bundle' note so the customer
        // sees their full pick acknowledged on the comparison step.
        sections.push({ type: type, typeMeta: typeMeta, qty: qty, options: [], hasBundles: false });
      }
    });

    // Only bother surfacing the bundle step if at least one section has a
    // real bundle option to offer.
    const anyBundle = sections.some(function (s) { return s.hasBundles; });
    if (!anyBundle) return null;
    return sections;
  }

  function renderGroupBundleDeals(sections) {
    const intro = $("#ow-group-bundle-intro");
    const wrap = $("#ow-group-bundle-options");
    if (!intro || !wrap || !sections || !sections.length) return;

    // Initialise selection map + extras map if not present.
    if (!state.groupBundleSelection || typeof state.groupBundleSelection !== "object" ||
        Array.isArray(state.groupBundleSelection)) {
      state.groupBundleSelection = {};
    }
    if (!state.bundleExtraSizes || typeof state.bundleExtraSizes !== "object") {
      state.bundleExtraSizes = {};
    }

    const printLabels = state.groupPrintLocations.map(locationLabel).join(" + ");
    intro.innerHTML =
      '<strong>Pick a path for each type of gear.</strong> ' +
      'Bundle pricing comes with free shipping. Mix and match — bundle the tees, keep the hoodies individual, whatever works.';

    wrap.innerHTML = "";
    sections.forEach(function (section) {
      const sectionEl = document.createElement("div");
      sectionEl.className = "ow-bundle-section";
      sectionEl.setAttribute("data-bundle-section-type", section.type);
      sectionEl.style.cssText = "margin-bottom:18px";

      const heading = document.createElement("div");
      heading.style.cssText = "font-size:13px;font-weight:700;color:var(--ow-text);margin:0 0 8px;text-transform:uppercase;letter-spacing:.04em";
      heading.textContent = section.typeMeta.title + " — " + section.qty + " garment" + (section.qty === 1 ? "" : "s");
      sectionEl.appendChild(heading);

      if (!section.hasBundles) {
        // No bundle available for this type — surface it honestly so the
        // customer doesn't think we forgot their kids tees or whatever.
        const note = document.createElement("div");
        note.style.cssText = "font-size:13px;color:var(--ow-text-dim);padding:10px 12px;background:var(--ow-bg-soft);border-radius:6px;line-height:1.4";
        note.textContent = "No bundle for this one — they'll be priced individually and included in your order.";
        sectionEl.appendChild(note);
        wrap.appendChild(sectionEl);
        return;
      }

      const options = section.options;
      const cheapest = options.reduce(function (best, cur) { return cur.total < best.total ? cur : best; }, options[0]);
      const worstValue = options.reduce(function (worst, cur) {
        const curPer = cur.garments > 0 ? cur.total / cur.garments : Infinity;
        const worstPer = worst.garments > 0 ? worst.total / worst.garments : Infinity;
        return curPer > worstPer ? cur : worst;
      }, options[0]);

      // Default this type's selection to the cheapest option if not already set
      // OR if previously-saved option no longer exists in the current list.
      const prevSel = state.groupBundleSelection[section.type];
      if (!prevSel || !options.find(function (o) { return o.key === prevSel.key; })) {
        state.groupBundleSelection[section.type] = cheapest;
      }

      // RRP per garment for this type
      const product = state.groupSelectedProducts[section.type];
      const productPrice = product && product.variants && product.variants[0]
        ? parseFloat(product.variants[0].price) : 0;
      const printsPerUnit = state.groupPrintLocations.reduce(function (s, l) {
        return s + (CONFIG.printPrices[l] || 0);
      }, 0);
      const rrpPerGarment = productPrice + printsPerUnit;

      options.forEach(function (o) {
        const sel = state.groupBundleSelection[section.type];
        const isSel = sel && sel.key === o.key;
        const isCheap = o === cheapest && options.length > 1;
        const isStretch = !!o.stretch;
        const isWorst = o === worstValue && options.length > 1 && !isCheap && !isStretch;
        const isBundle = !!(o.bundles && o.bundles.length > 0);
        const perGarment = o.garments > 0 ? (o.total / o.garments) : 0;
        const rrp = rrpPerGarment * o.garments;
        const savingsVsRrp = rrp - o.total;

        const shippingHtml = isBundle
          ? '<div class="ow-bundle-opt__shipping">Free shipping</div>'
          : '<div class="ow-bundle-opt__shipping-out">+ shipping at checkout</div>';
        const rrpHtml = (savingsVsRrp > 0)
          ? '<div class="ow-bundle-opt__rrp">Full price $' + rrp.toFixed(2) + '</div>' +
            '<div class="ow-bundle-opt__save">You save $' + savingsVsRrp.toFixed(2) + '</div>'
          : "";

        let colorClass = "";
        if (isCheap) colorClass = " is-cheapest";
        else if (isStretch) colorClass = " is-stretch";
        else if (isWorst) colorClass = " is-worst";

        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "ow-bundle-opt" + (isSel ? " is-selected" : "") + colorClass;
        btn.setAttribute("data-ow-action", "pick-group-bundle-option");
        btn.setAttribute("data-key", o.key);
        btn.setAttribute("data-product-type", section.type);
        btn.innerHTML =
          '<div class="ow-bundle-opt__body">' +
            '<div class="ow-bundle-opt__label">' + o.label + '</div>' +
            '<div class="ow-bundle-opt__detail">' + o.detail + '</div>' +
          '</div>' +
          '<div>' +
            '<div class="ow-bundle-opt__price">$' + o.total.toFixed(2) + '</div>' +
            '<div class="ow-bundle-opt__per">$' + perGarment.toFixed(2) + ' / garment</div>' +
            shippingHtml +
            rrpHtml +
          '</div>';
        btn._owBundleOpt = o;
        sectionEl.appendChild(btn);
      });

      // If this section's selected option is a stretch bundle, render an
      // inline size picker for its extras inside this section.
      const sel = state.groupBundleSelection[section.type];
      if (sel && sel.stretch && sel.extraSizesNeeded) {
        const picker = buildBundleExtraSizesPicker(sel, section.type);
        if (picker) sectionEl.appendChild(picker);
      }

      wrap.appendChild(sectionEl);
    });
  }

  /**
   * Build an inline size-matrix DOM node for the EXTRA garments a stretch
   * bundle adds on top of what the customer originally entered. Returns
   * the element (not auto-attached) so the multi-section render can drop
   * one picker INSIDE each type's section. State is per-type so multiple
   * stretches across types are tracked independently.
   */
  function buildBundleExtraSizesPicker(option, type) {
    if (!option || !option.stretch || !option.extraSizesNeeded) return null;
    const product = state.groupSelectedProducts[type];
    if (!product) return null;
    const cls = classifyOptions(product);
    const sizes = uniqueOptionValues(product, cls.sizeIdx);
    if (!state.bundleExtraSizes) state.bundleExtraSizes = {};
    if (!state.bundleExtraSizes[type]) state.bundleExtraSizes[type] = {};
    const target = option.extraSizesNeeded;
    let current = 0;
    Object.keys(state.bundleExtraSizes[type]).forEach(function (s) {
      current += parseInt(state.bundleExtraSizes[type][s], 10) || 0;
    });

    const picker = document.createElement("div");
    picker.className = "ow-size-matrix ow-bundle-extra-sizes";
    picker.setAttribute("data-extras-type", type);
    picker.style.cssText =
      "margin:10px 0 0;border-color:var(--ow-orange);background:#fff7ed";
    picker.innerHTML =
      '<div class="ow-size-matrix__header" style="background:var(--ow-orange);color:#fff;border-bottom-color:rgba(0,0,0,0.1)">' +
        '<span>Pick sizes for your ' + target + ' extra garment' + (target === 1 ? '' : 's') + '</span>' +
        '<span class="ow-size-matrix__total" data-extras-counter="' + type + '" style="color:#fff;opacity:.95">' +
          current + ' of ' + target +
        '</span>' +
      '</div>' +
      '<div class="ow-size-matrix__body">' +
        sizes.map(function (s) {
          const val = parseInt(state.bundleExtraSizes[type][s], 10) || 0;
          return '<div class="ow-size-cell">' +
                   '<span class="ow-size-cell__label">' + s + '</span>' +
                   '<div class="ow-size-cell__stepper">' +
                     '<button class="ow-size-cell__btn" type="button" data-ow-action="bundle-extra-size-dec" ' +
                       'data-product-type="' + type + '" data-size="' + s + '" aria-label="Decrease ' + s + '">−</button>' +
                     '<input class="ow-size-cell__input" type="number" min="0" max="' + target + '" ' +
                       'data-ow-bundle-extra-input data-product-type="' + type + '" data-size="' + s + '" ' +
                       'value="' + (val || "") + '" inputmode="numeric" placeholder="0">' +
                     '<button class="ow-size-cell__btn" type="button" data-ow-action="bundle-extra-size-inc" ' +
                       'data-product-type="' + type + '" data-size="' + s + '" aria-label="Increase ' + s + '">+</button>' +
                   '</div>' +
                 '</div>';
        }).join("") +
      '</div>';
    return picker;
  }

  /** Update the live "X of Y" counter on a specific section's extras picker without re-rendering. */
  function updateBundleExtraTotal(type) {
    const lbl = document.querySelector('[data-extras-counter="' + type + '"]');
    if (!lbl) return;
    const sel = state.groupBundleSelection && state.groupBundleSelection[type];
    if (!sel) return;
    const target = sel.extraSizesNeeded || 0;
    const typeExtras = (state.bundleExtraSizes && state.bundleExtraSizes[type]) || {};
    let current = 0;
    Object.keys(typeExtras).forEach(function (s) {
      current += parseInt(typeExtras[s], 10) || 0;
    });
    lbl.textContent = current + ' of ' + target;
  }

  // ------------------------------------------------------------------------
  // GROUP FLOW
  // ------------------------------------------------------------------------

  const GROUP_PRODUCT_TYPES = [
    { key: "adult_tee",    title: "Adult Tees",    desc: "Mens/womens t-shirts", audience: "adults" },
    { key: "adult_hoodie", title: "Adult Hoodies", desc: "Hoodies + crew necks",  audience: "adults" },
    { key: "kids_tee",     title: "Kids Tees",     desc: "Youth t-shirts",        audience: "kids"   },
    { key: "kids_hoodie",  title: "Kids Hoodies",  desc: "Youth hoodies",         audience: "kids"   }
  ];

  function groupTotalGarments() {
    return GROUP_PRODUCT_TYPES.reduce(function (sum, t) {
      const sizes = state.groupSizes[t.key] || {};
      Object.keys(sizes).forEach(function (s) {
        sum += parseInt(sizes[s], 10) || 0;
      });
      return sum;
    }, 0);
  }

  function activeTierFor(qty) {
    const tiers = CONFIG.tierTable || [];
    let active = null;
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (qty >= tiers[i].minQty) { active = tiers[i]; break; }
    }
    return active;
  }

  function nextTierAfter(qty) {
    const tiers = CONFIG.tierTable || [];
    for (let i = 0; i < tiers.length; i++) {
      if (qty < tiers[i].minQty) return tiers[i];
    }
    return null;
  }

  /**
   * Live tier card for the group totals step. Same component shape as the
   * solo tier card but reads from groupAdults + groupKids combined.
   */
  function renderGroupTierCard() {
    const wrap = $("#ow-group-tier-card");
    if (!wrap) return;
    const qty = (state.groupAdults || 0) + (state.groupKids || 0);
    if (qty <= 0) {
      wrap.innerHTML =
        '<div class="ow-tier-card__current">Tell me roughly how many people and I\'ll show you the discount tier.</div>';
      return;
    }
    const tiers = CONFIG.tierTable || [];
    // No tier ladder — pack products are the bulk pricing. Show a simple
    // count confirmation + pack pointer instead of discount promises.
    if (!tiers.length) {
      wrap.innerHTML =
        '<div class="ow-tier-card__current">' +
          qty + ' garments — nice. Our <strong>value packs</strong> (10 &amp; 20 packs, prints included) ' +
          'are the best bulk pricing; I\'ll show you any that fit your order at the next step.' +
        '</div>';
      return;
    }
    const current = activeTierFor(qty);
    const next = nextTierAfter(qty);

    let html = "";
    if (current) {
      html +=
        '<div class="ow-tier-card__current ow-tier-card__current--active">' +
          '<span class="ow-tier-card__check">✓</span>' +
          'At ' + qty + ' items you save ' + current.percentOff + '% on every item' +
        '</div>';
    } else {
      html += '<div class="ow-tier-card__current">' +
              qty + ' items — bulk discounts start at 5 items.' +
              '</div>';
    }
    if (next) {
      const need = next.minQty - qty;
      html += '<div class="ow-tier-card__next">Add <strong>' + need + ' more</strong> to get <strong>' + next.percentOff + '% off</strong> the lot</div>';
    } else if (current) {
      html += '<div class="ow-tier-card__next">You\'re at the top tier 🔥</div>';
    }
    html += '<div class="ow-tier-table">' +
      '<div class="ow-tier-table__row ow-tier-table__header">' +
        '<div class="ow-tier-table__cell">Quantity</div><div class="ow-tier-table__cell">Discount</div>' +
      '</div>';
    tiers.forEach(function (t) {
      const isCurrent = current && t.minQty === current.minQty;
      html +=
        '<div class="ow-tier-table__row' + (isCurrent ? ' ow-tier-table__row--current' : '') + '">' +
          '<div class="ow-tier-table__cell">' + t.minQty + '+ items</div>' +
          '<div class="ow-tier-table__cell">' + t.percentOff + '% off</div>' +
        '</div>';
    });
    html += '</div>';
    wrap.innerHTML = html;
    // Continue always enabled — group-totals-continue handler validates.
  }

  /**
   * Product type picker for group flow. Multi-select rows — customers can
   * mix tees, hoodies, adult and kids gear in a single order. The picker
   * always shows all four types; quantities are assigned later via the
   * per-type size matrix so the totals step doesn't gate visibility.
   */
  function renderGroupProductPick() {
    const wrap = $("#ow-group-product-pick");
    if (!wrap) return;
    wrap.innerHTML = "";

    GROUP_PRODUCT_TYPES.forEach(function (t) {
      const isSel = state.groupSelectedProductTypes.indexOf(t.key) >= 0;
      const row = document.createElement("button");
      row.type = "button";
      row.className = "ow-product-pick-row" + (isSel ? " is-selected" : "");
      row.setAttribute("data-ow-action", "toggle-group-product");
      row.setAttribute("data-product-type", t.key);
      row.innerHTML =
        '<span class="ow-product-pick-row__check">' + (isSel ? "✓" : "") + '</span>' +
        '<span class="ow-product-pick-row__body">' +
          '<span class="ow-product-pick-row__title">' + t.title + '</span>' +
          '<span class="ow-product-pick-row__desc">' + t.desc + '</span>' +
        '</span>';
      wrap.appendChild(row);
    });

    // Continue always enabled — group-products-continue handler validates.
  }

  /**
   * Pick which Shopify product fits each product type. Uses the wizard-
   * recommended collection — first product of each detected category.
   * If multiple recommended exist for a type, picks the first (Roland's
   * order in the collection wins).
   */
  async function resolveGroupProducts() {
    const collection = await fetchCollectionProducts("wizard-recommended");
    const list = collection || [];

    function categoryFor(p) {
      const c = categorize(p);
      if (c === "hoodie") return "hoodie";
      // tee + long_sleeve + crew all grouped under tee for group flow
      if (c === "tee" || c === "long_sleeve") return "tee";
      return null;
    }
    function isKids(p) {
      const t = ((p.title || "") + " " + (p.product_type || "")).toLowerCase();
      return /kids|youth|child|toddler/.test(t);
    }

    const map = {};
    state.groupSelectedProductTypes.forEach(function (type) {
      const wantHoodie = /hoodie/.test(type);
      const wantKids = /kids/.test(type);
      const candidate = list.find(function (p) {
        const cat = categoryFor(p);
        const kid = isKids(p);
        return (wantHoodie ? cat === "hoodie" : cat === "tee") && (kid === wantKids);
      });
      if (candidate) map[type] = candidate;
    });
    state.groupSelectedProducts = map;
    return map;
  }

  /**
   * Render one size-matrix block per selected product type. Inputs are
   * delegated through ow-size-input.
   */
  async function renderGroupSizeMatrices() {
    const wrap = $("#ow-group-size-matrices");
    if (!wrap) return;
    wrap.innerHTML = '<div class="ow-loading">Loading products…</div>';

    await resolveGroupProducts();

    wrap.innerHTML = "";
    state.groupSelectedProductTypes.forEach(function (type) {
      const t = GROUP_PRODUCT_TYPES.find(function (x) { return x.key === type; });
      const product = state.groupSelectedProducts[type];
      if (!product) {
        const empty = document.createElement("div");
        empty.className = "ow-error";
        empty.textContent = t.title + ": no recommended product found. Add one to the Wizard Recommended collection in Shopify.";
        wrap.appendChild(empty);
        return;
      }
      const cls = classifyOptions(product);
      const sizes = uniqueOptionValues(product, cls.sizeIdx);
      // Initialise per-type qty map
      if (!state.groupSizes[type]) state.groupSizes[type] = {};

      const matrix = document.createElement("div");
      matrix.className = "ow-size-matrix";
      let subTotal = 0;
      Object.keys(state.groupSizes[type]).forEach(function (s) {
        subTotal += parseInt(state.groupSizes[type][s], 10) || 0;
      });
      matrix.innerHTML =
        '<div class="ow-size-matrix__header">' +
          '<span>' + t.title + ' — ' + product.title + '</span>' +
          '<span class="ow-size-matrix__total" data-ow-subtotal="' + type + '">' + subTotal + ' total</span>' +
        '</div>' +
        '<div class="ow-size-matrix__body">' +
          sizes.map(function (s) {
            const val = state.groupSizes[type][s] || 0;
            return '<div class="ow-size-cell">' +
                     '<span class="ow-size-cell__label">' + s + '</span>' +
                     '<div class="ow-size-cell__stepper">' +
                       '<button class="ow-size-cell__btn" type="button" data-ow-action="group-size-dec" ' +
                         'data-product-type="' + type + '" data-size="' + s + '" aria-label="Decrease ' + s + '">−</button>' +
                       '<input class="ow-size-cell__input" type="number" min="0" max="500" ' +
                         'data-ow-size-input data-product-type="' + type + '" data-size="' + s + '" ' +
                         'value="' + (val || "") + '" inputmode="numeric" placeholder="0">' +
                       '<button class="ow-size-cell__btn" type="button" data-ow-action="group-size-inc" ' +
                         'data-product-type="' + type + '" data-size="' + s + '" aria-label="Increase ' + s + '">+</button>' +
                     '</div>' +
                   '</div>';
          }).join("") +
        '</div>';
      wrap.appendChild(matrix);
    });

    updateGroupProgress();
  }

  function updateGroupProgress() {
    const wrap = $("#ow-group-progress");
    if (!wrap) return;
    const total = groupTotalGarments();
    const current = activeTierFor(total);
    const next = nextTierAfter(total);
    // Just count what the customer's actually picked + how it stacks on
    // discount tiers — the upfront Adults/Kids totals were a soft hint,
    // not a target, and showing "X of Y" after we changed the picker
    // produced nonsense like "20 of 10".
    let html = '<strong>' + total + '</strong> item' + (total === 1 ? '' : 's');
    if (current) {
      html += ' &middot; saving ' + current.percentOff + '%';
    }
    if (next) {
      html += ' &middot; ' + (next.minQty - total) + ' more for ' + next.percentOff + '% off';
    }
    wrap.innerHTML = html;

    // Update each matrix's subtotal label too
    state.groupSelectedProductTypes.forEach(function (type) {
      const lbl = document.querySelector('[data-ow-subtotal="' + type + '"]');
      if (!lbl) return;
      const sizes = state.groupSizes[type] || {};
      let sub = 0;
      Object.keys(sizes).forEach(function (s) { sub += parseInt(sizes[s], 10) || 0; });
      lbl.textContent = sub + " total";
    });
  }

  /**
   * Compute the intersection of colours offered by every selected product.
   * Falls back to the union if there's no overlap (rare).
   */
  function groupAvailableColours() {
    const types = state.groupSelectedProductTypes;
    if (!types.length) return [];
    const sets = types.map(function (type) {
      const p = state.groupSelectedProducts[type];
      if (!p) return null;
      const cls = classifyOptions(p);
      return uniqueOptionValues(p, cls.colourIdx);
    }).filter(Boolean);
    if (!sets.length) return [];
    // Intersection
    const first = sets[0];
    const inter = first.filter(function (c) {
      return sets.every(function (s) { return s.indexOf(c) >= 0; });
    });
    if (inter.length) return inter;
    // Fall back to union if no intersection
    const seen = {}, union = [];
    sets.forEach(function (s) {
      s.forEach(function (c) { if (!seen[c]) { seen[c] = true; union.push(c); } });
    });
    return union;
  }

  function renderGroupColourPicker() {
    const wrap = $("#ow-group-swatches");
    if (!wrap) return;
    let colours = groupAvailableColours();
    // If EVERY product the customer picked is core-range, restrict the
    // colour picker to fast-ship colours so the in-stock promise applies
    // to the whole order. Mixed orders (any non-core type) keep the full
    // colour set since fast-ship isn't guaranteed anyway.
    const types = state.groupSelectedProductTypes || [];
    const allCore = types.length > 0 && types.every(function (t) {
      const p = state.groupSelectedProducts && state.groupSelectedProducts[t];
      return p && isCoreRange(p);
    });
    if (allCore) {
      const allowed = (CONFIG.coreRangeFastColours || []).map(function (c) {
        return String(c).toLowerCase();
      });
      if (allowed.length) {
        const filtered = colours.filter(function (c) {
          return allowed.indexOf(String(c).toLowerCase()) >= 0;
        });
        if (filtered.length) colours = filtered;
      }
    }
    if (!colours.length) {
      wrap.innerHTML = '<div class="ow-error">No matching colour found across the products you picked.</div>';
      return;
    }
    if (!state.groupColour || colours.indexOf(state.groupColour) < 0) {
      // Default to Black if available, else first
      const def = colours.find(function (c) { return c.toLowerCase() === "black"; });
      state.groupColour = def || colours[0];
    }
    wrap.innerHTML = "";
    colours.forEach(function (c) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "ow-swatch" + (c === state.groupColour ? " is-selected" : "");
      b.setAttribute("data-ow-action", "pick-group-colour");
      b.setAttribute("data-value", c);
      b.title = c;
      const dot = document.createElement("span");
      dot.className = "ow-swatch__dot";
      const css = colourToCss(c);
      if (css) dot.setAttribute("style", "background:" + css);
      b.appendChild(dot);
      const name = document.createElement("span");
      name.className = "ow-swatch__name";
      name.textContent = c;
      b.appendChild(name);
      wrap.appendChild(b);
    });
    // Continue always enabled — group-colour-continue handler validates.
  }

  function renderGroupPrintPicker() {
    const wrap = $("#ow-group-print-options");
    if (!wrap) return;
    wrap.innerHTML = "";
    const opts = [
      { key: "large_front",      title: "Large Front",      desc: "Up to 38 × 48 cm.",                    price: CONFIG.printPrices.large_front,      svg: SVG_LARGE_FRONT },
      { key: "back_full",        title: "Back Full Size",   desc: "Up to 38 × 48 cm.",                    price: CONFIG.printPrices.back_full,        svg: SVG_BACK_FULL },
      { key: "front_left_chest", title: "Front Left Chest", desc: "Up to 10 × 10 cm, on the left chest.", price: CONFIG.printPrices.front_left_chest, svg: SVG_FRONT_LEFT_CHEST }
    ];
    opts.forEach(function (o) {
      const isSel = state.groupPrintLocations.indexOf(o.key) >= 0;
      const b = document.createElement("button");
      b.type = "button";
      b.className = "ow-print-option" + (isSel ? " is-selected" : "");
      b.setAttribute("data-ow-action", "toggle-group-print");
      b.setAttribute("data-key", o.key);
      b.innerHTML =
        '<div class="ow-print-option__thumb">' + o.svg + '</div>' +
        '<div class="ow-print-option__body">' +
          '<div class="ow-print-option__title">' + o.title + '</div>' +
          '<div class="ow-print-option__desc">' + o.desc + '</div>' +
        '</div>' +
        '<div class="ow-print-option__price">+$' + o.price.toFixed(2) + '</div>';
      wrap.appendChild(b);
    });
    // Continue always enabled — group-print-continue handler validates.
  }

  function renderGroupArtworkList() {
    const wrap = $("#ow-group-artwork-list");
    if (!wrap) return;
    wrap.innerHTML = "";
    const hasAi = !!state.designUrl;
    const saved = getSavedDesigns();

    state.groupPrintLocations.forEach(function (loc) {
      const url = state.groupArtwork[loc] || "";
      const source = state.groupArtworkSources[loc] || "";
      const showSavedPanel = state.groupSavedPickerOpen === loc;
      const row = document.createElement("div");
      row.className = "ow-artwork-row";
      const previewHtml = url
        ? '<img src="' + url + '" alt="' + locationLabel(loc) + ' artwork">'
        : '<div class="ow-artwork-row__noimg">' + (source === "later" ? "Sending later" : "Pick one") + '</div>';
      row.innerHTML =
        '<div class="ow-artwork-row__header">' + locationLabel(loc) + ' print</div>' +
        '<div class="ow-artwork-row__preview">' +
          previewHtml +
          '<div class="ow-artwork-row__source">' + sourceLabel(source) + '</div>' +
        '</div>' +
        '<div class="ow-artwork-buttons">' +
          (hasAi
            ? '<button class="ow-artwork-btn' + (source === "current" ? " is-active" : "") +
                '" type="button" data-ow-action="group-pick-current" data-location="' + loc + '">My AI design</button>'
            : '<button class="ow-artwork-btn ow-artwork-btn--create" type="button" data-ow-action="create-design" data-location="' + loc + '">✨ Create a design with AI</button>') +
          '<button class="ow-artwork-btn' + (source === "upload" ? " is-active" : "") +
            '" type="button" data-ow-action="group-pick-upload" data-location="' + loc + '">Upload file</button>' +
          '<button class="ow-artwork-btn' + (showSavedPanel ? " is-active" : "") +
            '" type="button" data-ow-action="group-toggle-saved" data-location="' + loc + '">My saved designs</button>' +
          '<button class="ow-artwork-btn' + (source === "later" ? " is-active" : "") +
            '" type="button" data-ow-action="group-pick-later" data-location="' + loc + '">Send artwork later</button>' +
        '</div>';

      if (showSavedPanel) {
        const grid = document.createElement("div");
        grid.className = "ow-saved-grid";
        if (!saved.length) {
          grid.innerHTML = '<div class="ow-saved-empty">No saved designs yet — save them as you make them on the designer.</div>';
        } else {
          saved.forEach(function (d) {
            const thumb = document.createElement("button");
            thumb.type = "button";
            thumb.className = "ow-saved-thumb";
            thumb.setAttribute("data-ow-action", "group-pick-saved-design");
            thumb.setAttribute("data-location", loc);
            thumb.setAttribute("data-design-url", d.draftUrl || "");
            thumb.title = d.prompt || "Saved design";
            thumb.innerHTML = '<img src="' + (d.thumbnail || d.draftUrl || "") +
              '" alt="' + (d.prompt || "saved").replace(/"/g, "&quot;") + '">';
            grid.appendChild(thumb);
          });
        }
        row.appendChild(grid);
      }

      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = "image/png,image/jpeg,image/webp,image/svg+xml,.pdf,.ai,.eps,.tif,.tiff";
      fileInput.style.display = "none";
      fileInput.setAttribute("data-ow-group-file", loc);
      fileInput.addEventListener("change", function () {
        const f = fileInput.files && fileInput.files[0];
        if (f) handleGroupFileUpload(loc, f);
      });
      row.appendChild(fileInput);

      wrap.appendChild(row);
    });
  }

  async function handleGroupFileUpload(location, file) {
    const errEl = $("#ow-group-artwork-error");
    if (errEl) { errEl.setAttribute("hidden", ""); errEl.textContent = ""; }
    const row = document.querySelector('[data-ow-group-file="' + location + '"]');
    const srcEl = row && row.parentElement && row.parentElement.querySelector(".ow-artwork-row__source");
    if (srcEl) srcEl.textContent = "Uploading " + file.name + "...";
    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await fetch(CONFIG.workerUrl + "/api/customer-upload", { method: "POST", body: formData });
      if (!r.ok) throw new Error((await r.text()) || ("HTTP " + r.status));
      const data = await r.json();
      if (!data || !data.url) throw new Error("Upload returned no URL");
      state.groupArtwork[location] = data.url;
      state.groupArtworkSources[location] = "upload";
      state.groupArtworkLabels[location] = file.name;
      state.groupSavedPickerOpen = null;
      renderGroupArtworkList();
    } catch (e) {
      console.warn("[OrderWizard] group file upload failed", e);
      if (errEl) {
        errEl.textContent = "Upload failed: " + (e.message || e);
        errEl.removeAttribute("hidden");
      }
      renderGroupArtworkList();
    }
  }

  /**
   * Group confirm summary. Rolls all selected sizes and product types into
   * a single discounted total based on TOTAL garment count.
   */
  function renderGroupConfirmSummary() {
    const sum = $("#ow-group-summary");
    if (!sum) return;

    // ----- MULTI-PRODUCT BUNDLE PATH summary -----
    // If ANY product type used a bundle, render a per-type breakdown.
    // Each type contributes its own bundle rows + filler line; types still
    // on individuals fall through to the individuals summary below.
    const selections = (state.groupBundleSelection && typeof state.groupBundleSelection === "object" &&
                        !Array.isArray(state.groupBundleSelection)) ? state.groupBundleSelection : {};
    const printsPerUnitTotal = state.groupPrintLocations.reduce(function (s, loc) { return s + (CONFIG.printPrices[loc] || 0); }, 0);
    let anyBundle = false;
    Object.keys(selections).forEach(function (type) {
      const sel = selections[type];
      if (sel && sel.bundles && sel.bundles.length > 0 && sel.key !== "individuals") anyBundle = true;
    });

    if (anyBundle) {
      let html = "";
      let grandTotal = 0;
      state.groupSelectedProductTypes.forEach(function (type) {
        const sel = selections[type];
        const product = state.groupSelectedProducts[type];
        if (!product) return;
        const sizes = state.groupSizes[type] || {};
        const typeQty = Object.keys(sizes).reduce(function (s, k) { return s + (parseInt(sizes[k], 10) || 0); }, 0);
        if (typeQty === 0) return;
        const typeMeta = GROUP_PRODUCT_TYPES.find(function (t) { return t.key === type; }) || { title: type };
        const productPrice = product.variants && product.variants[0] ? parseFloat(product.variants[0].price) : 0;
        const perUnitForType = productPrice + printsPerUnitTotal;

        // Section header per type
        html += '<div class="ow-summary__row" style="font-weight:700;color:var(--ow-text);margin-top:4px"><span>' +
                typeMeta.title + '</span><span></span></div>';

        if (sel && sel.bundles && sel.bundles.length > 0 && sel.key !== "individuals") {
          // Bundle rows
          sel.bundles.forEach(function (bRule) {
            const bp = state.bundleProducts[bRule.handle];
            if (!bp) return;
            html +=
              '<div class="ow-summary__row"><span class="ow-summary__label">' + bp.title + '</span>' +
              '<span class="ow-summary__val">$' + bp.price.toFixed(2) + '</span></div>';
          });
          // Filler
          if (sel.fillerQty > 0) {
            const fillerSubtotal = perUnitForType * sel.fillerQty;
            const fillerTier = activeTierFor(sel.fillerQty);
            const fillerDiscPct = fillerTier ? fillerTier.percentOff : 0;
            const fillerNet = fillerSubtotal - (fillerSubtotal * fillerDiscPct / 100);
            html +=
              '<div class="ow-summary__row"><span class="ow-summary__label">' + sel.fillerQty +
              ' extra individual' + (sel.fillerQty === 1 ? '' : 's') + ' + prints</span>' +
              '<span class="ow-summary__val">$' + fillerNet.toFixed(2) + '</span></div>';
          }
          // Extras picked (stretch)
          if (sel.stretch && sel.extraSizesNeeded) {
            const extras = (state.bundleExtraSizes && state.bundleExtraSizes[type]) || {};
            const extrasStr = Object.keys(extras)
              .filter(function (s) { return (parseInt(extras[s], 10) || 0) > 0; })
              .map(function (s) { return s + ":" + extras[s]; })
              .join(" ");
            if (extrasStr) {
              html +=
                '<div class="ow-summary__row" style="font-size:12px;color:var(--ow-text-dim);">' +
                  '<span>Extras picked: ' + extrasStr + '</span>' +
                  '<span></span>' +
                '</div>';
            }
          }
          grandTotal += sel.total;
        } else {
          // Individuals row for this type
          const tier = activeTierFor(typeQty);
          const discPct = tier ? tier.percentOff : 0;
          const subtotal = perUnitForType * typeQty;
          const net = subtotal - (subtotal * discPct / 100);
          html +=
            '<div class="ow-summary__row"><span class="ow-summary__label">' + typeQty +
            ' × individuals + prints</span>' +
            '<span class="ow-summary__val">$' + net.toFixed(2) + '</span></div>';
          grandTotal += net;
        }
      });

      html +=
        '<div class="ow-summary__row ow-summary__row--total">' +
          '<span class="ow-summary__label">Total</span>' +
          '<span class="ow-summary__val">$' + grandTotal.toFixed(2) + '</span></div>';

      sum.innerHTML = html;
      const stretchEl = $("#ow-group-stretch-hint");
      if (stretchEl) stretchEl.setAttribute("hidden", "");
      return;
    }

    // ----- INDIVIDUALS PATH (original) -----
    let html = "";
    let garmentSubtotal = 0;
    let printSubtotalPerUnit = 0;
    const totalGarments = groupTotalGarments();

    // Print add-on per-unit
    state.groupPrintLocations.forEach(function (loc) {
      printSubtotalPerUnit += CONFIG.printPrices[loc] || 0;
    });

    // Per product type breakdown
    state.groupSelectedProductTypes.forEach(function (type) {
      const product = state.groupSelectedProducts[type];
      if (!product) return;
      const typeMeta = GROUP_PRODUCT_TYPES.find(function (t) { return t.key === type; }) || { title: type };
      const sizes = state.groupSizes[type] || {};
      const typeQty = Object.keys(sizes).reduce(function (s, k) { return s + (parseInt(sizes[k], 10) || 0); }, 0);
      if (typeQty === 0) return;
      const unitPrice = product.variants && product.variants[0] ? parseFloat(product.variants[0].price) : 0;
      const lineSubtotal = unitPrice * typeQty;
      garmentSubtotal += lineSubtotal;
      const breakdown = Object.keys(sizes)
        .filter(function (s) { return (parseInt(sizes[s], 10) || 0) > 0; })
        .map(function (s) { return sizes[s] + "×" + s; })
        .join(" ");
      html +=
        '<div class="ow-summary__row">' +
          '<span class="ow-summary__label">' + typeMeta.title + ' — ' + typeQty + ' (' + breakdown + ')</span>' +
          '<span class="ow-summary__val">$' + lineSubtotal.toFixed(2) + '</span>' +
        '</div>';
    });

    // Print lines (multiplied by total garment count)
    state.groupPrintLocations.forEach(function (loc) {
      const lineTotal = (CONFIG.printPrices[loc] || 0) * totalGarments;
      html +=
        '<div class="ow-summary__row">' +
          '<span class="ow-summary__label">' + locationLabel(loc) + ' × ' + totalGarments + '</span>' +
          '<span class="ow-summary__val">+$' + lineTotal.toFixed(2) + '</span>' +
        '</div>';
    });

    const printSubtotalTotal = printSubtotalPerUnit * totalGarments;
    const subtotal = garmentSubtotal + printSubtotalTotal;
    const tier = activeTierFor(totalGarments);
    const discountPct = tier ? tier.percentOff : 0;
    const discountAmount = subtotal * discountPct / 100;
    const total = subtotal - discountAmount;

    if (tier) {
      html += '<div class="ow-summary__row ow-summary__row--subtotal">' +
              '<span class="ow-summary__label">Full price (no discount)</span>' +
              '<span class="ow-summary__val">$' + subtotal.toFixed(2) + '</span></div>';
      html += '<div class="ow-summary__row ow-summary__row--discount">' +
              '<span class="ow-summary__label">Bulk discount (' + discountPct + '% off)</span>' +
              '<span class="ow-summary__val">−$' + discountAmount.toFixed(2) + '</span></div>';
    }
    // Rush production fee — off the DISCOUNTED total, not the pre-discount
    // subtotal, so this matches what Hulk Discounts actually charges once
    // the customer lands on the real cart.
    const rushOpt = (CONFIG.rushOptions || []).find(function (o) { return o.key === state.rushSelection; });
    let rushFee = 0;
    if (rushOpt && rushOpt.surchargePct > 0) {
      const rushResult = computeRushFee(rushOpt, total);
      rushFee = rushResult.fee;
      const note = rushResult.hitMin ? ' (min $' + rushOpt.minFee + ')'
                 : rushResult.hitMax ? ' (capped)'
                 : ' (' + rushOpt.surchargePct + '%)';
      html += '<div class="ow-summary__row" style="color:var(--ow-orange)">' +
              '<span class="ow-summary__label">' + rushOpt.label + note + '</span>' +
              '<span class="ow-summary__val">+$' + rushFee.toFixed(2) + '</span></div>';
    }
    const grandTotal = total + rushFee;
    if (state.groupReorderMode) {
      html += '<div class="ow-summary__row" style="color:var(--ow-text-dim);font-size:12px">' +
              '<span class="ow-summary__label">Reorder of order ' + (state.groupReorderOrderNumber || "") + '</span>' +
              '<span class="ow-summary__val"></span></div>';
    }
    html += '<div class="ow-summary__row ow-summary__row--total">' +
            '<span class="ow-summary__label">Total</span>' +
            '<span class="ow-summary__val">$' + grandTotal.toFixed(2) + '</span></div>';

    sum.innerHTML = html;

    // Honest-stretch hint — only suggest if the marginal cost per extra
    // item is less than the new tier's per-item rate. Otherwise we'd
    // be pushing the customer to spend more for nothing.
    const stretch = $("#ow-group-stretch-hint");
    if (stretch) {
      const next = nextTierAfter(totalGarments);
      if (next) {
        const needed = next.minQty - totalGarments;
        // Estimate average per-item price (garment + prints)
        const avgPerItem = totalGarments > 0 ? subtotal / totalGarments : 0;
        // Stretched scenario
        const stretchedSubtotal = avgPerItem * next.minQty;
        const stretchedDiscount = stretchedSubtotal * next.percentOff / 100;
        const stretchedTotal = stretchedSubtotal - stretchedDiscount;
        const extraSpend = stretchedTotal - total;
        const extraPerItem = needed > 0 ? extraSpend / needed : 0;
        // Worth suggesting when the cost of extras is less than the new
        // tier's effective per-item rate (i.e. they're getting more value)
        const stretchedPerItem = stretchedTotal / next.minQty;
        if (extraPerItem < stretchedPerItem && extraPerItem > 0 && needed <= Math.max(5, Math.ceil(totalGarments * 0.5))) {
          stretch.innerHTML =
            '<strong>Quick heads-up:</strong> bumping to ' + next.minQty +
            ' items unlocks <strong>' + next.percentOff + '% off</strong>. ' +
            'That\'s ' + needed + ' more items for about <strong>$' + extraPerItem.toFixed(2) + ' each</strong> — ' +
            'cheaper per shirt than what you\'re paying now. Want to add a few more?';
          stretch.removeAttribute("hidden");
        } else {
          stretch.setAttribute("hidden", "");
        }
      } else {
        stretch.setAttribute("hidden", "");
      }
    }
  }

  /**
   * Add the entire group order to Shopify cart as multiple line items.
   * One line item per (product type × size). Print add-ons multiplied
   * across the total garment count.
   */
  async function groupAddToCart() {
    const items = [];
    const errEl = $("#ow-group-cart-error");
    if (errEl) errEl.setAttribute("hidden", "");

    // Build artwork properties (shared across every garment line item)
    const sharedProps = {};
    const locLabels = state.groupPrintLocations.map(locationLabel);

    if (state.groupReorderMode) {
      // Reorder path — no new artwork/mockup, staff match this against the
      // named past order manually. Still sets "Front/Large Front/Back
      // artwork" per selected location (not a real artwork label, just a
      // reorder note) — the cart-page reconciler uses THESE EXACT
      // properties as its only signal that a shirt claims a given print;
      // leaving them blank made every reorder shirt look print-less,
      // firing a false "Cart needs review" warning on real orders.
      const reorderNoteG = "Reorder — see details above";
      if (state.groupPrintLocations.indexOf("front_left_chest") >= 0) sharedProps["Front artwork"] = reorderNoteG;
      if (state.groupPrintLocations.indexOf("large_front") >= 0) sharedProps["Large Front artwork"] = reorderNoteG;
      if (state.groupPrintLocations.indexOf("back_full") >= 0) sharedProps["Back artwork"] = reorderNoteG;
      sharedProps["Print Location"] = locLabels.join(" + ");
      sharedProps["T-Shirt Print Location"] = locLabels.join(" + ");
      sharedProps["Reorder — Previous order #"] = state.groupReorderOrderNumber;
      sharedProps["Reorder — Details"] = state.groupReorderDetails;
    } else {
      function artworkLabelG(loc) {
        const lbl = state.groupArtworkLabels[loc];
        if (lbl) return lbl;
        return state.groupArtworkSources[loc] === "upload" ? "Uploaded file" :
               state.groupArtworkSources[loc] === "saved"  ? "Saved AI design" :
               "AI design attached";
      }
      if (state.groupPrintLocations.indexOf("large_front") >= 0) {
        const url = state.groupArtworkSources.large_front === "later" ? "" : (state.groupArtwork.large_front || state.designUrl || "");
        sharedProps["_Large Front artwork URL"] = url;
        sharedProps["Large Front artwork"] = artworkLabelG("large_front");
        if (url) sharedProps["Large Front print file"] = url;
      }
      if (state.groupPrintLocations.indexOf("back_full") >= 0) {
        const url = state.groupArtworkSources.back_full === "later" ? "" : (state.groupArtwork.back_full || state.designUrl || "");
        sharedProps["_Back artwork URL"] = url;
        sharedProps["Back artwork"] = artworkLabelG("back_full");
        if (url) sharedProps["Back print file"] = url;
      }
      if (state.groupPrintLocations.indexOf("front_left_chest") >= 0) {
        const url = state.groupArtworkSources.front_left_chest === "later" ? "" : (state.groupArtwork.front_left_chest || state.designUrl || "");
        sharedProps["_Front artwork URL"] = url;
        sharedProps["Front artwork"] = artworkLabelG("front_left_chest");
        if (url) sharedProps["Front Left Chest print file"] = url;
      }
      sharedProps["Print Location"] = locLabels.join(" + ");
      sharedProps["T-Shirt Print Location"] = locLabels.join(" + "); // backward compat

      for (const loc of state.groupPrintLocations) {
        if (state.groupArtworkSources[loc] === "later") continue; // nothing to snapshot yet
        try {
          const canvas = await renderMockupSnapshot("group", loc);
          if (canvas) sharedProps["_ft_preview_" + loc] = await uploadMockupPreview(canvas);
        } catch (e) {
          console.warn("[OrderWizard] mockup preview snapshot failed for", loc, e);
        }
      }
    }

    const bundleSelections = (state.groupBundleSelection && typeof state.groupBundleSelection === "object" &&
                              !Array.isArray(state.groupBundleSelection)) ? state.groupBundleSelection : {};
    const totalGarments = groupTotalGarments();
    // Track print add-on quantities. Bundles include their own prints so
    // they don't need extra print add-on line items; only individual
    // garments + filler get a corresponding print add-on per location.
    let printAddOnQty = 0;
    let bundleLookupFailed = false;

    // Walk every selected product type. Each type independently chooses
    // bundle (one or more) vs individuals via state.groupBundleSelection.
    state.groupSelectedProductTypes.forEach(function (type) {
      const product = state.groupSelectedProducts[type];
      if (!product) return;
      const cls = classifyOptions(product);
      const sizes = state.groupSizes[type] || {};
      const typeQty = Object.keys(sizes).reduce(function (s, k) {
        return s + (parseInt(sizes[k], 10) || 0);
      }, 0);
      if (typeQty === 0) return;

      // Real print_mm/offset_mm for THIS garment type specifically — the
      // same fractional placement can mean a different real mm size on a
      // tee vs a hoodie, so this can't be part of the shared props above.
      // Skipped in reorder mode — there's no real placement to describe.
      const typePrintSpec = state.groupReorderMode ? {} : buildPrintSpecForProduct("group", product.handle);
      const typeProps = Object.keys(typePrintSpec).length
        ? Object.assign({}, sharedProps, { "_Print Spec": JSON.stringify(typePrintSpec) })
        : sharedProps;

      const sel = bundleSelections[type];
      const usingBundle = sel && sel.key !== "individuals" && sel.bundles && sel.bundles.length > 0;

      if (usingBundle) {
        // ---- BUNDLE PATH for this type ----
        // Merge entered sizes + any stretch extras chosen for THIS type.
        const combinedSizes = Object.assign({}, sizes);
        if (sel.stretch && state.bundleExtraSizes && state.bundleExtraSizes[type]) {
          Object.keys(state.bundleExtraSizes[type]).forEach(function (s) {
            const extra = parseInt(state.bundleExtraSizes[type][s], 10) || 0;
            if (extra > 0) {
              combinedSizes[s] = (parseInt(combinedSizes[s], 10) || 0) + extra;
            }
          });
        }
        const bundleQtys = sel.bundles.map(function (b) { return b.qty; });
        const perBundleSizes = splitSizesAcrossBundles(combinedSizes, bundleQtys);
        const totalBundleGarments = bundleQtys.reduce(function (s, q) { return s + q; }, 0);

        sel.bundles.forEach(function (bRule, idx) {
          const bp = state.bundleProducts[bRule.handle];
          if (!bp || !bp.variantId) {
            bundleLookupFailed = true;
            return;
          }
          const slotSizes = perBundleSizes[idx] || {};
          const slotBreakdown = Object.keys(slotSizes)
            .filter(function (s) { return (parseInt(slotSizes[s], 10) || 0) > 0; })
            .map(function (s) { return s + ":" + slotSizes[s]; })
            .join(" ");
          const bundleProps = Object.assign({}, typeProps, {
            "Bundle": bp.title,
            "Colour": state.groupColour,
            "Size breakdown": slotBreakdown,
            "Total garments (this bundle)": String(bRule.qty),
            "Total garments (this product type)": String(totalBundleGarments + (sel.fillerQty || 0))
          });
          if (sel.bundles.length > 1) {
            bundleProps["Bundle group"] = (idx + 1) + " of " + sel.bundles.length;
          }
          items.push({ id: bp.variantId, quantity: 1, properties: bundleProps });
        });

        // Filler individuals if customer ordered more than this type's bundles cover.
        if (sel.fillerQty > 0) {
          const totalBundleCovered = bundleQtys.reduce(function (s, q) { return s + q; }, 0);
          const sizeKeys = Object.keys(sizes).filter(function (s) { return (parseInt(sizes[s], 10) || 0) > 0; });
          const remaining = {};
          let totalCovered = 0;
          sizeKeys.forEach(function (s) {
            const wanted = parseInt(sizes[s], 10) || 0;
            const inBundles = Math.min(wanted, Math.max(0, totalBundleCovered - totalCovered));
            totalCovered += inBundles;
            const inFiller = wanted - inBundles;
            if (inFiller > 0) remaining[s] = inFiller;
          });
          Object.keys(remaining).forEach(function (s) {
            const vid = findVariantId(product, cls.colourIdx, cls.sizeIdx, state.groupColour, s);
            if (!vid) return;
            items.push({ id: vid, quantity: remaining[s], properties: typeProps });
          });
          printAddOnQty += sel.fillerQty;
        }
      } else {
        // ---- INDIVIDUALS PATH for this type ----
        Object.keys(sizes).forEach(function (size) {
          const qty = parseInt(sizes[size], 10) || 0;
          if (qty <= 0) return;
          const vid = findVariantId(product, cls.colourIdx, cls.sizeIdx, state.groupColour, size);
          if (!vid) {
            console.warn("[OrderWizard] no variant for", type, state.groupColour, size);
            return;
          }
          items.push({ id: vid, quantity: qty, properties: typeProps });
        });
        printAddOnQty += typeQty;
      }
    });

    if (bundleLookupFailed) {
      if (errEl) {
        errEl.textContent = "One of the bundle products couldn't be found — try selecting individuals instead.";
        errEl.removeAttribute("hidden");
      }
      return;
    }

    // Print add-on line items covering individuals + bundle-filler garments.
    if (printAddOnQty > 0) {
      state.groupPrintLocations.forEach(function (loc) {
        const pp = state.printProducts[loc];
        if (!pp || !pp.variantId) return;
        items.push({
          id: pp.variantId,
          quantity: printAddOnQty,
          properties: { "Order reference": locationLabel(loc) }
        });
      });
    }

    if (!items.length) {
      if (errEl) {
        errEl.textContent = "No items to add — go back and check sizes / colour.";
        errEl.removeAttribute("hidden");
      }
      return;
    }

    const btn = $("#ow-group-add-to-cart");
    if (btn) { btn.disabled = true; btn.textContent = "Adding..."; }

    // Rush production fee for group orders. Quantity = ceil(fee / unitPrice)
    // so the cart shows a sensible number of line items (e.g. 9 × $25 for a
    // $225 fee) instead of 225 × $1. We round UP so the customer never gets
    // undercharged on small remainders.
    const rushOptGroup = (CONFIG.rushOptions || []).find(function (o) { return o.key === state.rushSelection; });
    if (rushOptGroup && rushOptGroup.surchargePct > 0 && state.rushFeeProduct && state.rushFeeProduct.variantId) {
      const rushResult = computeRushFee(rushOptGroup, computeDiscountedSubtotal("group"));
      if (rushResult.fee > 0) {
        const unit = Math.max(1, CONFIG.rushFeeUnitPrice || 1);
        const rushQty = Math.ceil(rushResult.fee / unit);
        items.push({
          id: state.rushFeeProduct.variantId,
          quantity: rushQty,
          properties: {
            "Production speed": rushOptGroup.label,
            "Surcharge": rushOptGroup.surchargePct + "% of order" +
              (rushResult.hitMin ? " (min $" + rushOptGroup.minFee + ")" :
               rushResult.hitMax ? " (capped at $" + rushOptGroup.maxFee + ")" : "")
          }
        });
      }
    }

    if (window.console && console.info) {
      console.info("[OrderWizard] GROUP cart add payload:", JSON.parse(JSON.stringify({ items: items })));
    }

    try {
      const r = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ items: items }),
        credentials: "same-origin"
      });
      if (!r.ok) throw new Error((await r.text()) || ("HTTP " + r.status));
      // Success — go to cart
      window.location.href = "/cart";
    } catch (e) {
      console.warn("[OrderWizard] group cart add failed", e);
      if (errEl) {
        errEl.textContent = "Add to cart failed: " + (e.message || e);
        errEl.removeAttribute("hidden");
      }
      if (btn) { btn.disabled = false; btn.textContent = "Add to cart"; }
    }
  }

  // ------------------------------------------------------------------------
  // Mockup preview (composites artwork onto product image)
  // ------------------------------------------------------------------------

  /**
   * Rewrite R2 URLs through the worker's /cdn proxy so the browser can
   * load them with CORS (needed to composite onto a canvas without
   * tainting). Non-R2 URLs pass through unchanged.
   */
  function toCorsSafeUrl(url) {
    if (!url) return "";
    try {
      const u = new URL(url, location.origin);
      if (/\.r2\.dev$/.test(u.hostname)) {
        return CONFIG.workerUrl.replace(/\/$/, "") + "/cdn/" + u.pathname.replace(/^\//, "");
      }
    } catch (_) {}
    return url;
  }

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = function () { resolve(img); };
      img.onerror = function (e) { reject(new Error("Image load failed: " + src)); };
      img.src = src;
    });
  }

  /**
   * Draw a schematic back-view tee outline filled with the customer's
   * selected garment colour. Used as the base for the back-view mockup
   * panel so it doesn't reuse the front product photo (which would be
   * confusing). Stylised on purpose so it reads as a representation.
   */
  function drawTeeBackBase(ctx, W, H, fillCss) {
    const sx = W / 100, sy = H / 110;
    ctx.fillStyle = fillCss || "#2a2a2a";
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(18 * sx,  8 * sy);
    ctx.lineTo(30 * sx,  2 * sy);
    ctx.lineTo(70 * sx,  2 * sy);
    ctx.lineTo(82 * sx,  8 * sy);
    ctx.lineTo(96 * sx, 26 * sy);
    ctx.lineTo(82 * sx, 36 * sy);
    ctx.lineTo(82 * sx, 105 * sy);
    ctx.lineTo(18 * sx, 105 * sy);
    ctx.lineTo(18 * sx, 36 * sy);
    ctx.lineTo( 4 * sx, 26 * sy);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  /**
   * Approximate print zone rectangles, expressed as fractions of the
   * canvas. These match a typical Shopify product photo where the
   * garment fills most of the frame, centred.
   */
  // Fallback ONLY — used when a product has no real measured zone config
  // (see fetchPrintZonesConfig below). Approximate, and flagged as such in
  // the UI; this is a stopgap for products nobody has photographed/measured
  // yet, never the source of truth for what actually gets printed.
  function printZone(loc, canvasW, canvasH) {
    switch (loc) {
      case "large_front": return { x: 0.34, y: 0.32, w: 0.32, h: 0.42, opacity: 1 };
      case "back_full":   return { x: 0.34, y: 0.22, w: 0.32, h: 0.48, opacity: 1 };
      case "front_left_chest": return { x: 0.56, y: 0.24, w: 0.10, h: 0.10, opacity: 1 };
      default: return null;
    }
  }

  // ------------------------------------------------------------------------
  // Real measured per-product print-zone config — the SAME file
  // (ft-print-zones.json) the standalone product-page mockup builder
  // (ft-mockup-builder.js) reads, fetched live so the two can never drift
  // out of sync. This is what actually drives placement/size for any
  // product that's been measured — the true print position and mm
  // dimensions the fulfilment side needs, not just a screen approximation.
  // ------------------------------------------------------------------------

  let _printZonesConfig = null;
  let _printZonesConfigPromise = null;

  function printZonesConfigUrl() {
    if (!SELF_SCRIPT_URL) return null;
    return SELF_SCRIPT_URL.replace(/ai-designer\.js.*$/, "ft-print-zones.json");
  }

  function fetchPrintZonesConfig() {
    if (_printZonesConfig) return Promise.resolve(_printZonesConfig);
    if (_printZonesConfigPromise) return _printZonesConfigPromise;
    const url = printZonesConfigUrl();
    if (!url) { _printZonesConfig = { products: {} }; return Promise.resolve(_printZonesConfig); }
    _printZonesConfigPromise = fetch(url, { credentials: "omit" })
      .then(function (r) { return r.ok ? r.json() : { products: {} }; })
      .then(function (data) {
        _printZonesConfig = (data && data.products) ? data : { products: {} };
        return _printZonesConfig;
      })
      .catch(function (e) {
        console.warn("[OrderWizard] print zones config fetch failed — falling back to approximate placement", e);
        _printZonesConfig = { products: {} };
        return _printZonesConfig;
      });
    return _printZonesConfigPromise;
  }

  // Maps this wizard's print-location keys to the shared config's side/zone
  // keys (the mockup builder uses slightly different zone names).
  const WIZARD_LOC_TO_CONFIG_ZONE = {
    front_left_chest: { side: "front", zone: "left_chest" },
    large_front:       { side: "front", zone: "full_front" },
    back_full:         { side: "back",  zone: "full_back" }
  };

  // Resolves the REAL measured zone for a product handle + location, or
  // null if that product hasn't been measured yet. Returns the fraction
  // rect (derived from the real pixel rect ÷ real canvas size — so it's
  // still usable by the same fraction-space drag/resize/DOM code), plus
  // the raw config so mm/DPI maths can reference the real print_mm values.
  function configuredZoneFor(productHandle, loc) {
    if (!_printZonesConfig || !productHandle) return null;
    const productCfg = _printZonesConfig.products && _printZonesConfig.products[productHandle];
    if (!productCfg) return null;
    const map = WIZARD_LOC_TO_CONFIG_ZONE[loc];
    if (!map) return null;
    const sideCfg = productCfg.sides && productCfg.sides[map.side];
    const zoneCfg = sideCfg && sideCfg.zones && sideCfg.zones[map.zone];
    if (!zoneCfg) return null;
    return {
      configured: true,
      productCfg: productCfg,
      sideCfg: sideCfg,
      zoneCfg: zoneCfg,
      rect: {
        x: zoneCfg.rect.x / productCfg.canvas.width,
        y: zoneCfg.rect.y / productCfg.canvas.height,
        w: zoneCfg.rect.width / productCfg.canvas.width,
        h: zoneCfg.rect.height / productCfg.canvas.height
      },
      align: zoneCfg.align || "center"
    };
  }

  function currentProductHandle(mode) {
    if (mode === "group") {
      const type = getActiveProductType(mode);
      const product = type && state.groupSelectedProducts ? state.groupSelectedProducts[type] : null;
      return product && product.handle;
    }
    return state.selectedProduct && state.selectedProduct.handle;
  }

  // Single entry point every placement/size/photo calculation goes
  // through — real measured zone when the product's been photographed and
  // measured, the approximate universal fallback otherwise.
  function zoneInfoFor(mode, loc) {
    const handle = currentProductHandle(mode);
    const configured = configuredZoneFor(handle, loc);
    if (configured) return configured;
    return { configured: false, rect: printZone(loc), zoneCfg: null, sideCfg: null, align: "center" };
  }

  // mm per unit of stage-fraction, in each axis — lets placement (which is
  // stored as 0-1 stage fractions, same space the drag/resize code already
  // works in) convert to the real print_mm/offset_mm the fulfilment side
  // needs, without carrying pixel values through the whole UI layer.
  function mmPerFraction(zoneInfo) {
    if (!zoneInfo.configured) return null;
    return {
      w: zoneInfo.zoneCfg.print_mm.width / zoneInfo.rect.w,
      h: zoneInfo.zoneCfg.print_mm.height / zoneInfo.rect.h
    };
  }

  function computePrintMm(zoneInfo, dispW, dispH) {
    const mpf = mmPerFraction(zoneInfo);
    if (!mpf) return null;
    return { w: Math.round(dispW * mpf.w), h: Math.round(dispH * mpf.h) };
  }

  function computeOffsetMm(zoneInfo, rect) {
    const mpf = mmPerFraction(zoneInfo);
    if (!mpf) return null;
    return {
      x: Math.round((rect.x - zoneInfo.rect.x) * mpf.w),
      y: Math.round((rect.y - zoneInfo.rect.y) * mpf.h)
    };
  }

  // Real placement spec (mirrors ft-mockup-builder.js's _ft_design JSON) —
  // this is what the fulfilment/renderer side needs to reproduce the exact
  // print the customer positioned, in mm relative to the zone's top-left.
  // Only includes locations on a product that's actually been measured;
  // never emits a fake number for an unconfigured product.
  function buildPrintSpecForProduct(mode, productHandle) {
    const ctx = mockupCtx(mode);
    const spec = {};
    ctx.locations.forEach(function (loc) {
      const zoneInfo = configuredZoneFor(productHandle, loc);
      const rect = ctx.placement[loc];
      if (!zoneInfo || !rect) return;
      spec[loc] = {
        zone_label: zoneInfo.zoneCfg.label || loc,
        print_mm: computePrintMm(zoneInfo, rect.w, rect.h),
        offset_mm: computeOffsetMm(zoneInfo, rect)
      };
    });
    return spec;
  }

  // ------------------------------------------------------------------------
  // Auto-crop to content — ported verbatim from ft-mockup-builder.js so a
  // design with unused padding around it gets trimmed to its true size
  // before placement maths run, same as the standalone product-page tool.
  // ------------------------------------------------------------------------

  function detectContentBBox(img) {
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return null;

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);

    let data;
    try {
      data = ctx.getImageData(0, 0, w, h).data;
    } catch (e) {
      return null; // tainted canvas — skip trimming rather than fail the upload
    }

    const step = Math.max(1, Math.floor(Math.min(w, h) / 900));

    let hasAlpha = false;
    for (let i = 3; i < data.length; i += 4 * 211) {
      if (data[i] < 250) { hasAlpha = true; break; }
    }

    let isBackground;
    if (hasAlpha) {
      isBackground = function (idx) { return data[idx + 3] < 10; };
    } else {
      const corners = [[0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]];
      let br = 0, bg = 0, bb = 0;
      corners.forEach(function (c) {
        const idx = (c[1] * w + c[0]) * 4;
        br += data[idx]; bg += data[idx + 1]; bb += data[idx + 2];
      });
      br /= 4; bg /= 4; bb /= 4;
      const threshold = 18;
      isBackground = function (idx) {
        const dr = data[idx] - br, dg = data[idx + 1] - bg, db = data[idx + 2] - bb;
        return Math.sqrt(dr * dr + dg * dg + db * db) < threshold;
      };
    }

    let minX = w, minY = h, maxX = -1, maxY = -1;
    for (let y = 0; y < h; y += step) {
      for (let x = 0; x < w; x += step) {
        const idx = (y * w + x) * 4;
        if (!isBackground(idx)) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null;

    const pad = Math.round(Math.min(w, h) * 0.01);
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(w - 1, maxX + pad);
    maxY = Math.min(h - 1, maxY + pad);

    const bboxW = maxX - minX + 1;
    const bboxH = maxY - minY + 1;

    if (bboxW * bboxH > w * h * 0.97) return null;

    return { x: minX, y: minY, width: bboxW, height: bboxH, sourceCanvas: canvas };
  }

  function cropToCanvas(bbox) {
    const out = document.createElement("canvas");
    out.width = bbox.width;
    out.height = bbox.height;
    const ctx = out.getContext("2d");
    ctx.drawImage(bbox.sourceCanvas, bbox.x, bbox.y, bbox.width, bbox.height, 0, 0, bbox.width, bbox.height);
    return out;
  }

  // ------------------------------------------------------------------------
  // Interactive mockup step — background/black removal, auto-crop, and
  // drag/resize placement, replacing the old text-only "designer will mock
  // it up in 24h" confirmation with something the customer can actually see
  // and adjust. Uses the real measured zone (zoneInfoFor) whenever the
  // product's been photographed/measured; falls back to an approximate
  // universal zone otherwise. No canvas library — a single draggable,
  // aspect-locked-resizable box clamped to a rect doesn't need one.
  // ------------------------------------------------------------------------

  const BG_WAND_TOLERANCE = 10;        // matches a Photoshop magic-wand tolerance of ~10
  const BLACK_KNOCKOUT_TOLERANCE = 10;

  function channelMaxDiff(r1, g1, b1, r2, g2, b2) {
    return Math.max(Math.abs(r1 - r2), Math.abs(g1 - g2), Math.abs(b1 - b2));
  }

  /**
   * "Magic wand" background removal + (on black garments, large prints
   * only) black knockout. Samples the four corners for the background
   * colour. Returns a new canvas, or null if nothing needed removing.
   */
  function removeBackground(img, knockOutBlack) {
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return null;

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);

    let imageData;
    try {
      imageData = ctx.getImageData(0, 0, w, h);
    } catch (e) {
      console.warn("[OrderWizard] background removal skipped (tainted canvas)", e);
      return null;
    }
    const data = imageData.data;

    const corners = [[0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]];
    let br = 0, bg = 0, bb = 0;
    corners.forEach(function (c) {
      const idx = (c[1] * w + c[0]) * 4;
      br += data[idx]; bg += data[idx + 1]; bb += data[idx + 2];
    });
    br /= 4; bg /= 4; bb /= 4;

    let changed = false;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      if (channelMaxDiff(r, g, b, br, bg, bb) <= BG_WAND_TOLERANCE) {
        data[i + 3] = 0;
        changed = true;
        continue;
      }
      if (knockOutBlack && channelMaxDiff(r, g, b, 0, 0, 0) <= BLACK_KNOCKOUT_TOLERANCE) {
        data[i + 3] = 0;
        changed = true;
      }
    }

    if (!changed) return null;
    ctx.putImageData(imageData, 0, 0);
    return canvas;
  }

  function isBlackColour(name) {
    return /(^|\s)black$/i.test((name || "").trim());
  }

  // Left chest is a small logo print — knocking out black there strips the
  // outlines/detail a small print relies on. Large prints on a black
  // garment don't print black ink at all (the shirt shows through), so
  // knockout only applies to the large zones.
  function isLargeZoneLoc(loc) {
    return loc !== "front_left_chest";
  }

  async function uploadProcessedArtwork(canvas) {
    const blob = await new Promise(function (resolve) { canvas.toBlob(resolve, "image/png"); });
    if (!blob) throw new Error("Canvas export failed");
    const formData = new FormData();
    formData.append("file", new File([blob], "cleaned-artwork.png", { type: "image/png" }));
    const r = await fetch(CONFIG.workerUrl + "/api/customer-upload", { method: "POST", body: formData });
    if (!r.ok) throw new Error((await r.text()) || ("HTTP " + r.status));
    const data = await r.json();
    if (!data || !data.url) throw new Error("Upload returned no URL");
    return data.url;
  }

  /**
   * Persisted mockup preview — mirrors ft-mockup-builder.js's
   * renderSnapshotSide()/snapshotPreviews(), which renders each side to a
   * canvas and uploads it as properties[_ft_preview_<sideId>] so the order
   * carries a real image, not just raw artwork + placement numbers. This
   * wizard's interactive stage (#ow-mockup-stage / #ow-group-mockup-stage)
   * is DOM-positioned <img> elements, not a canvas, so there's nothing to
   * snapshot directly — this recreates the same visual result on an actual
   * canvas: draw the garment image with the same "contain" fit the stage's
   * CSS uses, then the art layer at its placement fraction of that same
   * square, matching applyRectToDom()'s percentage-of-stage positioning.
   * Confirmed missing entirely on real orders #2401 and #2371 (2026-08-25)
   * — this wizard never had a preview-saving feature at all, unlike
   * ft-mockup-builder.js. Best-effort; never blocks add-to-cart.
   */
  async function renderMockupSnapshot(mode, loc) {
    const ctx = mockupCtx(mode);
    const rect = ctx.placement[loc];
    const artUrl = ctx.artwork[loc];
    if (!rect || !artUrl) return null;
    await fetchPrintZonesConfig();
    const garmentUrl = garmentPhotoFor(mode, loc);
    if (!garmentUrl) return null;
    let garmentImg, artImg;
    try {
      const loaded = await Promise.all([
        loadImage(toCorsSafeUrl(garmentUrl)),
        loadImage(toCorsSafeUrl(artUrl))
      ]);
      garmentImg = loaded[0];
      artImg = loaded[1];
    } catch (e) {
      console.warn("[OrderWizard] mockup snapshot image load failed for", loc, e);
      return null;
    }
    const SIZE = 1000; // square canvas — matches the stage's CSS aspect-ratio:1/1
    const canvas = document.createElement("canvas");
    canvas.width = SIZE; canvas.height = SIZE;
    const c2d = canvas.getContext("2d");
    c2d.fillStyle = "#ffffff";
    c2d.fillRect(0, 0, SIZE, SIZE);
    // object-fit: contain, matching .ow-mockup__garment's CSS
    const gw = garmentImg.naturalWidth || garmentImg.width || 1;
    const gh = garmentImg.naturalHeight || garmentImg.height || 1;
    const scale = Math.min(SIZE / gw, SIZE / gh);
    const dw = gw * scale, dh = gh * scale;
    c2d.drawImage(garmentImg, (SIZE - dw) / 2, (SIZE - dh) / 2, dw, dh);
    // art layer — positioned as a fraction of the stage, matching applyRectToDom()
    c2d.drawImage(artImg, rect.x * SIZE, rect.y * SIZE, rect.w * SIZE, rect.h * SIZE);
    return canvas;
  }

  async function uploadMockupPreview(canvas) {
    const blob = await new Promise(function (resolve) { canvas.toBlob(resolve, "image/png"); });
    if (!blob) throw new Error("Canvas export failed");
    const formData = new FormData();
    formData.append("file", new File([blob], "mockup-preview.png", { type: "image/png" }));
    const r = await fetch(CONFIG.workerUrl + "/api/customer-upload", { method: "POST", body: formData });
    if (!r.ok) throw new Error((await r.text()) || ("HTTP " + r.status));
    const data = await r.json();
    if (!data || !data.url) throw new Error("Upload returned no URL");
    return data.url;
  }

  // ----- mode-aware helpers (solo vs group share almost everything) -----

  function mockupIds(mode) {
    return mode === "group" ? {
      stage: "ow-group-mockup-stage", garment: "ow-group-mockup-garment",
      artlayer: "ow-group-mockup-artlayer", art: "ow-group-mockup-art",
      handle: "ow-group-mockup-handle", loading: "ow-group-mockup-loading",
      productTabs: "ow-group-mockup-product-tabs",
      reset: "ow-group-mockup-reset", sizeChip: "ow-group-mockup-size-chip",
      hint: "ow-group-mockup-hint", title: "ow-group-mockup-title",
      stepCount: "ow-group-mockup-step-count"
    } : {
      stage: "ow-mockup-stage", garment: "ow-mockup-garment",
      artlayer: "ow-mockup-artlayer", art: "ow-mockup-art",
      handle: "ow-mockup-handle", loading: "ow-mockup-loading",
      productTabs: null,
      reset: "ow-mockup-reset", sizeChip: "ow-mockup-size-chip",
      hint: "ow-mockup-hint", title: "ow-mockup-title",
      stepCount: "ow-mockup-step-count"
    };
  }

  function mockupCtx(mode) {
    return mode === "group" ? {
      mode: "group",
      locations: state.groupPrintLocations.filter(function (l) { return state.groupArtworkSources[l] !== "later"; }),
      artwork: state.groupArtwork,
      placement: state.groupPrintPlacement,
      bgRemoved: state.groupBgRemoved,
      original: state.groupArtworkOriginal,
      naturalSize: state.groupArtworkNaturalSize,
      colour: state.groupColour,
      productTypes: state.groupSelectedProductTypes,
      products: state.groupSelectedProducts
    } : {
      mode: "solo",
      locations: state.selectedPrintLocations.filter(function (l) { return state.artworkSources[l] !== "later"; }),
      artwork: state.printArtwork,
      placement: state.printPlacement,
      bgRemoved: state.printBgRemoved,
      original: state.printArtworkOriginal,
      naturalSize: state.printArtworkNaturalSize,
      colour: state.selectedColour,
      productTypes: null,
      products: null
    };
  }

  function getActiveLoc(mode) {
    return mode === "group" ? state.groupMockupActiveLocation : state.mockupActiveLocation;
  }
  function setActiveLoc(mode, loc) {
    if (mode === "group") state.groupMockupActiveLocation = loc; else state.mockupActiveLocation = loc;
  }
  function getActiveProductType(mode) {
    return mode === "group" ? state.groupMockupActiveProductType : null;
  }
  function setActiveProductType(mode, t) {
    if (mode === "group") state.groupMockupActiveProductType = t;
  }

  function mockupClamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

  // Centred contain-fit at 85% of the zone (leaves headroom to resize
  // larger). Stage is enforced square via CSS, so width/height fractions
  // compare directly without a pixel conversion. Honours the real zone's
  // align rule (top-center vs center) when the product's been measured.
  function defaultPlacementFor(zoneInfo, naturalW, naturalH) {
    const zone = zoneInfo.rect;
    const ar = naturalW / naturalH;
    const zoneAr = zone.w / zone.h;
    let fitW, fitH;
    if (ar >= zoneAr) { fitW = zone.w * 0.85; fitH = fitW / ar; }
    else { fitH = zone.h * 0.85; fitW = fitH * ar; }
    const x = zone.x + (zone.w - fitW) / 2;
    const y = zoneInfo.align === "top-center" ? zone.y : zone.y + (zone.h - fitH) / 2;
    return { x: x, y: y, w: fitW, h: fitH };
  }

  /**
   * Processes one location's artwork: loads it CORS-safely, runs the
   * magic-wand background/black removal, auto-crops to the real content
   * bounding box (same as the standalone product-page tool), and if
   * anything changed, re-uploads the result and overwrites artwork[loc] in
   * place — this is the one thing that keeps addToCart()/groupAddToCart()
   * untouched, they just read whatever URL ends up here. Never blocks
   * progress on failure.
   */
  async function processLocationArtwork(mode, loc) {
    const ctx = mockupCtx(mode);
    const sourceUrl = ctx.artwork[loc];
    if (!sourceUrl) return;
    try {
      await fetchPrintZonesConfig();
      const img = await loadImage(toCorsSafeUrl(sourceUrl));
      const knockOutBlack = isBlackColour(ctx.colour) && isLargeZoneLoc(loc);
      const stripped = removeBackground(img, knockOutBlack);
      const bgRemoved = !!stripped;
      const bboxSource = stripped || img;

      const bbox = detectContentBBox(bboxSource);
      const cropped = bbox ? cropToCanvas(bbox) : null;
      const finalCanvas = cropped || (bgRemoved ? stripped : null);

      let naturalW = img.naturalWidth, naturalH = img.naturalHeight;
      if (finalCanvas) {
        const uploadedUrl = await uploadProcessedArtwork(finalCanvas);
        ctx.original[loc] = sourceUrl;
        ctx.artwork[loc] = uploadedUrl;
        naturalW = finalCanvas.width;
        naturalH = finalCanvas.height;
      }
      ctx.bgRemoved[loc] = bgRemoved;
      ctx.naturalSize[loc] = { w: naturalW, h: naturalH };

      if (!ctx.placement[loc]) {
        const zoneInfo = zoneInfoFor(mode, loc);
        ctx.placement[loc] = defaultPlacementFor(zoneInfo, naturalW, naturalH);
      }
    } catch (e) {
      console.warn("[OrderWizard] artwork clean-up skipped for", loc, e);
    }
  }

  function garmentPhotoFor(mode, loc) {
    const ctx = mockupCtx(mode);
    const handle = currentProductHandle(mode);
    const zoneInfo = configuredZoneFor(handle, loc);

    // Real measured template photo for this exact side — the same asset
    // the standalone builder was calibrated against, not a generic Shopify
    // product photo (which may be angled, cropped, or lifestyle-shot and
    // wouldn't line up with the measured zone rect at all).
    if (zoneInfo && zoneInfo.sideCfg && zoneInfo.sideCfg.templates) {
      const templates = zoneInfo.sideCfg.templates;
      const lower = (ctx.colour || "").toLowerCase();
      const key = Object.keys(templates).filter(function (k) { return k !== "_default"; })
        .find(function (k) { return lower.indexOf(k) !== -1; });
      let url = templates[key] || templates._default || "";
      if (url.indexOf("//") === 0) url = "https:" + url; // protocol-relative CDN URL
      return url;
    }

    // Fallback for unmeasured products — generic product photo, or (for
    // back_full specifically) a schematic tee-back silhouette in the
    // customer's garment colour, since back-view photos aren't reliably
    // available across the catalogue.
    let product;
    if (mode === "group") {
      const type = getActiveProductType(mode) || (ctx.productTypes && ctx.productTypes[0]);
      product = type && ctx.products ? ctx.products[type] : null;
    } else {
      product = state.selectedProduct;
    }
    const photo = product && product.images && product.images[0] ? product.images[0].src : "";
    if (loc === "back_full") {
      const canvas = document.createElement("canvas");
      canvas.width = 600; canvas.height = 660;
      const c2d = canvas.getContext("2d");
      c2d.fillStyle = "#ffffff";
      c2d.fillRect(0, 0, 600, 660);
      drawTeeBackBase(c2d, 600, 660, colourToCss(ctx.colour) || "#2a2a2a");
      return canvas.toDataURL("image/png");
    }
    return photo;
  }

  function applyRectToDom(mode, rect) {
    const ids = mockupIds(mode);
    const layer = $("#" + ids.artlayer);
    if (!layer) return;
    layer.style.left = (rect.x * 100) + "%";
    layer.style.top = (rect.y * 100) + "%";
    layer.style.width = (rect.w * 100) + "%";
    layer.style.height = (rect.h * 100) + "%";
  }

  function updateMockupSizeChip(mode, loc, rect) {
    const ids = mockupIds(mode);
    const chip = $("#" + ids.sizeChip);
    if (!chip) return;
    const zoneInfo = zoneInfoFor(mode, loc);
    if (zoneInfo.configured) {
      const mm = computePrintMm(zoneInfo, rect.w, rect.h);
      chip.hidden = false;
      chip.className = "ow-mockup__size-chip";
      chip.textContent = mm ? ("Final print size: " + mm.w + " × " + mm.h + "mm") : "";
    } else {
      // No measured template for this product yet — say so honestly rather
      // than showing a number that isn't real.
      chip.hidden = false;
      chip.className = "ow-mockup__size-chip ow-mockup__size-chip--approx";
      chip.textContent = "Placement shown is approximate for this product — we'll confirm the exact size and position before printing.";
    }
  }

  function showMockupLocation(mode, loc) {
    const ids = mockupIds(mode);
    const ctx = mockupCtx(mode);
    setActiveLoc(mode, loc);

    const garmentEl = $("#" + ids.garment);
    const artEl = $("#" + ids.art);
    const resetBtn = $("#" + ids.reset);
    const layerEl = $("#" + ids.artlayer);
    const titleEl = $("#" + ids.title);
    const stepCountEl = $("#" + ids.stepCount);
    if (!garmentEl || !artEl) return;

    garmentEl.src = garmentPhotoFor(mode, loc);
    artEl.src = toCorsSafeUrl(ctx.artwork[loc]);

    const zoneInfo = zoneInfoFor(mode, loc);
    const rect = ctx.placement[loc] || zoneInfo.rect;
    applyRectToDom(mode, rect);
    updateMockupSizeChip(mode, loc, rect);

    // Show the resize handle right away on a fresh placement/location
    // switch — deselected only once the customer clicks off it.
    if (layerEl) layerEl.classList.add("is-selected");

    if (resetBtn) resetBtn.hidden = !ctx.bgRemoved[loc];

    // Each print location is its own step in the flow (Back/Continue moves
    // between them) rather than a tab the customer clicks to switch —
    // title + count reflect exactly where they are.
    const idx = ctx.locations.indexOf(loc);
    if (titleEl) titleEl.textContent = "Position your " + locationLabel(loc) + " design";
    if (stepCountEl) {
      stepCountEl.textContent = ctx.locations.length > 1
        ? "Location " + (idx + 1) + " of " + ctx.locations.length
        : "";
    }

    bindMockupInteraction(mode);
  }

  // Garment-type tabs only (group flow, when multiple product types are
  // selected) — this switches which product's photo is shown, unrelated to
  // print-location sequencing, which is now Back/Continue driven instead
  // of a tab a customer clicks.
  function buildMockupProductTabs(mode) {
    const ids = mockupIds(mode);
    const ctx = mockupCtx(mode);

    if (mode === "group" && ids.productTabs) {
      const productTabsWrap = $("#" + ids.productTabs);
      if (productTabsWrap) {
        const types = ctx.productTypes || [];
        const activeType = getActiveProductType(mode);
        if (types.length > 1) {
          productTabsWrap.innerHTML = types.map(function (t) {
            const product = ctx.products && ctx.products[t];
            const label = (product && product.title) || t;
            const active = t === activeType ? " is-active" : "";
            return '<button type="button" class="ow-mockup__product-tab' + active + '" data-product-type="' + t + '">' + label + '</button>';
          }).join("");
          productTabsWrap.hidden = false;
        } else {
          productTabsWrap.innerHTML = "";
          productTabsWrap.hidden = true;
        }
      }
    }
  }

  async function renderMockupStep(mode) {
    const ctx = mockupCtx(mode);
    const ids = mockupIds(mode);
    const loadingEl = $("#" + ids.loading);
    const loadingTextEl = loadingEl && loadingEl.querySelector(".ow-mockup__loading-text");
    if (!ctx.locations.length) return;

    if (loadingEl) loadingEl.hidden = false;
    for (let i = 0; i < ctx.locations.length; i++) {
      if (loadingTextEl) {
        loadingTextEl.textContent = ctx.locations.length > 1
          ? "Cleaning up your artwork (" + (i + 1) + " of " + ctx.locations.length + ")…"
          : "Cleaning up your artwork…";
      }
      await processLocationArtwork(mode, ctx.locations[i]);
    }
    if (loadingEl) loadingEl.hidden = true;

    if (mode === "group" && ctx.productTypes && ctx.productTypes.length) {
      if (!getActiveProductType(mode) || ctx.productTypes.indexOf(getActiveProductType(mode)) < 0) {
        setActiveProductType(mode, ctx.productTypes[0]);
      }
    }
    const active = getActiveLoc(mode) && ctx.locations.indexOf(getActiveLoc(mode)) >= 0
      ? getActiveLoc(mode)
      : ctx.locations[0];
    setActiveLoc(mode, active);

    buildMockupProductTabs(mode);
    showMockupLocation(mode, active);
  }

  async function resetMockupArtwork(mode) {
    const ctx = mockupCtx(mode);
    const loc = getActiveLoc(mode);
    if (!loc || !ctx.original[loc]) return;
    ctx.artwork[loc] = ctx.original[loc];
    ctx.bgRemoved[loc] = false;
    delete ctx.placement[loc];
    const img = await loadImage(toCorsSafeUrl(ctx.artwork[loc])).catch(function () { return null; });
    if (img) {
      ctx.naturalSize[loc] = { w: img.naturalWidth, h: img.naturalHeight };
      ctx.placement[loc] = defaultPlacementFor(zoneInfoFor(mode, loc), img.naturalWidth, img.naturalHeight);
    }
    showMockupLocation(mode, loc);
  }

  // "Max size" — jumps straight to the full contain-fit (same maths as
  // defaultPlacementFor, just without the 0.85 headroom ratio), honouring
  // the zone's own alignment rule. Customer can still drag/resize smaller
  // afterwards if they want.
  function maxOutMockupPlacement(mode) {
    const ctx = mockupCtx(mode);
    const loc = getActiveLoc(mode);
    const naturalSize = loc && ctx.naturalSize[loc];
    if (!loc || !naturalSize) return;

    const zoneInfo = zoneInfoFor(mode, loc);
    const zone = zoneInfo.rect;
    const ar = naturalSize.w / naturalSize.h;
    const zoneAr = zone.w / zone.h;
    let fitW, fitH;
    if (ar >= zoneAr) { fitW = zone.w; fitH = fitW / ar; }
    else { fitH = zone.h; fitW = fitH * ar; }
    const x = zone.x + (zone.w - fitW) / 2;
    const y = zoneInfo.align === "top-center" ? zone.y : zone.y + (zone.h - fitH) / 2;
    const rect = { x: x, y: y, w: fitW, h: fitH };

    ctx.placement[loc] = rect;
    applyRectToDom(mode, rect);
    updateMockupSizeChip(mode, loc, rect);

    const layerEl = $("#" + mockupIds(mode).artlayer);
    if (layerEl) layerEl.classList.add("is-selected");
  }

  // Each selected print location is its own step in the flow — Continue
  // moves forward through them one at a time, Back moves back through
  // them, same as any other multi-step part of the wizard. Only once
  // you're past the last (Continue) or before the first (Back) does it
  // hand off to the surrounding steps (rush / artwork).
  //
  // Returns true if it moved to another location within this same step,
  // false if the caller should proceed to the next/previous OUTER step
  // instead (already at the last/first location).
  function stepMockupLocation(mode, delta) {
    const ctx = mockupCtx(mode);
    const current = getActiveLoc(mode);
    const idx = ctx.locations.indexOf(current);
    const nextIdx = idx + delta;
    if (nextIdx < 0 || nextIdx >= ctx.locations.length) return false;
    showMockupLocation(mode, ctx.locations[nextIdx]);
    return true;
  }

  // Drag/resize + tab-click delegation, bound once per mode (idempotent —
  // the DOM these bind to is built once at boot by injectDom()).
  const _mockupBound = {};

  function bindMockupInteraction(mode) {
    if (_mockupBound[mode]) return;
    _mockupBound[mode] = true;

    const ids = mockupIds(mode);
    const stageEl = $("#" + ids.stage);
    const layerEl = $("#" + ids.artlayer);
    const handleEl = $("#" + ids.handle);
    if (!stageEl || !layerEl || !handleEl) return;

    // Clicking off the design (the stage background/garment photo, not the
    // design itself) deselects it — hides the outline + resize handle.
    // Clicking the design re-selects it. Stopping propagation on the
    // layer/handle's own click is what makes "off" mean "outside", not
    // just "anywhere in the stage" (clicks on the layer would otherwise
    // bubble up and immediately re-trigger the stage's deselect handler).
    stageEl.addEventListener("click", function () {
      layerEl.classList.remove("is-selected");
    });
    layerEl.addEventListener("click", function (e) {
      e.stopPropagation();
      layerEl.classList.add("is-selected");
    });
    handleEl.addEventListener("click", function (e) {
      e.stopPropagation();
    });

    let dragState = null;
    let resizeState = null;

    function currentRect() {
      const ctx = mockupCtx(mode);
      const loc = getActiveLoc(mode);
      return ctx.placement[loc] || zoneInfoFor(mode, loc).rect;
    }
    function commitRect(rect) {
      const ctx = mockupCtx(mode);
      const loc = getActiveLoc(mode);
      ctx.placement[loc] = rect;
    }

    layerEl.addEventListener("pointerdown", function (e) {
      if (e.target === handleEl) return;
      e.preventDefault();
      const stageRect = stageEl.getBoundingClientRect();
      const rect = currentRect();
      dragState = {
        pointerId: e.pointerId,
        startClientX: e.clientX, startClientY: e.clientY,
        stageW: stageRect.width, stageH: stageRect.height,
        origX: rect.x, origY: rect.y, w: rect.w, h: rect.h
      };
      layerEl.setPointerCapture(e.pointerId);
    });
    layerEl.addEventListener("pointermove", function (e) {
      if (!dragState || e.pointerId !== dragState.pointerId) return;
      const loc = getActiveLoc(mode);
      const zone = zoneInfoFor(mode, loc).rect;
      const dx = (e.clientX - dragState.startClientX) / dragState.stageW;
      const dy = (e.clientY - dragState.startClientY) / dragState.stageH;
      const rect = {
        x: mockupClamp(dragState.origX + dx, zone.x, zone.x + zone.w - dragState.w),
        y: mockupClamp(dragState.origY + dy, zone.y, zone.y + zone.h - dragState.h),
        w: dragState.w, h: dragState.h
      };
      applyRectToDom(mode, rect);
      commitRect(rect);
    });
    function endDrag(e) {
      if (!dragState || e.pointerId !== dragState.pointerId) return;
      dragState = null;
    }
    layerEl.addEventListener("pointerup", endDrag);
    layerEl.addEventListener("pointercancel", endDrag);

    handleEl.addEventListener("pointerdown", function (e) {
      e.preventDefault();
      e.stopPropagation();
      const stageRect = stageEl.getBoundingClientRect();
      const rect = currentRect();
      resizeState = {
        pointerId: e.pointerId,
        startClientX: e.clientX,
        stageW: stageRect.width,
        origX: rect.x, origY: rect.y, origW: rect.w, origH: rect.h,
        ar: rect.w / rect.h
      };
      handleEl.setPointerCapture(e.pointerId);
    });
    handleEl.addEventListener("pointermove", function (e) {
      if (!resizeState || e.pointerId !== resizeState.pointerId) return;
      const loc = getActiveLoc(mode);
      const zone = zoneInfoFor(mode, loc).rect;
      const dx = (e.clientX - resizeState.startClientX) / resizeState.stageW;
      const ar = resizeState.ar;
      const maxW = (zone.x + zone.w) - resizeState.origX;
      const maxH = (zone.y + zone.h) - resizeState.origY;
      const newW = mockupClamp(resizeState.origW + dx, 0.02, Math.min(maxW, maxH * ar));
      const newH = newW / ar;
      const minDim = Math.min(zone.w, zone.h) * 0.15;
      if (newW < minDim || newH < minDim) return;
      const rect = { x: resizeState.origX, y: resizeState.origY, w: newW, h: newH };
      applyRectToDom(mode, rect);
      commitRect(rect);
      updateMockupSizeChip(mode, loc, rect);
    });
    function endResize(e) {
      if (!resizeState || e.pointerId !== resizeState.pointerId) return;
      resizeState = null;
    }
    handleEl.addEventListener("pointerup", endResize);
    handleEl.addEventListener("pointercancel", endResize);

    if (mode === "group" && ids.productTabs) {
      const productTabsWrap = $("#" + ids.productTabs);
      if (productTabsWrap) {
        productTabsWrap.addEventListener("click", function (e) {
          const btn = e.target.closest(".ow-mockup__product-tab");
          if (!btn) return;
          setActiveProductType(mode, btn.getAttribute("data-product-type"));
          productTabsWrap.querySelectorAll(".ow-mockup__product-tab").forEach(function (t) {
            t.classList.toggle("is-active", t === btn);
          });
          showMockupLocation(mode, getActiveLoc(mode));
        });
      }
    }
  }

  // ------------------------------------------------------------------------
  // Cart add
  // ------------------------------------------------------------------------

  async function addToCart() {
    const items = [];
    const product = state.selectedProduct;
    const sel = state.bundleSelection;
    const usingBundle = sel && sel.key !== "individuals";

    if (!product || !state.selectedVariantId) {
      state.cartError = "Pick a size first.";
      renderConfirmSummary();
      return;
    }

    // Build the main product line item with line-item properties matching
    // what the existing print-location-selector sets at order time:
    //   _Large Front artwork URL / _Back artwork URL / _Front artwork URL
    //   T-Shirt Print Location  (human-readable summary)
    // Uses state.printArtwork[location] (per-location artwork picked by
    // the customer) — defaults to state.designUrl but can be a custom
    // upload or a saved design.
    const props = {};
    const locLabels = state.selectedPrintLocations.map(locationLabel);

    if (state.reorderMode) {
      // Reorder path — no new artwork/mockup, staff match this against the
      // named past order manually. Still sets "Front/Large Front/Back
      // artwork" per selected location (not a real artwork label, just a
      // reorder note) — the cart-page reconciler (tidyRushFeeInCart's
      // print/shirt matcher, below in this file) uses THESE EXACT
      // properties as its only signal that a shirt claims a given print;
      // leaving them blank made every reorder shirt look print-less,
      // firing a false "Cart needs review" warning on real orders.
      const reorderNote = "Reorder — see details above";
      if (state.selectedPrintLocations.indexOf("front_left_chest") >= 0) props["Front artwork"] = reorderNote;
      if (state.selectedPrintLocations.indexOf("large_front") >= 0) props["Large Front artwork"] = reorderNote;
      if (state.selectedPrintLocations.indexOf("back_full") >= 0) props["Back artwork"] = reorderNote;
      props["Print Location"] = locLabels.join(" + ");
      props["T-Shirt Print Location"] = locLabels.join(" + ");
      props["Reorder — Previous order #"] = state.reorderOrderNumber;
      props["Reorder — Details"] = state.reorderDetails;
    } else {
      function artworkLabel(loc) {
        // Prefer the descriptive label (filename or prompt), fall back to a
        // generic descriptor based on source. Always populated so the
        // visible property in cart/order admin has useful info.
        const lbl = state.artworkLabels[loc];
        if (lbl) return lbl;
        return state.artworkSources[loc] === "upload" ? "Uploaded file" :
               state.artworkSources[loc] === "saved"  ? "Saved AI design" :
               "AI design attached";
      }
      if (state.selectedPrintLocations.indexOf("large_front") >= 0) {
        const url = state.artworkSources.large_front === "later" ? "" : (state.printArtwork.large_front || state.designUrl || "");
        props["_Large Front artwork URL"] = url;
        props["Large Front artwork"] = artworkLabel("large_front");
        if (url) props["Large Front print file"] = url; // visible URL, clear name
      }
      if (state.selectedPrintLocations.indexOf("back_full") >= 0) {
        const url = state.artworkSources.back_full === "later" ? "" : (state.printArtwork.back_full || state.designUrl || "");
        props["_Back artwork URL"] = url;
        props["Back artwork"] = artworkLabel("back_full");
        if (url) props["Back print file"] = url;
      }
      if (state.selectedPrintLocations.indexOf("front_left_chest") >= 0) {
        const url = state.artworkSources.front_left_chest === "later" ? "" : (state.printArtwork.front_left_chest || state.designUrl || "");
        props["_Front artwork URL"] = url;
        props["Front artwork"] = artworkLabel("front_left_chest");
        if (url) props["Front Left Chest print file"] = url;
      }
      props["Print Location"] = locLabels.join(" + ");
      props["T-Shirt Print Location"] = locLabels.join(" + "); // backward-compat for existing fulfilment templates

      for (const loc of state.selectedPrintLocations) {
        if (state.artworkSources[loc] === "later") continue; // nothing to snapshot yet
        try {
          const canvas = await renderMockupSnapshot("solo", loc);
          if (canvas) props["_ft_preview_" + loc] = await uploadMockupPreview(canvas);
        } catch (e) {
          console.warn("[OrderWizard] mockup preview snapshot failed for", loc, e);
        }
      }

      // Real print_mm/offset_mm per location — this is what the fulfilment
      // side needs to reproduce the exact size/position the customer set in
      // the mockup step, not just an artwork URL with no placement info.
      const printSpec = buildPrintSpecForProduct("solo", product.handle);
      if (Object.keys(printSpec).length) {
        props["_Print Spec"] = JSON.stringify(printSpec);
      }
    }

    const qty = Math.max(1, state.selectedQuantity || 1);

    if (usingBundle && sel.bundles && sel.bundles.length > 0) {
      // BUNDLE PATH — push one line item per bundle in the option (supports
      // stacked options like "20-pack + 10-pack"), then any filler individuals
      // + their print add-ons.
      //
      // Solo is always one colour + one size, so every bundle line shares
      // the same Size / Colour / Size breakdown.
      const totalBundleGarments = sel.bundles.reduce(function (s, b) { return s + b.qty; }, 0);
      let bundleLookupFailed = false;
      sel.bundles.forEach(function (bRule, idx) {
        const bp = state.bundleProducts[bRule.handle];
        if (!bp || !bp.variantId) {
          bundleLookupFailed = true;
          return;
        }
        const bundleProps = Object.assign({}, props, {
          "Bundle": bp.title,
          "Colour": state.selectedColour || "",
          "Size": state.selectedSize || "",
          "Size breakdown": (state.selectedSize || "?") + ":" + bRule.qty,
          "Total garments (this bundle)": String(bRule.qty),
          "Total garments (full order)": String(totalBundleGarments + (sel.fillerQty || 0))
        });
        if (sel.bundles.length > 1) {
          bundleProps["Bundle group"] = (idx + 1) + " of " + sel.bundles.length;
        }
        items.push({
          id: bp.variantId,
          quantity: 1,
          properties: bundleProps
        });
      });
      if (bundleLookupFailed) {
        state.cartError = "One of the bundle products couldn't be found — try selecting individuals instead.";
        renderConfirmSummary();
        return;
      }

      // Filler individuals (only when customer picked "bundle + N extras").
      // Filler variant has size + colour baked into the variantId itself, but
      // we still attach the design properties so the print team sees the same
      // artwork info on every line.
      const fillerQty = sel.fillerQty || 0;
      if (fillerQty > 0) {
        items.push({
          id: state.selectedVariantId,
          quantity: fillerQty,
          properties: props
        });
        // Print add-ons × filler count
        state.selectedPrintLocations.forEach(function (k) {
          const pp = state.printProducts[k];
          if (!pp || !pp.variantId) return;
          const ref = k === "large_front" ? "Large Front Print" :
                      k === "back_full"   ? "Back Full Size" :
                      k === "front_left_chest" ? "Front Left Chest" : k;
          items.push({
            id: pp.variantId,
            quantity: fillerQty,
            properties: { "Order reference": ref }
          });
        });
      }
    } else {
      // INDIVIDUALS PATH (default) — original behaviour
      items.push({
        id: state.selectedVariantId,
        quantity: qty,
        properties: props
      });

      // Print add-on line items — each adds its own product to cart with an
      // Order reference back to the parent location. Quantity matches the
      // garment quantity so 5 tees + Large Front = 5 Large Front Print line
      // items (matches how the existing PDP flow stages the cart).
      state.selectedPrintLocations.forEach(function (k) {
        const pp = state.printProducts[k];
        if (!pp || !pp.variantId) return; // missing print product — skip silently
        const ref = k === "large_front" ? "Large Front Print" :
                    k === "back_full"   ? "Back Full Size" :
                    k === "front_left_chest" ? "Front Left Chest" : k;
        items.push({
          id: pp.variantId,
          quantity: qty,
          properties: { "Order reference": ref }
        });
      });
    }

    state.cartError = null;
    renderConfirmSummary();
    const btn = $("#ow-add-to-cart");
    if (btn) { btn.disabled = true; btn.textContent = "Adding..."; }

    // Rush production fee line item. Quantity = ceil(fee / unitPrice) so a
    // $225 fee at a $25 unit price becomes 9 × $25 in the cart instead of
    // 225 × $1. Always rounds UP so we never undercharge the customer.
    const rushOptSolo = (CONFIG.rushOptions || []).find(function (o) { return o.key === state.rushSelection; });
    if (rushOptSolo && rushOptSolo.surchargePct > 0 && state.rushFeeProduct && state.rushFeeProduct.variantId) {
      const rushResult = computeRushFee(rushOptSolo, computeDiscountedSubtotal("solo"));
      if (rushResult.fee > 0) {
        const unit = Math.max(1, CONFIG.rushFeeUnitPrice || 1);
        const rushQty = Math.ceil(rushResult.fee / unit);
        items.push({
          id: state.rushFeeProduct.variantId,
          quantity: rushQty,
          properties: {
            "Production speed": rushOptSolo.label,
            "Surcharge": rushOptSolo.surchargePct + "% of order" +
              (rushResult.hitMin ? " (min $" + rushOptSolo.minFee + ")" :
               rushResult.hitMax ? " (capped at $" + rushOptSolo.maxFee + ")" : "")
          }
        });
      }
    }

    if (window.console && console.info) {
      console.info("[OrderWizard] cart add payload:", JSON.parse(JSON.stringify({ items: items })));
    }

    try {
      const r = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ items: items }),
        credentials: "same-origin"
      });
      if (!r.ok) {
        const errText = await r.text();
        throw new Error(errText || ("HTTP " + r.status));
      }
      // Success — show the upsell / continue step instead of jumping to cart
      const titleEl = $("#ow-success-title");
      if (titleEl) {
        const qtyTxt = qty > 1 ? " (× " + qty + ")" : "";
        titleEl.textContent = "Added to cart" + qtyTxt + " ✓";
      }
      pushChat("Sweet — added to cart. Want this design on something else, or head to checkout?", "ai");
      if (btn) { btn.disabled = false; btn.textContent = "Add to cart"; }
      window.ftTrack("ai_wizard_add_to_cart", { qty: qty, flow: "solo" });
      showStep("solo-success");
    } catch (e) {
      console.warn("[OrderWizard] cart add failed", e);
      state.cartError = "Add to cart failed. " + (e.message || e);
      renderConfirmSummary();
      if (btn) { btn.disabled = false; btn.textContent = "Add to cart"; }
    }
  }

  function $(sel) { return document.querySelector(sel); }

  /**
   * Visually highlight a missing/required element with an orange pulse and
   * scroll it into view. Used to draw the customer's eye to what they need
   * to fill in when they hit Continue without completing a step.
   * Auto-clears after a few seconds so the highlight doesn't linger.
   */
  function markMissing(el) {
    if (!el) return;
    el.classList.add("ow-missing");
    try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
    setTimeout(function () { if (el) el.classList.remove("ow-missing"); }, 2500);
  }

  /**
   * Show an inline step-level validation error inside the step that owns
   * the given error element. Pulses + scrolls so the message can't be
   * missed.
   */
  function showStepError(errEl, message) {
    if (!errEl) return;
    errEl.textContent = message;
    errEl.removeAttribute("hidden");
    try { errEl.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
  }

  function clearStepError(errEl) {
    if (!errEl) return;
    errEl.setAttribute("hidden", "");
    errEl.textContent = "";
  }
  // ============= Chat with typing animation =============
  // AI messages queue so each one gets its own typing-indicator pause
  // before appearing. Steps shown via showStep() are deferred until the
  // queue drains — keeps buttons from popping in while the bot is still
  // "speaking". User messages bypass the queue so clicks feel instant.
  const _aiQueue = [];
  let _aiActive = false;
  let _pendingStep = null;

  // ===== Audit fixes 2026-09-06 =====================================
  // (a) "Create a design with AI" round trip: the artwork step stashes the
  //     order so far in sessionStorage, the customer makes a design on
  //     /pages/design-with-ai, and "Put it on a tee" → open() restores it.
  // (b) A running estimated total shown on every step (the confirm-step
  //     summary was the only price the customer ever saw).
  const RESUME_KEY = "ow_resume";
  const RESUME_TTL_MS = 6 * 60 * 60 * 1000;
  const RESUME_FIELDS = ["audience", "selectedCategory", "selectedProduct", "selectedColour",
    "selectedSize", "selectedVariantId", "selectedQuantity", "selectedPrintLocations",
    "rushSelection", "bundleSelection", "groupAdults", "groupKids", "groupSelectedProductTypes",
    "groupSelectedProducts", "groupSizes", "groupColour", "groupPrintLocations"];

  function saveResumeSnapshot() {
    try {
      const snap = { ts: Date.now(), step: state.step };
      RESUME_FIELDS.forEach(function (k) { if (state[k] !== undefined) snap[k] = state[k]; });
      sessionStorage.setItem(RESUME_KEY, JSON.stringify(snap));
    } catch (_) {}
  }
  function readResumeSnapshot() {
    try {
      const raw = sessionStorage.getItem(RESUME_KEY);
      if (!raw) return null;
      const snap = JSON.parse(raw);
      if (!snap || !snap.ts || Date.now() - snap.ts > RESUME_TTL_MS) { sessionStorage.removeItem(RESUME_KEY); return null; }
      return snap;
    } catch (_) { return null; }
  }
  function clearResumeSnapshot() { try { sessionStorage.removeItem(RESUME_KEY); } catch (_) {} }

  function computeSoloEstimate() {
    const product = state.selectedProduct;
    if (!product || !product.variants || !product.variants[0]) return null;
    const qty = Math.max(1, state.selectedQuantity || 1);
    const sel = state.bundleSelection;
    if (sel && sel.key !== "individuals" && typeof sel.total === "number") {
      return { total: sel.total, qty: qty, pct: 0, bundle: true };
    }
    const productPrice = parseFloat(product.variants[0].price) || 0;
    let printsPerUnit = 0;
    (state.selectedPrintLocations || []).forEach(function (k) { printsPerUnit += CONFIG.printPrices[k] || 0; });
    const subtotal = (productPrice + printsPerUnit) * qty;
    const tier = activeTierFor(qty);
    const pct = tier ? tier.percentOff : 0;
    let total = subtotal - subtotal * pct / 100;
    const rushOpt = (CONFIG.rushOptions || []).find(function (o) { return o.key === state.rushSelection; });
    if (rushOpt && rushOpt.surchargePct > 0) total += computeRushFee(rushOpt, total).fee;
    return { total: total, qty: qty, pct: pct, bundle: false };
  }

  const RUNNING_HIDE_ON = { "optin": 1, "audience": 1, "solo-category": 1, "solo-product": 1, "solo-confirm": 1 };
  function renderRunningTotal() {
    const el = document.getElementById("ow-running");
    if (!el) return;
    if (state.audience === "group" || RUNNING_HIDE_ON[state.step]) { el.setAttribute("hidden", ""); return; }
    const est = computeSoloEstimate();
    if (!est) { el.setAttribute("hidden", ""); return; }
    const valEl = document.getElementById("ow-running-val");
    const noteEl = document.getElementById("ow-running-note");
    if (valEl) valEl.textContent = "$" + est.total.toFixed(2);
    if (noteEl) {
      noteEl.textContent =
        (est.qty === 1 ? "1 shirt" : est.qty + " shirts") +
        (est.bundle ? " · bundle price" : (est.pct ? " · " + est.pct + "% bulk discount applied" : "")) +
        " · prices ex GST · postage worked out at checkout";
    }
    el.removeAttribute("hidden");
  }

  function showStep(name) {
    if (_aiActive || _aiQueue.length > 0) {
      // AI is still typing; remember which step to show when it's done.
      _pendingStep = name;
      return;
    }
    _doShowStep(name);
  }

  function _doShowStep(name) {
    state.step = name;
    document.querySelectorAll("#order-wizard-root .ow-step").forEach(function (p) {
      if (p.getAttribute("data-step") === name) p.removeAttribute("hidden");
      else p.setAttribute("hidden", "");
    });
    // If reorder mode was already switched on back at the optin step, land
    // on the artwork step already reflecting that instead of asking the
    // customer to tick the same box a second time.
    if (name === "solo-artwork") _syncSoloReorderUi();
    if (name === "group-artwork") _syncGroupReorderUi();
    renderRunningTotal();
  }

  // Sets the checkbox + shows/hides the reorder fields vs. the normal
  // artwork list to match state.reorderMode. Shared by the optin-step
  // shortcut (state set before the step is even rendered) and the
  // in-step checkbox toggle itself.
  function _syncSoloReorderUi() {
    const cb = document.getElementById("ow-reorder-toggle");
    if (cb) cb.checked = !!state.reorderMode;
    const fieldsEl = document.getElementById("ow-reorder-fields");
    const listEl = document.getElementById("ow-artwork-list");
    if (fieldsEl) fieldsEl.hidden = !state.reorderMode;
    if (listEl) listEl.hidden = state.reorderMode;
  }

  function _syncGroupReorderUi() {
    const cb = document.getElementById("ow-group-reorder-toggle");
    if (cb) cb.checked = !!state.groupReorderMode;
    const fieldsEl = document.getElementById("ow-group-reorder-fields");
    const listEl = document.getElementById("ow-group-artwork-list");
    if (fieldsEl) fieldsEl.hidden = !state.groupReorderMode;
    if (listEl) listEl.hidden = state.groupReorderMode;
  }

  // Swaps the modal header between "Order your design" and "Reorder a
  // design" so reorder mode stays visually obvious for the rest of the
  // flow, not just at the moment it was switched on.
  function _setReorderTitle(reordering) {
    const titleEl = document.getElementById("ow-modal-title");
    if (titleEl) titleEl.textContent = reordering ? "Reorder a design" : "Order your design";
  }

  /**
   * Reset chat queue state. Called on modal open so a fresh conversation
   * doesn't inherit leftover queued messages or pending step changes from
   * a previous session.
   */
  function _resetChatQueue() {
    _aiQueue.length = 0;
    _aiActive = false;
    _pendingStep = null;
  }

  function pushChat(text, who) {
    const chat = $("#ow-chat");
    if (!chat) return;
    if (who === "user") {
      // User messages always appear instantly — they're echoes of an action
      // the customer just took, so delaying them feels broken.
      _appendBubble(text, "user");
      return;
    }
    _aiQueue.push(text);
    if (!_aiActive) _drainAiQueue();
  }

  function _appendBubble(text, who) {
    const chat = $("#ow-chat");
    if (!chat) return null;
    const b = document.createElement("div");
    b.className = "ow-chat__bubble ow-chat__bubble--" + who + " ow-chat__bubble--in";
    b.textContent = text;
    chat.appendChild(b);
    // Scroll the chat wrapper (not the inner chat) so the newest message
    // stays in view as messages stack up.
    const wrap = $("#ow-chat-wrap");
    if (wrap) wrap.scrollTop = wrap.scrollHeight;
    return b;
  }

  function _appendTypingIndicator() {
    const chat = $("#ow-chat");
    if (!chat) return { remove: function () {} };
    const ind = document.createElement("div");
    ind.className = "ow-chat__bubble ow-chat__bubble--ai ow-chat__typing";
    ind.setAttribute("aria-label", "typing");
    ind.innerHTML =
      '<span class="ow-chat__dot"></span>' +
      '<span class="ow-chat__dot"></span>' +
      '<span class="ow-chat__dot"></span>';
    chat.appendChild(ind);
    const wrap = $("#ow-chat-wrap");
    if (wrap) wrap.scrollTop = wrap.scrollHeight;
    return ind;
  }

  async function _drainAiQueue() {
    _aiActive = true;
    while (_aiQueue.length > 0) {
      const text = _aiQueue.shift();
      // Typing-indicator duration loosely scales with message length so
      // a longer message feels like it took longer to write.
      const indicator = _appendTypingIndicator();
      const wait = Math.max(450, Math.min(1400, 280 + text.length * 11));
      await new Promise(function (r) { setTimeout(r, wait); });
      if (indicator && indicator.remove) indicator.remove();
      _appendBubble(text, "ai");
    }
    _aiActive = false;
    // Any step queued while AI was "typing" gets shown now.
    if (_pendingStep) {
      const s = _pendingStep;
      _pendingStep = null;
      _doShowStep(s);
    }
  }

  function openModal(designUrl, designPrompt) {
    const img = $("#ow-design-context-img");
    const prom = $("#ow-design-context-prompt");
    const banner = $("#ow-testmode-banner");
    const chat = $("#ow-chat");
    const modal = document.querySelector("#order-wizard-root .ow-modal");
    const root = $("#order-wizard-root");
    const designContext = $("#ow-design-context");

    // Resuming a session that's already past the opening question (customer
    // picked "solo" or "group" already, so they're somewhere in
    // category/product/print-location/artwork territory). Reopening in that
    // case must NOT reset back to "how are we doing the artwork?" — that
    // ask is only for a genuinely fresh session. Re-triggering the wizard
    // (a second "Put it on a tee" click, the floating launcher, a gallery
    // tile) while mid-flow was wiping the customer's progress and chat back
    // to the start every time; audience being set is the earliest reliable
    // sign real progress exists.
    const resuming = !!state.audience;

    // Only adopt a newly-passed design; never clobber one already chosen
    // (e.g. reopening from a generic launcher with no design context of its
    // own shouldn't blank out an in-progress order's artwork).
    if (designUrl) {
      state.designUrl = designUrl;
      state.designPrompt = designPrompt || null;
      if (img) { img.src = designUrl; img.setAttribute("alt", designPrompt || "Your design"); }
      if (prom) prom.textContent = designPrompt || "";
      if (designContext) designContext.removeAttribute("hidden");
    }

    if (banner) {
      if (GATE.reason === "admin" || GATE.reason === "url") banner.removeAttribute("hidden");
      else banner.setAttribute("hidden", "");
    }
    root.removeAttribute("hidden");
    modal.removeAttribute("hidden");
    document.body.classList.add("ow-no-scroll");
    state.open = true;

    window.ftTrack("ai_wizard_open", { has_design: !!designUrl });
    // Audit fix 2026-09-06: back from the designer with a new design after
    // "Create a design with AI" — restore the order and land on artwork.
    const resumeSnap = (!resuming && designUrl) ? readResumeSnapshot() : null;
    if (resumeSnap) {
      RESUME_FIELDS.forEach(function (k) { if (resumeSnap[k] !== undefined) state[k] = resumeSnap[k]; });
      clearResumeSnapshot();
      const isGroup = state.audience === "group";
      const locs = isGroup ? (state.groupPrintLocations || []) : (state.selectedPrintLocations || []);
      const label = state.designPrompt ? "AI design: " + state.designPrompt.slice(0, 80) : "AI design";
      locs.forEach(function (loc) {
        if (isGroup) {
          state.groupArtwork[loc] = state.designUrl; state.groupArtworkSources[loc] = "current"; state.groupArtworkLabels[loc] = label;
        } else {
          state.printArtwork[loc] = state.designUrl; state.artworkSources[loc] = "current"; state.artworkLabels[loc] = label;
        }
      });
      if (chat) chat.innerHTML = "";
      _resetChatQueue();
      pushChat("Welcome back — your order is exactly where you left it, and your new design is now on every print. Swap any of them if you like.", "ai");
      if (isGroup) { renderGroupArtworkList(); showStep("group-artwork"); }
      else { renderArtworkList(); showStep("solo-artwork"); }
      return;
    }

    if (resuming) {
      // Just make sure the DOM matches whatever step they were last on —
      // chat history and all prior selections stay exactly as they were.
      showStep(state.step || "audience");
    } else {
      if (chat) chat.innerHTML = "";
      _resetChatQueue();

      // Branch on whether a design was passed in:
      //   YES → existing opt-in flow ("Sweet design. Want me to help?")
      //   NO  → skip straight into the normal flow (audience → category →
      //         product → print locations). Artwork is only ever asked for
      //         on the artwork step, once print locations are chosen — Roland
      //         was explicit that asking for a design up front, before the
      //         customer even knows what they're ordering, is a blocker, not
      //         a convenience.
      // Lead with a "here's what I do" message so customers know what to
      // expect before being asked anything — sets the wizard up as helpful,
      // not a sales gimmick.
      const INTRO_MESSAGE =
        "Hey, I'm here to make this easy. I'll walk you through it, " +
        "point out our best sellers and the right options for what you need, " +
        "and make sure you get the best deal we can do.";

      if (state.designUrl) {
        if (designContext) designContext.removeAttribute("hidden");
        pushChat(INTRO_MESSAGE, "ai");
        pushChat("Nice design — want me to take you through ordering it, or have you got it from here?", "ai");
        showStep("optin");
      } else {
        // No design — hide the empty design-context bar entirely to avoid
        // showing a broken-looking placeholder image at the top.
        if (designContext) designContext.setAttribute("hidden", "");
        pushChat(INTRO_MESSAGE, "ai");
        pushChat("Let's get you sorted — you'll pick your artwork once we know what you're ordering and where the print's going. Just you, or a group?", "ai");
        showStep("audience");
      }
    }

    // Clear product cache on every wizard open so newly-tagged products
    // show up immediately during testing.
    _productsCache = null;
    _productsCacheTs = 0;

    // Kick off print-product + bundle + rush-fee resolution in the background
    // so it's ready by the time we need to compute totals or add to cart.
    if (!state.printProducts || !Object.keys(state.printProducts).length) {
      resolvePrintProducts().then(function (pp) { state.printProducts = pp; });
    }
    if (!state.bundleProducts || !Object.keys(state.bundleProducts).length) {
      resolveBundleProducts().then(function (bp) { state.bundleProducts = bp; });
    }
    if (!state.rushFeeProduct) {
      resolveRushFeeProduct().then(function (rp) { state.rushFeeProduct = rp; });
    }
  }

  /**
   * Resolve the Rush Production Fee product so we have its variant ID
   * ready when we need to add it to cart. Single $1 product — wizard adds
   * quantity = surcharge amount in dollars.
   */
  async function resolveRushFeeProduct() {
    // Hard-coded variant ID short-circuits the fetch entirely — works
    // even if Roland unpublishes the product from the storefront.
    if (CONFIG.rushFeeVariantId) {
      const rp = {
        variantId: CONFIG.rushFeeVariantId,
        price: 1.00,
        title: "Rush order fee",
        handle: CONFIG.rushFeeHandle
      };
      if (window.console && console.info) {
        console.info("[OrderWizard] rush fee product (hard-coded):", rp.title, "(variant " + rp.variantId + ")");
      }
      return rp;
    }
    // Fallback — fetch by handle (used if no variant ID is configured)
    try {
      const r = await fetch("/products/" + CONFIG.rushFeeHandle + ".json", { credentials: "same-origin" });
      if (!r.ok) {
        console.warn("[OrderWizard] rush fee product not found at handle:", CONFIG.rushFeeHandle);
        return null;
      }
      const data = await r.json();
      const p = data.product;
      if (!p || !p.variants || !p.variants[0]) return null;
      return {
        variantId: p.variants[0].id,
        price: parseFloat(p.variants[0].price),
        title: p.title,
        handle: p.handle
      };
    } catch (e) {
      console.warn("[OrderWizard] rush fee fetch failed", e);
      return null;
    }
  }

  /**
   * Pre-discount subtotal for the customer's current pick. Used as the
   * base for rush surcharge calculation. Handles bundle path (uses bundle
   * total) and individuals path (sum of garments + prints).
   */
  function computePreDiscountSubtotal(flow) {
    if (flow === "group") {
      // Sum the cost per product type — bundle types use the bundle total,
      // individuals types use price-with-prints × qty.
      const selections = (state.groupBundleSelection && typeof state.groupBundleSelection === "object" &&
                          !Array.isArray(state.groupBundleSelection)) ? state.groupBundleSelection : {};
      const printsPerUnit = state.groupPrintLocations.reduce(function (s, loc) {
        return s + (CONFIG.printPrices[loc] || 0);
      }, 0);
      let subtotal = 0;
      state.groupSelectedProductTypes.forEach(function (type) {
        const product = state.groupSelectedProducts[type];
        if (!product) return;
        const sizes = state.groupSizes[type] || {};
        const typeQty = Object.keys(sizes).reduce(function (s, k) {
          return s + (parseInt(sizes[k], 10) || 0);
        }, 0);
        if (typeQty === 0) return;
        const sel = selections[type];
        if (sel && sel.bundles && sel.bundles.length > 0 && sel.key !== "individuals") {
          subtotal += sel.total;
        } else {
          const price = product.variants && product.variants[0] ? parseFloat(product.variants[0].price) : 0;
          subtotal += (price + printsPerUnit) * typeQty;
        }
      });
      return subtotal;
    }
    // solo
    const sel = state.bundleSelection;
    if (sel && sel.key !== "individuals" && sel.bundles && sel.bundles.length > 0) {
      return sel.total;
    }
    const product = state.selectedProduct;
    if (!product) return 0;
    const qty = Math.max(1, state.selectedQuantity || 1);
    const productPrice = product.variants && product.variants[0] ? parseFloat(product.variants[0].price) : 0;
    let printsPerUnit = 0;
    state.selectedPrintLocations.forEach(function (loc) {
      printsPerUnit += CONFIG.printPrices[loc] || 0;
    });
    return (productPrice + printsPerUnit) * qty;
  }

  /**
   * Post-discount subtotal — computePreDiscountSubtotal() with the active
   * bulk tier applied. Keyed off total garment quantity, matching how
   * activeTierFor() is already used elsewhere in this flow. This is what
   * the customer is ACTUALLY being charged for garments+prints once Hulk
   * Discounts' real bulk ladder applies, and is the correct base for rush
   * fee — a fee computed off the pre-discount subtotal overcharges every
   * bulk order (confirmed live 2026-08-26: a 20-shirt cart's rush fee
   * needed to be 25% of the discounted $319.80, not the undiscounted
   * $426.40). Bundle-path solo orders are already flat-priced with no
   * further layer (bundle SKUs are bought at cart qty 1, so they never
   * reach Hulk's quantity thresholds in the first place) — skip the tier
   * lookup there to match. Group orders that MIX bundle and individual
   * product types are a known simplification: the tier is applied to the
   * whole blended subtotal rather than only the individuals portion,
   * since Hulk itself only discounts the individuals portion in that
   * case — rare enough (and still strictly closer to correct than
   * applying no discount at all) that it's not worth the complexity of
   * splitting computePreDiscountSubtotal's per-type math apart to fix.
   */
  function computeDiscountedSubtotal(flow) {
    if (flow === "group") {
      const subtotal = computePreDiscountSubtotal("group");
      const tier = activeTierFor(groupTotalGarments());
      return tier ? subtotal - (subtotal * tier.percentOff / 100) : subtotal;
    }
    // solo
    const sel = state.bundleSelection;
    if (sel && sel.key !== "individuals" && sel.bundles && sel.bundles.length > 0) {
      return sel.total; // bundle path — already flat-priced, no further tier layer
    }
    const subtotal = computePreDiscountSubtotal("solo");
    const tier = activeTierFor(Math.max(1, state.selectedQuantity || 1));
    return tier ? subtotal - (subtotal * tier.percentOff / 100) : subtotal;
  }

  /**
   * Apply % + min/max floor & cap to compute the rush fee in whole dollars.
   * Returns { fee, hitMin, hitMax } so the renderer can show a small note
   * when the floor or cap is in effect.
   */
  function computeRushFee(opt, subtotal) {
    if (!opt || opt.surchargePct === 0) return { fee: 0, hitMin: false, hitMax: false };
    let fee = subtotal * opt.surchargePct / 100;
    let hitMin = false, hitMax = false;
    if (opt.minFee != null && fee < opt.minFee) { fee = opt.minFee; hitMin = true; }
    if (opt.maxFee != null && fee > opt.maxFee) { fee = opt.maxFee; hitMax = true; }
    return { fee: Math.round(fee), hitMin: hitMin, hitMax: hitMax };
  }

  function computeRushSurcharge(flow) {
    const opt = (CONFIG.rushOptions || []).find(function (o) { return o.key === state.rushSelection; });
    if (!opt || opt.surchargePct === 0) return 0;
    return computeRushFee(opt, computeDiscountedSubtotal(flow)).fee;
  }

  /**
   * Are all garments in the current order from the core range? Same-day
   * production needs every item to be in stock + ready, so a mixed order
   * has to fall back to standard or 5-day rush.
   */
  function orderIsAllCoreRange(flow) {
    if (flow === "group") {
      const types = state.groupSelectedProductTypes || [];
      if (!types.length) return false;
      return types.every(function (t) {
        const p = state.groupSelectedProducts && state.groupSelectedProducts[t];
        return p && isCoreRange(p);
      });
    }
    // solo
    return !!(state.selectedProduct && isCoreRange(state.selectedProduct));
  }

  function renderRushPicker(flow) {
    const wrap = document.getElementById(flow === "group" ? "ow-group-rush-options" : "ow-rush-options");
    if (!wrap) return;
    wrap.innerHTML = "";
    const subtotal = computeDiscountedSubtotal(flow);
    const allCore = orderIsAllCoreRange(flow);
    (CONFIG.rushOptions || []).forEach(function (o) {
      const result = computeRushFee(o, subtotal);
      const isSel = state.rushSelection === o.key;
      // Same-day production needs every garment to be in stock. If the
      // customer picked something outside the core range, lock the option
      // and tell them how to unlock it.
      const lockedForCoreOnly = (o.key === "same_day" && !allCore);
      let priceLabel;
      if (o.surchargePct === 0) {
        priceLabel = '<span class="ow-rush-opt__free">FREE</span>';
      } else {
        let noteHtml = '<span class="ow-rush-opt__pct">(' + o.surchargePct + '%)</span>';
        if (result.hitMin) {
          noteHtml = '<span class="ow-rush-opt__pct">($' + o.minFee + ' minimum)</span>';
        } else if (result.hitMax) {
          noteHtml = '<span class="ow-rush-opt__pct">(capped at $' + o.maxFee + ')</span>';
        }
        priceLabel = '<span class="ow-rush-opt__price">+$' + result.fee.toFixed(2) + ' ' + noteHtml + '</span>';
      }
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "ow-rush-opt" + (isSel ? " is-selected" : "") + (lockedForCoreOnly ? " is-disabled" : "");
      btn.setAttribute("data-ow-action", "pick-rush");
      btn.setAttribute("data-key", o.key);
      if (lockedForCoreOnly) btn.setAttribute("data-locked-core-only", "1");
      const lockNote = lockedForCoreOnly
        ? '<span class="ow-rush-opt__lock">⚡ Core range only — pick a core product to unlock</span>'
        : '';
      btn.innerHTML =
        '<div class="ow-rush-opt__body">' +
          '<div class="ow-rush-opt__label">' + o.label + '</div>' +
          '<div class="ow-rush-opt__desc">' + o.desc + '</div>' +
          lockNote +
        '</div>' +
        '<div class="ow-rush-opt__pricecol">' + priceLabel + '</div>';
      wrap.appendChild(btn);
    });
    // Audit fix 2026-09-07: same deadline route as the cart speed picker.
    const fpw = window.FT_PRODUCTION || {};
    const dl = document.createElement("p");
    dl.className = "ow-rush-deadline";
    dl.innerHTML = (fpw.deadline || "Need it by a particular date? Tell us the date before you pay and we'll confirm we can meet it.") + ' <a href="' + (fpw.contactUrl || '/pages/contact') + '" target="_blank" rel="noopener">Contact us</a>';
    wrap.appendChild(dl);
  }

  /**
   * Cart cleanup — when the customer is on /cart, find any Rush Production
   * Fee row and hide its quantity controls so the line reads as a simple
   * fee. Run once at boot, then again after Shopify-style cart updates.
   */
  function tidyRushFeeInCart() {
    if (!/^\/cart/i.test(location.pathname)) return;
    // Multiple selectors so we find the row whether the product is
    // published (link present) or unpublished (only data attrs / IDs).
    const vid = CONFIG.rushFeeVariantId;
    const handle = CONFIG.rushFeeHandle;
    const selectors = [
      'a[href*="/products/' + handle + '"]',
      '[data-product-handle="' + handle + '"]',
      vid ? '[data-variant-id="' + vid + '"]'  : null,
      vid ? '[data-variant="' + vid + '"]'     : null,
      vid ? 'input[name="updates[' + vid + ']"]' : null,
      vid ? '[name="id"][value="' + vid + '"]' : null,
      vid ? '[data-line-item-id-' + vid + ']'  : null
    ].filter(Boolean);
    const hits = [];
    selectors.forEach(function (sel) {
      try {
        document.querySelectorAll(sel).forEach(function (el) { hits.push(el); });
      } catch (_) {}
    });
    hits.forEach(function (el) {
      const row = el.closest('tr, li, [class*="line-item"], [class*="cart-item"], [class*="LineItem"], form[class*="cart"] > *');
      if (row && !row.classList.contains("ow-rush-fee-row")) {
        row.classList.add("ow-rush-fee-row");
      }
    });
  }

  /**
   * Render the bundle comparison step. Each option is a clickable card.
   * Marks the cheapest with a "BEST DEAL" badge. State.bundleSelection
   * tracks which one the customer picked.
   */
  function renderBundleDeals(options) {
    const intro = $("#ow-bundle-intro");
    const wrap = $("#ow-bundle-options");
    if (!intro || !wrap || !options || !options.length) return;

    // Compute saving vs baseline (first option = individuals)
    const baseline = options.find(function (o) { return o.key === "individuals"; })
                 || options[0];
    const cheapest = options.reduce(function (best, cur) {
      return cur.total < best.total ? cur : best;
    }, options[0]);

    const productTitle = (state.selectedProduct && state.selectedProduct.title) || "your product";
    const qty = state.selectedQuantity || 1;
    const printLabels = state.selectedPrintLocations.map(locationLabel).join(" + ");
    intro.innerHTML =
      '<strong>You picked ' + qty + ' × ' + productTitle + ' with ' + printLabels + ' prints.</strong> ' +
      'We have bundle pricing for this kind of order — same prints you picked, just at the bundle price. Pick what works for you.';

    if (!state.bundleSelection || !options.find(function (o) { return o.key === state.bundleSelection.key; })) {
      // Default the cheapest selection
      state.bundleSelection = cheapest;
    }

    // RRP per garment (no discount, no bundle) — used to show savings off
    // the no-discount baseline.
    const product = state.selectedProduct;
    const productPrice = product && product.variants && product.variants[0]
      ? parseFloat(product.variants[0].price) : 0;
    const printsPerUnit = state.selectedPrintLocations.reduce(function (s, l) {
      return s + (CONFIG.printPrices[l] || 0);
    }, 0);
    const rrpPerGarment = productPrice + printsPerUnit;

    // Most expensive option = mark RED so customer sees the bad-value baseline
    // Worst value = highest $/garment, NOT highest total. The total can be
    // misleading because a stretch option has high total but cheap $/garment.
    // The customer's individuals path is usually what scores worst on $/garment
    // (because they're not benefiting from bundle pricing).
    const worstValue = options.reduce(function (worst, cur) {
      const curPer = cur.garments > 0 ? cur.total / cur.garments : Infinity;
      const worstPer = worst.garments > 0 ? worst.total / worst.garments : Infinity;
      return curPer > worstPer ? cur : worst;
    }, options[0]);

    wrap.innerHTML = "";
    options.forEach(function (o) {
      const isSel = state.bundleSelection && state.bundleSelection.key === o.key;
      const isCheap = o === cheapest && options.length > 1;
      const isStretch = !!o.stretch;
      const isWorst = o === worstValue && options.length > 1 && !isCheap && !isStretch;
      const isBundle = !!(o.bundles && o.bundles.length > 0);
      const perGarment = o.garments > 0 ? (o.total / o.garments) : 0;
      const rrp = rrpPerGarment * o.garments;
      const savingsVsRrp = rrp - o.total;

      const shippingHtml = isBundle
        ? '<div class="ow-bundle-opt__shipping">Free shipping</div>'
        : '<div class="ow-bundle-opt__shipping-out">+ shipping at checkout</div>';
      const rrpHtml = (savingsVsRrp > 0)
        ? '<div class="ow-bundle-opt__rrp">Full price $' + rrp.toFixed(2) + '</div>' +
          '<div class="ow-bundle-opt__save">You save $' + savingsVsRrp.toFixed(2) + '</div>'
        : "";

      // Priority: CHEAPEST wins over STRETCH. If the stretch is also the
      // cheapest, it's the best deal — colour green not orange. STRETCH
      // only applies when the option costs more than the baseline.
      let colorClass = "";
      if (isCheap) colorClass = " is-cheapest";
      else if (isStretch) colorClass = " is-stretch";
      else if (isWorst) colorClass = " is-worst";

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "ow-bundle-opt" + (isSel ? " is-selected" : "") + colorClass;
      btn.setAttribute("data-ow-action", "pick-bundle-option");
      btn.setAttribute("data-key", o.key);
      btn.innerHTML =
        '<div class="ow-bundle-opt__body">' +
          '<div class="ow-bundle-opt__label">' + o.label + '</div>' +
          '<div class="ow-bundle-opt__detail">' + o.detail + '</div>' +
        '</div>' +
        '<div>' +
          '<div class="ow-bundle-opt__price">$' + o.total.toFixed(2) + '</div>' +
          '<div class="ow-bundle-opt__per">$' + perGarment.toFixed(2) + ' / garment</div>' +
          shippingHtml +
          rrpHtml +
        '</div>';
      btn._owBundleOpt = o;
      wrap.appendChild(btn);
    });
  }
  function closeModal() {
    const modal = document.querySelector("#order-wizard-root .ow-modal");
    const root = $("#order-wizard-root");
    if (modal) modal.setAttribute("hidden", "");
    if (root) root.setAttribute("hidden", "");
    document.body.classList.remove("ow-no-scroll");
    state.open = false;
  }

  function discoverActiveDesign(clickedEl) {
    // 1. Explicit binding via data attribute on the clicked element (or an
    //    ancestor). This is set by gallery tiles' "Order this design →"
    //    buttons and the designer page's "Put it on a tee" CTA — both
    //    intentionally tell the wizard which design the customer just chose.
    const explicit = clickedEl.closest("[data-design-url]");
    if (explicit) return { url: explicit.getAttribute("data-design-url"), prompt: explicit.getAttribute("data-design-prompt") || "" };
    // 2. Clicked inside an AI chat message containing an image — used when
    //    the customer clicks the wizard launcher from inside the designer
    //    chat thread, so we pick up the most recent assistant turn's image.
    const msg = clickedEl.closest(".ai-msg, .ai-message, [data-ai-message]");
    if (msg) {
      const img = msg.querySelector("img");
      if (img && img.src) {
        let prompt = "";
        const prev = msg.previousElementSibling && msg.previousElementSibling.querySelector && msg.previousElementSibling.querySelector(".ai-msg__text, .ai-message__text");
        if (prev) prompt = prev.textContent.trim();
        return { url: img.src, prompt: prompt };
      }
    }
    // 3. Page-wide fallback — only safe on the AI designer page itself
    //    (/pages/design-with-ai) where "the most recent R2 design image on
    //    the page" reliably maps to the customer's current draft. On every
    //    other page (homepage, PDPs, gallery, my-designs) this would scoop
    //    up some unrelated image — an inspiration tile, someone else's
    //    showcased design, a saved-design preview — and silently treat it
    //    as the customer's choice, which is exactly the wrong UX.
    if (/^\/pages\/(design-with-ai|ai-designer)/i.test(location.pathname)) {
      const imgs = document.querySelectorAll("img");
      for (let i = imgs.length - 1; i >= 0; i--) {
        const src = imgs[i].src || "";
        if (/r2\.dev\/(designs|prints)\//.test(src) || /\/cdn\/(designs|prints)\//.test(src)) {
          return { url: src, prompt: "" };
        }
      }
    }
    return { url: null, prompt: null };
  }

  function bindListeners() {
    // Running total follows every quantity / print / bundle change.
    document.addEventListener("click", function (ev) {
      if (ev.target.closest && ev.target.closest("#order-wizard-root [data-ow-action]")) setTimeout(renderRunningTotal, 0);
    });
    document.addEventListener("input", function (ev) {
      if (ev.target.closest && ev.target.closest("#order-wizard-root")) setTimeout(renderRunningTotal, 0);
    });
    document.addEventListener("click", function (ev) {
      const trigger = ev.target.closest('[data-action="shop-tees"], [data-wizard-trigger]');
      if (!trigger) return;
      ev.preventDefault();
      ev.stopPropagation();
      const ctx = discoverActiveDesign(trigger);
      openModal(ctx.url, ctx.prompt);
    }, true);

    document.addEventListener("click", function (ev) {
      const root = $("#order-wizard-root");
      if (!root || !root.contains(ev.target)) return;
      if (ev.target.closest("[data-ow-close]")) { closeModal(); return; }
      const a = ev.target.closest("[data-ow-action]");
      if (!a) return;
      const action = a.getAttribute("data-ow-action");
      switch (action) {
        case "create-design": {
          // Audit fix 2026-09-06 — keep the whole order, go make a design,
          // open() brings the customer straight back to this step.
          saveResumeSnapshot();
          pushChat("Create a design with AI", "user");
          window.location.href = "/pages/design-with-ai?from=order";
          break;
        }
        case "optin-yes":
          pushChat("Yeah, help me out", "user");
          pushChat("Cool — is this just for you, or a group?", "ai");
          showStep("audience");
          break;
        case "optin-no":
          pushChat("I'll take it from here", "user");
          closeModal();
          break;
        case "reorder-shortcut": {
          // Lives on both "optin" (shown only when a pre-made AI design was
          // passed in) and "audience" (the real first step for the far more
          // common cold-start entry — see the `!resuming` branch in
          // openModal(), which skips "optin" entirely when there's no
          // designUrl). Covering both means whichever step a customer
          // actually lands on first, the option is right there.
          //
          // Set both flags now — we don't yet know solo vs group, so
          // whichever path they pick next already has reorder mode primed.
          // The artwork step (wherever they land) syncs its checkbox/panel
          // to this on entry (see _doShowStep) instead of making them
          // re-declare it after already telling us here.
          state.reorderMode = true;
          state.groupReorderMode = true;
          _setReorderTitle(true);
          pushChat("I'm reordering a design", "user");
          if (state.step === "audience") {
            // Already looking at "Just me" / "A group" — no need to
            // re-navigate, just confirm and let them pick.
            pushChat("Got it, no new upload needed. Just you, or a group?", "ai");
          } else {
            pushChat("No worries — I just need what you're ordering (product, size, print spot) and I'll skip straight past the upload step. Is this just for you, or a group?", "ai");
            showStep("audience");
          }
          break;
        }
        case "audience-solo":
          state.audience = "solo";
          pushChat("Just me", "user");
          pushChat("Sweet. What gear are you after?", "ai");
          showStep("solo-category");
          break;
        case "audience-group":
          state.audience = "group";
          pushChat("A group", "user");
          pushChat("Cool — let's get the totals first so I can show you the discount tier upfront.", "ai");
          renderGroupTierCard();
          showStep("group-totals");
          break;

        case "back-to-audience":
          showStep("audience");
          break;

        case "group-totals-continue": {
          const total = (state.groupAdults || 0) + (state.groupKids || 0);
          if (total <= 0) {
            showStepError($("#ow-group-totals-error"), "Tell me how many adults and/or kids are in the group.");
            markMissing(document.querySelector(".ow-totals"));
            return;
          }
          clearStepError($("#ow-group-totals-error"));
          const bits = [];
          if (state.groupAdults) bits.push(state.groupAdults + " adult" + (state.groupAdults === 1 ? "" : "s"));
          if (state.groupKids)   bits.push(state.groupKids   + " kid"   + (state.groupKids   === 1 ? "" : "s"));
          pushChat(bits.join(" + "), "user");
          const t = activeTierFor(total);
          const n = nextTierAfter(total);
          let aiMsg = "Got it — " + total + " items total.";
          if (t) aiMsg += " You're saving " + t.percentOff + "% on every item at that level.";
          if (n) aiMsg += " Heads up: at " + n.minQty + " items it bumps up to " + n.percentOff + "% off.";
          aiMsg += " What gear are you after?";
          pushChat(aiMsg, "ai");
          renderGroupProductPick();
          showStep("group-products");
          break;
        }

        case "back-to-group-totals":
          renderGroupTierCard();
          showStep("group-totals");
          break;

        case "toggle-group-product": {
          const type = a.getAttribute("data-product-type");
          const idx = state.groupSelectedProductTypes.indexOf(type);
          if (idx >= 0) {
            state.groupSelectedProductTypes.splice(idx, 1);
          } else {
            state.groupSelectedProductTypes.push(type);
          }
          renderGroupProductPick();
          break;
        }

        case "group-products-continue": {
          if (!state.groupSelectedProductTypes.length) {
            showStepError($("#ow-group-products-error"), "Pick at least one type of gear to continue.");
            markMissing($("#ow-group-product-pick"));
            return;
          }
          clearStepError($("#ow-group-products-error"));
          const picks = state.groupSelectedProductTypes
            .map(function (k) { return (GROUP_PRODUCT_TYPES.find(function (t) { return t.key === k; }) || {}).title || k; })
            .join(", ");
          pushChat(picks, "user");
          pushChat("Pick the sizes for each. Mix any way you like — totals update as you go.", "ai");
          renderGroupSizeMatrices();
          showStep("group-sizes");
          break;
        }

        case "back-to-group-products":
          renderGroupProductPick();
          showStep("group-products");
          break;

        case "group-adults-inc": {
          state.groupAdults = Math.min(500, (state.groupAdults || 0) + 1);
          const inp = $("#ow-group-adults");
          if (inp) inp.value = String(state.groupAdults);
          renderGroupTierCard();
          clearStepError($("#ow-group-totals-error"));
          break;
        }
        case "group-adults-dec": {
          state.groupAdults = Math.max(0, (state.groupAdults || 0) - 1);
          const inp = $("#ow-group-adults");
          if (inp) inp.value = String(state.groupAdults);
          renderGroupTierCard();
          if (state.groupAdults || state.groupKids) clearStepError($("#ow-group-totals-error"));
          break;
        }
        case "group-kids-inc": {
          state.groupKids = Math.min(500, (state.groupKids || 0) + 1);
          const inp = $("#ow-group-kids");
          if (inp) inp.value = String(state.groupKids);
          renderGroupTierCard();
          clearStepError($("#ow-group-totals-error"));
          break;
        }
        case "group-kids-dec": {
          state.groupKids = Math.max(0, (state.groupKids || 0) - 1);
          const inp = $("#ow-group-kids");
          if (inp) inp.value = String(state.groupKids);
          renderGroupTierCard();
          if (state.groupAdults || state.groupKids) clearStepError($("#ow-group-totals-error"));
          break;
        }

        case "group-size-inc":
        case "group-size-dec": {
          const type = a.getAttribute("data-product-type");
          const size = a.getAttribute("data-size");
          if (!type || !size) return;
          if (!state.groupSizes[type]) state.groupSizes[type] = {};
          const cur = parseInt(state.groupSizes[type][size], 10) || 0;
          const delta = action === "group-size-inc" ? 1 : -1;
          state.groupSizes[type][size] = Math.max(0, Math.min(500, cur + delta));
          // Update the matching input field directly so we don't need a full re-render
          const inp = document.querySelector('input[data-ow-size-input][data-product-type="' + type + '"][data-size="' + size + '"]');
          if (inp) inp.value = state.groupSizes[type][size] || "";
          updateGroupProgress();
          if (groupTotalGarments() > 0) clearStepError($("#ow-group-sizes-error"));
          break;
        }

        case "group-sizes-continue": {
          const total = groupTotalGarments();
          const errEl = $("#ow-group-sizes-error");
          if (total === 0) {
            if (errEl) {
              errEl.textContent = "Pick at least one size before continuing.";
              errEl.removeAttribute("hidden");
            }
            return;
          }
          if (errEl) errEl.setAttribute("hidden", "");
          pushChat(total + " items sorted across " + state.groupSelectedProductTypes.length + " product" +
            (state.groupSelectedProductTypes.length === 1 ? "" : "s"), "user");
          const t = activeTierFor(total);
          let msg = "Sweet — " + total + " items locked in.";
          if (t) msg += " That's " + t.percentOff + "% off across the lot.";
          msg += " Pick a colour — same colour across the whole order.";
          pushChat(msg, "ai");
          renderGroupColourPicker();
          showStep("group-colour");
          break;
        }

        case "back-to-group-sizes":
          renderGroupSizeMatrices();
          showStep("group-sizes");
          break;

        case "pick-group-colour": {
          const colour = a.getAttribute("data-value");
          state.groupColour = colour;
          renderGroupColourPicker();
          if (state.groupColour) clearStepError($("#ow-group-colour-error"));
          break;
        }

        case "group-colour-continue": {
          if (!state.groupColour) {
            showStepError($("#ow-group-colour-error"), "Pick a colour to continue.");
            markMissing($("#ow-group-swatches"));
            return;
          }
          clearStepError($("#ow-group-colour-error"));
          pushChat(state.groupColour, "user");
          pushChat("Where would you like your prints to be? Same locations across every garment.", "ai");
          renderGroupPrintPicker();
          showStep("group-print");
          break;
        }

        case "back-to-group-colour":
          renderGroupColourPicker();
          showStep("group-colour");
          break;

        case "toggle-group-print": {
          const key = a.getAttribute("data-key");
          const idx = state.groupPrintLocations.indexOf(key);
          if (idx >= 0) {
            state.groupPrintLocations.splice(idx, 1);
          } else {
            // Mutual exclusion same as solo flow
            if (key === "large_front") {
              const flcIdx = state.groupPrintLocations.indexOf("front_left_chest");
              if (flcIdx >= 0) state.groupPrintLocations.splice(flcIdx, 1);
            } else if (key === "front_left_chest") {
              const lfIdx = state.groupPrintLocations.indexOf("large_front");
              if (lfIdx >= 0) state.groupPrintLocations.splice(lfIdx, 1);
            }
            state.groupPrintLocations.push(key);
          }
          renderGroupPrintPicker();
          if (state.groupPrintLocations.length) clearStepError($("#ow-group-print-error"));
          break;
        }

        case "group-print-continue": {
          if (!state.groupPrintLocations.length) {
            showStepError($("#ow-group-print-error"), "Pick at least one print location to continue.");
            markMissing($("#ow-group-print-options"));
            return;
          }
          clearStepError($("#ow-group-print-error"));
          pushChat(state.groupPrintLocations.map(locationLabel).join(" + "), "user");
          // Pre-fill artwork with the AI design where present
          state.groupPrintLocations.forEach(function (loc) {
            if (!state.groupArtwork[loc]) {
              if (state.designUrl) {
                state.groupArtwork[loc] = state.designUrl;
                state.groupArtworkSources[loc] = "current";
                state.groupArtworkLabels[loc] = state.designPrompt
                  ? "AI design: " + state.designPrompt.slice(0, 80)
                  : "AI design";
              }
            }
          });
          // Check for bundle comparison — group bundles are the primary use case
          {
            const bundleOpts = computeGroupBundleOptions();
            if (bundleOpts && bundleOpts.length >= 2) {
              pushChat("Heads up — there's a bundle deal that lines up with your order. Could save you money. Have a look.", "ai");
              renderGroupBundleDeals(bundleOpts);
              showStep("group-bundle-deal");
              break;
            }
          }
          pushChat(artworkIntroMessage(), "ai");
          renderGroupArtworkList();
          showStep("group-artwork");
          break;
        }

        case "pick-group-bundle-option": {
          const trigger = a;
          const opt = trigger._owBundleOpt;
          if (!opt) return;
          const type = trigger.getAttribute("data-product-type");
          if (!type) return;
          if (!state.groupBundleSelection || typeof state.groupBundleSelection !== "object" ||
              Array.isArray(state.groupBundleSelection)) {
            state.groupBundleSelection = {};
          }
          // Switching this type's selection invalidates the extras it had.
          const prev = state.groupBundleSelection[type];
          if (!prev || prev.key !== opt.key) {
            if (state.bundleExtraSizes && state.bundleExtraSizes[type]) {
              state.bundleExtraSizes[type] = {};
            }
          }
          state.groupBundleSelection[type] = opt;
          const lastSections = computeGroupBundleOptions();
          if (lastSections) renderGroupBundleDeals(lastSections);
          break;
        }

        case "bundle-extra-size-inc":
        case "bundle-extra-size-dec": {
          const size = a.getAttribute("data-size");
          const type = a.getAttribute("data-product-type");
          if (!size || !type) return;
          if (!state.bundleExtraSizes) state.bundleExtraSizes = {};
          if (!state.bundleExtraSizes[type]) state.bundleExtraSizes[type] = {};
          const sel = state.groupBundleSelection && state.groupBundleSelection[type];
          const target = (sel && sel.extraSizesNeeded) || 0;
          let total = 0;
          Object.keys(state.bundleExtraSizes[type]).forEach(function (s) {
            total += parseInt(state.bundleExtraSizes[type][s], 10) || 0;
          });
          const cur = parseInt(state.bundleExtraSizes[type][size], 10) || 0;
          const delta = action === "bundle-extra-size-inc" ? 1 : -1;
          if (delta > 0 && total >= target) return;
          state.bundleExtraSizes[type][size] = Math.max(0, cur + delta);
          const inp = document.querySelector(
            'input[data-ow-bundle-extra-input][data-product-type="' + type + '"][data-size="' + size + '"]'
          );
          if (inp) inp.value = state.bundleExtraSizes[type][size] || "";
          updateBundleExtraTotal(type);
          break;
        }

        case "group-bundle-continue": {
          // Validate every section that has a stretch pick — block continue
          // until extras are fully assigned across all stretched types.
          const selections = state.groupBundleSelection || {};
          const unfinishedType = Object.keys(selections).find(function (type) {
            const sel = selections[type];
            if (!sel || !sel.stretch || !sel.extraSizesNeeded) return false;
            const typeExtras = (state.bundleExtraSizes && state.bundleExtraSizes[type]) || {};
            let assigned = 0;
            Object.keys(typeExtras).forEach(function (s) {
              assigned += parseInt(typeExtras[s], 10) || 0;
            });
            return assigned !== sel.extraSizesNeeded;
          });
          if (unfinishedType) {
            const sel = selections[unfinishedType];
            const typeExtras = (state.bundleExtraSizes && state.bundleExtraSizes[unfinishedType]) || {};
            let assigned = 0;
            Object.keys(typeExtras).forEach(function (s) { assigned += parseInt(typeExtras[s], 10) || 0; });
            const left = sel.extraSizesNeeded - assigned;
            const typeMeta = GROUP_PRODUCT_TYPES.find(function (t) { return t.key === unfinishedType; });
            pushChat(
              "You've still got " + left + " size" + (left === 1 ? "" : "s") +
              " to pick for the " + (typeMeta ? typeMeta.title.toLowerCase() : "extras") +
              " before we can keep going.",
              "ai"
            );
            const picker = document.querySelector('[data-extras-type="' + unfinishedType + '"]');
            if (picker) {
              picker.style.transition = "box-shadow 0.4s ease";
              picker.style.boxShadow = "0 0 0 4px rgba(255,107,0,0.35)";
              try { picker.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
              setTimeout(function () { picker.style.boxShadow = "none"; }, 1500);
            }
            return;
          }
          // Summarise the choices the customer made across types
          const summaryBits = Object.keys(selections).map(function (type) {
            const sel = selections[type];
            const typeMeta = GROUP_PRODUCT_TYPES.find(function (t) { return t.key === type; });
            const typeName = typeMeta ? typeMeta.title : type;
            if (sel && sel.key !== "individuals") {
              return typeName + ": " + sel.label;
            }
            return typeName + ": individuals";
          });
          pushChat(summaryBits.join(" · "), "user");
          pushChat("Sweet. Pick the artwork for each print — default is the design you made, you can swap any of them.", "ai");
          renderGroupArtworkList();
          showStep("group-artwork");
          break;
        }

        case "back-to-group-print":
          renderGroupPrintPicker();
          showStep("group-print");
          break;

        case "group-pick-later": {
          const loc = a.getAttribute("data-location");
          state.groupArtwork[loc] = "";
          state.groupArtworkSources[loc] = "later";
          state.groupArtworkLabels[loc] = "Will upload after ordering";
          state.groupSavedPickerOpen = null;
          renderGroupArtworkList();
          break;
        }

        case "group-pick-current": {
          const loc = a.getAttribute("data-location");
          if (!state.designUrl) return;
          state.groupArtwork[loc] = state.designUrl;
          state.groupArtworkSources[loc] = "current";
          state.groupArtworkLabels[loc] = state.designPrompt
            ? "AI design: " + state.designPrompt.slice(0, 80) : "AI design";
          state.groupSavedPickerOpen = null;
          renderGroupArtworkList();
          break;
        }
        case "group-pick-upload": {
          const loc = a.getAttribute("data-location");
          state.groupSavedPickerOpen = null;
          const fileInput = document.querySelector('#order-wizard-root input[data-ow-group-file="' + loc + '"]');
          if (fileInput) fileInput.click();
          break;
        }
        case "group-toggle-saved": {
          const loc = a.getAttribute("data-location");
          state.groupSavedPickerOpen = state.groupSavedPickerOpen === loc ? null : loc;
          renderGroupArtworkList();
          break;
        }
        case "group-pick-saved-design": {
          const loc = a.getAttribute("data-location");
          const url = a.getAttribute("data-design-url");
          if (!url) return;
          state.groupArtwork[loc] = url;
          state.groupArtworkSources[loc] = "saved";
          const lib = getSavedDesigns();
          const m = lib.find(function (d) { return d.draftUrl === url; });
          state.groupArtworkLabels[loc] = m && m.prompt ? "Saved AI: " + m.prompt.slice(0, 80) : "Saved AI design";
          state.groupSavedPickerOpen = null;
          renderGroupArtworkList();
          break;
        }

        case "group-toggle-reorder": {
          state.groupReorderMode = !!a.checked;
          _syncGroupReorderUi();
          _setReorderTitle(state.groupReorderMode);
          const errEl = $("#ow-group-artwork-error");
          if (errEl) errEl.setAttribute("hidden", "");
          break;
        }

        case "group-artwork-continue": {
          const errEl = $("#ow-group-artwork-error");

          if (state.groupReorderMode) {
            const numEl = $("#ow-group-reorder-order-number");
            const detEl = $("#ow-group-reorder-details");
            state.groupReorderOrderNumber = ((numEl && numEl.value) || "").trim();
            state.groupReorderDetails = ((detEl && detEl.value) || "").trim();
            // Not required (2026-09-03, Roland's feedback) — an order
            // number, a client name, or nothing at all are all fine; the
            // toggle itself is the real signal, this field is a bonus for
            // staff. Was previously a hard gate here.
            if (errEl) errEl.setAttribute("hidden", "");
            pushChat("Reordering" + (state.groupReorderOrderNumber ? " " + state.groupReorderOrderNumber : ""), "user");
            pushChat("When do you need it?", "ai");
            renderRushPicker("group");
            showStep("group-rush");
            break;
          }

          const allLaterG = state.groupPrintLocations.length > 0 && state.groupPrintLocations.every(function (loc) { return state.groupArtworkSources[loc] === "later"; });
          if (allLaterG) {
            if (errEl) errEl.setAttribute("hidden", "");
            pushChat("I'll send the artwork after ordering", "user");
            pushChat("No worries — send it through after ordering and we'll email you a mockup to approve before anything is printed. When do you need it?", "ai");
            renderRushPicker("group");
            showStep("group-rush");
            break;
          }
          const missing = state.groupPrintLocations.filter(function (loc) {
            return !state.groupArtwork[loc] && state.groupArtworkSources[loc] !== "later";
          });
          if (missing.length) {
            if (errEl) {
              errEl.textContent = "Pick artwork for: " + missing.map(locationLabel).join(", ");
              errEl.removeAttribute("hidden");
            }
            return;
          }
          if (errEl) errEl.setAttribute("hidden", "");
          pushChat("Artwork sorted", "user");
          showStep("group-mockup");
          renderMockupStep("group").catch(function (e) { console.warn("[OrderWizard] group mockup render failed", e); });
          break;
        }

        case "group-mockup-continue":
          if (stepMockupLocation("group", 1)) break; // moved to the next print location's step
          pushChat("Design placed", "user");
          pushChat("When do you need it?", "ai");
          renderRushPicker("group");
          showStep("group-rush");
          break;

        case "group-mockup-back":
          if (stepMockupLocation("group", -1)) break; // moved back to the previous print location's step
          renderGroupArtworkList();
          showStep("group-artwork");
          break;

        case "group-mockup-reset":
          resetMockupArtwork("group").catch(function (e) { console.warn("[OrderWizard] group mockup reset failed", e); });
          break;

        case "group-mockup-maxsize":
          maxOutMockupPlacement("group");
          break;

        case "back-to-group-mockup": {
          if (state.groupReorderMode) { showStep("group-artwork"); break; }
          // Land on the LAST location — that's the one they were on right
          // before continuing to rush, so going back should return there.
          const gLocs = state.groupPrintLocations;
          setActiveLoc("group", gLocs[gLocs.length - 1]);
          showStep("group-mockup");
          break;
        }

        case "back-to-group-artwork":
          renderGroupArtworkList();
          showStep("group-artwork");
          break;

        case "back-to-group-sizes-from-confirm":
          // From confirm step, customer wants to add more sizes
          renderGroupSizeMatrices();
          showStep("group-sizes");
          break;

        case "group-add-to-cart":
          groupAddToCart();
          break;

        case "pick-category": {
          const cat = a.getAttribute("data-category");
          state.selectedCategory = cat;
          const label = cat === "tee" ? "Tees" : cat === "hoodie" ? "Hoodies" : "Both";
          pushChat(label, "user");
          const followup = cat === "tee"    ? "Here are our best-selling tees — a great place to start."
                         : cat === "hoodie" ? "Here are our best-selling hoodies — a great place to start."
                         : "Here are our best sellers — a great place to start.";
          pushChat(followup, "ai");
          showStep("solo-product");
          renderProductGrid();
          break;
        }

        case "back-to-category":
          showStep("solo-category");
          break;

        case "browse-all": {
          // Customer wants to leave the wizard and shop normally. Stash the
          // design info using the same sessionStorage key the existing
          // ai-design-attach.js / PDP flow reads on page load — so the design
          // is auto-attached wherever they land.
          ev.preventDefault();
          try {
            sessionStorage.setItem("ai_design_pending", JSON.stringify({
              url: state.designUrl || "",
              prompt: state.designPrompt || "",
              ts: Date.now()
            }));
          } catch (_) {}
          pushChat("Browse the full shop", "user");
          const cat = state.selectedCategory;
          const dest = cat === "hoodie" ? "/collections/hoodies"
                     : cat === "tee"    ? "/collections/tees"
                     : "/collections/all";
          window.location.href = dest;
          break;
        }

        case "product-pick": {
          const handle = a.getAttribute("data-product-handle");
          const product = state.products.find(function (p) { return p.handle === handle; });
          if (!product) return;
          state.selectedProduct = product;
          state.selectedColour = null;
          state.selectedSize = null;
          state.selectedVariantId = null;
          pushChat(product.title, "user");
          pushChat("Cool. Pick a colour and size.", "ai");
          renderVariantPicker();
          showStep("solo-variant");
          break;
        }

        case "pick-colour": {
          const colour = a.getAttribute("data-value");
          state.selectedColour = colour;
          state.selectedSize = null;
          state.selectedVariantId = null;
          renderVariantPicker();
          break;
        }

        case "pick-size": {
          if (a.disabled) return;
          const size = a.getAttribute("data-value");
          state.selectedSize = size;
          renderVariantPicker();
          if (state.selectedVariantId) clearStepError($("#ow-variant-error"));
          break;
        }

        case "back-to-product":
          showStep("solo-product");
          break;

        case "variant-continue": {
          if (!state.selectedVariantId) {
            const errEl = $("#ow-variant-error");
            if (!state.selectedColour) {
              showStepError(errEl, "Pick a colour first.");
              markMissing($("#ow-swatches"));
            } else if (!state.selectedSize) {
              showStepError(errEl, "Pick a size first.");
              markMissing($("#ow-sizes"));
            } else {
              showStepError(errEl, "That colour and size combo isn't available — try another.");
            }
            return;
          }
          clearStepError($("#ow-variant-error"));
          pushChat(state.selectedColour + " · " + state.selectedSize, "user");
          pushChat("Where would you like your prints to be?", "ai");
          renderPrintPicker();
          showStep("solo-print");
          break;
        }

        case "toggle-print": {
          const key = a.getAttribute("data-key");
          const idx = state.selectedPrintLocations.indexOf(key);
          if (idx >= 0) {
            state.selectedPrintLocations.splice(idx, 1);
          } else {
            // Mutual exclusion: Large Front and Front Left Chest are both
            // front-of-garment options — only one can be picked at a time.
            // Selecting one auto-deselects the other. Back stays independent.
            if (key === "large_front") {
              const flcIdx = state.selectedPrintLocations.indexOf("front_left_chest");
              if (flcIdx >= 0) state.selectedPrintLocations.splice(flcIdx, 1);
            } else if (key === "front_left_chest") {
              const lfIdx = state.selectedPrintLocations.indexOf("large_front");
              if (lfIdx >= 0) state.selectedPrintLocations.splice(lfIdx, 1);
            }
            state.selectedPrintLocations.push(key);
          }
          renderPrintPicker();
          if (state.selectedPrintLocations.length) clearStepError($("#ow-print-error"));
          break;
        }

        case "back-to-variant":
          showStep("solo-variant");
          break;

        case "print-continue":
          if (!state.selectedPrintLocations.length) {
            showStepError($("#ow-print-error"), "Pick at least one print location to continue.");
            markMissing($("#ow-print-options"));
            return;
          }
          clearStepError($("#ow-print-error"));
          pushChat(state.selectedPrintLocations.map(locationLabel).join(" + "), "user");
          // Pre-fill artwork: default each location to the AI design from chat
          // (if one exists). Customer can swap per-location on the next step.
          state.selectedPrintLocations.forEach(function (loc) {
            if (!state.printArtwork[loc]) {
              if (state.designUrl) {
                state.printArtwork[loc] = state.designUrl;
                state.artworkSources[loc] = "current";
                state.artworkLabels[loc] = state.designPrompt
                  ? "AI design: " + state.designPrompt.slice(0, 80)
                  : "AI design";
              } else {
                state.printArtwork[loc] = "";
                state.artworkSources[loc] = "";
                state.artworkLabels[loc] = "";
              }
            }
          });
          // Check for bundle comparison before artwork. Skip if nothing useful.
          {
            const bundleOpts = computeBundleOptions();
            if (bundleOpts && bundleOpts.length >= 2) {
              pushChat("Quick heads-up — there's a bundle deal that might save you money. Let me show you the options.", "ai");
              renderBundleDeals(bundleOpts);
              showStep("solo-bundle-deal");
              break;
            }
          }
          pushChat(artworkIntroMessage(), "ai");
          renderArtworkList();
          showStep("solo-artwork");
          break;

        case "pick-bundle-option": {
          // Click on a card in the bundle comparison. Toggle selection.
          const trigger = a;
          const opt = trigger._owBundleOpt;
          if (!opt) return;
          state.bundleSelection = opt;
          // Re-render to update the selected styling
          const lastOpts = computeBundleOptions();
          if (lastOpts) renderBundleDeals(lastOpts);
          break;
        }

        case "bundle-continue": {
          // Commit the customer's bundle pick and proceed to artwork
          const sel = state.bundleSelection;
          if (sel && sel.key !== "individuals") {
            pushChat("Going with " + sel.label + " ($" + sel.total.toFixed(2) + ")", "user");
          } else {
            pushChat("Keep it as " + (state.selectedQuantity || 1) + " individuals", "user");
          }
          pushChat("Sweet. " + artworkIntroMessage(), "ai");
          renderArtworkList();
          showStep("solo-artwork");
          break;
        }

        case "pick-later": {
          const loc = a.getAttribute("data-location");
          state.printArtwork[loc] = "";
          state.artworkSources[loc] = "later";
          state.artworkLabels[loc] = "Will upload after ordering";
          state.savedPickerOpen = null;
          renderArtworkList();
          break;
        }

        case "pick-current": {
          const loc = a.getAttribute("data-location");
          if (!state.designUrl) return;
          state.printArtwork[loc] = state.designUrl;
          state.artworkSources[loc] = "current";
          state.artworkLabels[loc] = state.designPrompt
            ? "AI design: " + state.designPrompt.slice(0, 80)
            : "AI design";
          state.savedPickerOpen = null;
          renderArtworkList();
          break;
        }

        case "pick-upload": {
          const loc = a.getAttribute("data-location");
          state.savedPickerOpen = null;
          const fileInput = document.querySelector('#order-wizard-root input[data-ow-file="' + loc + '"]');
          if (fileInput) fileInput.click();
          break;
        }

        case "toggle-saved": {
          const loc = a.getAttribute("data-location");
          state.savedPickerOpen = state.savedPickerOpen === loc ? null : loc;
          renderArtworkList();
          break;
        }

        case "pick-saved-design": {
          const loc = a.getAttribute("data-location");
          const url = a.getAttribute("data-design-url");
          if (!url) return;
          state.printArtwork[loc] = url;
          state.artworkSources[loc] = "saved";
          // Try to grab the prompt from the saved design list for a nicer label.
          const lib = getSavedDesigns();
          const match = lib.find(function (d) { return d.draftUrl === url; });
          state.artworkLabels[loc] = match && match.prompt
            ? "Saved AI: " + match.prompt.slice(0, 80)
            : "Saved AI design";
          state.savedPickerOpen = null;
          renderArtworkList();
          break;
        }

        case "toggle-reorder": {
          state.reorderMode = !!a.checked;
          _syncSoloReorderUi();
          _setReorderTitle(state.reorderMode);
          const errEl = document.getElementById("ow-artwork-error");
          if (errEl) errEl.setAttribute("hidden", "");
          break;
        }

        case "artwork-continue": {
          const errEl = document.getElementById("ow-artwork-error");

          if (state.reorderMode) {
            const numEl = document.getElementById("ow-reorder-order-number");
            const detEl = document.getElementById("ow-reorder-details");
            state.reorderOrderNumber = ((numEl && numEl.value) || "").trim();
            state.reorderDetails = ((detEl && detEl.value) || "").trim();
            // Not required (2026-09-03, Roland's feedback) — an order
            // number, a client name, or nothing at all are all fine; the
            // toggle itself is the real signal, this field is a bonus for
            // staff. Was previously a hard gate here.
            if (errEl) errEl.setAttribute("hidden", "");
            pushChat("Reordering" + (state.reorderOrderNumber ? " " + state.reorderOrderNumber : ""), "user");
            pushChat("When do you need it?", "ai");
            renderRushPicker("solo");
            showStep("solo-rush");
            break;
          }

          const missing = state.selectedPrintLocations.filter(function (loc) {
            return !state.printArtwork[loc] && state.artworkSources[loc] !== "later";
          });
          const allLater = state.selectedPrintLocations.length > 0 && state.selectedPrintLocations.every(function (loc) { return state.artworkSources[loc] === "later"; });
          if (!missing.length && allLater) {
            if (errEl) errEl.setAttribute("hidden", "");
            pushChat("I'll send the artwork after ordering", "user");
            pushChat("No worries — send it through after ordering and we'll email you a mockup to approve before anything is printed. When do you need it?", "ai");
            renderRushPicker("solo");
            showStep("solo-rush");
            break;
          }
          if (missing.length) {
            if (errEl) {
              errEl.textContent = "Pick artwork for: " + missing.map(locationLabel).join(", ");
              errEl.removeAttribute("hidden");
            }
            return;
          }
          if (errEl) errEl.setAttribute("hidden", "");
          pushChat("Artwork sorted", "user");
          showStep("solo-mockup");
          renderMockupStep("solo").catch(function (e) { console.warn("[OrderWizard] mockup render failed", e); });
          break;
        }

        case "mockup-continue":
          if (stepMockupLocation("solo", 1)) break; // moved to the next print location's step
          pushChat("Design placed", "user");
          pushChat("When do you need it?", "ai");
          renderRushPicker("solo");
          showStep("solo-rush");
          break;

        case "mockup-back":
          if (stepMockupLocation("solo", -1)) break; // moved back to the previous print location's step
          showStep("solo-artwork");
          break;

        case "mockup-reset":
          resetMockupArtwork("solo").catch(function (e) { console.warn("[OrderWizard] mockup reset failed", e); });
          break;

        case "mockup-maxsize":
          maxOutMockupPlacement("solo");
          break;

        case "back-to-mockup": {
          if (state.reorderMode) { showStep("solo-artwork"); break; }
          // Land on the LAST location — that's the one they were on right
          // before continuing to rush, so going back should return there.
          const sLocs = state.selectedPrintLocations;
          setActiveLoc("solo", sLocs[sLocs.length - 1]);
          showStep("solo-mockup");
          break;
        }

        case "pick-rush": {
          // Block selection of a locked option (e.g. same-day on non-core
          // products) and explain what they need to change.
          if (a.getAttribute("data-locked-core-only") === "1") {
            pushChat(
              "Same-day production only works with our core range — those products are in stock, ready to print straight away. Go back and pick a core-range product (look for the ⚡ Ships fast badge) if you need it same day.",
              "ai"
            );
            return;
          }
          state.rushSelection = a.getAttribute("data-key");
          // Re-render the picker so the selected card lights up + show price update
          renderRushPicker(state.audience === "group" ? "group" : "solo");
          break;
        }

        case "rush-continue": {
          const opt = (CONFIG.rushOptions || []).find(function (o) { return o.key === state.rushSelection; });
          const optLabel = opt ? opt.label : "Standard";
          pushChat(optLabel, "user");
          pushChat("Have a look — ready to add to cart?", "ai");
          renderConfirmSummary();
          showStep("solo-confirm");
          break;
        }

        case "group-rush-continue": {
          const opt = (CONFIG.rushOptions || []).find(function (o) { return o.key === state.rushSelection; });
          const optLabel = opt ? opt.label : "Standard";
          pushChat(optLabel, "user");
          pushChat("Have a look — ready to add to cart?", "ai");
          renderGroupConfirmSummary();
          showStep("group-confirm");
          break;
        }

        case "back-to-print":
          showStep("solo-print");
          break;

        case "back-to-artwork":
          showStep("solo-artwork");
          break;

        case "add-to-cart":
          addToCart();
          break;

        case "qty-inc": {
          state.selectedQuantity = Math.min(200, (state.selectedQuantity || 1) + 1);
          const inp = $("#ow-qty-val");
          if (inp) inp.value = String(state.selectedQuantity);
          renderTierCard();
          break;
        }

        case "qty-dec": {
          state.selectedQuantity = Math.max(1, (state.selectedQuantity || 1) - 1);
          const inp2 = $("#ow-qty-val");
          if (inp2) inp2.value = String(state.selectedQuantity);
          renderTierCard();
          break;
        }

        case "add-another": {
          // Keep the design + artwork choices in state, but reset the
          // product/colour/size/print/qty so the customer picks a fresh
          // item. The cart already has the first item — this just adds
          // another with potentially the same design.
          state.selectedProduct = null;
          state.selectedColour = null;
          state.selectedSize = null;
          state.selectedVariantId = null;
          state.selectedQuantity = 1;
          state.selectedPrintLocations = [];
          state.selectedCategory = null;
          // Keep state.printArtwork as the customer's previous picks —
          // when they choose new print locations the wizard will reuse
          // the same artwork as a sensible default.
          pushChat("Add another item", "user");
          pushChat("Cool — what's next? Tees, hoodies, or both?", "ai");
          showStep("solo-category");
          break;
        }

        case "goto-cart":
          window.location.href = "/cart";
          break;
      }
    });

    // Manual edits to the quantity input (solo flow)
    document.addEventListener("input", function (ev) {
      const t = ev.target;
      if (!t) return;

      // Solo qty stepper
      if (t.id === "ow-qty-val") {
        const v = parseInt(t.value, 10);
        state.selectedQuantity = isNaN(v) ? 1 : Math.max(1, Math.min(200, v));
        renderTierCard();
        return;
      }

      // Group totals inputs
      if (t.id === "ow-group-adults") {
        const v = parseInt(t.value, 10);
        state.groupAdults = isNaN(v) ? 0 : Math.max(0, Math.min(500, v));
        renderGroupTierCard();
        return;
      }
      if (t.id === "ow-group-kids") {
        const v = parseInt(t.value, 10);
        state.groupKids = isNaN(v) ? 0 : Math.max(0, Math.min(500, v));
        renderGroupTierCard();
        return;
      }

      // Group size matrix inputs
      if (t.hasAttribute("data-ow-size-input")) {
        const type = t.getAttribute("data-product-type");
        const size = t.getAttribute("data-size");
        if (!type || !size) return;
        const v = parseInt(t.value, 10);
        if (!state.groupSizes[type]) state.groupSizes[type] = {};
        state.groupSizes[type][size] = isNaN(v) ? 0 : Math.max(0, Math.min(500, v));
        updateGroupProgress();
        return;
      }
    });

    document.addEventListener("keydown", function (ev) {
      if (state.open && ev.key === "Escape") closeModal();
    });
  }

  window.OrderWizard = { open: openModal, close: closeModal, state: state, config: CONFIG, gate: GATE };
})();


/* ==========================================================================
   === PDP WIZARD LAUNCHER ===================================================

   Injects a prominent "Start your order with our wizard" button onto every
   product detail page (PDP). The button uses data-wizard-trigger so the
   existing global click handler picks it up and opens the wizard modal —
   no navigation, no page reload.

   Respects a 30-day "classic mode" preference stored in localStorage. If a
   customer clicks the "use classic ordering" escape link, we suppress the
   launcher on all PDPs for 30 days so they get the unmodified theme flow.
   ========================================================================== */
(function () {
  // DISABLED (2026-09-02, order-path cleanup brief): product pages had
  // three competing ways to start an order — this banner + "or use
  // classic ordering", the floating pill below, and the inline builder
  // that actually converts. The inline builder (ft-order-builder ->
  // ft-order-wizard's "Next: Review") is now the one path; this launcher
  // and its classic-mode escape hatch are switched off rather than
  // deleted, so re-enabling later (or restoring it on a different page
  // type) is a one-line revert. window.OrderWizard itself (the modal) is
  // untouched — only this entry point onto product pages is gone.
  return;

  // Matches both the canonical /products/<handle> URL and the
  // /collections/<handle>/products/<handle> URL a customer actually lands
  // on when clicking through from a collection grid — the anchored
  // ^/products/ version was missing every collection-scoped visit, which
  // is most real PDP traffic.
  if (!/\/products\/[^/?#]+\/?$/.test(location.pathname)) return;

  const CLASSIC_PREF_KEY = "wizard_classic_mode_until";
  const CLASSIC_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  // Honour the preference — bail before doing any DOM work so the theme's
  // own product form is untouched.
  function isInClassicMode() {
    try {
      const until = parseInt(localStorage.getItem(CLASSIC_PREF_KEY) || "0", 10);
      return until > Date.now();
    } catch (_) { return false; }
  }
  if (isInClassicMode()) return;

  // Find the theme's product Add-to-cart button. Themes vary wildly so we
  // try a battery of selectors in priority order. First hit wins. Returns
  // the button element OR null if nothing matches.
  function findAddToCartButton() {
    const selectors = [
      // Size-quantity-matrix pages replace the theme's standard buy button
      // with their own — check for it FIRST so the wizard CTA anchors above
      // the matrix on printable PDPs (was silently failing to inject there).
      '.size-qty-matrix__btn',
      'product-form button[name="add"]',
      '[data-product-form] button[name="add"]',
      'form[action*="/cart/add"] button[name="add"]',
      'form[action*="/cart/add"] button[type="submit"]',
      'button[name="add"]',
      '.product-form__submit',
      '.product-form__buttons button[type="submit"]',
      '.product__buy button[type="submit"]'
    ];
    for (let i = 0; i < selectors.length; i++) {
      const el = document.querySelector(selectors[i]);
      if (el) return el;
    }
    return null;
  }

  // Inject scoped styles once. Kept inline so this whole feature is contained
  // in ai-designer.js — no theme.liquid or external CSS file required.
  function injectStyles() {
    if (document.getElementById("pdp-wizard-cta-styles")) return;
    const s = document.createElement("style");
    s.id = "pdp-wizard-cta-styles";
    s.textContent =
      "[data-pdp-wizard-launcher]{margin:18px 0;display:flex;flex-direction:column;align-items:stretch;gap:8px;font-family:inherit;animation:pdp-wiz-in 0.32s cubic-bezier(.2,.7,.3,1)}" +
      "@keyframes pdp-wiz-in{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}" +
      ".pdp-wizard-cta{width:100%;padding:15px 22px;background:linear-gradient(135deg,#ff6b00,#ff8c33);color:#fff;border:0;border-radius:10px;font-size:16px;font-weight:700;cursor:pointer;box-shadow:0 4px 14px rgba(255,107,0,0.28);transition:transform .15s ease, box-shadow .15s ease;letter-spacing:.01em;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:8px}" +
      ".pdp-wizard-cta:hover{transform:translateY(-1px);box-shadow:0 6px 18px rgba(255,107,0,0.36);background:linear-gradient(135deg,#e85f00,#ff7a1a)}" +
      ".pdp-wizard-cta__icon{font-size:18px}" +
      ".pdp-wizard-sub{font-size:12px;color:#5a5a5a;text-align:center;margin:0;line-height:1.4;font-family:inherit}" +
      ".pdp-wizard-sub strong{color:#1a1a1a;font-weight:700}" +
      ".pdp-classic-link{font-size:12px;color:#9a9a9a;text-decoration:none;text-align:center;padding:4px 0;font-family:inherit;cursor:pointer;background:none;border:0;display:inline-block;margin:0 auto}" +
      ".pdp-classic-link:hover{color:#5a5a5a;text-decoration:underline}";
    document.head.appendChild(s);
  }

  function buildLauncher() {
    const wrap = document.createElement("div");
    wrap.setAttribute("data-pdp-wizard-launcher", "1");
    wrap.innerHTML =
      '<button type="button" data-wizard-trigger class="pdp-wizard-cta">' +
        '<span class="pdp-wizard-cta__icon" aria-hidden="true">✨</span>' +
        '<span>Start your order with our wizard</span>' +
      '</button>' +
      '<p class="pdp-wizard-sub">Pick sizes, see <strong>bundle deals</strong>, and add your design — all in one place.</p>' +
      '<button type="button" class="pdp-classic-link" data-pdp-classic-link>or use classic ordering →</button>';

    wrap.querySelector("[data-pdp-classic-link]").addEventListener("click", function (e) {
      e.preventDefault();
      try {
        localStorage.setItem(CLASSIC_PREF_KEY, String(Date.now() + CLASSIC_DURATION_MS));
      } catch (_) {}
      // Remove launcher with a quick fade-out then scroll to where the
      // classic Add to cart was — so they don't lose their place.
      wrap.style.transition = "opacity .18s ease";
      wrap.style.opacity = "0";
      setTimeout(function () {
        const fallbackTarget = findAddToCartButton();
        if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
        if (fallbackTarget) {
          try { fallbackTarget.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
        }
      }, 200);
    });

    return wrap;
  }

  // Once the customer has clicked/changed/typed anything in the buy box
  // (picked a size, a colour, a quantity, ticked the reorder box, whatever),
  // they've committed to this path — cross-promoting the other ordering
  // system at that point is just noise, so the banner fades out for the
  // rest of this pageview. taskStarted also blocks tryInject()/the
  // reinject observer below from bringing it straight back the moment its
  // own removal triggers a DOM mutation.
  //
  // `anchor` alone isn't reliable here: on printable PDPs the real widget
  // (size-qty-matrix, print-location-selector, the mockup builder) is often
  // rendered as a separate sibling section, NOT nested inside whatever form
  // findAddToCartButton() happened to resolve `anchor` from — confirmed live
  // on Mens Stencil Hood 5102, where .size-qty-matrix sits completely
  // outside the theme's own product-single__form. So this also attaches to
  // every known widget container that's actually present on the page.
  const KNOWN_WIDGET_SELECTORS = [
    ".size-qty-matrix",
    ".pls-wrap",
    "[data-ft-order-builder]",
    "[data-ft-mockup-builder]"
  ];
  let taskStarted = false;
  function watchForTaskStart(anchor, launcher) {
    const targets = [anchor];
    KNOWN_WIDGET_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (targets.indexOf(el) < 0) targets.push(el);
      });
    });

    function hideLauncher() {
      taskStarted = true;
      targets.forEach(function (el) {
        el.removeEventListener("click", onInteract, true);
        el.removeEventListener("change", onInteract, true);
        el.removeEventListener("input", onInteract, true);
      });
      if (!launcher.parentNode) return;
      launcher.style.transition = "opacity .18s ease";
      launcher.style.opacity = "0";
      setTimeout(function () {
        if (launcher.parentNode) launcher.parentNode.removeChild(launcher);
      }, 200);
    }
    function onInteract() { hideLauncher(); }
    targets.forEach(function (el) {
      el.addEventListener("click", onInteract, true);
      el.addEventListener("change", onInteract, true);
      el.addEventListener("input", onInteract, true);
    });
  }

  // Try to inject. Bails if we can't find a sensible anchor.
  function tryInject() {
    if (taskStarted) return true; // customer's mid-task — stay hidden
    if (document.querySelector("[data-pdp-wizard-launcher]")) return true; // already done
    const addBtn = findAddToCartButton();
    if (!addBtn) return false;
    // Find the closest <form> so the launcher sits ABOVE the entire buy box
    // (variant picker + add to cart) rather than wedged inside the form.
    // Falls back to the button's parent if there's no form (rare but possible).
    const anchor = addBtn.closest("form") || addBtn.parentElement;
    if (!anchor || !anchor.parentNode) return false;
    injectStyles();
    const launcher = buildLauncher();
    anchor.parentNode.insertBefore(launcher, anchor);
    watchForTaskStart(anchor, launcher);
    return true;
  }

  // Themes often render the product form async or after a hydration delay.
  // Retry a few times before giving up so we work on slow + dynamic themes.
  let attempts = 0;
  const MAX_ATTEMPTS = 20; // 20 × 250ms = 5 seconds
  function attemptInject() {
    if (tryInject()) return;
    if (++attempts >= MAX_ATTEMPTS) return;
    setTimeout(attemptInject, 250);
  }

  // ── Printable-product gate ──
  // Merch products (event merch, marketplace items, pre-printed stock)
  // don't take custom prints, so offering the order wizard on them is
  // confusing. Rule: any product with a tag containing "merch"
  // (case-insensitive — covers "Event Merch", "60ft Merch", etc.)
  // gets NO wizard UI: no PDP launcher, no floating "Start your order"
  // button. Printable products keep everything.
  //
  // On fetch failure we fail OPEN (show the wizard) so a transient
  // product.js hiccup can't hide the wizard from real printable products.
  function startWithGate() {
    const handle = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || "";
    if (!handle) { begin(); return; }
    fetch("/products/" + handle + ".js", { headers: { "Accept": "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (product) {
        const tags = (product && product.tags ? product.tags : []).map(function (t) {
          return String(t).toLowerCase();
        });
        const isMerch = tags.some(function (t) { return t.indexOf("merch") !== -1; });
        if (isMerch) {
          // Suppress every wizard entry point on this page:
          // 1. Flag checked by the wizard's own injectLaunchButton (and any
          //    future injectors) before creating UI.
          window.__owSuppressLauncher = true;
          // 2. Remove the floating launcher if the wizard IIFE already
          //    injected it before this fetch resolved.
          const floatBtn = document.getElementById("ow-launch-btn");
          if (floatBtn && floatBtn.parentNode) floatBtn.parentNode.removeChild(floatBtn);
          // 3. Never inject the PDP launcher (just don't call begin()).
          return;
        }
        begin();
      })
      .catch(function () { begin(); });
  }

  function begin() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", attemptInject);
    } else {
      attemptInject();
    }

    // Watch for theme features that re-render the product form (e.g. variant
    // change replacing the buy box, quick-view modals on PDP). If our launcher
    // gets removed, re-inject it.
    const reinjectObs = new MutationObserver(function () {
      if (taskStarted) return;
      if (!document.querySelector("[data-pdp-wizard-launcher]")) {
        // Only re-inject if we still have a target
        if (findAddToCartButton()) attemptInject();
      }
    });
    reinjectObs.observe(document.body, { childList: true, subtree: true });
    // Stop observing after 2 minutes so we don't churn CPU forever.
    setTimeout(function () { reinjectObs.disconnect(); }, 2 * 60 * 1000);
  }

  startWithGate();
})();


/* ==========================================================================
   === CART RUSH-FEE CONSOLIDATOR ===========================================

   The wizard adds the rush production fee to the cart as a $1 product
   multiplied by the dollar amount of the fee (e.g. $225 fee = 225 × $1).
   Without this script, customers see a wall of "225 × Rush order fee" rows
   in the cart which looks unprofessional.

   What this does:
     1. Polls /cart.js to read the actual cart contents.
     2. If there are rush-fee line items, hides their cart rows via CSS
        (matching by product handle 'rush-order-fee', cross-theme safe).
     3. Injects a single "Rush production fee — $N" row in the cart's
        line-items area showing the summed total.
     4. Watches the DOM so cart drawers, ajax cart updates, and section
        re-renders don't bring the hidden rows back.

   Runs on every page so cart drawers triggered from anywhere are covered.
   ========================================================================== */
(function () {
  // Product handles that get consolidated into a single readable summary
  // row instead of dozens of $1 line items showing to the customer.
  const RUSH_HANDLE = "rush-order-fee";
  const GANG_SHEET_HANDLE = "dtf-gang-sheet";
  const CONSOLIDATED_HANDLES = [RUSH_HANDLE, GANG_SHEET_HANDLE];
  const STYLE_ID = "ow-rush-consolidator-styles";
  const SUMMARY_ATTR = "data-ow-rush-summary";
  const RUSH_SUMMARY_KEY = "rush";
  const GANG_SUMMARY_KEY = "gang-sheet";

  let lastCartFingerprint = "";
  let pollInterval = null;

  // Build a big union CSS selector that hides ALL consolidated product rows.
  // Themes use various naming conventions (BEM, dash, snake, data-attr) so
  // we cover them all here. hideConsolidatedRowsViaJs() is a JS fallback for
  // any pattern the :has() selector doesn't catch.
  function injectHideStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const s = document.createElement("style");
    s.id = STYLE_ID;
    const rowSelectors = [];
    CONSOLIDATED_HANDLES.forEach(function (h) {
      const link = 'a[href*="/products/' + h + '"]';
      // [class*="cart__item"] matches BOTH the intended single-row class
      // AND this theme's own PLURAL wrapper class "cart__items" (it's a
      // pure substring match, no word-boundary) — combined with :has(),
      // which matches any ancestor containing the link anywhere in its
      // subtree, that hid the entire cart__items container along with the
      // one row it was meant to hide (the whole line-item list vanished
      // behind a correct-looking total, found live 2026-08-27). The
      // :not([class*="...s"]) guard on every "item" pattern here excludes
      // that plural collision without losing the substring match itself
      // (needed since real classes are often compound, e.g.
      // "cart__item--product").
      rowSelectors.push(
        'tr:has(' + link + ')',
        'li:has(' + link + ')',
        '[data-cart-item]:has(' + link + ')',
        '[class*="cart__item"]:not([class*="cart__items"]):has(' + link + ')',
        '[class*="cart-item"]:not([class*="cart-items"]):has(' + link + ')',
        '[class*="cart_item"]:not([class*="cart_items"]):has(' + link + ')',
        '[class*="line-item"]:not([class*="line-items"]):has(' + link + ')',
        '[class*="line__item"]:not([class*="line__items"]):has(' + link + ')',
        '[class*="line_item"]:not([class*="line_items"]):has(' + link + ')',
        '[class*="CartItem"]:not([class*="CartItems"]):has(' + link + ')',
        '[data-product-handle="' + h + '"]',
        '[data-handle="' + h + '"]'
      );
    });
    s.textContent =
      rowSelectors.join(",") + "{display:none!important}" +
      // Style for the injected summary rows.
      '[' + SUMMARY_ATTR + ']{display:flex;justify-content:space-between;align-items:center;padding:14px 16px;margin:8px 0;border:1px solid #e5e7eb;border-radius:10px;background:#fff8f0;font:14px/1.4 inherit;color:#1a1a1a}' +
      '[' + SUMMARY_ATTR + '="gang-sheet"]{background:#f0f4ff;border-color:#c7d2fe}' +
      '[' + SUMMARY_ATTR + '] .ow-rush-summary__label{font-weight:600;display:flex;align-items:center;gap:8px}' +
      '[' + SUMMARY_ATTR + '] .ow-rush-summary__icon{width:24px;height:24px;border-radius:50%;background:#ff6b00;color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700}' +
      '[' + SUMMARY_ATTR + '="gang-sheet"] .ow-rush-summary__icon{background:#4f46e5}' +
      '[' + SUMMARY_ATTR + '] .ow-rush-summary__sub{font-size:12px;color:#5a5a5a;display:block;margin-top:2px;font-weight:400}' +
      '[' + SUMMARY_ATTR + '] .ow-rush-summary__price{font-weight:700;font-size:16px}' +
      // Delivery speed picker
      '[' + SPEED_PICKER_ATTR + ']{margin:12px 0;padding:14px 16px;border:1px solid #e5e7eb;border-radius:10px;background:#fafafa}' +
      '.ow-speed-picker__title{font-weight:700;font-size:14px;margin-bottom:10px}' +
      '.ow-speed-picker__opts{display:flex;flex-direction:column;gap:8px}' +
      '.ow-speed-opt{display:flex;align-items:center;justify-content:space-between;gap:12px;width:100%;padding:10px 14px;border:2px solid #e5e7eb;border-radius:8px;background:#fff;text-align:left;font:inherit;cursor:pointer;transition:border-color .12s ease}' +
      '.ow-speed-opt:hover:not(.is-disabled){border-color:#ff6b00}' +
      '.ow-speed-opt.is-selected{border-color:#ff6b00;background:#fff7ed}' +
      '.ow-speed-opt.is-disabled{opacity:.5;cursor:not-allowed;background:#f5f5f5}' +
      '.ow-speed-opt__label{font-size:14px;font-weight:700;color:#1a1a1a}' +
      '.ow-speed-opt__desc{display:block;font-size:12px;color:#5a5a5a;margin-top:2px}' +
      '.ow-speed-opt__pricecol{text-align:right;white-space:nowrap}' +
      '.ow-speed-opt__price{font-size:15px;font-weight:700;color:#ff6b00}' +
      '.ow-speed-opt__note{display:block;font-size:11px;color:#5a5a5a;font-weight:600}' +
      '.ow-speed-opt__free{font-size:14px;font-weight:700;color:#5a5a5a;letter-spacing:.04em}';
    document.head.appendChild(s);
  }

  /**
   * Fallback: walk up from every rush-fee OR gang-sheet anchor and hide the
   * closest line-item ancestor by name OR by structure. Works even when
   * class names don't match any of our CSS patterns.
   */
  function hideConsolidatedRowsViaJs() {
    CONSOLIDATED_HANDLES.forEach(function (handle) {
      const links = document.querySelectorAll('a[href*="/products/' + handle + '"]');
      links.forEach(function (a) {
        // Try named ancestors first
        // Same plural-collision guard as injectHideStyles() above — without
        // it, .closest() can walk past the real row and hide the whole
        // "...items" wrapper if the row's own class doesn't match first.
        const named = a.closest(
          'tr, li, [data-cart-item],' +
          '[class*="cart__item"]:not([class*="cart__items"]),' +
          '[class*="cart-item"]:not([class*="cart-items"]),' +
          '[class*="cart_item"]:not([class*="cart_items"]),' +
          '[class*="line-item"]:not([class*="line-items"]),' +
          '[class*="line__item"]:not([class*="line__items"]),' +
          '[class*="line_item"]:not([class*="line_items"]),' +
          '[class*="CartItem"]:not([class*="CartItems"])'
        );
        if (named) {
          named.style.display = "none";
          return;
        }
        // Fallback: walk up until we hit a block element that's not the body
        // or an obvious form container, then hide it.
        let el = a.parentElement;
        let hops = 0;
        while (el && hops < 8) {
          const tag = el.tagName;
          const role = el.getAttribute("role");
          if (tag === "BODY" || tag === "FORM" || role === "main") break;
          if (el.children.length >= 2 && (tag === "DIV" || tag === "ARTICLE")) {
            el.style.display = "none";
            return;
          }
          el = el.parentElement;
          hops++;
        }
      });
    });
  }

  // Find a reasonable place to insert the summary row — try common cart
  // line-items containers in priority order, fall back to the form.
  function findCartContainer() {
    // Preferred containers — right where line items normally sit.
    const preferred = [
      'form[action="/cart"] [class*="line-items"]',
      'form[action="/cart"] [class*="cart-items"]',
      'form[action="/cart"] tbody',
      'form[action="/cart"] ul',
      '[class*="cart-drawer"] [class*="line-items"]',
      '[class*="cart-drawer"] [class*="cart-items"]',
      '[class*="cart-drawer"] ul',
      // Additional patterns for common Dawn / Impulse / OS 2.0 themes
      'cart-items',
      '[data-cart-items]',
      'main [class*="cart__contents"]',
      'main [class*="cart-contents"]',
    ];
    for (let i = 0; i < preferred.length; i++) {
      const el = document.querySelector(preferred[i]);
      if (el) return el;
    }
    // Fallback containers — page-level. Better than null; the summary row
    // will still render even if a bit outside the ideal spot.
    const fallback = [
      'form[action="/cart"]',
      '[class*="cart-drawer"]',
      '.cart',
      '#cart',
      // Nuclear option: use main content area on /cart itself. Prevents the
      // "empty cart with only a total" bug that fired when none of the
      // theme-specific selectors matched Roland's theme.
      location.pathname === '/cart' ? 'main' : null,
      location.pathname === '/cart' ? '[role="main"]' : null,
    ].filter(Boolean);
    for (let i = 0; i < fallback.length; i++) {
      const el = document.querySelector(fallback[i]);
      if (el) return el;
    }
    return null;
  }

  function formatMoney(cents) {
    return "$" + (cents / 100).toFixed(2);
  }

  /**
   * Detect whether a cart line item is a print add-on that must stay
   * locked to the garment count. Covers three signals so we catch both
   * wizard and classic-PDP-flow orders:
   *   1. "Order reference" property — the wizard's marker
   *   2. Product handle patterns — e.g. -print, -chest-print, full-size-print
   *   3. Product title patterns — "Front Left Chest Print", "Back Centre Full size print"
   *
   * Guards against false positives on actual printed garments ("Printed
   * Front and Back Tees" is a shirt, not an add-on) by excluding items
   * whose title also mentions common apparel keywords.
   */
  function isPrintAddOnItem(item) {
    if (!item) return false;
    // 1. Wizard-added marker — most reliable when present.
    if (item.properties && item.properties["Order reference"]) return true;
    const handle = String(item.handle || "").toLowerCase();
    const title = String(item.title || "").toLowerCase();
    // 2. Handle patterns (Roland's actual print product handles).
    if (/(^|[-_])(print)([-_]|$)|full-size-print|centre-full-size-print|left-chest-print|chest-print|cap-print|-print-(front|back|left|right|centre|center|chest)/.test(handle)) {
      // But avoid actual apparel that just uses "print" in the handle
      if (!/(tee|t-shirt|shirt|hood|hoodie|jumper|singlet|long-sleeve|longsleeve)/.test(handle)) {
        return true;
      }
    }
    // 3. Title patterns — must have "print" plus a placement keyword,
    //    and NOT be an apparel item.
    const hasPrintWord = /\bprint\b|\bprints?\b/.test(title);
    const hasPlacement = /(front\s*left\s*chest|left\s*chest|front\s*centre|front\s*center|back\s*centre|back\s*center|back\s*full|full\s*front|large\s*front|cap\s*print)/.test(title);
    const looksLikeApparel = /(t-?shirt|\btee\b|hoodie|\bhood\b|jumper|singlet|long\s*sleeve|\bshirt\b|beanie|cap\b(?!\s*print))/.test(title);
    if (hasPrintWord && hasPlacement && !looksLikeApparel) return true;
    return false;
  }

  // Read /cart.js + collect BOTH rush-fee totals AND the handles of any
  // print add-on line items so downstream can hide/lock them appropriately.
  // Catches both wizard-added AND classic PDP-added print add-ons via
  // isPrintAddOnItem().
  // Fetches /cart.js and pulls out everything the consolidator needs:
  //   rushTotal      — cents, sum of all rush-fee line items
  //   rushCount      — total qty of rush-fee items
  //   gangSheets     — array of { key, sheetWidth, sheetLength, uniqueDesigns,
  //                    totalTransfers, priceCents, note } (one per gang sheet
  //                    line in the cart). Multi-sheet orders show one row each.
  //   printHandles   — handles of any print add-on line items (locking uses this)
  //   fingerprint    — cheap change-detector so we only re-render on actual delta
  async function fetchRushTotal() {
    const empty = { total: 0, count: 0, gangSheets: [], printHandles: [], fingerprint: "", eligibleSubtotal: 0, garmentHandles: [], rushLineKey: null, rushTierLabel: "", itemCount: 0 };
    try {
      const r = await fetch("/cart.js", { headers: { "Accept": "application/json" } });
      if (!r.ok) return empty;
      const cart = await r.json();
      const items = cart.items || [];
      const rushItems = items.filter(function (item) {
        return item.handle === RUSH_HANDLE || (item.product_handle === RUSH_HANDLE);
      });
      const total = rushItems.reduce(function (s, item) { return s + (item.line_price || 0); }, 0);
      const count = rushItems.reduce(function (s, item) { return s + (item.quantity || 0); }, 0);
      // Everything a customer will need charged for delivery-speed selection
      // to work from the CART, not just the wizard: the subtotal the % rate
      // applies to (every real line except the rush fee itself and the DTF
      // gang sheet — gang sheets are a separately-fulfilled product, same as
      // the wizard's own computePreDiscountSubtotal never counted them),
      // which real garment products are in the cart (for the same-day
      // core-range eligibility check), and the existing rush line's key +
      // selected tier label so the picker can pre-select it and update
      // in-place via /cart/change.js instead of adding a second rush line.
      //
      // item.line_price from /cart.js is the FULL, PRE-discount price —
      // Hulk Discounts (the bulk-quantity discount app) applies its %
      // entirely client-side by rewriting the rendered DOM text, never
      // touching the underlying cart data, so /cart.js has no idea a
      // discount exists at all. Confirmed live 2026-08-26: a 20-shirt
      // cart discounted to $319.80 on screen still quoted a rush fee of
      // 25% × $426.40 (the undiscounted total) = $107, overcharging the
      // customer relative to what they're actually paying for the
      // shirts. Fixed by reading Hulk's own per-line discounted price
      // straight out of the DOM (it tags each line with the same `key`
      // /cart.js uses, in a `[data-key] .discounted_price` span) and
      // using that instead of item.line_price whenever it's present;
      // falls back to the raw price for any line Hulk hasn't discounted
      // (or hasn't rendered yet), so this only ever corrects the total
      // downward, never breaks it.
      function discountedLineTotal(item) {
        try {
          const row = document.querySelector('[data-key="' + item.key + '"] .discounted_price');
          if (row) {
            const unit = parseFloat(row.textContent.replace(/[^0-9.]/g, ""));
            if (isFinite(unit) && unit > 0) return Math.round(unit * 100) * (item.quantity || 0);
          }
        } catch (e) { /* fall through to raw price */ }
        return item.line_price || 0;
      }
      let eligibleSubtotal = 0;
      const garmentHandles = [];
      items.forEach(function (item) {
        if (item.handle === RUSH_HANDLE || item.product_handle === RUSH_HANDLE) return;
        if (item.handle === GANG_SHEET_HANDLE || item.product_handle === GANG_SHEET_HANDLE) return;
        eligibleSubtotal += discountedLineTotal(item);
        if (!isPrintAddOnItem(item)) {
          const h = item.handle || item.product_handle;
          if (h && garmentHandles.indexOf(h) < 0) garmentHandles.push(h);
        }
      });
      const rushLine = rushItems[0] || null;
      const rushLineKey = rushLine ? rushLine.key : null;
      const rushTierLabel = rushLine && rushLine.properties ? (rushLine.properties["Production speed"] || "") : "";

      // Gang sheet lines — one summary per line item so multi-sheet orders
      // show each sheet distinctly. Read the properties the builder wrote
      // at add-to-cart (see gang-sheet-builder.js addToCart()).
      const gangSheets = items
        .filter(function (item) {
          return item.handle === GANG_SHEET_HANDLE || item.product_handle === GANG_SHEET_HANDLE;
        })
        .map(function (item) {
          const p = item.properties || {};
          // Prefer the "Display price" the builder wrote (matches whatever
          // inc-GST / ex-GST mode the customer saw). Fall back to raw
          // line_price if the property is missing (older orders).
          const displayCents = parseInt(p["Display price cents"], 10);
          const displayLabel = p["Display price"] || "";
          return {
            key: item.key,
            sheetWidth: p["Sheet width"] || "",
            sheetLength: p["Sheet length used"] || "",
            uniqueDesigns: p["Unique designs"] || "",
            totalTransfers: p["Total transfers"] || "",
            rate: p["Rate applied"] || "",
            note: p["Note"] || "",
            priceCents: item.line_price || 0,
            displayLabel: displayLabel,
            displayCents: isFinite(displayCents) && displayCents > 0 ? displayCents : 0,
          };
        });

      const printHandles = [];
      items.forEach(function (item) {
        if (isPrintAddOnItem(item) && item.handle && printHandles.indexOf(item.handle) < 0) {
          printHandles.push(item.handle);
        }
      });
      // Fingerprint includes gang sheet lengths + prices so any change
      // (adding a sheet, editing qty, removing) triggers a fresh render.
      // Also includes eligibleSubtotal + garment handles so the delivery-
      // speed picker re-renders (new tier prices, core-range lock state)
      // whenever the customer's cart contents actually change.
      const gsFp = gangSheets.map(function (g) { return g.key + "@" + g.priceCents; }).join(";");
      const fingerprint = total + ":" + count + ":" + printHandles.join(",") + ":" + gsFp + ":" + (cart.item_count || 0) + ":" + eligibleSubtotal + ":" + garmentHandles.join(",");
      return {
        total: total,
        count: count,
        gangSheets: gangSheets,
        printHandles: printHandles,
        fingerprint: fingerprint,
        eligibleSubtotal: eligibleSubtotal,
        garmentHandles: garmentHandles,
        rushLineKey: rushLineKey,
        rushTierLabel: rushTierLabel,
        itemCount: cart.item_count || 0,
      };
    } catch (e) {
      return empty;
    }
  }

  /* ==========================================================================
     === DELIVERY SPEED PICKER (cart-level rush fee) ==========================

     The AI Designer wizard has always let a customer pick a production
     speed (Standard / Rush 5-day / Same day) and pay a % surcharge for it —
     but only inside the wizard. A customer buying through a plain product
     page (size-quantity-matrix.liquid) or the order-builder flow had no way
     to pay for rush production at all. This renders the same choice
     directly in the cart (page + drawer) so it's available regardless of
     how items got added, computed against the cart's actual subtotal at
     the moment of picking — same one-time-computed-at-selection behaviour
     the wizard already has, not a live auto-recompute-on-drift system (the
     wizard doesn't have that either; a customer can just re-pick a tier
     any time to refresh the amount against their current cart).
     ========================================================================== */

  // Keep in sync with DEFAULTS.rushOptions / rushFeeVariantId in the AI
  // Designer wizard IIFE above — same fee product, same tiers. Duplicated
  // rather than shared because the wizard and this cart-display IIFE are
  // separate closures in the same file.
  const RUSH_FEE_VARIANT_ID = 52233959833902;
  const RUSH_TIERS = [
    { key: "standard", label: "Standard production", desc: (window.FT_PRODUCTION && window.FT_PRODUCTION.standard) || "10–14 business days", surchargePct: 0 },
    { key: "rush_5", label: "Rush production", desc: "Ready within 5 business days", surchargePct: 25, minFee: 50, maxFee: 750 },
    { key: "same_day", label: "Same day print + ship", desc: "Order before 11am, ships same day", surchargePct: 75, minFee: 50, maxFee: 750 }
  ];

  function computeRushFeeDollars(tier, subtotalCents) {
    if (!tier || tier.surchargePct === 0) return { fee: 0, hitMin: false, hitMax: false };
    let fee = (subtotalCents / 100) * tier.surchargePct / 100;
    let hitMin = false, hitMax = false;
    if (tier.minFee != null && fee < tier.minFee) { fee = tier.minFee; hitMin = true; }
    if (tier.maxFee != null && fee > tier.maxFee) { fee = tier.maxFee; hitMax = true; }
    return { fee: Math.round(fee), hitMin: hitMin, hitMax: hitMax };
  }

  // Same-day needs every garment in the cart to be in-stock/core-range
  // stock — mirrors the wizard's orderIsAllCoreRange/isCoreRange. Cart.js
  // doesn't include product tags, so this fetches each distinct garment
  // product once and caches the result for the page's lifetime.
  const coreRangeCache = {};
  async function checkAllCoreRange(handles) {
    if (!handles.length) return false;
    const results = await Promise.all(handles.map(function (handle) {
      if (coreRangeCache[handle] !== undefined) return Promise.resolve(coreRangeCache[handle]);
      return fetch("/products/" + handle + ".js", { credentials: "same-origin" })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          const tags = (p && p.tags ? p.tags : []).toString().toLowerCase();
          const isCore = /core.?range|fast.?ship|in.?stock/.test(tags);
          coreRangeCache[handle] = isCore;
          return isCore;
        })
        .catch(function () { return false; });
    }));
    return results.every(Boolean);
  }

  const SPEED_PICKER_ATTR = "data-ow-speed-picker";
  let speedPickerBusy = false;

  async function applyDeliverySpeed(tierKey, rushData) {
    if (speedPickerBusy) return;
    speedPickerBusy = true;
    try {
      const tier = RUSH_TIERS.find(function (t) { return t.key === tierKey; });
      if (!tier) return;
      const result = computeRushFeeDollars(tier, rushData.eligibleSubtotal);
      const feeDollars = result.fee;

      if (feeDollars <= 0) {
        // Standard (or a tier that rounds to $0) — remove any existing rush line.
        if (rushData.rushLineKey) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: rushData.rushLineKey, quantity: 0 }),
          });
        }
      } else {
        const props = {
          "Production speed": tier.label,
          "Surcharge": tier.surchargePct + "% of order" +
            (result.hitMin ? " (min $" + tier.minFee + ")" : result.hitMax ? " (capped at $" + tier.maxFee + ")" : "")
        };
        if (rushData.rushLineKey) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: rushData.rushLineKey, quantity: feeDollars, properties: props }),
          });
        } else {
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: [{ id: RUSH_FEE_VARIANT_ID, quantity: feeDollars, properties: props }] }),
          });
        }
      }
      // Refresh both the drawer and page renderings, then re-run our own
      // refresh() so the summary row + picker reflect the new cart state.
      document.dispatchEvent(new Event("cart:build"));
      await refresh();
    } catch (e) {
      console.warn("[OrderWizard] delivery speed selection failed", e);
    } finally {
      speedPickerBusy = false;
    }
  }

  function speedPickerMarkup(rushData, allCoreRange) {
    const subtotal = rushData.eligibleSubtotal;
    const selectedLabel = rushData.rushTierLabel;
    let html = '<div class="ow-speed-picker__title">How fast do you need it?</div><div class="ow-speed-picker__opts">';
    RUSH_TIERS.forEach(function (tier) {
      const result = computeRushFeeDollars(tier, subtotal);
      const isSelected = selectedLabel
        ? selectedLabel === tier.label
        : tier.key === "standard"; // no rush line yet — Standard reads as the current state
      const lockedForCoreOnly = tier.key === "same_day" && !allCoreRange;
      let priceHtml;
      if (tier.surchargePct === 0) {
        priceHtml = '<span class="ow-speed-opt__free">FREE</span>';
      } else {
        let note = '(' + tier.surchargePct + '%)';
        if (result.hitMin) note = '($' + tier.minFee + ' minimum)';
        else if (result.hitMax) note = '(capped at $' + tier.maxFee + ')';
        priceHtml = '<span class="ow-speed-opt__price">+$' + result.fee.toFixed(2) + '</span><span class="ow-speed-opt__note">' + note + '</span>';
      }
      html +=
        '<button type="button" class="ow-speed-opt' + (isSelected ? ' is-selected' : '') + (lockedForCoreOnly ? ' is-disabled' : '') + '"' +
          (lockedForCoreOnly ? ' disabled' : '') +
          ' data-ow-speed-key="' + tier.key + '">' +
          '<span class="ow-speed-opt__body"><span class="ow-speed-opt__label">' + tier.label + '</span>' +
          '<span class="ow-speed-opt__desc">' + (lockedForCoreOnly ? "Core range only — pick in-stock items to unlock" : tier.desc) + '</span></span>' +
          '<span class="ow-speed-opt__pricecol">' + priceHtml + '</span>' +
        '</button>';
    });
    html += '</div>';
    // Audit fix 2026-09-07: a route for customers with a fixed date, before they pay.
    const fp = window.FT_PRODUCTION || {};
    html += '<p class="ow-speed-picker__deadline">' + (fp.deadline || "Need it by a particular date? Tell us the date before you pay and we'll confirm we can meet it.") + ' <a href="' + (fp.contactUrl || '/pages/contact') + '">Contact us</a></p>';
    return html;
  }

  function bindSpeedPicker(container, rushData) {
    container.querySelectorAll("[data-ow-speed-key]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        if (btn.disabled) return;
        applyDeliverySpeed(btn.getAttribute("data-ow-speed-key"), rushData);
      });
    });
  }

  // Injects (or updates) the picker into a given cart <form>, right above
  // its checkout button. Works for both #CartPageForm and #CartDrawerForm —
  // they're genuinely separate DOM trees in this theme (not one driving the
  // other), so each needs its own copy.
  async function renderSpeedPickerInto(form, rushData, allCoreRange) {
    if (!form) return;
    const checkoutBtn = form.querySelector(".cart__checkout");
    if (!checkoutBtn) return;
    let container = form.querySelector("[" + SPEED_PICKER_ATTR + "]");
    if (!container) {
      container = document.createElement("div");
      container.setAttribute(SPEED_PICKER_ATTR, "1");
      const anchor = checkoutBtn.closest(".cart__checkout-wrapper") || checkoutBtn;
      anchor.parentNode.insertBefore(container, anchor);
    }
    container.innerHTML = speedPickerMarkup(rushData, allCoreRange);
    bindSpeedPicker(container, rushData);
  }

  async function renderSpeedPicker(rushData) {
    if (!rushData.itemCount || rushData.eligibleSubtotal <= 0) {
      document.querySelectorAll("[" + SPEED_PICKER_ATTR + "]").forEach(function (el) {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
      return;
    }
    const allCoreRange = await checkAllCoreRange(rushData.garmentHandles);
    renderSpeedPickerInto(document.querySelector("#CartPageForm"), rushData, allCoreRange);
    renderSpeedPickerInto(document.querySelector("#CartDrawerForm"), rushData, allCoreRange);
  }

  /**
   * Print add-on rows (Large Front Print / Back Print / Front Left Chest)
   * are pushed as separate line items whose qty must match the garment
   * count. If a customer edits or removes the qty in the cart, their
   * order goes out of sync. Lock the qty stepper + Remove button on
   * those rows and show a small "🔒 Locked by wizard" indicator so it's
   * visible what's happening.
   */
  function lockPrintRows(handles) {
    if (!handles || !handles.length) return;
    handles.forEach(function (handle) {
      const links = document.querySelectorAll('a[href*="/products/' + handle + '"]');
      links.forEach(function (a) {
        const row = a.closest(
          'tr, li, [data-cart-item], [class*="cart__item"], [class*="cart-item"],' +
          '[class*="cart_item"], [class*="line-item"], [class*="line__item"],' +
          '[class*="line_item"], [class*="CartItem"]'
        );
        if (!row || row.getAttribute("data-ow-print-locked") === "1") return;
        row.setAttribute("data-ow-print-locked", "1");

        // Hide qty controls — cover as many theme conventions as possible.
        row.querySelectorAll(
          '[class*="js-qty"], [class*="quantity-selector"], [class*="quantity_selector"],' +
          '[class*="quantity-input"], [class*="qty-input"], [class*="qty__wrapper"],' +
          'input[type="text"][id^="cart_updates_"], input[type="number"][name*="quantity" i],' +
          '[data-cart-quantity], [data-quantity-input]'
        ).forEach(function (el) { el.style.display = "none"; });

        // Hide remove links + close buttons.
        row.querySelectorAll(
          'a[href*="/cart/change"], a[href*="quantity=0"], button[name="remove" i],' +
          '[data-cart-remove], [class*="cart__remove"], [class*="cart-remove"]'
        ).forEach(function (el) { el.style.display = "none"; });
        row.querySelectorAll('a, button').forEach(function (el) {
          const txt = (el.textContent || "").trim().toLowerCase();
          if (txt === "remove" || txt === "x" || txt === "×") el.style.display = "none";
        });

        // Inject a small lock indicator so customers understand the row
        // exists but isn't editable. Placed near where the qty control was.
        const marker = document.createElement("div");
        marker.setAttribute("data-ow-print-lock", "1");
        marker.style.cssText =
          "display:inline-flex;align-items:center;gap:6px;background:#f5f5f5;color:#5a5a5a;" +
          "padding:6px 12px;border-radius:100px;font:600 11px/1 inherit;letter-spacing:.04em;" +
          "margin-top:8px;white-space:nowrap;max-width:100%";
        marker.innerHTML =
          '<span aria-hidden="true">🔒</span>' +
          '<span>Locked to your garment count</span>';
        // Try to place it next to a hidden qty control's parent so it stays
        // in the cart-row action column visually.
        const anchor =
          row.querySelector('[class*="js-qty"], [class*="quantity"], [class*="qty"]') ||
          row.querySelector(".ink-nothing");
        if (anchor && anchor.parentNode) {
          anchor.parentNode.appendChild(marker);
        } else {
          row.appendChild(marker);
        }
      });
    });
  }

  /**
   * Classifies a print add-on line item into one of three canonical types
   * based on handle + title. Returns "front" | "large_front" | "back" | null.
   * Wizard-added items already have this in item.properties["Order reference"];
   * classic-flow items don't, so we do handle/title matching as fallback.
   */
  function printTypeFor(item) {
    // Classify by handle + title ONLY. The "Order reference" property is
    // deliberately excluded: some flows write a COMBINED reference listing
    // every placement in the order ("Front Left Chest [...], Back Full
    // Size [...]"), which poisoned classification — a Front Chest print
    // line was read as "back" because its reference mentioned the back
    // print too.
    const bag = (String(item.handle || item.product_handle || "") + " " +
                 String(item.title || "")).toLowerCase();
    // Order matters: check "back" FIRST. "Back Centre Full size print"
    // contains both "back" and "full size" — the back token must win, or
    // the back print gets misclassified as large_front.
    if (/back/.test(bag)) return "back";
    if (/large[-\s]?front|full[-\s]?front|full[-\s]?size/.test(bag)) return "large_front";
    if (/chest|left[-\s]?chest|front[-\s]?left|front[-\s]?centre|front[-\s]?center|\bfront\b/.test(bag)) return "front";
    return null;
  }

  /**
   * Cart reconciler — the real bug fix.
   *
   * Symptom: customer ordered 2 shirts, got charged for only 1 print. Root
   * cause: print add-on qty is a SNAPSHOT taken at wizard/matrix add-to-cart
   * time. If the customer then goes back to a PDP and adds another shirt
   * (or bumps the qty on an existing shirt), the print line item doesn't
   * follow. lockPrintRows() prevents the CUSTOMER from editing print qty
   * but does nothing when shirt qty changes.
   *
   * Fix: read /cart.js, sum shirt qty by "which print type is included"
   * property, compare against each matching print add-on's line-item qty,
   * fire /cart/change.js when they diverge. Runs on every poll.
   *
   * Idempotent — once qtys match, subsequent runs find no drift and do
   * nothing. Guarded by a flag to prevent concurrent runs racing.
   */
  let reconciling = false;
  let lastReconcileNote = "";
  async function reconcilePrintAddOns() {
    if (reconciling) return null;
    reconciling = true;
    try {
      const r = await fetch("/cart.js", { headers: { "Accept": "application/json" } });
      if (!r.ok) return null;
      const cart = await r.json();
      const items = cart.items || [];

      // Sum shirt quantity per print type. Two flows mark shirts differently:
      //   1. Wizard/matrix flow — 'Front print' / 'Large Front print' /
      //      'Back print' props set to "Included".
      //   2. Artwork-upload (PDP) flow — 'Front artwork' / 'Back artwork'
      //      props set to "✓ Received ...". These don't say WHICH front
      //      placement (chest vs large front), so they're counted into
      //      per-SIDE pools and matched against whatever front/back print
      //      line exists in the cart.
      const shirtQty = { front: 0, large_front: 0, back: 0 };
      const artworkQty = { frontSide: 0, backSide: 0 };
      items.forEach(function (item) {
        if (isPrintAddOnItem(item)) return;
        if (item.handle === RUSH_HANDLE || item.product_handle === RUSH_HANDLE) return;
        const props = item.properties || {};
        const qty = item.quantity || 0;
        let flagged = false;
        if (String(props["Front print"] || "").toLowerCase() === "included") { shirtQty.front += qty; flagged = true; }
        if (String(props["Large Front print"] || "").toLowerCase() === "included") { shirtQty.large_front += qty; flagged = true; }
        if (String(props["Back print"] || "").toLowerCase() === "included") { shirtQty.back += qty; flagged = true; }
        // Artwork-flow fallback — only when the Included flags are absent,
        // so a shirt is never double-counted across both schemes.
        //
        // Bug: print-location-selector.liquid — the real, current source of
        // truth for both the wizard/matrix AND the order-builder flows —
        // writes a SEPARATE "Large Front artwork" property, distinct from
        // "Front artwork". This only ever checked "Front artwork", so any
        // shirt with ONLY a Large Front print selected (no chest print)
        // looked like it had zero front-side artwork, and its legitimate
        // large_front print add-on was flagged as "orphaned" — a false
        // "Cart needs review" warning on every such order. Chest and Large
        // Front intentionally share one "frontSide" pool (see
        // frontSideLineCount below), so this checks either property but
        // still adds qty only once per shirt, not twice, if a shirt
        // somehow has both.
        if (!flagged) {
          if (props["Front artwork"] || props["Large Front artwork"]) artworkQty.frontSide += qty;
          if (props["Back artwork"]) artworkQty.backSide += qty;
        }
      });

      // Group print add-on line items by type.
      const printsByType = { front: [], large_front: [], back: [] };
      items.forEach(function (item) {
        if (!isPrintAddOnItem(item)) return;
        const t = printTypeFor(item);
        if (t && printsByType[t]) printsByType[t].push(item);
      });

      // Work out required updates. Rules:
      //   1. If shirts+prints both zero → nothing to do.
      //   2. If shirts want a print but no print line exists → warn (customer
      //      to add it) — the reconciler doesn't add new lines.
      //   3. If print lines exist but no shirt claims that print type → WARN
      //      only, NEVER delete. This is the guard against wiping prints when
      //      a customer lowers a shirt line to zero, or when their shirt was
      //      added without the "Included" property (classic PDP flow).
      //   4. If exactly one print line + shirts want that type + qty differs
      //      → sync (never below 1).
      //   5. Multiple print lines summing wrong → warn only.
      // How many print LINES exist per side — used to decide when the
      // per-side artwork pool can safely drive an auto-sync. If the front
      // side has BOTH a chest line and a large-front line, syncing each to
      // the full pool would double the prints, so we only auto-sync when a
      // side has exactly one print line.
      const frontSideLineCount = printsByType.front.length + printsByType.large_front.length;

      const updates = [];
      const warnings = [];
      ["front", "large_front", "back"].forEach(function (t) {
        const expected = shirtQty[t];
        const lines = printsByType[t];
        const sideArtwork = (t === "back") ? artworkQty.backSide : artworkQty.frontSide;
        const sideLineCount = (t === "back") ? printsByType.back.length : frontSideLineCount;

        const label = t.replace("_", " ");

        if (expected === 0 && lines.length === 0) return;
        if (expected > 0 && lines.length === 0) {
          warnings.push({
            text: expected + " of your shirts " + (expected === 1 ? "is" : "are") + " set up for a " + label +
              " print, but that print isn't in your cart. Go back and add it, or that shirt will print blank there.",
            removeKeys: null
          });
          return;
        }
        if (expected === 0 && lines.length > 0) {
          // No Included-flag shirts for this type. Check the artwork-flow
          // pool before assuming the prints are orphaned — PDP-flow shirts
          // mark artwork per SIDE, not per placement.
          if (sideArtwork > 0) {
            if (lines.length === 1 && sideLineCount === 1) {
              // Exactly one print line on this side + artwork shirts exist:
              // safe to sync qty to the shirt count (never below 1).
              const target = Math.max(1, sideArtwork);
              if (lines[0].quantity !== target) {
                updates.push({ key: lines[0].key, from: lines[0].quantity, to: target, type: t, title: lines[0].title });
              }
              // Matching or synced — no warning either way.
              return;
            }
            // Multiple lines on the side — auto-sync would be ambiguous.
            // Only warn if the counts actually disagree.
            const actual = lines.reduce(function (s, l) { return s + (l.quantity || 0); }, 0);
            if (actual === sideArtwork) return; // consistent — leave it alone
            warnings.push({
              text: "Your " + label + " print quantity (" + actual + ") doesn't match your shirt count (" + sideArtwork +
                "). Please check your cart before ordering.",
              removeKeys: null
            });
            return;
          }
          // Genuinely orphaned prints — warn, never delete automatically.
          // Safe to offer a one-click removal here specifically: zero shirts
          // claim this print type by any signal (Included flag or artwork),
          // so there's nothing a removal could be wrongly taking away from.
          const actual = lines.reduce(function (s, l) { return s + (l.quantity || 0); }, 0);
          warnings.push({
            text: "You have " + actual + " " + label + " print(s) in your cart that aren't linked to any shirt " +
              "— they won't be printed on anything as-is. Remove them below, or go back and pick a shirt for this print.",
            removeKeys: lines.map(function (l) { return l.key; })
          });
          return;
        }
        if (lines.length === 1) {
          const line = lines[0];
          // MIXED-FLOW carts: wizard shirts carry Included flags, PDP shirts
          // carry artwork props. Both can coexist in one cart, and the print
          // line covers BOTH. Only counting the Included shirts here shrank a
          // legitimate 10-print line down to 4 in testing (undercharge bug).
          // When this is the side's ONLY print line, the artwork pool
          // unambiguously belongs to it — add it to the target.
          let target;
          if (sideArtwork > 0 && sideLineCount > 1) {
            // Ambiguous: artwork shirts exist but multiple print lines share
            // the side — auto-sync could double-count. Leave qty alone; the
            // multi-line consistency warning below (per side) covers gross
            // mismatches.
            target = null;
          } else {
            target = expected + (sideLineCount === 1 ? sideArtwork : 0);
          }
          if (target !== null) {
            // Never go below 1 — the reconciler is not a deletion tool.
            target = Math.max(1, target);
            if (line.quantity !== target) {
              updates.push({ key: line.key, from: line.quantity, to: target, type: t, title: line.title });
            }
          }
        } else if (lines.length > 1) {
          const actual = lines.reduce(function (s, l) { return s + (l.quantity || 0); }, 0);
          // Compare against Included + artwork shirts so mixed carts don't
          // trigger false warnings.
          const totalExpected = expected + sideArtwork;
          if (actual !== totalExpected) {
            warnings.push({
              text: "Your " + label + " print has multiple cart lines totalling " + actual + ", but your shirts need " +
                totalExpected + ". Please check your cart before ordering.",
              removeKeys: null
            });
          }
        }
      });

      // Apply updates. Sequential to avoid Shopify cart race conditions
      // (cart mutations can conflict when fired in parallel). Extra defensive:
      // filter out any qty ≤ 0 that snuck through — /cart/change.js treats 0
      // as "remove entirely" which is exactly what caused the last bug.
      const safeUpdates = updates.filter(function (u) { return u.to >= 1; });
      for (let i = 0; i < safeUpdates.length; i++) {
        const u = safeUpdates[i];
        try {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
            body: JSON.stringify({ id: u.key, quantity: u.to }),
            credentials: "same-origin",
          });
        } catch (e) { /* keep going, we'll retry on next poll */ }
      }

      // Show the customer a friendly toast when we corrected something.
      // Only fire once per change so we don't spam on every 2s poll.
      if (safeUpdates.length) {
        const note = safeUpdates.map(function (u) { return u.title + ": " + u.from + " → " + u.to; }).join(" | ");
        if (note !== lastReconcileNote) {
          lastReconcileNote = note;
          showReconcileToast("Prints updated to match your shirt count. Refreshing cart…", safeUpdates);
        }
      }
      // Same idea for warnings — one warning at a time.
      if (warnings.length && !safeUpdates.length) {
        const wnote = warnings.map(function (w) { return w.text; }).join(" · ");
        if (wnote !== lastReconcileNote) {
          lastReconcileNote = wnote;
          showReconcileWarning(warnings);
        }
      }
      return { updates: safeUpdates, warnings: warnings };
    } catch (e) {
      return null;
    } finally {
      reconciling = false;
    }
  }

  // In-page toast that surfaces "we fixed your cart" messages. Fixed-position
  // top-right, auto-dismisses after 6s, single instance at a time.
  const TOAST_ATTR = "data-ow-print-reconcile-toast";
  function showReconcileToast(message, updates) {
    removeReconcileToast();
    const el = document.createElement("div");
    el.setAttribute(TOAST_ATTR, "1");
    el.style.cssText =
      "position:fixed;top:20px;right:20px;z-index:99999;max-width:320px;" +
      "background:#065f46;color:#fff;padding:14px 16px;border-radius:10px;" +
      "font:14px/1.4 system-ui,-apple-system,sans-serif;box-shadow:0 8px 24px rgba(0,0,0,0.2);" +
      "animation:ow-toast-in 0.3s ease-out";
    let detail = "";
    if (updates && updates.length) {
      detail = "<div style='font-size:12px;opacity:0.85;margin-top:6px'>" +
        updates.map(function (u) {
          return "• " + (u.title || u.type) + ": qty " + u.from + " → " + u.to;
        }).join("<br>") +
        "</div>";
    }
    el.innerHTML =
      '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px">' +
        '<div><strong>Cart updated</strong><div style="font-size:13px;opacity:0.95;margin-top:2px">' + message + '</div>' + detail + '</div>' +
        '<button type="button" style="background:none;border:none;color:#fff;font-size:18px;cursor:pointer;padding:0;line-height:1" aria-label="Dismiss">×</button>' +
      '</div>';
    el.querySelector("button").addEventListener("click", removeReconcileToast);
    document.body.appendChild(el);
    // Force a cart refresh in themes that don't observe /cart/change.js.
    // Many ajax cart drawers re-poll on 'cart:refresh' or 'cart:updated' events.
    ["cart:refresh", "cart:updated", "cart:change"].forEach(function (name) {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
    });
    // Reload the cart page if we're on /cart so the qty displays update.
    setTimeout(function () {
      if (location.pathname === "/cart") location.reload();
    }, 800);
    setTimeout(removeReconcileToast, 6000);
  }
  // One-click fix for the one warning type that's safe to auto-resolve
  // (genuinely orphaned prints — see the "Genuinely orphaned" branch above).
  // Zeroes each affected line via /cart/change.js (Shopify's own remove-by-
  // quantity-0 convention, same one the reconciler's main sync loop uses),
  // then reloads so the customer sees their now-clean cart immediately.
  async function removeOrphanedPrintLines(keys, btn) {
    if (btn) { btn.disabled = true; btn.textContent = "Removing…"; }
    try {
      for (let i = 0; i < keys.length; i++) {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ id: keys[i], quantity: 0 }),
          credentials: "same-origin",
        });
      }
    } catch (e) { /* fall through to reload regardless — worst case they see it's still there */ }
    lastReconcileNote = "";
    location.reload();
  }
  function showReconcileWarning(warnings) {
    removeReconcileToast();
    const el = document.createElement("div");
    el.setAttribute(TOAST_ATTR, "1");
    el.style.cssText =
      "position:fixed;top:20px;right:12px;left:12px;margin:0 auto;max-width:340px;z-index:99999;" +
      "background:#b45309;color:#fff;padding:14px 16px;border-radius:10px;" +
      "font:14px/1.4 system-ui,-apple-system,sans-serif;box-shadow:0 8px 24px rgba(0,0,0,0.2)";
    const removableKeys = warnings.filter(function (w) { return w.removeKeys && w.removeKeys.length; })
      .reduce(function (all, w) { return all.concat(w.removeKeys); }, []);
    el.innerHTML =
      '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px">' +
        '<div><strong>A couple of things to check in your cart</strong><div style="font-size:13px;opacity:0.95;margin-top:4px">' +
          warnings.map(function (w) { return "• " + w.text; }).join("<br>") +
        '</div>' +
        (removableKeys.length
          ? '<button type="button" data-ow-remove-orphans style="margin-top:10px;background:#fff;color:#92400e;border:none;' +
            'border-radius:6px;padding:8px 12px;font-size:13px;font-weight:600;cursor:pointer">Remove ' +
            (removableKeys.length === 1 ? "this print" : "these prints") + " for me</button>"
          : "") +
        '</div>' +
        '<button type="button" style="background:none;border:none;color:#fff;font-size:18px;cursor:pointer;padding:0;line-height:1" aria-label="Dismiss">×</button>' +
      '</div>';
    el.querySelector('button[aria-label="Dismiss"]').addEventListener("click", removeReconcileToast);
    const removeBtn = el.querySelector("[data-ow-remove-orphans]");
    if (removeBtn) {
      removeBtn.addEventListener("click", function () { removeOrphanedPrintLines(removableKeys, removeBtn); });
    }
    document.body.appendChild(el);
  }
  function removeReconcileToast() {
    document.querySelectorAll("[" + TOAST_ATTR + "]").forEach(function (n) {
      if (n.parentNode) n.parentNode.removeChild(n);
    });
  }

  function removeExistingSummary() {
    document.querySelectorAll("[" + SUMMARY_ATTR + "]").forEach(function (el) {
      if (el.parentNode) el.parentNode.removeChild(el);
    });
  }

  function escapeHtml(s) {
    return String(s || "").replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  // Renders replacement summary rows for anything the consolidator hid, so
  // customers can actually SEE what's in their cart. Two categories:
  //   1. Rush fee — one aggregate row (all rush items summed)
  //   2. Gang sheet — ONE ROW PER SHEET so multi-sheet orders stay legible
  //
  // Without these rows, the raw $1×N lines get hidden by injectHideStyles
  // and the customer sees an empty cart with a mystery total. That was the
  // bug Roland flagged.
  function renderSummary(rushCents, gangSheets) {
    removeExistingSummary();
    const container = findCartContainer();
    if (!container) return;
    let rendered = 0;

    // Rush fee row (single aggregate)
    if (rushCents && rushCents > 0) {
      const row = document.createElement("div");
      row.setAttribute(SUMMARY_ATTR, "1");
      row.innerHTML =
        '<span class="ow-rush-summary__label">' +
          '<span class="ow-rush-summary__icon" aria-hidden="true">⚡</span>' +
          '<span>Rush production fee' +
            '<span class="ow-rush-summary__sub">Chosen delivery speed</span>' +
          '</span>' +
        '</span>' +
        '<span class="ow-rush-summary__price">' + formatMoney(rushCents) + '</span>';
      container.appendChild(row);
      rendered++;
    }

    // Gang sheet rows (one per sheet)
    (gangSheets || []).forEach(function (g, i) {
      const dimensions = [g.sheetWidth, g.sheetLength].filter(Boolean).join(" × ");
      const sizePart = dimensions ? dimensions : "Custom size";
      const designsPart = g.uniqueDesigns
        ? g.uniqueDesigns + " design" + (g.uniqueDesigns === "1" ? "" : "s")
        : "";
      const transfersPart = g.totalTransfers
        ? g.totalTransfers + " transfer" + (g.totalTransfers === "1" ? "" : "s")
        : "";
      const subParts = [designsPart, transfersPart, g.rate].filter(Boolean);
      const sub = subParts.length
        ? '<span class="ow-rush-summary__sub">' + escapeHtml(subParts.join(" · ")) + '</span>'
        : '';
      const heading = "DTF Gang Sheet — " + escapeHtml(sizePart);
      // Price to show: prefer the display label ("$329.45 inc. GST") that
      // the builder captured. Falls back to inc-GST cents, then raw line
      // price if the property is missing (old orders before this shipped).
      let priceHtml;
      if (g.displayLabel) {
        priceHtml = escapeHtml(g.displayLabel);
      } else if (g.displayCents > 0) {
        priceHtml = formatMoney(g.displayCents);
      } else {
        priceHtml = formatMoney(g.priceCents || 0);
      }
      const row = document.createElement("div");
      row.setAttribute(SUMMARY_ATTR, "gang-sheet");
      row.setAttribute("data-ow-gs-index", String(i));
      row.innerHTML =
        '<span class="ow-rush-summary__label">' +
          '<span class="ow-rush-summary__icon" aria-hidden="true">🖶</span>' +
          '<span>' + heading + sub + '</span>' +
        '</span>' +
        '<span class="ow-rush-summary__price">' + priceHtml + '</span>';
      container.appendChild(row);
      rendered++;
    });

    return rendered;
  }

  // Guards against overlapping refresh() calls (the 2s interval and the
  // MutationObserver below can both fire close together) and against
  // refresh() re-triggering itself. Its own output (the "Cart needs review"
  // warning banner, the rush/gang-sheet summary rows) is DOM this same
  // MutationObserver watches, and that output routinely contains the
  // literal word "cart", which is exactly what the observer's own trigger
  // regex matches. Without disconnecting while refresh() mutates the DOM,
  // that creates an unbounded loop — refresh injects a banner -> observer
  // sees it -> calls refresh again -> repeat — hammering /cart.js and
  // blocking real checkouts. Disconnecting during the mutating work (and
  // reconnecting after) closes this off regardless of which exact string
  // is matching, rather than trying to patch the regex or allowlist
  // specific markup.
  let refreshRunning = false;
  async function refresh() {
    if (refreshRunning) return;
    refreshRunning = true;
    obs.disconnect();
    try {
      // Always run row hiding — cheap and catches cases where the CSS :has()
      // selector doesn't match the theme's class names.
      hideConsolidatedRowsViaJs();
      // Reconcile print add-on qty against live shirt count BEFORE we compute
      // fingerprint — if the reconciler applied any updates, the cart will
      // change and the next poll picks up the new state. Awaited so lock/summary
      // render below sees the reconciled cart.
      await reconcilePrintAddOns();
      const result = await fetchRushTotal();
      // Print add-on locking runs every poll because rows can be re-injected
      // by ajax cart drawers between polls.
      lockPrintRows(result.printHandles);
      if (result.fingerprint !== lastCartFingerprint) {
        lastCartFingerprint = result.fingerprint;
        renderSummary(result.total, result.gangSheets);
        await renderSpeedPicker(result);
      }
    } finally {
      refreshRunning = false;
      obs.observe(document.body, { childList: true, subtree: true });
    }
  }

  function start() {
    injectHideStyles();
    refresh();
    // Poll every 2s for cart changes that ajax-cart drawers cause without
    // a hard page reload. Cheap enough — just one /cart.js call.
    if (pollInterval) clearInterval(pollInterval);
    pollInterval = setInterval(refresh, 2000);
  }

  // Watch for cart drawer opening or any DOM injection of rush-fee rows
  // and re-run the consolidator. Most themes re-render the cart on add/
  // remove, so this also catches the case where the page hasn't reloaded.
  const obs = new MutationObserver(function (mutations) {
    let cartChanged = false;
    mutations.forEach(function (m) {
      m.addedNodes && m.addedNodes.forEach(function (n) {
        if (n.nodeType !== 1) return;
        // Cheap heuristic — only re-check when something cart-y was added
        const txt = n.outerHTML || "";
        if (/cart|line-item|drawer|rush-order-fee|centre-full-size-print|left-chest-print|full-size-print|dtf-gang-sheet/i.test(txt)) cartChanged = true;
      });
    });
    if (cartChanged) refresh();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      start();
      obs.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    start();
    obs.observe(document.body, { childList: true, subtree: true });
  }
})();


/* ==========================================================================
   === MOBILE WIDGET OVERLAP FIX ============================================

   OptiMonk's floating "10% OFF" tab and Podium's chat bubble are both
   fixed-position, and on phone-width screens they land on top of the order
   wizard (any step — the modal doesn't paint edge-to-edge, so the OptiMonk
   tab still shows through past its right edge) and the real /cart page's
   checkout button. Podium sets its own z-index near the int32 max, so it
   always paints above everything else on the page regardless of our own
   z-index — out-stacking it isn't possible, hiding it while it'd be in the
   way is the only reliable fix. Scoped to mobile widths only: confirmed via
   live testing that neither widget overlaps the wizard at desktop widths.
   ========================================================================== */
(function () {
  const STYLE_ID = "ow-widget-overlap-fix-styles";
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent =
    // Audit fix 2026-09-06: OptiMonk's full-screen campaign (#om-holder-*)
    // was interrupting active orders. Hidden at every width while the order
    // wizard (ow-no-scroll), the inline AI generator (ft-ai-open) or the
    // product-page design step (ft-design-open) is up.
    'body.ow-no-scroll [id^="om-holder"],body.ow-no-scroll .om-holder,' +
    'body.ft-ai-open [id^="om-holder"],body.ft-ai-open .om-holder,' +
    'body.ft-design-open [id^="om-holder"],body.ft-design-open .om-holder' +
    "{display:none!important}" +
    // Audit fix 2026-09-08: the persistent OptiMonk tab + Podium chat
    // bubble hide was mobile-only (max-width:768px), so on desktop the
    // "10% OFF" tab still floated over the open wizard modal. Now applies
    // at every width while the wizard is open.
    'body.ow-no-scroll [class*="om-tab-wrapper"],' +
    "body.ow-no-scroll #podium-bubble" +
    "{display:none!important}" +
    "@media (max-width:768px){" +
    'body.template-cart [class*="om-tab-wrapper"],' +
    "body.template-cart #podium-bubble" +
    "{display:none!important}" +
    "}";
  document.head.appendChild(s);
})();


/* ==========================================================================
   === CORE-RANGE PDP BADGE =================================================

   On product detail pages, inject a "⚡ In stock — ships fast" badge for
   products tagged 'core-range'. Reads the product JSON from Shopify's
   own /products/<handle>.js endpoint so we don't need to scrape Liquid
   variables out of the page.
   ========================================================================== */
(function () {
  // Same collection-scoped-URL fix as the PDP wizard launcher above —
  // /collections/<handle>/products/<handle> is how most real PDP visits
  // arrive, and the anchored ^/products/ check was missing all of them.
  if (!/\/products\/[^/?#]+\/?$/.test(location.pathname)) return;

  const STYLE_ID = "ow-corerange-pdp-styles";
  const BADGE_ATTR = "data-ow-corerange-badge";

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const s = document.createElement("style");
    s.id = STYLE_ID;
    s.textContent =
      '[' + BADGE_ATTR + ']{display:inline-flex;align-items:center;gap:8px;padding:10px 14px;margin:12px 0;background:linear-gradient(135deg,#fff8f0,#fff1e0);border:1px solid #ffd9b3;border-radius:8px;font:13px/1.4 inherit;color:#1a1a1a;animation:ow-cr-in 0.32s cubic-bezier(.2,.7,.3,1)}' +
      '@keyframes ow-cr-in{from{opacity:0;transform:translateY(-3px)}to{opacity:1;transform:translateY(0)}}' +
      '[' + BADGE_ATTR + '] .ow-cr-icon{width:24px;height:24px;border-radius:50%;background:#ff6b00;color:#fff;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700}' +
      '[' + BADGE_ATTR + '] strong{color:#ff6b00;font-weight:700}' +
      '[' + BADGE_ATTR + '] .ow-cr-sub{display:block;font-size:11px;color:#5a5a5a;margin-top:2px}';
    document.head.appendChild(s);
  }

  function findInsertPoint() {
    const targets = [
      'product-form button[name="add"]',
      '[data-product-form] button[name="add"]',
      'form[action*="/cart/add"] button[type="submit"]',
      'button[name="add"]',
      '.product-form__submit',
      '.product__price',
      '[class*="product-price"]'
    ];
    for (let i = 0; i < targets.length; i++) {
      const el = document.querySelector(targets[i]);
      if (el) return el;
    }
    return null;
  }

  function injectBadge() {
    if (document.querySelector("[" + BADGE_ATTR + "]")) return; // already there

    // Read the configured fast-ship colours from the wizard config (loaded
    // globally via this same script) so the badge copy stays in sync with
    // whatever the wizard considers fast-ship.
    let fastColours = "Black";
    try {
      const cfg = window.OrderWizard && window.OrderWizard.config;
      if (cfg && Array.isArray(cfg.coreRangeFastColours) && cfg.coreRangeFastColours.length) {
        fastColours = cfg.coreRangeFastColours.map(function (c) {
          return String(c).charAt(0).toUpperCase() + String(c).slice(1);
        }).join(", ");
      }
    } catch (_) {}
    // Reworded 2026-09-02 (order-path cleanup brief): the old copy ("In
    // stock · ships fast — Black only · ready to print + ship in days")
    // read as a blanket claim, but this badge is injected once at page
    // load and never updates when the customer picks a different colour
    // swatch — so a customer choosing Sage saw the exact same "ships
    // fast" promise as one choosing Black. Leading with the specific
    // colour(s) it actually applies to keeps this honest without needing
    // to wire up live colour-selection tracking.
    // Corrected again 2026-09-03 (Roland): "made to order" was factually
    // wrong — nothing here is custom-manufactured per order. Non-fast
    // colours are ordinary stock Roland orders in from the garment
    // supplier (AS Colour) as needed, which is what actually adds the
    // lead time, not production. Black just tends to already be on hand.
    const rowHTML =
      '<span class="product-info-card__icon" aria-hidden="true">⚡</span>' +
      '<span><strong>' + fastColours + ' ships fastest</strong> — usually in stock. Other colours are ordered in from our supplier as needed, same print quality.</span>';

    // Preferred (2026-09-03, Roland's "a little overwhelming" feedback):
    // snippets/product-template.liquid now renders one consolidated info
    // card with a reserved, empty first row for this exact badge — fill
    // it in place instead of injecting a second, separately-styled box
    // above it. Falls back to the original standalone badge below for any
    // page that renders this script without that card (e.g. a custom
    // template that hasn't picked up the change).
    const cardRow = document.querySelector("[data-info-card-fastship]");
    if (cardRow) {
      cardRow.setAttribute(BADGE_ATTR, "1");
      cardRow.innerHTML = rowHTML;
      cardRow.hidden = false;
      return true;
    }

    const target = findInsertPoint();
    if (!target) return false;
    const anchor = target.closest("form") || target.parentElement;
    if (!anchor || !anchor.parentNode) return false;
    injectStyles();
    const badge = document.createElement("div");
    badge.setAttribute(BADGE_ATTR, "1");
    badge.innerHTML =
      '<div class="ow-cr-icon" aria-hidden="true">⚡</div>' +
      '<div><strong>' + fastColours + ' ships fastest</strong>' +
        '<span class="ow-cr-sub">Usually in stock — other colours ordered in from our supplier as needed</span>' +
      '</div>';
    anchor.parentNode.insertBefore(badge, anchor);
    return true;
  }

  async function check() {
    try {
      const handle = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || "";
      if (!handle) return;
      const resp = await fetch("/products/" + handle + ".js");
      if (!resp.ok) return;
      const product = await resp.json();
      const tags = (product.tags || []).join(",").toLowerCase();
      const isCore = /core.?range|fast.?ship|in.?stock/.test(tags);
      if (!isCore) return;
      // Retry inject up to 20× with a 250ms gap (theme might render the
      // product form lazily).
      let attempts = 0;
      function attempt() {
        if (injectBadge()) return;
        if (++attempts < 20) setTimeout(attempt, 250);
      }
      attempt();
    } catch (e) {
      // Silent — PDP badge is purely cosmetic, don't break anything.
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", check);
  } else {
    check();
  }
})();

/* Audit fix 2026-09-06: the customer arrived from the order wizard's
   "Create a design with AI" — tell them the order is safe and how to get
   back to it (the wizard restores itself on "Put it on a tee"). */
(function () {
  if (!/^\/pages\/(design-with-ai|ai-designer)/i.test(location.pathname)) return;
  let snap = null;
  try { snap = JSON.parse(sessionStorage.getItem("ow_resume") || "null"); } catch (_) {}
  if (!snap || !snap.ts || Date.now() - snap.ts > 6 * 60 * 60 * 1000) return;
  const chat = document.querySelector(".ai-chat");
  if (!chat || !chat.parentNode || document.getElementById("ow-resume-banner")) return;
  const b = document.createElement("div");
  b.id = "ow-resume-banner";
  b.setAttribute("role", "status");
  b.style.cssText = "margin:0 auto 12px;max-width:880px;padding:12px 16px;border-radius:10px;background:#fff7ed;border:1px solid #ff6b00;color:#1a1a1a;font-size:14px;line-height:1.45";
  const title = (snap.selectedProduct && snap.selectedProduct.title) ? String(snap.selectedProduct.title).replace(/[<>&]/g, "") : "your shirts";
  b.innerHTML = "<strong>Your order is saved.</strong> Describe your design below, and when you\u2019re happy tap <strong>Put it on a tee</strong> \u2014 you\u2019ll go straight back to " + title + " with the design already on your prints.";
  chat.parentNode.insertBefore(b, chat);
})();

/* Audit fix 2026-09-07: funnel event when a generated design is chosen for a print. */
document.addEventListener("click", function (ev) {
  const t = ev.target && ev.target.closest && ev.target.closest('[data-action="use-on-page"], [data-ow-action="pick-current"], [data-ow-action="group-pick-current"], [data-ow-action="pick-saved-design"], [data-ow-action="group-pick-saved-design"]');
  if (!t) return;
  window.ftTrack("ai_design_selected", { via: t.getAttribute("data-action") || t.getAttribute("data-ow-action"), location: t.getAttribute("data-location") || "" });
});
